import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, PLATFORM_ID, Optional, APP_INITIALIZER, NgModule, SkipSelf, EventEmitter, Directive, Output, HostListener, forwardRef, Component, Input, ViewChild, Pipe } from '@angular/core';
import { ReplaySubject, of, BehaviorSubject, throwError, Subscription, concat } from 'rxjs';
import { map, tap, timeout, catchError, first, mergeMap, filter, take, distinctUntilChanged, switchMap, publishLast, refCount } from 'rxjs/operators';
import * as i1 from '@angular/common/http';
import { HttpClient, HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { __awaiter, __decorate, __param } from 'tslib';
import * as i1$2 from '@angular/common';
import { isPlatformBrowser, DOCUMENT, APP_BASE_HREF, CommonModule } from '@angular/common';
import * as i1$1 from '@angular/platform-browser';
import * as i2 from '@angular/forms';
import { NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { Md5 } from 'md5-typescript';
import { CookieService } from 'ngx-cookie-service';
export { CookieService } from 'ngx-cookie-service';
import * as i4 from 'primeng/calendar';
import { CalendarModule } from 'primeng/calendar';
import moment from 'moment';
import { NgSelectModule } from '@ng-select/ng-select';
import * as i1$3 from 'ng2-charts';
import { ChartsModule } from 'ng2-charts';
import * as i3 from 'primeng/autocomplete';
import { AutoCompleteModule } from 'primeng/autocomplete';
import get from 'lodash/get';
import merge from 'lodash/merge';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';

const EU_APP_CONFIG = new InjectionToken('Eu App Config');
class AppConfigServiceBase {
    constructor(additionalConfig = {}) {
        this.configSubject = new ReplaySubject();
        this.additionalConfig = additionalConfig;
        const config = this.mergeAdditionalConfig({});
        this.config$ = this.configSubject.asObservable();
        if (this.isValidConfig(config)) {
            this.setConfig(config);
        }
    }
    getConfigValue(key) {
        throw new Error('not implemented');
    }
    setConfigValue(key, newValue) {
        throw new Error('not implemented');
    }
    isValidConfig(config) {
        return (config === null || config === void 0 ? void 0 : config.opCo) != null;
    }
    /**
     * Please note that for applying additional config, only 1 level of nesting is supported.
     * @param config baseConfig
     */
    mergeAdditionalConfig(config) {
        config = Object.assign(config, this.additionalConfig.common || {});
        config = Object.assign(config, this.additionalConfig[config.opCo] || {});
        return config;
    }
    setConfig(config) {
        if (this.isValidConfig(config)) {
            this.config = config;
            this.configSubject.next(config);
        }
        else {
            console.error('Attempt to set an invalid config: ', config);
        }
    }
    buildConfig() {
        return this.getBaseConfig()
            .pipe(map(c => {
            let config = c || {};
            config = this.mergeAdditionalConfig(config);
            return config;
        }), tap(c => {
            this.setConfig(c);
        }));
    }
    initialize() {
        return this.buildConfig().toPromise();
    }
}

class EUAppConfig {
}
function mergeEUAppConfig(configs = []) {
    const mergedConfig = {};
    for (const c of configs) {
        if (c) {
            mergedConfig.common = Object.assign(mergedConfig.common || {}, c.common || {});
            mergedConfig.ACE = Object.assign(mergedConfig.ACE || {}, c.ACE || {});
            mergedConfig.BGE = Object.assign(mergedConfig.BGE || {}, c.BGE || {});
            mergedConfig.ComEd = Object.assign(mergedConfig.ComEd || {}, c.ComEd || {});
            mergedConfig.DPL = Object.assign(mergedConfig.DPL || {}, c.DPL || {});
            mergedConfig.PECO = Object.assign(mergedConfig.PECO || {}, c.PECO || {});
            mergedConfig.PEPCO = Object.assign(mergedConfig.PEPCO || {}, c.PEPCO || {});
        }
    }
    return mergedConfig;
}

/** This class describes the configuration of the hosting EUCOMS site by AppConfigService and can be merged with application local configuration. */
class EUWebConfig {
}

const DEFAULT_CONFIG_ENDPOINT = '/api/Services/MyAccountService.svc/GetConfiguration';
const NO_CONFIG_ENDPOINT = '';
const APP_CONFIG_OPTIONS = new InjectionToken('App Config Options');
/**
 * Note: if you're adding a public method, please add it to @link AppConfigServiceBase.
 */
class AppConfigService extends AppConfigServiceBase {
    constructor(httpClient, options) {
        super(mergeEUAppConfig(Array.isArray(options.additionalConfig)
            ? options.additionalConfig
            : [options.additionalConfig]));
        this.httpClient = httpClient;
        this.options = options;
        if (this.options.configEndpoint == null) {
            this.options.configEndpoint = DEFAULT_CONFIG_ENDPOINT;
        }
    }
    /**
     * Read configuration of the parent EUCOMS site
     */
    getBaseConfig() {
        const callWebService = (this.options.configEndpoint
            && this.options.configEndpoint.length > 0
            && this.options.configEndpoint !== NO_CONFIG_ENDPOINT);
        if (callWebService) {
            return this.httpClient.get(this.options.configEndpoint);
        }
        else {
            console.warn('Deprecated: NO_CONFIG_ENDPOINT usage is deprecated, please use EuCoreServicesModule.mockAppConfigService()');
            return of(new EUWebConfig());
        }
    }
}
AppConfigService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AppConfigService, deps: [{ token: i1.HttpClient }, { token: APP_CONFIG_OPTIONS }], target: i0.ɵɵFactoryTarget.Injectable });
AppConfigService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AppConfigService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AppConfigService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [APP_CONFIG_OPTIONS]
                    }] }];
    } });

class MockAppConfigService extends AppConfigServiceBase {
    constructor(additionalConfig) {
        super(additionalConfig);
    }
    getBaseConfig() {
        return of({}); // mock config is included in the constructor 'additionalConfig'
    }
}
MockAppConfigService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MockAppConfigService, deps: [{ token: EU_APP_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
MockAppConfigService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MockAppConfigService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MockAppConfigService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: EUAppConfig, decorators: [{
                        type: Inject,
                        args: [EU_APP_CONFIG]
                    }] }];
    } });

const SIGNIN_URL = '/Pages/Login.aspx';
const DEFAULT_SESSION_ENDPOINT = '/api/Services/MyAccountService.svc/GetSession';
const NO_SESSION_ENDPOINT = '';
const VIEW_ACCOUNT_ENDPOINT = '/api/Services/AccountList.svc/ViewAccount';
const APP_SESSION_OPTIONS = new InjectionToken('App Session Options');
/** @dynamic */
class AppSessionService {
    constructor(options, document, appConfigService, httpClient, platformId) {
        this.options = options;
        this.document = document;
        this.appConfigService = appConfigService;
        this.httpClient = httpClient;
        this.platformId = platformId;
        if (this.options.sessionEndpoint == null) {
            this.options.sessionEndpoint = DEFAULT_SESSION_ENDPOINT;
        }
        this.sessionSubject = new BehaviorSubject(null);
        this.session$ = this.sessionSubject.asObservable();
    }
    /** @returns cold observable (call to parent site is made when subscribed to) */
    getSession() {
        const callWebService = this.options.sessionEndpoint &&
            this.options.sessionEndpoint.length > 0 &&
            this.options.sessionEndpoint !== NO_SESSION_ENDPOINT;
        let newSession$;
        if (callWebService) {
            newSession$ = this.httpClient.get(this.options.sessionEndpoint);
            //If a timeout is specified and is a valid number...
            if (this.appConfigService.config.getSessionTimeout && !isNaN(Number(this.appConfigService.config.getSessionTimeout))) {
                //Set that as the maximum timeout value.
                const getSessionTimeout = Number(this.appConfigService.config.getSessionTimeout);
                newSession$ = newSession$.pipe(timeout(getSessionTimeout), catchError(() => of({})));
            }
        }
        else {
            newSession$ = of({});
        }
        return newSession$.pipe(map((newSessionValue) => {
            if (this.options.sessionOverrides != null) {
                newSessionValue = Object.assign(newSessionValue, this.options.sessionOverrides);
            }
            return newSessionValue;
        }), tap((newSessionValue) => {
            this.session = newSessionValue;
            this.sessionSubject.next(newSessionValue);
        }));
    }
    /**
     * refresh the session without requiring a subscription or promise structure.
     * this helper method just simplies the updating of session data.
     */
    refreshSession() {
        this.getSession().pipe(first()).subscribe(); // calling for side-effect
    }
    signIn(payload) {
        if (!this.appConfigService.config || !this.appConfigService.config) {
            console.warn('[appSessionService] signIn called before appConfig available.');
            return of({ Location: SIGNIN_URL });
        }
        const authenticationRedirectUrl = this.appConfigService.config
            .authenticationRedirectUrl;
        if (isPlatformBrowser(this.platformId)) {
            const form = this.document.createElement('form');
            form.setAttribute('method', 'post');
            form.setAttribute('action', authenticationRedirectUrl);
            for (const key in payload) {
                if (payload.hasOwnProperty(key)) {
                    const hiddenField = this.document.createElement('input');
                    hiddenField.setAttribute('type', 'hidden');
                    hiddenField.setAttribute('name', key);
                    hiddenField.setAttribute('value', payload[key]);
                    form.appendChild(hiddenField);
                }
            }
            this.document.body.appendChild(form);
            form.submit();
        }
    }
    // this method is called from a APP_INITIALIZER to ensure
    // we have session information *before* the app root module bootstraps
    initialize() {
        return __awaiter(this, void 0, void 0, function* () {
            var session;
            try {
                session = yield this.getSession().toPromise();
            }
            catch (e) {
                if (this.appConfigService.config.initializeOnSessionError) {
                    session = {};
                }
                else
                    throw e;
            }
            if (this.session && this.session.encryptedUsername) {
                sessionStorage.setItem('encryptedUsername', this.session.encryptedUsername);
            }
            else {
                sessionStorage.removeItem('encryptedUsername');
            }
            return session;
        });
    }
    isUserAuthenticated() {
        return this.session && this.session.username != null && this.session.token != null;
    }
    isResidential() {
        return this.session && String(this.session.isResidential).toLowerCase() === 'true';
    }
    /**
     * Updates parent site session to switch to a context of the specified account
     * @param accountNumber
     */
    selectAccount(accountNumber) {
        if (this.isUserAuthenticated()) {
            return this.httpClient
                .post(VIEW_ACCOUNT_ENDPOINT, { accountNumber }) // this call ensures the update of the parent site session
                .pipe(mergeMap(() => this.getSession()), // refresh session
            map((res) => this.session != null), // map to success/failure boolean
            catchError((err) => of(false)));
        }
        else {
            return of(false);
        }
    }
}
AppSessionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AppSessionService, deps: [{ token: APP_SESSION_OPTIONS }, { token: DOCUMENT }, { token: AppConfigService }, { token: i1.HttpClient }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable });
AppSessionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AppSessionService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AppSessionService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [APP_SESSION_OPTIONS]
                    }] }, { type: Document, decorators: [{
                        type: Inject,
                        args: [DOCUMENT]
                    }] }, { type: AppConfigService }, { type: i1.HttpClient }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [PLATFORM_ID]
                    }] }];
    } });

function Dummyable(conf) {
    // tslint:disable-next-line: only-arrow-functions
    return function (targetClass, propertyKey, descriptor) {
        const originalMethod = descriptor.value;
        const config = factoryDummyableConfig(conf);
        if (config.override) {
            descriptor.value = function () {
                // tslint:disable-next-line: no-console
                console.info(`%c [Dummyable] at ${String(propertyKey)}`, 'color: goldenrod');
                this.originalMethod = originalMethod.bind(this);
                const res = config.factoryResponse.bind(this);
                return res();
            };
        }
        return descriptor;
    };
}
function factoryDummyableConfig(conf) {
    if (!conf.hasOwnProperty('override')) {
        conf.override = true;
    }
    if (!conf.hasOwnProperty('factoryResponse')) {
        conf.factoryResponse = () => ({});
    }
    return conf;
}

const APP_ALERTS_OPTIONS = new InjectionToken('App Alerts Options');
// this call is handled at the middleware and is invariant across opcodes
const ALERTS_ENDPOINT = '/api/Services/MyAccountService.svc/GetAllAlerts';
const NO_ALERTS_ENDPOINT = 'dummy_data';
var AlertType;
(function (AlertType) {
    AlertType[AlertType["Global"] = 0] = "Global";
    AlertType[AlertType["CSS"] = 1] = "CSS";
    AlertType[AlertType["Login"] = 2] = "Login";
    AlertType[AlertType["PeakRewards"] = 3] = "PeakRewards";
    AlertType[AlertType["Storm"] = 4] = "Storm";
    AlertType[AlertType["Outage"] = 5] = "Outage";
    AlertType[AlertType["Payment"] = 6] = "Payment";
    AlertType[AlertType["Usage"] = 7] = "Usage";
    AlertType[AlertType["LoginError"] = 8] = "LoginError";
    AlertType[AlertType["Promotion"] = 9] = "Promotion";
})(AlertType || (AlertType = {}));
const defaultDisplayAlerts = [AlertType.Global, AlertType.Login];
var AlertPriority;
(function (AlertPriority) {
    AlertPriority["Error"] = "#FFA1A2";
    AlertPriority["Info"] = "#D0E4F4";
    AlertPriority["Positive"] = "#C5EB93";
    AlertPriority["Warning"] = "#FFE9A7";
    AlertPriority["Storm"] = "#F26529";
})(AlertPriority || (AlertPriority = {}));
const defaultBackgroundColor = AlertPriority.Warning;

/** mockableGetter - AOP decorator to allow override of getAlerts in a fashion that does not pollute the function.
 * options.alertsEndpoint is overridden in the same fashion as other eu core services (to avoid depcreations),
 * and represents either a URL to reach or a flag to not call any AJAX.
 *
 * If the endpoint override is set to the flag, we need to mock the response.
 *
 * otherwise we need to get the alerts using the endpoint that was specified.
 *
 * these methods are used by method decorators in a way that are bound to their target, so `this` is set.
 **/
function mockableGetter$1() {
    if (this.alertsEndpoint === NO_ALERTS_ENDPOINT) {
        if (typeof this.mockedResponse === 'function') {
            return this.mockedResponse();
        }
        return defaultMockedResponse$1();
    }
    else {
        return this.originalMethod();
    }
}
function defaultMockedResponse$1() {
    const alertsResponse = [];
    return of(alertsResponse);
}

/** the following typescript hint is needed to compile the package with ngc  (packager) since we have to modify the prototype */
// @dynamic
class AppAlertsService {
    constructor(http, config, platformId) {
        this.http = http;
        this.config = config;
        this.platformId = platformId;
        this.alerts = [];
        if (AppAlertsService.instance && isPlatformBrowser(platformId)) {
            //Only log an error if client side rendering; SSR will run on the server for each route
            console.error('AppAlertsService is already loaded. Declare it as a parameter in the constructor of a component only');
            return;
        }
        AppAlertsService.instance = this;
        /** split this way to follow the singleton pattern used in eu core services and still allow testing with custom instantiations */
        this._unsafeConstructor(http, config);
    }
    _unsafeConstructor(http, config = {}) {
        if (config.alertsEndpoint == null) {
            config.alertsEndpoint = ALERTS_ENDPOINT;
        }
        AppAlertsService.options = config;
        AppAlertsService.httpClient = http;
        /** must be set on the prototype as it is referenced by the getAlerts decorator's signature */
        const prototype = AppAlertsService.prototype;
        prototype.alertsEndpoint = config.alertsEndpoint;
    }
    initialize() {
        if (this.config.alertsOverrides) {
            this.processAlertsOverrides();
        }
        this.callAndForget();
        return Promise.resolve(true);
    }
    refreshAlerts() {
        this.callAndForget();
    }
    /** this encapsulates getAlerts and subscription setting local alerts property,
     *  to allow call-and-forget behavior (compare to getAlerts) */
    callAndForget() {
        this.getAlerts().subscribe((response) => {
            this.alerts = (Array.isArray(response) && response) || [];
        });
    }
    /** this raw get returns either mocked data (based on env configuration passed in)
     *  or the parsed response */
    getAlerts() {
        return AppAlertsService.httpClient.get(AppAlertsService.options.alertsEndpoint)
            .pipe(catchError(this.handleError('getAlerts')));
    }
    handleError(operation = 'operation', result = []) {
        return (error) => {
            console.warn(`[EUCoreServices:AppAlerts] ${operation} failed: ${error.message}`);
            // Let the app keep running by returning an empty result.
            return of(result);
        };
    }
    /** this function fulfills the pattern of setting up new prooerties in alertsOverides. Each property must be added to the interface.
     * additionally, each property must have an initializer set up in the class follwing the naming convention such that:
     *
     * // for alertsOverrides.someNewOption
     * propertySomeNewOption ()
     */
    processAlertsOverrides() {
        const self = this;
        Object.keys(this.config.alertsOverrides).forEach((key) => {
            const methodSymbol = 'processOverrides' + key.charAt(0).toUpperCase() + key.slice(1);
            if (typeof self[methodSymbol] === 'function') {
                self[methodSymbol]();
            }
        });
    }
    processOverridesAlerts() {
        /** must be set on the prototype as it is referenced by the getAlerts decorator's signature */
        const prototype = AppAlertsService.prototype;
        prototype.mockedResponse = () => this.mockAsResponse(this.config.alertsOverrides.alerts);
    }
    processOverridesFactoryAlerts() {
        /** must be set on the prototype as it is referenced by the getAlerts decorator's signature */
        const prototype = AppAlertsService.prototype;
        prototype.mockedResponse = () => this.mockAsResponse(this.config.alertsOverrides.factoryAlerts());
    }
    mockAsResponse(alerts = []) {
        return of(alerts);
    }
}
AppAlertsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AppAlertsService, deps: [{ token: i1.HttpClient }, { token: APP_ALERTS_OPTIONS }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable });
AppAlertsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AppAlertsService });
__decorate([
    Dummyable({ factoryResponse: mockableGetter$1 })
], AppAlertsService.prototype, "getAlerts", null);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AppAlertsService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [APP_ALERTS_OPTIONS]
                    }] }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [PLATFORM_ID]
                    }] }];
    }, propDecorators: { getAlerts: [] } });

const nullLogger = {
    debug(msg, ...optionalParams) { },
    info(msg, ...optionalParams) { },
    log(msg, ...optionalParams) { },
    warn(msg, ...optionalParams) { },
    error(msg, ...optionalParams) { },
    fatal(msg, ...optionalParams) { },
    clear() { },
};

var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["All"] = 0] = "All";
    LogLevel[LogLevel["Debug"] = 1] = "Debug";
    LogLevel[LogLevel["Info"] = 2] = "Info";
    LogLevel[LogLevel["Warn"] = 3] = "Warn";
    LogLevel[LogLevel["Error"] = 4] = "Error";
    LogLevel[LogLevel["Fatal"] = 5] = "Fatal";
    LogLevel[LogLevel["Off"] = 6] = "Off";
})(LogLevel || (LogLevel = {}));

function cleanStringify(item) {
    if (item && typeof item === 'object') {
        item = copyWithoutCircularReferences([item], item);
    }
    return JSON.stringify(item);
    function copyWithoutCircularReferences(references, object) {
        const cleanObject = {};
        Object.keys(object).forEach(function (key) {
            const value = object[key];
            if (value && typeof value === 'object') {
                if (references.indexOf(value) < 0) {
                    references.push(value);
                    cleanObject[key] = copyWithoutCircularReferences(references, value);
                    references.pop();
                }
                else {
                    cleanObject[key] = '###_Circular_###';
                }
            }
            else if (typeof value === 'function') {
                cleanObject[key] = '###_Function:' + value.name + '_###';
            }
            else {
                cleanObject[key] = value;
            }
        });
        return cleanObject;
    }
}

class LogEntry {
    constructor() {
        // Public properties
        this.entryDate = new Date();
        this.message = '';
        this.level = LogLevel.Debug;
        this.extraInfo = [];
        this.logWithDate = true;
    }
    buildLogString() {
        let ret = '[' + LogLevel[this.level] + '] ';
        if (this.logWithDate) {
            ret += new Date().toLocaleString() + '. ';
        }
        ret += this.message;
        if (this.extraInfo != null
            && this.extraInfo.length > 0) {
            ret += ' - Extra Info: ' + this.formatParams(this.extraInfo);
        }
        return ret;
    }
    formatParams(params) {
        let ret = params.join(',');
        if (params.some(p => typeof p === 'object')) {
            ret = '';
            for (const item of params) {
                ret += cleanStringify(item) + ',';
            }
        }
        return ret;
    }
}

class LogWriter {
    constructor(location) {
        this.location = location || 'eucoms';
    }
}

class ConsoleLogWriter extends LogWriter {
    constructor() {
        super();
    }
    log(record) {
        // Log to the console
        if (record.extraInfo != null && record.extraInfo.length > 0) {
            console.log(record.buildLogString(), record.extraInfo);
        }
        else {
            console.log(record.buildLogString());
        }
        return of(true);
    }
    clear() {
        console.clear();
        return of(true);
    }
}

class LocalStorageLogWriter extends LogWriter {
    constructor(location) {
        super(location);
    }
    getAll() {
        let values;
        // Retrieve all values from local storage
        values = JSON.parse(localStorage.getItem(this.location)) || [];
        return of(values);
    }
    log(record) {
        let ret = true;
        let values;
        try {
            values = JSON.parse(localStorage.getItem(this.location)) || [];
            // Add new log entry to the array
            values.push(record);
            // Store the complete array into local storage
            localStorage.setItem(this.location, cleanStringify(values));
        }
        catch (ex) {
            ret = false;
        }
        return of(ret);
    }
    clear() {
        localStorage.removeItem(this.location);
        return of(true);
    }
}

var LogWriterType;
(function (LogWriterType) {
    LogWriterType[LogWriterType["Console"] = 0] = "Console";
    LogWriterType[LogWriterType["LocalStorage"] = 1] = "LocalStorage"; // LocalStorageLogWriter
})(LogWriterType || (LogWriterType = {}));
class LogWriterConfig {
}
class AppLoggerServiceOptions {
}
const APP_LOGGER_OPTIONS = new InjectionToken('App Logger Options');
const defaultLoggingOptions = {
    level: LogLevel.Off,
    logWithDate: true,
    writers: [
        {
            enabled: true,
            location: 'eucoms',
            type: LogWriterType.Console
        },
        {
            enabled: true,
            location: 'eucoms',
            type: LogWriterType.LocalStorage
        }
    ]
};
class AppLoggerService {
    constructor(options) {
        const writers = [];
        options = options || defaultLoggingOptions;
        if (options != null) {
            for (const writerConfig of options.writers) {
                if (writerConfig.enabled) {
                    switch (writerConfig.type) {
                        case LogWriterType.Console:
                            writers.push(new ConsoleLogWriter());
                            break;
                        case LogWriterType.LocalStorage:
                            writers.push(new LocalStorageLogWriter(writerConfig.location));
                            break;
                        default:
                            break;
                    }
                }
            }
            this.level = options.level;
            this.logWithDate = options.logWithDate;
        }
        this.writers = writers;
    }
    shouldLog(level) {
        return this.writers.length > 0 && this.level !== LogLevel.Off && level >= this.level;
    }
    debug(msg, ...optionalParams) {
        this.writeToLog(msg, LogLevel.Debug, optionalParams);
    }
    info(msg, ...optionalParams) {
        this.writeToLog(msg, LogLevel.Info, optionalParams);
    }
    warn(msg, ...optionalParams) {
        this.writeToLog(msg, LogLevel.Warn, optionalParams);
    }
    error(msg, ...optionalParams) {
        this.writeToLog(msg, LogLevel.Error, optionalParams);
    }
    fatal(msg, ...optionalParams) {
        this.writeToLog(msg, LogLevel.Debug, optionalParams);
    }
    log(msg, ...optionalParams) {
        this.writeToLog(msg, LogLevel.All, optionalParams);
    }
    clear() {
        for (const logger of this.writers) {
            logger.clear();
        }
    }
    writeToLog(msg, level, params) {
        if (this.shouldLog(level)) {
            const entry = new LogEntry();
            entry.message = msg;
            entry.level = level;
            entry.extraInfo = params;
            entry.logWithDate = this.logWithDate;
            // Log the value to all publishers
            for (const logger of this.writers) {
                logger.log(entry).subscribe( /* do nothing */);
            }
        }
    }
}
AppLoggerService.nullLogger = nullLogger;
AppLoggerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AppLoggerService, deps: [{ token: APP_LOGGER_OPTIONS, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
AppLoggerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AppLoggerService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AppLoggerService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: AppLoggerServiceOptions, decorators: [{
                        type: Inject,
                        args: [APP_LOGGER_OPTIONS]
                    }, {
                        type: Optional
                    }] }];
    } });
function getNullLogger() {
    return new AppLoggerService({
        level: LogLevel.All,
        logWithDate: false,
        writers: []
    });
}

const WINDOW = new InjectionToken('Window');
const WINDOW_LOCATION = new InjectionToken('Window Location');
const LOCAL_STORAGE = new InjectionToken('Local Storage');
const SESSION_STORAGE = new InjectionToken('Session Storage');
function getWindow(PLATFORM_ID) {
    if (isPlatformBrowser(PLATFORM_ID)) {
        return window;
    }
}
function getWindowLocation(PLATFORM_ID) {
    if (isPlatformBrowser(PLATFORM_ID)) {
        return window.location;
    }
}
function getLocalStorage(PLATFORM_ID) {
    if (isPlatformBrowser(PLATFORM_ID)) {
        return localStorage;
    }
}
function getSessionStorage(PLATFORM_ID) {
    if (isPlatformBrowser(PLATFORM_ID)) {
        return sessionStorage;
    }
}

const PHITheme = {
    '--primaryBrand': '#170d67'
};

const aceTheme = Object.assign(Object.assign({}, PHITheme), { '--primaryColor1': '#170d67', '--primaryColor2': '#ba1c3d', '--primaryColor3': '#a61936', '--primaryColor4': '#bf002f', '--primaryColor5': '#0059a4', '--primaryColor6': '#103870', '--primaryColor7': '#0091b6', '--primaryColor8': '#78be20', '--primaryColor9': '#ffd100', '--primaryColor10': '#e5eef5' });

const bgeTheme = {
    '--primaryColor1': '#170d67',
    '--primaryColor2': '#ba1c3d',
    '--primaryColor3': '#a61936',
    '--primaryColor4': '#bf002f',
    '--primaryColor5': '#0059a4',
    '--primaryColor6': '#103870',
    '--primaryColor7': '#0091b6',
    '--primaryColor8': '#78be20',
    '--primaryColor9': '#ffd100',
    '--primaryColor10': '#e5eef5',
    '--primaryBrand': '#170d67'
};

const comedTheme = {
    '--primaryColor1': '#170d67',
    '--primaryColor2': '#ba1c3d',
    '--primaryColor3': '#a61936',
    '--primaryColor4': '#bf002f',
    '--primaryColor5': '#0059a4',
    '--primaryColor6': '#103870',
    '--primaryColor7': '#0091b6',
    '--primaryColor8': '#78be20',
    '--primaryColor9': '#ffd100',
    '--primaryColor10': '#e5eef5',
    '--primaryBrand': '#170d67'
};

const dplTheme = Object.assign(Object.assign({}, PHITheme), { '--primaryColor1': '#170d67', '--primaryColor2': '#ba1c3d', '--primaryColor3': '#a61936', '--primaryColor4': '#bf002f', '--primaryColor5': '#0059a4', '--primaryColor6': '#103870', '--primaryColor7': '#0091b6', '--primaryColor8': '#78be20', '--primaryColor9': '#ffd100', '--primaryColor10': '#e5eef5' });

const pecoTheme = {
    '--primaryColor1': '#170d67',
    '--primaryColor2': '#215A9F',
    '--primaryColor3': '#89B2D5',
    '--primaryColor4': '#E6F4FB',
    '--primaryColor5': '#0059a4',
    '--primaryColor6': '#e5eef5',
    '--primaryColor7': '#E6F4FB',
    '--primaryColor8': '#ce1141',
    '--primaryColor9': '#bf002f',
    '--primaryColor10': '#e5eef5',
    '--primaryBrand': '#170d67'
};

const pepcoTheme = Object.assign(Object.assign({}, PHITheme), { '--primaryColor1': '#170d67', '--primaryColor2': '#ba1c3d', '--primaryColor3': '#a61936', '--primaryColor4': '#bf002f', '--primaryColor5': '#0059a4', '--primaryColor6': '#103870', '--primaryColor7': '#0091b6', '--primaryColor8': '#78be20', '--primaryColor9': '#ffd100', '--primaryColor10': '#e5eef5' });

class AppBrandingService {
    constructor(appConfig, platformId, window) {
        this.appConfig = appConfig;
        this.platformId = platformId;
        this.window = window;
        this.themes = {
            ACE: aceTheme,
            BGE: bgeTheme,
            ComEd: comedTheme,
            DPL: dplTheme,
            PECO: pecoTheme,
            PEPCO: pepcoTheme,
        };
        this.themeInitialized = {};
    }
    applyTheme(opCo) {
        var _a, _b, _c, _d, _e, _f;
        //Theme application is only relevant client-side, there is no need for server-side branding.
        if (isPlatformBrowser(this.platformId)) {
            if (!opCo) {
                opCo = this.appConfig.config.opCo;
            }
            this.theme = this.themes[opCo];
            console.log('Branding: theme =', this.theme);
            const themeWrapper = (_b = (_a = this.window) === null || _a === void 0 ? void 0 : _a.document) === null || _b === void 0 ? void 0 : _b.querySelector('body');
            const themeColors = [
                '--primaryBrand',
                '--primaryColor1',
                // Note: disabled until we have a strategy for all OpCos
                // "--primaryColor2",
                // "--primaryColor3",
                // "--primaryColor4",
                // "--primaryColor5",
                // "--primaryColor6",
                // "--primaryColor7",
                // "--primaryColor8",
                // "--primaryColor9",
                // "--primaryColor10",
            ];
            let opCoStyles = '';
            for (const color of themeColors) {
                const colorName = color.replace('--', '');
                if (this.theme[color]) {
                    if (themeWrapper) {
                        themeWrapper.style.setProperty(color, this.theme[color]);
                    }
                    opCoStyles += `.bg-opco-${colorName}, .bg-${opCo}-${colorName} { background-color: ${this.theme[color]}; } `;
                    opCoStyles += `.text-opco-${colorName}, .text-${opCo}-${colorName} { color: ${this.theme[color]}; } `;
                }
            }
            const style = (_d = (_c = this.window) === null || _c === void 0 ? void 0 : _c.document) === null || _d === void 0 ? void 0 : _d.createElement('style');
            if (style) {
                style.innerHTML = opCoStyles;
                (_f = (_e = this.window) === null || _e === void 0 ? void 0 : _e.document) === null || _f === void 0 ? void 0 : _f.head.appendChild(style);
            }
        }
    }
    initialize() {
        const opCo = this.appConfig.config.opCo;
        if (!this.themeInitialized[opCo]) {
            this.applyTheme(opCo);
            this.themeInitialized[opCo] = true;
        }
        return Promise.resolve(true);
    }
}
AppBrandingService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AppBrandingService, deps: [{ token: AppConfigService }, { token: PLATFORM_ID }, { token: WINDOW }], target: i0.ɵɵFactoryTarget.Injectable });
AppBrandingService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AppBrandingService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AppBrandingService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: AppConfigService }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [PLATFORM_ID]
                    }] }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [WINDOW]
                    }] }];
    } });

function init_core_services(appAlertsService, appBrandingService, appConfigService, appSessionService) {
    return () => {
        return new Promise((resolve, reject) => {
            return appConfigService.initialize()
                .then(() => {
                return Promise.all([
                    appSessionService.initialize(),
                    appAlertsService.initialize(),
                    appBrandingService.initialize(),
                ]);
            })
                .then(() => {
                resolve();
            });
        });
    };
}
function init_base_href(platformId) {
    var _a, _b;
    if (!isPlatformBrowser(platformId))
        return "";
    const documentBase = document === null || document === void 0 ? void 0 : document.querySelector('base');
    const documentBaseHref = (_a = documentBase === null || documentBase === void 0 ? void 0 : documentBase.href) !== null && _a !== void 0 ? _a : '';
    const rootServerHref = window.location.protocol + '//' + window.location.host;
    const relativeBase = documentBaseHref.replace(rootServerHref, '');
    const nrBaseHrefSegments = (_b = relativeBase.split('/').filter((x) => x)) === null || _b === void 0 ? void 0 : _b.length; // removes empty entries
    if (nrBaseHrefSegments >= 1) {
        const pathSegments = window.location.pathname.split('/');
        let baseHref = pathSegments.slice(0, nrBaseHrefSegments + 1).join('/');
        //ensure trailing and leading '/' for consistency
        if (!baseHref.startsWith('/'))
            baseHref = '/' + baseHref;
        if (!baseHref.endsWith('/'))
            baseHref = baseHref + '/';
        console.log('setting base href to: ' + baseHref);
        return baseHref;
    }
    else {
        return documentBaseHref;
    }
}

class CookieInterceptor {
    intercept(req, next) {
        const newReq = req.clone({
            withCredentials: true
        });
        return next.handle(newReq);
    }
}
CookieInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CookieInterceptor, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CookieInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CookieInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CookieInterceptor, decorators: [{
            type: Injectable
        }] });

const CORE_SERVICES_INIT_OPTIONS = new InjectionToken('Core Services Init Options');
class EuCoreServicesModule {
    // Ensure singleton service
    constructor(parentModule) {
        if (parentModule) {
            throw new Error('EUCoreServicesModule is already loaded. Import it in the AppModule only');
        }
    }
    static forRoot(coreServicesInitOptions = {}) {
        return {
            ngModule: EuCoreServicesModule,
            providers: [
                { provide: WINDOW, useFactory: getWindow, deps: [PLATFORM_ID] },
                { provide: WINDOW_LOCATION, useFactory: getWindowLocation, deps: [PLATFORM_ID] },
                { provide: LOCAL_STORAGE, useFactory: getLocalStorage, deps: [PLATFORM_ID] },
                { provide: SESSION_STORAGE, useFactory: getSessionStorage, deps: [PLATFORM_ID] },
                HttpClient,
                AppAlertsService,
                AppLoggerService,
                AppConfigService,
                AppSessionService,
                AppBrandingService,
                {
                    provide: APP_INITIALIZER,
                    useFactory: init_core_services,
                    // the order of the dependencies matters as these services are passed to the useFactory in that order
                    deps: [AppAlertsService, AppBrandingService, AppConfigService, AppSessionService],
                    multi: true
                },
                {
                    provide: APP_CONFIG_OPTIONS,
                    useValue: {
                        configEndpoint: coreServicesInitOptions.configEndpoint,
                        additionalConfig: coreServicesInitOptions.additionalConfig,
                    }
                },
                {
                    provide: APP_SESSION_OPTIONS,
                    useValue: {
                        sessionEndpoint: coreServicesInitOptions.sessionEndpoint,
                        sessionOverrides: coreServicesInitOptions.sessionOverrides
                    }
                },
                {
                    provide: APP_ALERTS_OPTIONS,
                    useValue: {
                        alertsEndpoint: coreServicesInitOptions.alertsEndpoint,
                        alertsOverrides: coreServicesInitOptions.alertsOverrides
                    }
                },
                {
                    provide: APP_LOGGER_OPTIONS,
                    useValue: coreServicesInitOptions.loggerConfig
                },
                {
                    // This provider allows using the *actual* base href for route matching
                    provide: APP_BASE_HREF,
                    useFactory: init_base_href,
                    deps: [PLATFORM_ID]
                },
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: CookieInterceptor,
                    multi: true
                }
            ],
        };
    }
    static mockAppConfigService(mock) {
        return [
            {
                provide: EU_APP_CONFIG,
                useValue: mock
            },
            {
                provide: AppConfigService,
                useClass: MockAppConfigService
            }
        ];
    }
}
EuCoreServicesModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EuCoreServicesModule, deps: [{ token: EuCoreServicesModule, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.NgModule });
EuCoreServicesModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: EuCoreServicesModule, imports: [HttpClientModule] });
EuCoreServicesModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EuCoreServicesModule, imports: [HttpClientModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EuCoreServicesModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [HttpClientModule],
                }]
        }], ctorParameters: function () {
        return [{ type: EuCoreServicesModule, decorators: [{
                        type: Optional
                    }, {
                        type: SkipSelf
                    }] }];
    } });

/**
 * Enum for all Operating Companies (OpCo)
 */
var OpCo;
(function (OpCo) {
    OpCo["ACE"] = "ACE";
    OpCo["BGE"] = "BGE";
    OpCo["ComEd"] = "ComEd";
    OpCo["DPL"] = "DPL";
    OpCo["PECO"] = "PECO";
    OpCo["PEPCO"] = "PEPCO";
})(OpCo || (OpCo = {}));

const DEFAULT_API_BROKER_OPTIONS = {
    routePrefix: '/mobile/custom',
    sessionExpiredRedirect: true,
    sessionExpiredRedirectUrl: '/Pages/Login.aspx?expired=1',
    logRequest: false,
    logResponse: false
};
const API_BROKER_OPTIONS = new InjectionToken('App Store Settings');
// @dynamic
class ApiBrokerService {
    constructor(httpClient, appSessionService, appConfigService, appLogger, location /* TODO: LocationWrapper usage */, initialConfig = {}) {
        this.httpClient = httpClient;
        this.appSessionService = appSessionService;
        this.appConfigService = appConfigService;
        this.appLogger = appLogger;
        this.location = location;
        this.initialConfig = initialConfig;
        //A list of interceptor definitions for API Broker requests
        this.interceptors = [];
        if (!this.appLogger) {
            this.appLogger = getNullLogger();
            console.warn('AppLoggerService constructor: using NullLogger');
        }
        const appConfig = appConfigService.config;
        this.config = Object.assign({}, DEFAULT_API_BROKER_OPTIONS, this.initialConfig, {
            version: appConfig.euApiVersion,
            baseUrl: appConfig.baseUrl + appConfig.euApiUrl,
            routePrefix: appConfig.euApiRoutePrefix
        });
        this.appLogger.debug('ApiBrokerService constructor: ', this.config);
        if (this.config.interceptors) {
            this.config.interceptors.forEach(i => this.registerInterceptor(i));
        }
    }
    /**
     * Update apiBroker options
     * @param newOptions update to options
     */
    updateOptions(newOptions) {
        var _a;
        this.config = Object.assign(this.config, newOptions);
        if ((_a = newOptions.interceptors) === null || _a === void 0 ? void 0 : _a.length) {
            //Do not clear existing interceptors, just add new ones
            newOptions.interceptors.forEach(i => this.registerInterceptor(i));
        }
    }
    /**
     * Get options
     */
    getOptions() {
        return this.config;
    }
    get(url, options) {
        return this.processRequestPipeline('GET', url, null, options);
    }
    post(url, body, options) {
        return this.processRequestPipeline('POST', url, body, options);
    }
    put(url, body, options) {
        return this.processRequestPipeline('PUT', url, body, options);
    }
    delete(url, options, body) {
        return this.processRequestPipeline('DELETE', url, body || null, options);
    }
    /**
     * Register an interceptor with the ApiBrokerService
     * ApiBroker supports interceptors similar to middleware pattern (e.g. Express.js or ASP.NET Core)
     * Interceptors can be used to modify requests before they are sent, or responses before they are returned
     * @param interceptor the interceptor to register
     * @returns the interceptor that was registered or one that already exists with the same id
     */
    registerInterceptor(interceptor) {
        //find existing interceptor with same id
        const existingInterceptor = this.interceptors.find(i => i.id === interceptor.id);
        if (existingInterceptor && (!existingInterceptor.allowMultiple || !interceptor.allowMultiple)) {
            //if interceptor already exists, return it
            return existingInterceptor;
        }
        //if interceptor doesn't exist, add it
        this.interceptors.push(interceptor);
        return interceptor;
    }
    unregisterInterceptor(apiBrokerInterceptor) {
        this.interceptors = this.interceptors.filter(i => i.id !== apiBrokerInterceptor.id);
    }
    processRequestPipeline(method, url, body, options) {
        let apiRequest = { method, url, body, options };
        // Default handler if no interceptors are present
        let handlerChain = (apiRequest) => {
            return this.request(apiRequest.method, apiRequest.url, apiRequest.body, apiRequest.options);
        };
        // Build the handler chain, starting with the last interceptor
        for (let i = this.interceptors.length - 1; i >= 0; i--) {
            const currentInterceptor = this.interceptors[i];
            const nextHandler = handlerChain;
            handlerChain = (apiRequest) => currentInterceptor.handleRequest(apiRequest, nextHandler);
        }
        return handlerChain(apiRequest);
    }
    request(method, url, body, options) {
        const requestUrl = this.buildUrl(url, options);
        const requestOptions = {};
        const headers = this.buildHeaders(url);
        requestOptions.headers = headers;
        requestOptions.body = body;
        if (options) {
            // allow passing additional options
            requestOptions.headers = Object.assign(requestOptions.headers, options.headers || {});
        }
        if (this.config.logRequest) {
            this.appLogger.info('ApiBroker request: method, url, body, options, requestOptions', method, url, body, options, requestOptions);
        }
        return this.httpClient.request(method, requestUrl, requestOptions)
            .pipe(tap(response => {
            if (this.config.logResponse) {
                this.appLogger.info('ApiBroker response: ', response);
            }
        }), catchError((err) => {
            if (err.error instanceof Error) {
                // A client-side or network error occurred. Handle it accordingly.
                this.appLogger.error(`An error occurred: ${err.error.message}`, err);
            }
            else {
                // The backend returned an unsuccessful response code.
                this.appLogger.info(`Backend returned code ${err.status}, body was: ${err.error}`, err);
                if (err.status === 401 && this.appSessionService.isUserAuthenticated()) {
                    this.handleExpiredSession();
                }
            }
            return throwError(err);
        }));
    }
    buildUrl(url, options) {
        if (new RegExp('^(?:[a-z]+:)?//', 'i').test(url)) { // do not modify absolute URLs
            console.warn('apiBroker works better when used with relative routes');
            return url;
        }
        url = url.replace('/anon/', '/anon' + this.config.version + '/')
            .replace('/auth/', '/auth' + this.config.version + '/')
            .replace('/pref/', '/pref' + this.config.version + '/');
        if (url.includes('{{opCo}}')) {
            url = url.replace('{{opCo}}', this.formatOpCo());
        }
        if (url.includes('{{opco}}')) {
            console.warn('in apiBroker routes, please use {{opCo}} rather than {{opco}}.');
            url = url.replace('{{opco}}', this.formatOpCo());
        }
        if (url.includes('{{accountNumber}}')) {
            url = url.replace('{{accountNumber}}', this.appSessionService.session.accountNumber);
        }
        let routePrefix = this.config.routePrefix;
        if (options && options.routePrefix) {
            routePrefix = options.routePrefix;
        }
        return this.config.baseUrl + routePrefix + url;
    }
    handleExpiredSession() {
        if (this.config.sessionExpiredRedirect) {
            if (this.location) {
                this.location.href = this.config.sessionExpiredRedirectUrl
                    + `&TARGET=${encodeURIComponent(this.location.pathname)}`;
            }
            else {
                console.warn('window.location is not injected into ApiBrokerService');
            }
        }
    }
    buildHeaders(url) {
        let headers = {
            'Content-Type': 'application/json',
            'opco': this.formatOpCo(),
            'x-opco': this.formatOpCo(),
            'cache-control': 'no-cache',
        };
        if (this.appSessionService.isUserAuthenticated()) {
            headers = Object.assign(headers, {
                'Authorization': `Bearer ${this.appSessionService.session.token}`,
            });
        }
        return headers;
    }
    /**
     * our API layer unfortunately diverges if opco name is part of the route
     * @returns correct opco name for route formatting
     */
    formatOpCo() {
        const opCo = this.appConfigService.config.opCo;
        switch (opCo) {
            case OpCo.PEPCO:
                return 'PEP';
            case OpCo.ACE:
            case OpCo.BGE:
            case OpCo.ComEd:
            case OpCo.DPL:
            case OpCo.PECO:
                return opCo;
            default:
                throw new Error(`Invalid opCo ${opCo}.`);
        }
    }
}
ApiBrokerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ApiBrokerService, deps: [{ token: i1.HttpClient }, { token: AppSessionService }, { token: AppConfigService }, { token: AppLoggerService, optional: true }, { token: WINDOW_LOCATION, optional: true }, { token: API_BROKER_OPTIONS, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
ApiBrokerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ApiBrokerService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ApiBrokerService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: i1.HttpClient }, { type: AppSessionService }, { type: AppConfigService }, { type: AppLoggerService, decorators: [{
                        type: Optional
                    }] }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [WINDOW_LOCATION]
                    }] }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [API_BROKER_OPTIONS]
                    }] }];
    } });

var MockNotFoundBehavior;
(function (MockNotFoundBehavior) {
    /** call through to apiBroker. This is the default behavior */
    MockNotFoundBehavior[MockNotFoundBehavior["CallThrough"] = 0] = "CallThrough";
    /** returns a successful 200 response with empty response payload */
    MockNotFoundBehavior[MockNotFoundBehavior["ReturnEmpty"] = 1] = "ReturnEmpty";
    /** throws exception */
    MockNotFoundBehavior[MockNotFoundBehavior["ThrowError"] = 2] = "ThrowError";
})(MockNotFoundBehavior || (MockNotFoundBehavior = {}));
const MOCK_API_BROKER_OPTIONS = new InjectionToken('Mock Api Broker Options');
class MockApiBrokerService extends ApiBrokerService {
    constructor(httpClient, appSession, appConfig, appLogger, location /* TODO: LocationWrapper usage */, options) {
        super(httpClient, appSession, appConfig, appLogger, location);
        this.httpClient = httpClient;
        this.appSession = appSession;
        this.appConfig = appConfig;
        this.appLogger = appLogger;
        this.location = location;
        this.options = {
            calls: options.calls || [],
            mockNotFoundBehavior: options.mockNotFoundBehavior || MockNotFoundBehavior.CallThrough
        };
    }
    /**
     * Returns mocked response if one set up, otherwise obeys this.options.mockNotFoundBehavior
     * @param method
     * @param url
     * @param body
     */
    request(method, url, body, options) {
        const mockCall = this.findMockCall(method, url, body);
        if (mockCall) {
            this.appLogger.info(`Returning mockedResponse for request ${method}, ${url}`, mockCall.response);
            return of(mockCall.response);
        }
        else {
            switch (this.options.mockNotFoundBehavior) {
                case MockNotFoundBehavior.CallThrough:
                    this.appLogger.info(`MockedResponse for request ${method}, ${url} not found, calling ApiBroker.`);
                    return super.request(method, url, body, options);
                case MockNotFoundBehavior.ReturnEmpty:
                    this.appLogger.info(`MockedResponse for request ${method}, ${url} not found, returning empty response.`);
                    return of({});
                case MockNotFoundBehavior.ThrowError:
                    this.appLogger.info(`MockedResponse for request ${method}, ${url} not found, throwing error.`);
                    throw new Error('Mock response not found in MockApiBrokerService');
            }
        }
    }
    /**
     * Find a matching mocked calls that matches the specified parameters
     * @param method
     * @param url
     * @param body
     */
    findMockCall(method, url, body) {
        for (const mockCall of this.options.calls) {
            if (mockCall.requestMatches(method, url, body)) {
                return mockCall;
            }
        }
        return null;
    }
}
MockApiBrokerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MockApiBrokerService, deps: [{ token: i1.HttpClient }, { token: AppSessionService }, { token: AppConfigService }, { token: AppLoggerService }, { token: WINDOW_LOCATION, optional: true }, { token: MOCK_API_BROKER_OPTIONS }], target: i0.ɵɵFactoryTarget.Injectable });
MockApiBrokerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MockApiBrokerService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MockApiBrokerService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: i1.HttpClient }, { type: AppSessionService }, { type: AppConfigService }, { type: AppLoggerService }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [WINDOW_LOCATION]
                    }] }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [MOCK_API_BROKER_OPTIONS]
                    }] }];
    } });

class GoogleAnalyticsBase {
}
GoogleAnalyticsBase.DEFAULT_EVENT = {
    'event': 'VirtualPageview',
    'virtualPageURL': 'Vpv/',
    'virtualPageTitle': ''
};

class UrlSanitizer {
    constructor(domSanitizer) {
        this.domSanitizer = domSanitizer;
    }
    /**
     * returns a 'safe' version of the url using DOMSanitizer that would have
     * otherwise been tagged as unsafe and not rendered by Angular.
     * used for object urls created on client side to render images etc.
     * @param url
     * @returns SafeUrl sanitizedUrl
     */
    getSanitizedUrl(url) {
        const sanitizedUrl = this.domSanitizer.bypassSecurityTrustUrl(url);
        return sanitizedUrl;
    }
}
UrlSanitizer.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UrlSanitizer, deps: [{ token: i1$1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Injectable });
UrlSanitizer.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UrlSanitizer });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UrlSanitizer, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.DomSanitizer }]; } });

class NavEntryDelegate {
    static processEntry(items, title, terms) { }
}

var NavEntryType;
(function (NavEntryType) {
    NavEntryType["TOP_NAVIGATION"] = "topNavigation";
    NavEntryType["TOP_NAVIGATION_CONTENT_API"] = "topNavigationContentApi";
    NavEntryType["BOTTOM_NAVIGATION"] = "bottomNavigation";
})(NavEntryType || (NavEntryType = {}));

class TopNavigationParser {
    static isEntry(key, value) {
        return (value
            && typeof value === 'object'
            && (value.hasOwnProperty('terms') || value.hasOwnProperty('exc_Url'))
            && value.hasOwnProperty('exc_Use')
            && value.exc_Use === NavEntryType.TOP_NAVIGATION.toString()
            && value.hasOwnProperty('name'));
    }
    /**
     * the top level of a recursive pass through the terms of a topNavigation entry. This one is different
     * than the others because of how we assign the result, so it gets its own function.
     * @param menuItems the item to assign elements to
     * @param key the 'tab title'
     * @param terms terms under that tab item
     */
    static processEntry(menuItems, attributes, terms) {
        const entry = {};
        TopNavigationParser.processAttributes(entry, attributes, true); // because this is a tab entry
        entry.topicEntries = [];
        if (Array.isArray(terms)) {
            terms.forEach((term) => {
                if (typeof term === 'object'
                    // && term.hasOwnProperty('attributes')
                    && term.hasOwnProperty('terms')) {
                    TopNavigationParser.processTopNavigationEntryTerms(entry.topicEntries, term, term.terms);
                }
            });
        }
        menuItems.push(entry);
    }
    /**
     * recursively process terms at any depth under a tab
     * @param topicEntries the item to assign elements to
     * @param attributes the definition for this node
     * @param terms the children of this node yet to process
     */
    static processTopNavigationEntryTerms(topicEntries, attributes, terms) {
        // tslint:disable-next-line: max-line-length
        if (typeof attributes === 'object' && attributes.hasOwnProperty('exc_Visibility') && (attributes.exc_Visibility.toLowerCase() === 'hidden' || attributes.exc_Visibility.toLowerCase() === 'hide')) {
            // console.log('[TopNavigationParser processTopNavigationEntryTerms] hidden', attributes, terms);
            return;
        }
        const entry = {};
        entry.topicEntries = [];
        TopNavigationParser.processAttributes(entry, attributes);
        terms.forEach((term) => {
            if (term.hasOwnProperty('title') || term.hasOwnProperty('name')) {
                TopNavigationParser.processTopNavigationEntryTerms(entry.topicEntries, term, []);
            }
        });
        topicEntries.push(entry);
    }
    static processAttributes(entry, attributes, isTab) {
        entry.title = attributes.name;
        const url = TopNavigationParser.computeUrl(attributes);
        if (url) {
            entry.url = url;
        }
        if (typeof attributes === 'object' &&
            attributes.hasOwnProperty('exc_NewTarget') &&
            typeof attributes.exc_NewTarget === 'string' &&
            ['ALL', 'TOP'].indexOf(attributes.exc_NewTarget.toUpperCase()) !== -1) {
            entry.newTarget = true;
        }
        if (isTab) {
            return;
        }
        if (typeof attributes === 'object' && attributes.hasOwnProperty('exc_authenticated')) {
            entry.authenticatedOnly = true;
        }
        if (typeof attributes === 'object' && attributes.hasOwnProperty('exc_unauthenticated')) {
            entry.unauthenticatedOnly = true;
        }
        if (typeof attributes === 'object' && attributes.hasOwnProperty('exc_residential')) {
            entry.residentialOnly = true;
        }
    }
    /**
     * urls may require processing
     */
    static computeUrl(attributes) {
        if (typeof attributes === 'object') {
            if (!attributes.exc_Url) {
                return;
            }
            if (/^[^\/]/.test(attributes.exc_Url)) {
                return attributes.exc_Url;
            }
            else if (attributes.hasOwnProperty('exc_Access') && attributes.exc_Access.toLowerCase() === 'secure') {
                return TopNavigationParser.baseUrl + attributes.exc_Url;
            }
            else {
                return TopNavigationParser.anonBaseUrl + attributes.exc_Url;
            }
        }
    }
}
TopNavigationParser.baseUrl = '';
TopNavigationParser.anonBaseUrl = '';

class BottomNavigationParser {
    static isEntry(key, value) {
        return (value
            && typeof value === 'object'
            && value.hasOwnProperty('terms')
            && value.hasOwnProperty('name'));
    }
    /**
     * the top level of a recursive pass through the terms of a bottomNavigation
     * entry. This one is different than the others because of how we assign the
     * result, so it gets its own function.
     * @param items the item to assign elements to
     * @param attributes of the 'section title'
     * @param terms terms under that section
     */
    static processEntry(items, attributes, terms) {
        const entry = {};
        BottomNavigationParser.processAttributes(entry, attributes, true); // because this is a tab entry
        entry.topicEntries = [];
        if (Array.isArray(terms)) {
            terms.forEach((term) => {
                if (typeof term === 'object' && term.hasOwnProperty('name')) {
                    BottomNavigationParser.processBottomNavigationEntry(entry.topicEntries, term);
                }
            });
        }
        items.push(entry);
    }
    /**
     * recursively process terms at any depth under a tab
     * @param topicEntries the item to assign elements to
     * @param attributes the definition for this node
     * @param terms the children of this node yet to process
     */
    static processBottomNavigationEntry(topicEntries, attributes) {
        // tslint:disable-next-line: max-line-length
        if (typeof attributes === 'object' && attributes.hasOwnProperty('exc_Visibility') && (attributes.exc_Visibility.toLowerCase() === 'hidden' || attributes.exc_Visibility.toLowerCase() === 'hide')) {
            // console.log('[BottomNavigationParser processTopNavigationEntryTerms] hidden', attributes, terms);
            return;
        }
        const entry = {};
        BottomNavigationParser.processAttributes(entry, attributes);
        topicEntries.push(entry);
    }
    static processAttributes(entry, attributes, isTab) {
        entry.title = attributes.name;
        const url = BottomNavigationParser.computeUrl(attributes);
        if (url) {
            entry.url = url;
        }
        if (typeof attributes === 'object' &&
            attributes.hasOwnProperty('exc_NewTarget') &&
            typeof attributes.exc_NewTarget === 'string' &&
            ['ALL', 'TOP'].indexOf(attributes.exc_NewTarget.toUpperCase()) !== -1) {
            entry.newTarget = true;
        }
        if (isTab) {
            return;
        }
        if (typeof attributes === 'object' && attributes.hasOwnProperty('exc_authenticated')) {
            entry.authenticatedOnly = true;
        }
        if (typeof attributes === 'object' && attributes.hasOwnProperty('exc_unauthenticated')) {
            entry.unauthenticatedOnly = true;
        }
        if (typeof attributes === 'object' && attributes.hasOwnProperty('exc_residential')) {
            entry.residentialOnly = true;
        }
    }
    /**
     * urls may require processing
     */
    static computeUrl(attributes) {
        if (typeof attributes === 'object') {
            if (!attributes.exc_Url) {
                return;
            }
            if (/^[^\/]/.test(attributes.exc_Url)) {
                return attributes.exc_Url;
            }
            else if (attributes.hasOwnProperty('exc_Access') && attributes.exc_Access.toLowerCase() === 'secure') {
                return BottomNavigationParser.baseUrl + attributes.exc_Url;
            }
            else {
                return BottomNavigationParser.anonBaseUrl + attributes.exc_Url;
            }
        }
    }
}
BottomNavigationParser.baseUrl = '';
BottomNavigationParser.anonBaseUrl = '';

class TopNavigationContentApiParser {
    static isEntry(key, value) {
        return (value
            && typeof value === 'object'
            && value.hasOwnProperty('displayText')
            && (value.hasOwnProperty('nodes') || value.hasOwnProperty('url')));
    }
    /**
     * the top level of a recursive pass through the terms of a topNavigation entry.
     * @param menuItems the item to assign elements to
     * @param key the 'tab title'
     * @param terms terms under that tab item
     */
    static processEntry(menuItems, attributes, terms) {
        const entry = {};
        TopNavigationContentApiParser.processAttributes(entry, attributes); // because this is a tab entry
        entry.topicEntries = [];
        if (Array.isArray(terms)) {
            terms.forEach((term) => {
                var _a;
                if (typeof term === 'object' && term.hasOwnProperty('nodes')) {
                    TopNavigationContentApiParser.processTopNavigationEntryTerms(entry.topicEntries, term, (_a = term.nodes) !== null && _a !== void 0 ? _a : []);
                }
            });
        }
        menuItems.push(entry);
    }
    /**
     * recursively process terms at any depth under a tab
     * @param topicEntries the item to assign elements to
     * @param attributes the definition for this node
     * @param terms the children of this node yet to process
     */
    static processTopNavigationEntryTerms(topicEntries, attributes, terms) {
        const entry = {};
        entry.topicEntries = [];
        TopNavigationContentApiParser.processAttributes(entry, attributes);
        terms.forEach((term) => {
            if (term.hasOwnProperty('displayText')) {
                TopNavigationContentApiParser.processTopNavigationEntryTerms(entry.topicEntries, term, []);
            }
        });
        topicEntries.push(entry);
    }
    static processAttributes(entry, attributes) {
        entry.title = attributes.displayText;
        const url = TopNavigationContentApiParser.computeUrl(attributes);
        if (url) {
            entry.url = url;
        }
        if (typeof attributes === 'object' &&
            attributes.hasOwnProperty('linkType') &&
            attributes.linkType === 'External') {
            entry.newTarget = true;
        }
    }
    /**
     * urls may require processing
     */
    static computeUrl(attributes) {
        if (typeof attributes === 'object') {
            if (!attributes.url) {
                return;
            }
            if (/^[^\/]/.test(attributes.url)) {
                return attributes.url;
            }
            else if (attributes.hasOwnProperty('linkType') && attributes.linkType.toLowerCase() === 'secure') {
                return TopNavigationContentApiParser.baseUrl + attributes.url;
            }
            else {
                return TopNavigationContentApiParser.anonBaseUrl + attributes.url;
            }
        }
    }
}
TopNavigationContentApiParser.baseUrl = '';
TopNavigationContentApiParser.anonBaseUrl = '';

const NAV_JSON_PARSER__entryProcessor = {};
NAV_JSON_PARSER__entryProcessor[NavEntryType.TOP_NAVIGATION.toString()] = TopNavigationParser;
NAV_JSON_PARSER__entryProcessor[NavEntryType.TOP_NAVIGATION_CONTENT_API.toString()] = TopNavigationContentApiParser;
NAV_JSON_PARSER__entryProcessor[NavEntryType.BOTTOM_NAVIGATION.toString()] = BottomNavigationParser;
class NavJSONParser {
    /**
     * parse the json object looking for navigation items. we do not limit ouselves to the top-level because
     * items could be nested.
     * @param navJson - json Object of the raw menu transform to json text.
     */
    static parseNav(navJson, type) {
        const items = [];
        NavJSONParser.traverse(navJson, (key, value) => NavJSONParser.selectItems(items, type, key, value));
        return items;
    }
    /**
     * conditional to a _type_ item, process the entry for it
     * @param items the item to assign elements to
     * @param type - the type of item to locate
     * @param key - the 'title' (given a payload that is a _type_ entry)
     * @param value - the payload for that entry
     */
    static selectItems(items, type, key, value) {
        var _a;
        //    console.log('selectItems', key, value, NavJSONParser.entryProcessor, NavJSONParser.entryProcessor[type]);
        if (NavJSONParser.entryProcessor[type.toString()].isEntry(key, value)) {
            NavJSONParser.entryProcessor[type.toString()].processEntry(items, value, (_a = value.terms) !== null && _a !== void 0 ? _a : value.nodes);
        }
    }
    /**
   * generic object traversal with callback
   * @param o the object
   * @param fn the processor function
   */
    static traverse(o, fn, memory = undefined) {
        if (!memory) {
            memory = new Set();
        }
        for (const key in o) {
            if (memory.has(o[key])) {
                continue;
            }
            fn.apply(this, [key, o[key]]);
            if (o[key] !== null && typeof (o[key]) === 'object' && key !== 'nodes') {
                // going one step down in the object tree!!
                NavJSONParser.traverse(o[key], fn, memory);
            }
            else {
                memory.add(o[key]);
            }
        }
    }
}
NavJSONParser.entryProcessor = NAV_JSON_PARSER__entryProcessor;

// tslint:disable: no-bitwise
function UUID() {
    let d = new Date().getTime();
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (d + Math.random() * 16) % 16 | 0;
        d = Math.floor(d / 16);
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
    return uuid;
}

class ClickOutsideDirective {
    constructor(_elementRef) {
        this._elementRef = _elementRef;
        this.appClickOutside = new EventEmitter();
    }
    onClick(targetElement) {
        const isClickedInside = this._elementRef.nativeElement.contains(targetElement);
        if (!isClickedInside) {
            this.appClickOutside.emit(null);
        }
    }
}
ClickOutsideDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ClickOutsideDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
ClickOutsideDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.12", type: ClickOutsideDirective, selector: "[appClickOutside]", outputs: { appClickOutside: "appClickOutside" }, host: { listeners: { "document:click": "onClick($event.target)" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ClickOutsideDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[appClickOutside]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { appClickOutside: [{
                type: Output
            }], onClick: [{
                type: HostListener,
                args: ['document:click', ['$event.target']]
            }] } });

const opcoStrings = {
    comed: {
        home: 'https://www.comed.com',
        logoDesktop: 'assets/images/logo-comed.svg',
        logoMobile: 'assets/images/logo-comed-mobile.svg',
        logoTitle: 'ComEd an Exelon company',
        footerMessage: '© Commonwealth Edison Company, ' + (new Date()).getFullYear() + '. All Rights Reserved.',
        termsOfUse: 'Terms of Use',
        privacy: 'Privacy',
        termsOfUseLink: 'https://www.exeloncorp.com/terms-and-conditions',
        privacyLink: 'https://www.exeloncorp.com/privacy-policy',
        eudsLogoDesktop: 'assets/svg/brand-logos/logo-comed.svg',
        eudsLogoMobile: 'assets/svg/brand-logos/logo-comed-sm.svg'
    },
    bge: {
        home: 'https://www.bge.com',
        logoDesktop: 'assets/images/logo-bge.svg',
        logoMobile: 'assets/images/logo-bge.svg',
        logoTitle: 'BGE an Exelon company',
        footerMessage: '© Baltimore Gas and Electric Company, ' + (new Date()).getFullYear() + '. All Rights Reserved.',
        termsOfUse: 'Terms of Use',
        privacy: 'Privacy',
        termsOfUseLink: 'https://www.exeloncorp.com/terms-and-conditions',
        privacyLink: 'https://www.exeloncorp.com/privacy-policy',
        eudsLogoDesktop: 'assets/svg/brand-logos/logo-bge.svg',
        eudsLogoMobile: 'assets/svg/brand-logos/logo-bge-sm.svg',
    },
    pepco: {
        home: 'https://www.pepco.com',
        logoDesktop: 'assets/images/logo-pepco.svg',
        logoMobile: 'assets/images/logo-pepco-mobile.svg',
        logoTitle: 'Pepco an Exelon Company',
        footerMessage: '© Potomac Electric Power Company, ' + (new Date()).getFullYear() + '. All Rights Reserved.',
        opcoShortName: 'pepco',
        opcoName: 'Pepco',
        custSvcNumber: '1-202-833-7500',
        termsOfUse: 'Terms of Use',
        privacy: 'Privacy',
        termsOfUseLink: 'https://www.exeloncorp.com/terms-and-conditions',
        privacyLink: 'https://www.exeloncorp.com/privacy-policy',
        eudsLogoDesktop: 'assets/svg/brand-logos/logo-pepco.svg',
        eudsLogoMobile: 'assets/svg/brand-logos/logo-pepco-sm.svg',
    },
    peco: {
        home: 'https://www.peco.com',
        logoDesktop: 'assets/images/logo-peco.svg',
        logoMobile: 'assets/images/logo-peco.svg',
        logoTitle: 'PECO an Exelon Company',
        footerMessage: '© PECO Energy Company, ' + (new Date()).getFullYear() + '. All Rights Reserved.',
        opcoShortName: 'peco',
        opcoName: 'PECO',
        custSvcNumber: '1-800-494-4000',
        termsOfUse: 'Terms of Use',
        privacy: 'Privacy',
        termsOfUseLink: 'https://www.exeloncorp.com/terms-and-conditions',
        privacyLink: 'https://www.exeloncorp.com/privacy-policy',
        eudsLogoDesktop: 'assets/svg/brand-logos/logo-peco.svg',
        eudsLogoMobile: 'assets/svg/brand-logos/logo-peco-sm.svg'
    },
    dpl: {
        home: 'https://www.delmarva.com',
        logoDesktop: 'assets/images/logo-delmarva.svg',
        logoMobile: 'assets/images/logo-dpl-mobile.svg',
        logoTitle: 'Delmarva Power an Exelon Company',
        footerMessage: '© Delmarva Power & Light Company, ' + (new Date()).getFullYear() + '. All Rights Reserved.',
        opcoShortName: 'dpl',
        opcoName: 'Delmarva Power',
        custSvcNumber: '1-800-375-7117',
        termsOfUse: 'Terms of Use',
        privacy: 'Privacy',
        termsOfUseLink: 'https://www.exeloncorp.com/terms-and-conditions',
        privacyLink: 'https://www.exeloncorp.com/privacy-policy',
        eudsLogoDesktop: 'assets/svg/brand-logos/logo-delmarva.svg',
        eudsLogoMobile: 'assets/svg/brand-logos/logo-delmarva-sm.svg',
    },
    ace: {
        home: 'https://www.atlanticcityelectric.com',
        logoDesktop: 'assets/images/logo-ace.svg',
        logoMobile: 'assets/images/logo-ace-mobile.svg',
        logoTitle: 'Atlantic City Electric an Exelon Company',
        footerMessage: '© Atlantic City Electric Company, ' + (new Date()).getFullYear() + '. All Rights Reserved.',
        opcoShortName: 'ace',
        opcoName: 'Atlantic City Electric',
        custSvcNumber: '1-800-642-3780',
        termsOfUse: 'Terms of Use',
        privacy: 'Privacy',
        termsOfUseLink: 'https://www.exeloncorp.com/terms-and-conditions',
        privacyLink: 'https://www.exeloncorp.com/privacy-policy',
        eudsLogoDesktop: 'assets/svg/brand-logos/logo-ace.svg',
        eudsLogoMobile: 'assets/svg/brand-logos/logo-ace-sm.svg',
    }
};

let SingletonComponentState = class SingletonComponentState {
    constructor(platformId) {
        this.platformId = platformId;
        if (!this.isMountable && isPlatformBrowser(platformId)) {
            //Angular Universal doesn't reset static variables between page refreshes.
            //Only throw an error if running client side.
            throw new Error('[SingletonComponentState] ' + this.constructor + ' is already instantiated');
        }
    }
    get instance() {
        return SingletonComponentState.instances.find(instance => instance instanceof this.constructor);
    }
    set instance(instance) {
        SingletonComponentState.instances.push(instance);
    }
    get isMountable() {
        return !SingletonComponentState.instances.some(instance => instance instanceof this.constructor);
    }
    mount() {
        if (!this.isMountable && isPlatformBrowser(this.platformId)) {
            throw new Error('[SingletonComponentState] ' + this.constructor + ' is already instantiated');
        }
        this.instance = this;
    }
};
SingletonComponentState.instances = [];
SingletonComponentState = __decorate([
    __param(0, Inject(PLATFORM_ID))
], SingletonComponentState);

// export * from './prettyprint.pipe'; -- this is purely for the library component, not meant to export
// export * from './interface-keys'; -- this is a hint to typescript and non-functional, so it can't be exported

class GoogleAnalyticsService extends GoogleAnalyticsBase {
    constructor(config) {
        super();
        this.pagination = {};
        this.pageNavigatedBody = GoogleAnalyticsBase.DEFAULT_EVENT;
        this.emitPageEventBody = GoogleAnalyticsBase.DEFAULT_EVENT;
        this.applyConfig(config !== null && config !== void 0 ? config : {});
    }
    /**
     * @private
     * @method applyConfig
     * @param config
     * @description apply any provided GOOGLE_ANALYTICS_CONFIG to this service during initial service providion.
     */
    applyConfig(config) {
        if (config.hasOwnProperty('pagination')) {
            this.pagination = config.pagination;
        }
        if (config.hasOwnProperty('pageNavigatedBody')) {
            this.pageNavigatedBody = config.pageNavigatedBody;
        }
        if (config.hasOwnProperty('emitPageEventBody')) {
            this.emitPageEventBody = config.emitPageEventBody;
        }
    }
    /**
     * @method pageNavigated
     * @param url -- url of page navigation event
     * @param _args additional arguments -- unused in the default implementation
     * @description trigger a page Navigated event.
     */
    pageNavigated(url, ..._args) {
        if (url.startsWith('/')) {
            url = url.substring(1);
        }
        if (dataLayer) {
            const body = this.getPageNavigatedBody(url);
            if (url.startsWith('/Vpv/')) {
                body.virtualPageURL = url;
            }
            dataLayer.push(body);
        }
    }
    /**
     * @private
     * @method getPageNavigatedBody
     * @param url -- url of page navigation event
     * @returns dataLayer push body
     */
    getPageNavigatedBody(url) {
        const body = JSON.parse(cleanStringify(this.pageNavigatedBody));
        body.virtualPageTitle += url;
        const urlParts = [];
        if (this.pagination.hasOwnProperty('type')) {
            urlParts.push(this.pagination.type);
            if (this.pagination.hasOwnProperty('subtype')) {
                urlParts.push(this.pagination.subtype);
            }
        }
        urlParts.push(url);
        body.virtualPageURL += urlParts.join('/');
        return body;
    }
    /**
     * @method emitPageEvent
     * @param pageName
     * @params additional arguments
     * @description trigger a page event for non-navigation events
     */
    emitPageEvent(pageName, ...args) {
        var _a;
        if (dataLayer && ((_a = pageName === null || pageName === void 0 ? void 0 : pageName.length) !== null && _a !== void 0 ? _a : 0 > 0)) {
            const body = this.getEmitPageEventBody(pageName);
            dataLayer.push(body);
        }
    }
    /**
     * @private
     * @method getEmitPageEventBody
     * @param pageName -- name for the event
     * @returns dataLayer push body
     */
    getEmitPageEventBody(pageName) {
        const body = JSON.parse(cleanStringify(this.emitPageEventBody));
        body.virtualPageTitle += pageName;
        body.virtualPageURL += pageName;
        return body;
    }
}
GoogleAnalyticsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GoogleAnalyticsService, deps: [{ token: 'GOOGLE_ANALYTICS_CONFIG', optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
GoogleAnalyticsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GoogleAnalyticsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GoogleAnalyticsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: ['GOOGLE_ANALYTICS_CONFIG']
                    }] }];
    } });

class EuAccountsService {
    constructor(apiBroker) {
        this.apiBroker = apiBroker;
    }
    getAccounts() {
        return this.apiBroker.get('/auth/accounts');
    }
    getAccountDetails(accountNumber) {
        if (accountNumber) {
            return this.apiBroker.get(`/auth/accounts/${accountNumber}`);
        }
        else {
            // apiBroker is session-aware and will use the 'current' account number
            return this.apiBroker.get(`/auth/accounts/{{accountNumber}}`);
        }
    }
}
EuAccountsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EuAccountsService, deps: [{ token: ApiBrokerService }], target: i0.ɵɵFactoryTarget.Injectable });
EuAccountsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EuAccountsService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EuAccountsService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: ApiBrokerService }]; } });

const NO_MEGAMENU_ENDPOINT = 'no megamenu endpoint';
class MegaMenuService {
    constructor(appConfigService, httpClient) {
        this.appConfigService = appConfigService;
        this.httpClient = httpClient;
        this.hasCalledGetMenu = false;
        if (this.megaMenuConfig.endPoint === NO_MEGAMENU_ENDPOINT && !this.isContentApi) {
            this.hasCalledGetMenu = true;
        }
        this.menuObservable = new BehaviorSubject(null);
    }
    get navigation() {
        return this._navigation;
    }
    set navigation(navigation) {
        this._navigation = Object.freeze(Object.assign({}, navigation));
    }
    get menu() {
        return this._menu;
    }
    set menu(menu) {
        this._menu = Object.freeze(menu.concat());
        this.menuObservable.next(this._menu);
    }
    get megaMenuConfig() {
        return (this.appConfigService.config &&
            this.appConfigService.config.additionalConfig &&
            this.appConfigService.config.additionalConfig.common) ? this.appConfigService.config.additionalConfig.common.megaMenu : {};
    }
    isContentApi() {
        if (this.appConfigService.config.contentApiBaseUrl) {
            return true;
        }
        else {
            return false;
        }
    }
    get megaMenuURI() {
        if (this.isContentApi()) {
            let url = `${this.appConfigService.config.contentApiBaseUrl}/api/content/navigation/mega-menu`;
            if (this.appConfigService.config.contentPublishingEnvironment) {
                url += `?environment=${this.appConfigService.config.contentPublishingEnvironment}`;
            }
            return url;
        }
        if (this.megaMenuConfig.endPoint === NO_MEGAMENU_ENDPOINT) {
            return NO_MEGAMENU_ENDPOINT;
        }
        // tslint:disable-next-line: max-line-length
        return `${this.appConfigService.config.baseUrl}/api/Services/MyAccountService.svc/GetOpCoNavigation`;
    }
    /**
     * get the menu from the static json document (assumes return is a json OBJECT)
     */
    getMenu() {
        this.hasCalledGetMenu = true;
        this.menu = [];
        const endpoint = this.megaMenuURI;
        if (this.megaMenuURI === NO_MEGAMENU_ENDPOINT && !this.isContentApi()) {
            return of({});
        }
        return this.httpClient.get(endpoint).pipe(tap(result => this.processMenu(result)));
    }
    processMenu(result) {
        this.navigation = result;
        TopNavigationParser.anonBaseUrl = this.appConfigService.config.anonBaseUrl;
        TopNavigationParser.baseUrl = this.appConfigService.config.baseUrl;
        TopNavigationContentApiParser.anonBaseUrl = this.appConfigService.config.anonBaseUrl;
        TopNavigationContentApiParser.baseUrl = this.appConfigService.config.baseUrl;
        if (this.isContentApi()) {
            let menu = [];
            result.nodes.forEach(node => {
                TopNavigationContentApiParser.processEntry(menu, node, node.nodes);
            });
            this.menu = menu;
            // NavJSONParser.parseNav(result, NavEntryType.TOP_NAVIGATION_CONTENT_API) as MegaMenuEntry[];
        }
        else {
            this.menu = NavJSONParser.parseNav(result.Navigation, NavEntryType.TOP_NAVIGATION);
        }
        return result;
    }
}
MegaMenuService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MegaMenuService, deps: [{ token: AppConfigService }, { token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
MegaMenuService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MegaMenuService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MegaMenuService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: AppConfigService }, { type: i1.HttpClient }]; } });

const iconSocialFacebook = 'exc-icon-social-facebook';
const iconSocialTwitter = 'exc-icon-social-twitter';
const iconSocialYouTube = 'exc-icon-social-youtube';
const iconSocialLinkedIn = 'exc-icon-social-linkedin';
const iconSocialInstagram = 'exc-icon-social-instagram';
const iconSocialPinterest = 'exc-icon-social-pinterest';
const iconSocialFlickr = 'exc-icon-social-flickr';
const iconSocialNextdoor = 'exc-icon-social-nextdoor';
const socialMediaLinks = {
    ComEd: [
        {
            url: 'http://www.facebook.com/ComEd',
            title: 'Visit ComEd on Facebook',
            iconClass: iconSocialFacebook,
            dataSocialIcon: 'facebook',
        },
        {
            url: 'http://www.twitter.com/ComEd',
            title: 'Visit ComEd on Twitter',
            iconClass: iconSocialTwitter,
            dataSocialIcon: 'twitter',
        },
        {
            url: 'https://www.youtube.com/user/CommonwealthEdison',
            title: 'Visit ComEd on YouTube',
            iconClass: iconSocialYouTube,
            dataSocialIcon: 'youtube',
        },
        {
            url: 'https://www.linkedin.com/company/comed',
            title: 'Visit ComEd on LinkedIn',
            iconClass: iconSocialLinkedIn,
            dataSocialIcon: 'linkedin',
        },
        {
            url: 'https://www.instagram.com/comed/',
            title: 'Visit ComEd on Instagram',
            iconClass: iconSocialInstagram,
            dataSocialIcon: 'instagram',
        },
        {
            url: 'https://www.pinterest.com/comedil/',
            title: 'Visit ComEd on Pinterest',
            iconClass: iconSocialPinterest,
            dataSocialIcon: 'pinterest',
        },
        {
            url: 'https://nextdoor.com/agency-detail/il/chicago/comed-9/?is=detail_author',
            title: 'Visit ComEd on Nextdoor',
            iconClass: iconSocialNextdoor,
            dataSocialIcon: 'nextdoor',
        }
    ],
    BGE: [
        {
            url: 'http://www.facebook.com/myBGE',
            title: 'Visit BGE on Facebook',
            iconClass: iconSocialFacebook,
            dataSocialIcon: 'facebook',
        },
        {
            url: 'http://twitter.com/mybge',
            title: 'Visit BGE on Twitter',
            iconClass: iconSocialTwitter,
            dataSocialIcon: 'twitter',
        },
        {
            url: 'http://www.youtube.com/BALTIMOREGASELECTRIC',
            title: 'Visit BGE on YouTube',
            iconClass: iconSocialYouTube,
            dataSocialIcon: 'youtube',
        },
        {
            url: 'http://www.linkedin.com/company/5115',
            title: 'Visit BGE on LinkedIn',
            iconClass: iconSocialLinkedIn,
            dataSocialIcon: 'linkedin',
        },
        {
            url: 'https://www.instagram.com/mybge/',
            title: 'Visit BGE on Instagram',
            iconClass: iconSocialInstagram,
            dataSocialIcon: 'instagram',
        },
        {
            url: 'https://nextdoor.com/agency-detail/md/baltimore/baltimore-gas-and-electric-company-1',
            title: 'Visit BGE on Nextdoor',
            iconClass: iconSocialNextdoor,
            dataSocialIcon: 'nextdoor',
        }
    ],
    PECO: [
        {
            url: 'https://www.facebook.com/pecoconnect',
            title: 'Visit PECO on Facebook',
            iconClass: iconSocialFacebook,
            dataSocialIcon: 'facebook',
        },
        {
            url: 'https://www.twitter.com/pecoconnect',
            title: 'Visit PECO on Twitter',
            iconClass: iconSocialTwitter,
            dataSocialIcon: 'twitter',
        },
        {
            url: 'https://www.youtube.com/pecoconnect',
            title: 'Visit PECO on YouTube',
            iconClass: iconSocialYouTube,
            dataSocialIcon: 'youtube',
        },
        {
            url: 'https://www.linkedin.com/company/4678',
            title: 'Visit PECO on LinkedIn',
            iconClass: iconSocialLinkedIn,
            dataSocialIcon: 'linkedin',
        },
        {
            url: 'https://www.instagram.com/pecoconnect/',
            title: 'Visit PECO on Instagram',
            iconClass: iconSocialInstagram,
            dataSocialIcon: 'instagram',
        },
        {
            url: 'https://nextdoor.com/agency-detail/pa/philadelphia/peco-3/',
            title: 'Visit PECO on Nextdoor',
            iconClass: iconSocialNextdoor,
            dataSocialIcon: 'nextdoor',
        }
    ],
    PEPCO: [
        {
            url: 'https://www.facebook.com/PepcoConnect',
            title: 'Visit Pepco on Facebook',
            iconClass: iconSocialFacebook,
            dataSocialIcon: 'facebook',
        },
        {
            url: 'https://twitter.com/PepcoConnect',
            title: 'Visit Pepco on Twitter',
            iconClass: iconSocialTwitter,
            dataSocialIcon: 'twitter',
        },
        {
            url: 'http://www.youtube.com/pepcotv',
            title: 'Visit Pepco on YouTube',
            iconClass: iconSocialYouTube,
            dataSocialIcon: 'youtube',
        },
        {
            url: 'https://www.linkedin.com/company/pepco',
            title: 'Visit Pepco on LinkedIn',
            iconClass: iconSocialLinkedIn,
            dataSocialIcon: 'linkedin',
        },
        {
            url: 'https://www.instagram.com/pepcoconnect',
            title: 'Visit Pepco on Instagram',
            iconClass: iconSocialInstagram,
            dataSocialIcon: 'instagram',
        },
        {
            url: 'https://nextdoor.com/agency/pepco-7/?i=lhclbntqdmdypxlsrbby',
            title: 'Visit Pepco on Nextdoor',
            iconClass: iconSocialNextdoor,
            dataSocialIcon: 'nextdoor',
        }
    ],
    ACE: [
        {
            url: 'https://www.facebook.com/AtlanticCityElectric',
            title: 'Visit Atlantic City Electric on Facebook',
            iconClass: iconSocialFacebook,
            dataSocialIcon: 'facebook',
        },
        {
            url: 'https://twitter.com/ACElecConnect',
            title: 'Visit Atlantic City Electric on Twitter',
            iconClass: iconSocialTwitter,
            dataSocialIcon: 'twitter',
        },
        {
            url: 'http://www.youtube.com/atlanticcityelectv',
            title: 'Visit Atlantic City Electric on YouTube',
            iconClass: iconSocialYouTube,
            dataSocialIcon: 'youtube',
        },
        {
            url: 'https://www.linkedin.com/company/atlantic-city-electric',
            title: 'Visit Atlantic City Electric on LinkedIn',
            iconClass: iconSocialLinkedIn,
            dataSocialIcon: 'linkedin',
        }
    ],
    DPL: [
        {
            url: 'https://www.facebook.com/DelmarvaPower',
            title: 'Visit Delmarva Power on Facebook',
            iconClass: iconSocialFacebook,
            dataSocialIcon: 'facebook'
        },
        {
            url: 'https://www.twitter.com/DelmarvaConnect',
            title: 'Visit Delmarva Power on Twitter',
            iconClass: iconSocialTwitter,
            dataSocialIcon: 'twitter'
        },
        {
            url: 'https://www.youtube.com/DelmarvaPowerTV',
            title: 'Visit Delmarva Power on YouTube',
            iconClass: iconSocialYouTube,
            dataSocialIcon: 'youtube'
        },
        {
            url: 'https://www.linkedin.com/company/delmarva-power',
            title: 'Visit Delmarva Power on LinkedIn',
            iconClass: iconSocialLinkedIn,
            dataSocialIcon: 'linkedin'
        }
    ]
};
const isDefined = value => value !== undefined && value !== null;
const BOTTOM_NAV_ENDPOINT = new InjectionToken('GetNavigation - bottom_nav json from xml');
const NO_BOTTOM_NAV_ENDPOINT = '';

class FooterService {
    constructor(megaMenuService, appConfigService, httpClient, bottomNavEndpoint) {
        this.megaMenuService = megaMenuService;
        this.appConfigService = appConfigService;
        this.httpClient = httpClient;
        this.bottomNavEndpoint = bottomNavEndpoint;
        this.defaultIsShowingNav = true;
        this.termsSubject = new BehaviorSubject([]);
        this.bottomNavigationSubject = new BehaviorSubject(null);
        this.bottomNavigation$ = this.bottomNavigationSubject.asObservable();
        this.bottomNavigation$
            .pipe(filter(isDefined))
            .subscribe(value => this.bottomNavigation = value);
        this.setupBottomNav();
    }
    get isShowingNav() {
        return this._isShowingNav !== undefined
            ? this._isShowingNav
            : this.defaultIsShowingNav;
    }
    set isShowingNav(state) {
        this._isShowingNav = state;
    }
    setupBottomNav() {
        if (this.bottomNavEndpoint === NO_BOTTOM_NAV_ENDPOINT && !this.isContentApi()) {
            return;
        }
        if (this.isContentApi()) {
            this.bottomNavUrl = `${this.appConfigService.config.contentApiBaseUrl}/api/content/navigation/footer`;
            if (this.appConfigService.config.contentPublishingEnvironment) {
                this.bottomNavUrl += `?environment=${this.appConfigService.config.contentPublishingEnvironment}`;
            }
            //this.getBottomNavigation().pipe(first(isDefined)).subscribe();
        }
        if (this.bottomNavEndpoint) {
            this.bottomNavUrl = this.bottomNavEndpoint;
            this.getBottomNavigation().pipe(first(isDefined)).subscribe();
        }
        const config = this.appConfigService.config || {};
        if (config.opCo && config.baseUrl) {
            this.constructAndRetrieveBottomNav();
        }
        else {
            this.appConfigService.config$
                .pipe(filter(c => isDefined(c.opCo)))
                .subscribe(_ => {
                if (this.bottomNavEndpoint === NO_BOTTOM_NAV_ENDPOINT && !this.isContentApi()) {
                    return;
                }
                this.constructAndRetrieveBottomNav();
            });
        }
    }
    isContentApi() {
        if (this.appConfigService.config.contentApiBaseUrl) {
            return true;
        }
        else {
            return false;
        }
    }
    constructAndRetrieveBottomNav() {
        const opCo = this.appConfigService.config.opCo;
        const baseUrl = this.appConfigService.config.baseUrl || '';
        if (this.isContentApi()) {
            this.bottomNavUrl = `${this.appConfigService.config.contentApiBaseUrl}/api/content/navigation/footer`;
            if (this.appConfigService.config.contentPublishingEnvironment) {
                this.bottomNavUrl += `?environment=${this.appConfigService.config.contentPublishingEnvironment}`;
            }
        }
        else {
            this.bottomNavUrl = `${baseUrl}/api/Services/MyAccountService.svc/GetNavigation?file=${opCo.toUpperCase()}_Bottom_Nav.xml`;
        }
        if (this.hasCalledGetBottomNavigation) {
            return;
        }
        this.getBottomNavigation().pipe(first(isDefined)).subscribe();
    }
    /** get's opCo Navigation from middleware and processes it for rendering
    //  */
    getNavigation() {
        if (this.isContentApi()) {
            return this.httpClient.get(this.bottomNavUrl).pipe(map((nav) => {
                return nav.nodes.map(node => {
                    return {
                        exc_Access: node.linkType,
                        exc_Url: node.url,
                        name: node.displayText,
                        exc_Use: "footerNavigation"
                    };
                });
            }));
        }
        else {
            if (this.megaMenuService.navigation) {
                this.processNavigation(this.megaMenuService.navigation);
            }
            else {
                if (!this.megaMenuService.hasCalledGetMenu) {
                    this.megaMenuService.getMenu();
                }
                this.megaMenuService.menuObservable.subscribe(() => {
                    const footerNavs = this.processNavigation(this.megaMenuService.navigation);
                    this.termsSubject.next(footerNavs);
                });
            }
            return this.termsSubject.asObservable();
        }
    }
    getBottomNavigation() {
        this.hasCalledGetBottomNavigation = true;
        return this.httpClient.get(this.bottomNavUrl).pipe(tap(this.processBottomNavigation.bind(this)));
    }
    processNavigation(payload) {
        if (!payload) {
            return;
        }
        const terms = payload.Navigation.terms.filter(x => x.exc_Use === 'footerNavigation');
        return terms;
    }
    processBottomNavigation(result) {
        BottomNavigationParser.anonBaseUrl = this.appConfigService.config.anonBaseUrl;
        BottomNavigationParser.baseUrl = this.appConfigService.config.baseUrl;
        this.bottomNavEntries = NavJSONParser.parseNav(result.Navigation, NavEntryType.BOTTOM_NAVIGATION);
        this.bottomNavigationSubject.next(result);
        return result;
    }
    getFooterSocialMedias() {
        return (this.footerSocialMedias = socialMediaLinks[this.appConfigService.config.opCo]);
    }
}
FooterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FooterService, deps: [{ token: MegaMenuService }, { token: AppConfigService }, { token: i1.HttpClient }, { token: BOTTOM_NAV_ENDPOINT, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
FooterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FooterService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FooterService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: MegaMenuService }, { type: AppConfigService }, { type: i1.HttpClient }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [BOTTOM_NAV_ENDPOINT]
                    }] }];
    } });

// anon base urls
const contactUsUrl = '/MyAccount/CustomerSupport/Pages/ContactUs.aspx';
const userRegistrationLandingUrl = '/Pages/UserRegistrationLanding.aspx';
// the following two urls and their inclusion as constants were discussed at length in teams on Nov. 22. opting to KIS
const registrationUrl = '/accounts/registration';
const registerVpv = '/Vpv/accounts/registration/header';
const signOutUrl = '/Pages/Logout.aspx';
const signInUrl = SIGNIN_URL;
const forgotUsernameUrl = '/Pages/ForgotUsername.aspx';
const forgotPasswordUrl = '/Pages/ForgotPassword.aspx';
const forgotCredentialsUrl = '/Pages/ForgotUsername.aspx';
const UTILITY_NAV_ENDPOINT = new InjectionToken('GetNavigation - utility_nav json from xml');
const NO_UTILITY_NAV_ENDPOINT = '';

function mockableGetter() {
    if (this.utilityNavEndpoint === NO_UTILITY_NAV_ENDPOINT) {
        return defaultMockedResponse();
    }
    else {
        return this.originalMethod();
    }
}
function defaultMockedResponse() {
    console.log('[getUtilityNav] NO_UTILITY_NAV_ENDPOINT set');
    return of({});
}

class HeaderService {
    constructor(httpClient, appConfigService, utilityNavEndpoint) {
        this.httpClient = httpClient;
        this.appConfigService = appConfigService;
        this.utilityNavEndpoint = utilityNavEndpoint;
        this.setupSearchUrl();
        this.setupUtilityNav();
    }
    get userRegistrationLandingUrl() {
        var _a;
        let anonBaseUrl = (_a = this.appConfigService.config) === null || _a === void 0 ? void 0 : _a.anonBaseUrl;
        anonBaseUrl = anonBaseUrl ? anonBaseUrl + '/' : '';
        return this._userRegistrationLandingUrl || `${anonBaseUrl}${userRegistrationLandingUrl}`;
    }
    set userRegistrationLandingUrl(value) {
        this._userRegistrationLandingUrl = value;
    }
    get forgotUsernameUrl() {
        return this._forgotUsernameUrl || forgotUsernameUrl;
    }
    set forgotUsernameUrl(value) {
        this._forgotUsernameUrl = value;
    }
    get forgotPasswordUrl() {
        return this._forgotPasswordUrl || forgotPasswordUrl;
    }
    set forgotPasswordUrl(value) {
        this._forgotPasswordUrl = value;
    }
    get forgotCredentialsUrl() {
        return this._forgotCredentialsUrl || forgotCredentialsUrl;
    }
    set forgotCredentialsUrl(value) {
        this._forgotCredentialsUrl = value;
    }
    get signInUrl() {
        return this._signInUrl || signInUrl;
    }
    set signInUrl(value) {
        this._signInUrl = value;
    }
    get signOutUrl() {
        return this._signOutUrl || signOutUrl;
    }
    set signOutUrl(value) {
        this._signOutUrl = value;
    }
    get registerUrl() {
        return this._registerUrl || registrationUrl;
    }
    set registerUrl(value) {
        this._registerUrl = value;
    }
    get registerVpv() {
        return this._registerVpv || registerVpv;
    }
    set registerVpv(value) {
        this._registerVpv = value;
    }
    get contactUsUrl() {
        if (!this._contactUsUrl) { // lazy-load the contactUsUrl
            let anonBaseUrl = this.appConfigService.config && this.appConfigService.config.anonBaseUrl;
            if (anonBaseUrl.endsWith('/') && contactUsUrl.startsWith('/')) {
                this._contactUsUrl = `${anonBaseUrl}${contactUsUrl.substring(1)}`;
            }
            else if (!anonBaseUrl.endsWith('/') && !contactUsUrl.startsWith('/')) {
                this._contactUsUrl = `${anonBaseUrl}/${contactUsUrl.substring(1)}`;
            }
            else {
                this._contactUsUrl = `${anonBaseUrl}${contactUsUrl}`;
            }
        }
        return this._contactUsUrl;
    }
    get outageUrl() {
        return this._outageUrl;
    }
    get payBillUrl() {
        return this._payBillUrl;
    }
    get movingUrl() {
        return this._movingUrl;
    }
    get españolUrl() {
        return this._españolUrl;
    }
    get chineseUrl() {
        return this._chineseUrl;
    }
    get utilityNavLinks() {
        return this._utilityNavLinks;
    }
    handleUtilityNav(result) {
        if (!result ||
            typeof result !== 'object' ||
            (!this.isContentApi() &&
                (!result.hasOwnProperty('Navigation')
                    || !result.Navigation.hasOwnProperty('terms') || !Array.isArray(result.Navigation.terms)))
            || (this.isContentApi() && !result.hasOwnProperty("nodes"))) {
            return;
        }
        this.setupAttributesUrls(result);
        this.setupUtilityNavLinks(result);
    }
    setupUtilityNav() {
        var _a, _b;
        if (this.utilityNavEndpoint === NO_UTILITY_NAV_ENDPOINT && !this.isContentApi()) {
            return;
        }
        else if (this.isContentApi()) {
            this.utilityNavUrl = `${this.appConfigService.config.contentApiBaseUrl}/api/content/navigation/header`;
            if (this.appConfigService.config.contentPublishingEnvironment) {
                this.utilityNavUrl += `?environment=${this.appConfigService.config.contentPublishingEnvironment}`;
            }
            this.getUtilityNav().subscribe(this.handleUtilityNav.bind(this));
        }
        if (this.utilityNavEndpoint) {
            this.utilityNavUrl = this.utilityNavEndpoint;
            this.getUtilityNav().subscribe(this.handleUtilityNav.bind(this));
        }
        let opCo;
        let baseUrl;
        if (((_a = this.appConfigService.config) === null || _a === void 0 ? void 0 : _a.opCo)
            && ((_b = this.appConfigService.config) === null || _b === void 0 ? void 0 : _b.baseUrl)) {
            opCo = this.appConfigService.config.opCo;
            baseUrl = this.appConfigService.config.baseUrl || '';
            this.setupUtilityNavUrlWithStrings(opCo, baseUrl);
            this.getUtilityNav().subscribe(this.handleUtilityNav.bind(this));
        }
        else {
            this.appConfigService.config$.pipe(filter(config => config.opCo != null)).subscribe(config => {
                if (this.utilityNavUrl) {
                    return; // user has set this up while we waited for the GetConfiguration call to complete
                }
                opCo = this.appConfigService.config.opCo;
                baseUrl = this.appConfigService.config.baseUrl || '';
                this.setupUtilityNavUrlWithStrings(opCo, baseUrl);
                this.getUtilityNav().subscribe(this.handleUtilityNav.bind(this));
            });
        }
    }
    setupUtilityNavUrlWithStrings(opCo, baseUrl) {
        if (this.isContentApi()) {
            this.utilityNavUrl = `${this.appConfigService.config.contentApiBaseUrl}/api/content/navigation/header`;
            if (this.appConfigService.config.contentPublishingEnvironment) {
                this.utilityNavUrl += `?environment=${this.appConfigService.config.contentPublishingEnvironment}`;
            }
        }
        else {
            this.utilityNavUrl = `${baseUrl}/api/Services/MyAccountService.svc/GetNavigation?file=${opCo.toUpperCase()}_Utility_Nav.xml`;
        }
    }
    setupSearchUrl() {
        this.generateSearchUrl = this.defaultSearch.bind(this);
    }
    isContentApi() {
        if (this.appConfigService.config.contentApiBaseUrl) {
            return true;
        }
        else {
            return false;
        }
    }
    findTerm(result, termName) {
        if (this.isContentApi()) {
            return result.nodes.find(term => term.displayText === termName);
        }
        else {
            result.Navigation.terms.find(term => term.name === term);
        }
    }
    setupAttributesUrls(result) {
        const outageAttributes = this.findTerm(result, 'Outage');
        const payBillAttributes = this.findTerm(result, 'Pay Bill');
        const movingAttributes = this.findTerm(result, 'Moving');
        const españolAttributes = this.findTerm(result, 'Español');
        const chineseAttributes = this.findTerm(result, '中文');
        this._outageUrl = this.computeAttributesUrl(outageAttributes);
        this._payBillUrl = this.computeAttributesUrl(payBillAttributes);
        this._movingUrl = this.computeAttributesUrl(movingAttributes);
        this._españolUrl = this.computeAttributesUrl(españolAttributes);
        this._chineseUrl = this.computeAttributesUrl(chineseAttributes);
    }
    setupUtilityNavLinks(result) {
        let returnNavLinks = [];
        if (this.isContentApi()) {
            result.nodes.forEach(term => {
                let thisNavLink = {};
                if (term.displayText) {
                    thisNavLink.name = term.displayText;
                }
                if (term.url) {
                    thisNavLink.exc_Url = term.url;
                }
                thisNavLink.url = this.computeAttributesUrl(term);
                returnNavLinks.push(thisNavLink);
            });
        }
        else {
            result.Navigation.terms.forEach(term => {
                let thisNavLink = {};
                if (term.name) {
                    thisNavLink.name = term.name;
                }
                if (term.exc_Url) {
                    thisNavLink.exc_Url = term.exc_Url;
                }
                thisNavLink.url = this.computeAttributesUrl(term);
                returnNavLinks.push(thisNavLink);
            });
        }
        this._utilityNavLinks = returnNavLinks;
    }
    computeAttributesUrl(attributes) {
        if (typeof attributes === 'object') {
            let url = this.isContentApi() ? attributes.url : attributes.exc_Url;
            let access = this.isContentApi() ? attributes.linkType : attributes.exc_Access;
            if (!url) {
                return;
            }
            if (/^[^\/]/.test(url)) {
                return url;
            }
            else if (access === 'Secure') {
                return this.appConfigService.config.baseUrl + url;
            }
            else {
                return this.appConfigService.config.anonBaseUrl + url;
            }
        }
    }
    getUtilityNav() {
        return this.httpClient.get(this.utilityNavUrl)
            .pipe(catchError(this.handleError('getUtilityNav')));
    }
    handleError(operation = 'operation', result = {}) {
        return (error) => {
            console.warn(`[HeaderService] ${operation} failed: ${error.message}`);
            // Let the app keep running by returning an empty result.
            return of(result);
        };
    }
    defaultSearch(term) {
        return this.appConfigService.config.anonBaseUrl + '/Search/Pages/results.aspx?k=' + encodeURI(term);
    }
}
HeaderService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: HeaderService, deps: [{ token: i1.HttpClient }, { token: AppConfigService }, { token: UTILITY_NAV_ENDPOINT, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
HeaderService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: HeaderService });
__decorate([
    Dummyable({ factoryResponse: mockableGetter })
], HeaderService.prototype, "getUtilityNav", null);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: HeaderService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: i1.HttpClient }, { type: AppConfigService }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [UTILITY_NAV_ENDPOINT]
                    }] }];
    }, propDecorators: { getUtilityNav: [] } });

class EuDataServicesModule {
    static mockApiBrokerService(options) {
        return [
            {
                provide: MOCK_API_BROKER_OPTIONS,
                useValue: options
            },
            {
                provide: ApiBrokerService,
                useClass: MockApiBrokerService
            }
        ];
    }
}
EuDataServicesModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EuDataServicesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
EuDataServicesModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: EuDataServicesModule, imports: [CommonModule] });
EuDataServicesModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EuDataServicesModule, providers: [
        ApiBrokerService,
        MegaMenuService,
        HeaderService,
        FooterService,
        GoogleAnalyticsService,
        EuAccountsService
    ], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EuDataServicesModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [],
                    providers: [
                        ApiBrokerService,
                        MegaMenuService,
                        HeaderService,
                        FooterService,
                        GoogleAnalyticsService,
                        EuAccountsService
                    ]
                }]
        }] });

function factoryPopupmenuOption(option = {}) {
    if (!option.hasOwnProperty('id')) {
        option.id = UUID();
    }
    if (!['value', 'template'].every(property => option.hasOwnProperty(property))) {
        option.value = UUID();
    }
    return option;
}

class AbstractValueAccessor {
    constructor() {
        // The internal data model
        this.innerValue = '';
    }
    // Placeholders for the callbacks which are later provided
    // by the Control Value Accessor
    onTouchedCallback() { }
    onChangeCallback(_) { }
    /*Getter for value of element.
    * @method value
    * @param none
    * @return {any} value
    */
    get value() {
        return this.innerValue;
    }
    /*Setter of value to the element and calls onChangeCallback
    * @method value
    * @param {any}
    * @return none
    */
    set value(v) {
        if (v !== this.innerValue) {
            this.innerValue = v;
            this.onChangeCallback(v);
        }
    }
    /*Writes a new value to the element.
    * @method writeValue
    * @param {value}
    * @return none
    */
    onBlur() {
        this.onTouchedCallback();
    }
    /*Writes a new value to the element.
    * @method writeValue
    * @param {value}
    * @return none
    */
    writeValue(value) {
        if (value !== this.innerValue) {
            this.innerValue = value;
        }
    }
    /*Registers a callback function that should be called when the control value changes in the UI.
    * @method registerOnChange
    * @param {Function}
    * @return none
    */
    registerOnChange(fn) {
        this.onChangeCallback = fn;
    }
    /*Registers a callback function that should be called when the control receives a blur event.
    * @method registerOnTouched
    * @param {Function}
    * @return none
    */
    registerOnTouched(fn) {
        this.onTouchedCallback = fn;
    }
}
function MakeProvider(type) {
    return {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => type),
        multi: true
    };
}

var SCREEN_SIZE;
(function (SCREEN_SIZE) {
    SCREEN_SIZE[SCREEN_SIZE["XS"] = 0] = "XS";
    SCREEN_SIZE[SCREEN_SIZE["SM"] = 1] = "SM";
    SCREEN_SIZE[SCREEN_SIZE["MD"] = 2] = "MD";
    SCREEN_SIZE[SCREEN_SIZE["LG"] = 3] = "LG";
    SCREEN_SIZE[SCREEN_SIZE["XL"] = 4] = "XL";
})(SCREEN_SIZE || (SCREEN_SIZE = {}));
const MEDIA_DESKTOP = [SCREEN_SIZE.XL, SCREEN_SIZE.LG];
const MEDIA_TABLET = [SCREEN_SIZE.MD];
const MEDIA_MOBILE = [SCREEN_SIZE.SM, SCREEN_SIZE.XS];
const MEDIA_DEVICES_NOT_DESKTOP = [SCREEN_SIZE.MD, SCREEN_SIZE.SM, SCREEN_SIZE.XS];
const MEDIA_DEVICES_NOT_MOBILE = [SCREEN_SIZE.MD, SCREEN_SIZE.LG, SCREEN_SIZE.XL];

function toCamelCase(str = '') {
    return str
        .replace(/\s(.)/g, function ($1) { return $1.toUpperCase(); })
        .replace(/[^A-Za-z0-9_\-]/g, '')
        .replace(/^(.)/, function ($1) { return $1.toLowerCase(); });
}

var copy$3 = {
    back: 'Back',
    subMenu: 'sub menu'
};

class MegaMenuComponent {
    // tslint:enable: no-output-rename member-ordering
    constructor(megaMenuService, appSession, appConfigService, appLoggerService = AppLoggerService.nullLogger, cd, window /* TODO: WindowWrapper usage */, location /* TODO: LocationWrapper usage */) {
        this.megaMenuService = megaMenuService;
        this.appSession = appSession;
        this.appConfigService = appConfigService;
        this.appLoggerService = appLoggerService;
        this.cd = cd;
        this.window = window;
        this.location = location;
        this.copyBack = copy$3.back;
        this.copySubMenu = copy$3.subMenu;
        this.tabs = [];
        this.panels = {};
        this.subscriptions = new Subscription();
        this.isHamburger = false; // for mobile
        this.mouseenterTabOptionEvent = new EventEmitter();
        this.mouseleaveTabOptionEvent = new EventEmitter();
        this.mouseenterMenuPanelEvent = new EventEmitter();
        this.mouseleaveMenuPanelEvent = new EventEmitter();
        this.mouseenterPanelOptionEvent = new EventEmitter();
        this.mouseleavePanelOptionEvent = new EventEmitter();
        this.clickOptionEvent = new EventEmitter();
        if (!this.megaMenuService.hasCalledGetMenu) {
            this.megaMenuService.getMenu().pipe(take(1)).subscribe(result => { });
        }
    }
    /**
     * true when we have interpreted the json returned from the service, producing a menu
     */
    get hasMenu() {
        return typeof this.megaMenuService.menu === 'object';
    }
    get menu() {
        const menu = this.megaMenuService.menu || [];
        const _menuHash = Md5.init(JSON.stringify(menu));
        if (_menuHash !== this._previousMenuHash) {
            this._previousMenuHash = _menuHash;
            this.generateTabs();
            this.generatePanels();
        }
        return menu;
    }
    /** true when the panel, the piece that gives the mega menu its name, is visible. in mobile,
     * true when section headers are visible in teh accordion.
     */
    get isShowingMenuPanel() {
        if (!(this.isHoveringTabOption || this.isHoveringPanel)
            && (this.activeTab || this.activePanelOption)) {
            setTimeout(this.setActiveTab.bind(this), 0);
        }
        return (this.isHoveringTabOption || this.isHoveringPanel) && this.panels[this.activeTab].length;
    }
    get megaMenuClass() {
        const classes = [];
        if (this.hasExpandedSection) {
            classes.push('panel-expanded');
        }
        if (this.isHamburger) {
            classes.push('mobile-only');
        }
        return classes.join(' ');
    }
    ngOnInit() {
        this.subscriptions.add(this.appSession.session$.subscribe((session) => {
            if (session) {
                this.generateTabs();
                this.generatePanels();
            }
        }));
        this.subscriptions.add(this.megaMenuService.menuObservable.subscribe(x => {
            this.generateTabs();
            this.generatePanels();
        }));
        this.opCo = this.appConfigService.config.opCo;
        if (!this.opCo) {
            this.appConfigService.config$.pipe(filter(config => config.opCo != null)).subscribe(value => {
                this.opCo = value.opco;
            });
        }
    }
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }
    setActiveTab() {
        this.activeTab = null;
        this.activePanelOption = null;
        this.cd.detectChanges();
    }
    generateTabs() {
        const self = this;
        this.tabs = this.menu
            .filter((_, index) => self.isMegaMenuItemDisplayable(self.menu[index]))
            .map(value => {
            const key = value.title;
            const tab = { type: 'tab', id: Md5.init(key), value: key, class: toCamelCase(key) };
            if (value.url) {
                tab.url = value.url;
            }
            return tab;
        });
    }
    generatePanels() {
        const self = this;
        this.menu
            .filter(value => self.isMegaMenuItemDisplayable(value))
            .forEach((value, index) => {
            const tab = value.title;
            self.panels[tab] = value.topicEntries
                .filter(self.isMegaMenuItemDisplayable.bind(self))
                .map(section => ({
                section: {
                    type: 'section',
                    id: `${Md5.init(tab)}--${Md5.init(JSON.stringify(section))}`,
                    class: toCamelCase(section.title),
                    value: section.title,
                    url: section.url,
                    newTarget: section.newTarget,
                },
                entries: section.topicEntries
                    .filter(self.isMegaMenuItemDisplayable.bind(self))
                    .map(entry => {
                    return {
                        type: 'entry',
                        id: `${Md5.init(tab)}--${Md5.init(JSON.stringify(section))}--${Md5.init(JSON.stringify(entry))}`,
                        class: toCamelCase(entry.title),
                        value: entry.title,
                        url: entry.url,
                        newTarget: entry.newTarget,
                    };
                })
            }));
        });
    }
    isMegaMenuItemDisplayable(entry) {
        const isAuthenticated = this.appSession.isUserAuthenticated();
        const isResidential = this.appSession.isResidential();
        if (entry.authenticatedOnly && !isAuthenticated) {
            return false;
        }
        if (entry.unauthenticatedOnly && isAuthenticated) {
            return false;
        }
        if (entry.residentialOnly && !isResidential) {
            return false;
        }
        return true;
    }
    onFocusTab(tab) {
        if (!this.isHamburger) {
            this.appLoggerService.debug('onFocusTab - info: onFoncus in non-mobile view', tab, this.activeTab, tab.value === this.activeTab);
            if (tab.value === this.activeTab && this.subMenuRef) {
                this.subMenuRef.nativeElement.focus();
            }
        }
    }
    onBlurSubMenu(option) {
        const panels = this.panels[this.activeTab];
        const lastIndex = panels.length - 1;
        const findEntry = entry => entry === option;
        if (panels[lastIndex].entries.findIndex(findEntry) === panels[lastIndex].entries.length - 1) {
            this.toggleTab(null, false);
            if (this.hasMenu) {
                this.menubarRef.nativeElement.focus();
            }
        }
    }
    onMouseenterTabOption(tab) {
        this.appLoggerService.debug('onMouseenterTabOption', this.panels);
        if (this.isHamburger) {
            return;
        }
        clearTimeout(this.timerSetHoveringTabOption);
        clearTimeout(this.timerSetHoveringPanel);
        this.willBeHoveringTabOption = true;
        const self = this;
        setTimeout(function () {
            if (self.willBeHoveringTabOption) {
                self.isHoveringTabOption = true;
                self.activeTab = tab.value;
                self.cd.detectChanges();
            }
        }, 400);
        this.mouseenterTabOptionEvent.emit(tab);
    }
    onMouseleaveTabOption(tab) {
        this.appLoggerService.debug('onMouseleaveTabOption');
        if (this.isHamburger) {
            return;
        }
        const self = this;
        this.timerSetHoveringTabOption = setTimeout(() => self.isHoveringTabOption = false, 100);
        this.willBeHoveringTabOption = false;
        this.mouseleaveTabOptionEvent.emit(tab);
    }
    onMouseenterMenuPanel() {
        this.appLoggerService.debug('onMouseenterMenuPanel');
        if (this.isHamburger) {
            return;
        }
        this.isHoveringPanel = true;
        this.mouseenterPanelOptionEvent.emit(null);
    }
    onMouseleaveMenuPanel() {
        this.appLoggerService.debug('onMouseleaveMenuPanel');
        if (this.isHamburger) {
            return;
        }
        const self = this;
        this.timerSetHoveringPanel = setTimeout(() => self.isHoveringPanel = false, 100);
        this.mouseleavePanelOptionEvent.emit(null);
    }
    onMouseenterPanelOption(panel) {
        this.appLoggerService.debug('onMouseenterPanelOption');
        if (this.isHamburger) {
            return;
        }
        this.activePanelOption = panel.id;
        this.mouseenterPanelOptionEvent.emit(panel);
    }
    onMouseleavePanelOption(panel) {
        this.appLoggerService.debug('onMouseleavePanelOption');
        this.mouseleavePanelOptionEvent.emit(panel);
    }
    toggleTab(tab, state) {
        if (state !== undefined) {
            if (state) {
                tab.isExpanded = true;
            }
            else {
                this.isHoveringTabOption = false;
                this.activeTab = null;
                if (tab) { // may be null
                    delete tab.isExpanded;
                }
                else {
                    this.tabs.forEach(t => {
                        if (t.hasOwnProperty('isExpanded')) {
                            delete t.isExpanded;
                        }
                    });
                }
                return;
            }
        }
        else {
            if (tab.isExpanded) {
                this.isHoveringTabOption = false;
                this.activeTab = null;
                delete tab.isExpanded;
                return;
            }
        }
        this.isHoveringTabOption = true;
        this.activeTab = tab.value;
        this.tabs.forEach(t => delete t.isExpanded);
        tab.isExpanded = true;
        this.cd.detectChanges();
    }
    toggleSection(section) {
        if (section.isExpanded) {
            delete section.isExpanded;
            this.hasExpandedSection = false;
            return;
        }
        Object.keys(this.panels).forEach(label => {
            this.panels[label].forEach(panel => {
                if (panel.section && panel.section.hasOwnProperty('isExpanded')) {
                    delete panel.section.isExpanded;
                }
            });
        });
        section.isExpanded = true;
        this.hasExpandedSection = true;
    }
    onClickSectionExpander(section, event) {
        if (section.isExpanded) {
            this.activePanelOption = null;
        }
        this.toggleSection(section);
        event.preventDefault();
        event.stopPropagation();
    }
    onClickOption(option, event) {
        this.clickOptionEvent.emit(option);
        if (event && event.target && event.target.parentElement) {
            if (event.target.parentElement.classList.contains('section--control')) {
                return;
            }
            if (event.type === 'keyup' && option.type === 'tab' && this.isHamburger) {
                return;
            }
        }
        if (option.type === 'tab') {
            this.toggleTab(option);
        }
        if (option.url) {
            this.isHoveringPanel = false;
            this.isHoveringTabOption = false;
            if (option.newTarget) {
                this.window.open(option.url, '_blank');
            }
            else {
                this.location.href = option.url;
            }
        }
    }
}
MegaMenuComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MegaMenuComponent, deps: [{ token: MegaMenuService }, { token: AppSessionService }, { token: AppConfigService }, { token: AppLoggerService, optional: true }, { token: i0.ChangeDetectorRef }, { token: WINDOW }, { token: WINDOW_LOCATION }], target: i0.ɵɵFactoryTarget.Component });
MegaMenuComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: MegaMenuComponent, selector: "app-mega-menu", inputs: { isHamburger: "isHamburger" }, outputs: { mouseenterTabOptionEvent: "mouseenterTabOption", mouseleaveTabOptionEvent: "mouseleaveTabOption", mouseenterMenuPanelEvent: "mouseenterMenuPanel", mouseleaveMenuPanelEvent: "mouseleaveMenuPanel", mouseenterPanelOptionEvent: "mouseenterPanelOption", mouseleavePanelOptionEvent: "mouseleavePanelOption", clickOptionEvent: "clickPanelOption" }, viewQueries: [{ propertyName: "menuPanelRef", first: true, predicate: ["menuPanel"], descendants: true, static: true }, { propertyName: "subMenuRef", first: true, predicate: ["subMenu"], descendants: true }, { propertyName: "menubarRef", first: true, predicate: ["menubar"], descendants: true }], ngImport: i0, template: "<nav class=\"component common mega-menu\" [ngClass]=\"megaMenuClass\">\r\n  <div class=tabs-wrapper>\r\n    <ul #menubar class=\"menu-banner tabs\" role=\"menu\" tabindex=-1 *ngIf=\"hasMenu\">\r\n      <li *ngFor=\"let tab of tabs\"\r\n          class=\"tab\"\r\n          [ngClass]=\"{active: activeTab === tab.value}\"\r\n          [attr.aria-label]=\"tab.value\"\r\n          [attr.aria-haspopup]=\"'true'\"\r\n          role=\"menuitem\"\r\n          tabindex=0\r\n          (click)=\"onClickOption(tab)\"\r\n          (keyup.enter)=\"onClickOption(tab, $event)\"\r\n          (keydown.tab)=\"onFocusTab(tab)\"\r\n          (mouseenter)=\"onMouseenterTabOption(tab)\"\r\n          (mouseleave)=\"onMouseleaveTabOption(tab)\">\r\n        <div class=tab-content>\r\n          <span [id]=\"'tab_' + tab.id\" class=\"copy tab-option\" [ngClass]=\"{active: activeTab === tab.value}\">{{ tab.value }}</span>\r\n\r\n          <div class=\"control expander tab--control\"\r\n               [ngClass]=\"{expanded: tab.isExpanded}\"\r\n               [attr.aria-label]=\"tab.isExpanded ? 'Collapse': 'Expand'\"\r\n               tabindex=0\r\n               (keyup.enter)=\"onClickOption(tab)\">\r\n            <span class=\"icon\" [ngClass]=\"'icon-' + (tab.isExpanded ? 'minus': 'plus')\"></span>\r\n          </div>\r\n        </div>\r\n        <ng-container [ngTemplateOutlet]=\"menuPanelRef\" *ngIf=\"isHamburger\"></ng-container>\r\n      </li>\r\n    </ul>\r\n  </div>\r\n  <ng-container [ngTemplateOutlet]=\"menuPanelRef\" *ngIf=\"!isHamburger\"></ng-container>\r\n</nav>\r\n\r\n<ng-template #menuPanel>\r\n  <div class=\"menu-panel-wrapper\" *ngIf=\"isShowingMenuPanel\">\r\n    <div #subMenu\r\n        role=\"menu\"\r\n        class=\"menu-panel\"\r\n        tabindex=-1\r\n        [attr.aria-label]=\"copySubMenu\"\r\n        (mouseenter)=\"onMouseenterMenuPanel()\"\r\n        (mouseleave)=\"onMouseleaveMenuPanel()\">\r\n      <ul class=\"panels\" role=\"presentation\">\r\n        <li *ngFor=\"let panel of panels[activeTab]\" class=\"panel\">\r\n          <ul [id]=\"'panel_' + panel.section.id\" class=\"panel-and-section\" [ngClass]=\"{expanded: panel.section.isExpanded}\">\r\n            <li class=\"panel-option section\"\r\n                [ngClass]=\"panel.section.class + (activePanelOption === panel.section.id ? ' active': (activePanelOption ? ' inactive' : '')) + (activePanelOption === panel.section.id ? ' bg-'+opCo+'-primaryBrand': '')\"\r\n                role=\"menuitem\"\r\n                [attr.aria-label]=\"panel.section.value\"\r\n                [attr.aria-haspopup]=\"!!panel.section.newTarget ? 'true': null\"\r\n                tabindex=0\r\n                (mouseenter)=\"onMouseenterPanelOption(panel.section)\"\r\n                (mouseleave)=\"onMouseleavePanelOption(panel.section)\"\r\n                (keyup.enter)=\"onClickOption(panel.section)\"\r\n                (click)=\"onClickOption(panel.section)\">\r\n              <span [id]=\"panel.section.id\" class=\"copy text-button--copy\">{{ panel.section.value }}</span>\r\n\r\n              <div class=\"control expander section--control\"\r\n                   [attr.aria-label]=\"panel.section.isExpanded ? 'Collapse': 'Expand'\"\r\n                   tabindex=0\r\n                   [ngClass]=\"{expanded: panel.section.isExpanded}\"\r\n                   (click)=\"onClickSectionExpander(panel.section, $event)\"\r\n                   (keyup.enter)=\"onClickSectionExpander(panel.section, $event)\">\r\n                <span class=\"icon\" [ngClass]=\"'icon-' + (panel.section.isExpanded ? 'minus': 'plus')\"></span>\r\n              </div>\r\n            </li>\r\n            <ul class=\"panel-options\" [ngClass]=\"{expanded: panel.section.isExpanded}\">\r\n              <li class=\"collapser section--control\" [ngClass]=\"{expanded: panel.section.isExpanded}\">\r\n                  <div class=\"control collapser section--control\"\r\n                       attr.aria-label=\"Collapse\"\r\n                       tabindex=0\r\n                       [ngClass]=\"{expanded: panel.section.isExpanded}\"\r\n                       (click)=\"onClickSectionExpander(panel.section, $event)\"\r\n                       (keyup.enter)=\"onClickSectionExpander(panel.section, $event)\">\r\n                    <span class=\"icon icon-chevron-left\"></span><span class=\"copy  control-back\">{{ copyBack }}</span>\r\n                  </div>\r\n              </li>\r\n              <li class=\"panel-option\"\r\n                  role=\"menuitem\"\r\n                  [ngClass]=\"option.class + (activePanelOption === option.id ? ' active':'') + (activePanelOption === option.id ? ' bg-'+opCo+'-primaryBrand': '')\"\r\n                  [attr.aria-label]=\"option.value\"\r\n                  [attr.aria-haspopup]=\"!!option.newTarget ? 'true': null\"\r\n                  tabindex=0\r\n                  (mouseenter)=\"onMouseenterPanelOption(option)\"\r\n                  (mouseleave)=\"onMouseleavePanelOption(option)\"\r\n                  (click)=\"onClickOption(option)\"\r\n                  (keyup.enter)=\"onClickOption(option)\"\r\n                  (keydown.tab)=\"onBlurSubMenu(option)\"\r\n                  *ngFor=\"let option of panel.entries;\">\r\n                <span [id]=\"'option_' + option.id\" [attr.aria-label]=\"option.value\" class=\"copy text-button--copy\">{{ option.value }}</span>\r\n              </li>\r\n            </ul>\r\n          </ul>\r\n        </li>\r\n      </ul>\r\n    </div>\r\n  </div>\r\n</ng-template>\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}:focus{outline:thin dotted}.component.common.mega-menu{position:relative}@media (max-width: 767.98px){.component.common.mega-menu{display:none}.component.common.mega-menu.mobile-only{display:initial}}@media (min-width: 768px){.component.common.mega-menu{display:initial}.component.common.mega-menu.mobile-only,.component.common.mega-menu .control{display:none}}.component.common.mega-menu ::ng-deep .btn-text:after{border-bottom:none}ul{list-style:none;margin:0;padding:0}.tabs-wrapper{display:flex;flex-direction:row;justify-content:center;width:100%;height:55px;background-color:#d8d8d8}.menu-banner.tabs{max-width:1170px}.tabs{width:100%;max-width:1070px;display:flex;flex-direction:row;justify-content:space-around}@media (max-width: 1199.98px){.tabs{max-width:100vw}}.tabs .tab{flex:1 1 100%;display:flex;flex-direction:column;justify-content:center;align-items:center;min-height:min-content}.tabs .tab.active{background-color:#ececec}.menu-panel,.menu-panel-wrapper{display:flex;flex-direction:row;justify-content:center}.menu-panel{width:100%;max-width:1280px;padding:30px;background-color:#ececec}.panels{display:flex;flex-direction:row;width:100%;animation-name:slidein;animation-duration:.25s;animation-fill-mode:forwards;animation-direction:alternate}.panels .panel{flex:1 1 100%}.panel-option{cursor:pointer;padding:10px}.panel-option.active{background-color:var(--primaryBrand)}.panel-option.active .copy{color:#fff}.copy{font-family:Open Sans Regular;font-weight:400;font-size:14px;color:#4a4a4a}.tab-option{font-size:16px}.tab-option.active,.section .text-button--copy{font-family:Open Sans Semi Bold;font-weight:600}.control.expander{margin:-10px;padding:10px;border-left:solid thin}.control.expander.tab--control{border-color:#ececec}.control.expander.section--control,.tab.active .tab--control{border-color:#d8d8d8}.control.collapser{display:flex;flex-direction:row}.control-back{margin-left:5px}li.collapser{padding:10px}@keyframes slidein{0%{margin-top:-100px}to{margin-top:0}}.icon{speak:none;font-family:icomoon;font-weight:600;font-size:16px;color:#737373;display:block}.icon-plus:before{content:\"\\e936\"}.icon-minus:before{content:\"\\e937\"}.icon-chevron-left:before{content:\"\\e93b\"}@media (max-width: 767.98px){.menu-panel-wrapper{overflow:hidden;position:absolute;top:55px;left:0;right:0;z-index:101}.component.common.mega-menu,.tabs-wrapper{position:relative;height:auto;width:100vw}.tabs,.panels{flex-direction:column}.tab{flex:1 1 100%;display:flex;flex-direction:column;justify-content:center;align-items:center}.tab+.tab{border-top:solid thin #ececec}.tab .tab-content{width:100%;padding:10px;display:flex;flex-direction:row;justify-content:space-between;align-items:center}.tab:not(.active) .menu-panel-wrapper{display:none}.panel-option{display:flex;flex-direction:row;justify-content:space-between;padding:10px}.panel,.panel-option:not(.section){border-top:solid thin #d8d8d8}.menu-panel-wrapper{position:initial;width:100vw}.menu-panel{padding:0}.collapser:not(.expanded),.panel-options:not(.expanded){display:none}.panel-option.section.inactive{padding:0;max-height:0;overflow:hidden}.panel-options.expanded{position:absolute;top:0;left:0;right:0;z-index:101;width:100vw;background-color:#ececec}.panel-expanded .tab:not(.active){display:none}.panel-expanded .menu-panel-wrapper{max-height:0;overflow:hidden}}\n"], dependencies: [{ kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MegaMenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-mega-menu', template: "<nav class=\"component common mega-menu\" [ngClass]=\"megaMenuClass\">\r\n  <div class=tabs-wrapper>\r\n    <ul #menubar class=\"menu-banner tabs\" role=\"menu\" tabindex=-1 *ngIf=\"hasMenu\">\r\n      <li *ngFor=\"let tab of tabs\"\r\n          class=\"tab\"\r\n          [ngClass]=\"{active: activeTab === tab.value}\"\r\n          [attr.aria-label]=\"tab.value\"\r\n          [attr.aria-haspopup]=\"'true'\"\r\n          role=\"menuitem\"\r\n          tabindex=0\r\n          (click)=\"onClickOption(tab)\"\r\n          (keyup.enter)=\"onClickOption(tab, $event)\"\r\n          (keydown.tab)=\"onFocusTab(tab)\"\r\n          (mouseenter)=\"onMouseenterTabOption(tab)\"\r\n          (mouseleave)=\"onMouseleaveTabOption(tab)\">\r\n        <div class=tab-content>\r\n          <span [id]=\"'tab_' + tab.id\" class=\"copy tab-option\" [ngClass]=\"{active: activeTab === tab.value}\">{{ tab.value }}</span>\r\n\r\n          <div class=\"control expander tab--control\"\r\n               [ngClass]=\"{expanded: tab.isExpanded}\"\r\n               [attr.aria-label]=\"tab.isExpanded ? 'Collapse': 'Expand'\"\r\n               tabindex=0\r\n               (keyup.enter)=\"onClickOption(tab)\">\r\n            <span class=\"icon\" [ngClass]=\"'icon-' + (tab.isExpanded ? 'minus': 'plus')\"></span>\r\n          </div>\r\n        </div>\r\n        <ng-container [ngTemplateOutlet]=\"menuPanelRef\" *ngIf=\"isHamburger\"></ng-container>\r\n      </li>\r\n    </ul>\r\n  </div>\r\n  <ng-container [ngTemplateOutlet]=\"menuPanelRef\" *ngIf=\"!isHamburger\"></ng-container>\r\n</nav>\r\n\r\n<ng-template #menuPanel>\r\n  <div class=\"menu-panel-wrapper\" *ngIf=\"isShowingMenuPanel\">\r\n    <div #subMenu\r\n        role=\"menu\"\r\n        class=\"menu-panel\"\r\n        tabindex=-1\r\n        [attr.aria-label]=\"copySubMenu\"\r\n        (mouseenter)=\"onMouseenterMenuPanel()\"\r\n        (mouseleave)=\"onMouseleaveMenuPanel()\">\r\n      <ul class=\"panels\" role=\"presentation\">\r\n        <li *ngFor=\"let panel of panels[activeTab]\" class=\"panel\">\r\n          <ul [id]=\"'panel_' + panel.section.id\" class=\"panel-and-section\" [ngClass]=\"{expanded: panel.section.isExpanded}\">\r\n            <li class=\"panel-option section\"\r\n                [ngClass]=\"panel.section.class + (activePanelOption === panel.section.id ? ' active': (activePanelOption ? ' inactive' : '')) + (activePanelOption === panel.section.id ? ' bg-'+opCo+'-primaryBrand': '')\"\r\n                role=\"menuitem\"\r\n                [attr.aria-label]=\"panel.section.value\"\r\n                [attr.aria-haspopup]=\"!!panel.section.newTarget ? 'true': null\"\r\n                tabindex=0\r\n                (mouseenter)=\"onMouseenterPanelOption(panel.section)\"\r\n                (mouseleave)=\"onMouseleavePanelOption(panel.section)\"\r\n                (keyup.enter)=\"onClickOption(panel.section)\"\r\n                (click)=\"onClickOption(panel.section)\">\r\n              <span [id]=\"panel.section.id\" class=\"copy text-button--copy\">{{ panel.section.value }}</span>\r\n\r\n              <div class=\"control expander section--control\"\r\n                   [attr.aria-label]=\"panel.section.isExpanded ? 'Collapse': 'Expand'\"\r\n                   tabindex=0\r\n                   [ngClass]=\"{expanded: panel.section.isExpanded}\"\r\n                   (click)=\"onClickSectionExpander(panel.section, $event)\"\r\n                   (keyup.enter)=\"onClickSectionExpander(panel.section, $event)\">\r\n                <span class=\"icon\" [ngClass]=\"'icon-' + (panel.section.isExpanded ? 'minus': 'plus')\"></span>\r\n              </div>\r\n            </li>\r\n            <ul class=\"panel-options\" [ngClass]=\"{expanded: panel.section.isExpanded}\">\r\n              <li class=\"collapser section--control\" [ngClass]=\"{expanded: panel.section.isExpanded}\">\r\n                  <div class=\"control collapser section--control\"\r\n                       attr.aria-label=\"Collapse\"\r\n                       tabindex=0\r\n                       [ngClass]=\"{expanded: panel.section.isExpanded}\"\r\n                       (click)=\"onClickSectionExpander(panel.section, $event)\"\r\n                       (keyup.enter)=\"onClickSectionExpander(panel.section, $event)\">\r\n                    <span class=\"icon icon-chevron-left\"></span><span class=\"copy  control-back\">{{ copyBack }}</span>\r\n                  </div>\r\n              </li>\r\n              <li class=\"panel-option\"\r\n                  role=\"menuitem\"\r\n                  [ngClass]=\"option.class + (activePanelOption === option.id ? ' active':'') + (activePanelOption === option.id ? ' bg-'+opCo+'-primaryBrand': '')\"\r\n                  [attr.aria-label]=\"option.value\"\r\n                  [attr.aria-haspopup]=\"!!option.newTarget ? 'true': null\"\r\n                  tabindex=0\r\n                  (mouseenter)=\"onMouseenterPanelOption(option)\"\r\n                  (mouseleave)=\"onMouseleavePanelOption(option)\"\r\n                  (click)=\"onClickOption(option)\"\r\n                  (keyup.enter)=\"onClickOption(option)\"\r\n                  (keydown.tab)=\"onBlurSubMenu(option)\"\r\n                  *ngFor=\"let option of panel.entries;\">\r\n                <span [id]=\"'option_' + option.id\" [attr.aria-label]=\"option.value\" class=\"copy text-button--copy\">{{ option.value }}</span>\r\n              </li>\r\n            </ul>\r\n          </ul>\r\n        </li>\r\n      </ul>\r\n    </div>\r\n  </div>\r\n</ng-template>\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}:focus{outline:thin dotted}.component.common.mega-menu{position:relative}@media (max-width: 767.98px){.component.common.mega-menu{display:none}.component.common.mega-menu.mobile-only{display:initial}}@media (min-width: 768px){.component.common.mega-menu{display:initial}.component.common.mega-menu.mobile-only,.component.common.mega-menu .control{display:none}}.component.common.mega-menu ::ng-deep .btn-text:after{border-bottom:none}ul{list-style:none;margin:0;padding:0}.tabs-wrapper{display:flex;flex-direction:row;justify-content:center;width:100%;height:55px;background-color:#d8d8d8}.menu-banner.tabs{max-width:1170px}.tabs{width:100%;max-width:1070px;display:flex;flex-direction:row;justify-content:space-around}@media (max-width: 1199.98px){.tabs{max-width:100vw}}.tabs .tab{flex:1 1 100%;display:flex;flex-direction:column;justify-content:center;align-items:center;min-height:min-content}.tabs .tab.active{background-color:#ececec}.menu-panel,.menu-panel-wrapper{display:flex;flex-direction:row;justify-content:center}.menu-panel{width:100%;max-width:1280px;padding:30px;background-color:#ececec}.panels{display:flex;flex-direction:row;width:100%;animation-name:slidein;animation-duration:.25s;animation-fill-mode:forwards;animation-direction:alternate}.panels .panel{flex:1 1 100%}.panel-option{cursor:pointer;padding:10px}.panel-option.active{background-color:var(--primaryBrand)}.panel-option.active .copy{color:#fff}.copy{font-family:Open Sans Regular;font-weight:400;font-size:14px;color:#4a4a4a}.tab-option{font-size:16px}.tab-option.active,.section .text-button--copy{font-family:Open Sans Semi Bold;font-weight:600}.control.expander{margin:-10px;padding:10px;border-left:solid thin}.control.expander.tab--control{border-color:#ececec}.control.expander.section--control,.tab.active .tab--control{border-color:#d8d8d8}.control.collapser{display:flex;flex-direction:row}.control-back{margin-left:5px}li.collapser{padding:10px}@keyframes slidein{0%{margin-top:-100px}to{margin-top:0}}.icon{speak:none;font-family:icomoon;font-weight:600;font-size:16px;color:#737373;display:block}.icon-plus:before{content:\"\\e936\"}.icon-minus:before{content:\"\\e937\"}.icon-chevron-left:before{content:\"\\e93b\"}@media (max-width: 767.98px){.menu-panel-wrapper{overflow:hidden;position:absolute;top:55px;left:0;right:0;z-index:101}.component.common.mega-menu,.tabs-wrapper{position:relative;height:auto;width:100vw}.tabs,.panels{flex-direction:column}.tab{flex:1 1 100%;display:flex;flex-direction:column;justify-content:center;align-items:center}.tab+.tab{border-top:solid thin #ececec}.tab .tab-content{width:100%;padding:10px;display:flex;flex-direction:row;justify-content:space-between;align-items:center}.tab:not(.active) .menu-panel-wrapper{display:none}.panel-option{display:flex;flex-direction:row;justify-content:space-between;padding:10px}.panel,.panel-option:not(.section){border-top:solid thin #d8d8d8}.menu-panel-wrapper{position:initial;width:100vw}.menu-panel{padding:0}.collapser:not(.expanded),.panel-options:not(.expanded){display:none}.panel-option.section.inactive{padding:0;max-height:0;overflow:hidden}.panel-options.expanded{position:absolute;top:0;left:0;right:0;z-index:101;width:100vw;background-color:#ececec}.panel-expanded .tab:not(.active){display:none}.panel-expanded .menu-panel-wrapper{max-height:0;overflow:hidden}}\n"] }]
        }], ctorParameters: function () {
        return [{ type: MegaMenuService }, { type: AppSessionService }, { type: AppConfigService }, { type: AppLoggerService, decorators: [{
                        type: Optional
                    }] }, { type: i0.ChangeDetectorRef }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [WINDOW]
                    }] }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [WINDOW_LOCATION]
                    }] }];
    }, propDecorators: { isHamburger: [{
                type: Input
            }], menuPanelRef: [{
                type: ViewChild,
                args: ['menuPanel', { static: true }]
            }], subMenuRef: [{
                type: ViewChild,
                args: ['subMenu']
            }], menubarRef: [{
                type: ViewChild,
                args: ['menubar']
            }], mouseenterTabOptionEvent: [{
                type: Output,
                args: ['mouseenterTabOption']
            }], mouseleaveTabOptionEvent: [{
                type: Output,
                args: ['mouseleaveTabOption']
            }], mouseenterMenuPanelEvent: [{
                type: Output,
                args: ['mouseenterMenuPanel']
            }], mouseleaveMenuPanelEvent: [{
                type: Output,
                args: ['mouseleaveMenuPanel']
            }], mouseenterPanelOptionEvent: [{
                type: Output,
                args: ['mouseenterPanelOption']
            }], mouseleavePanelOptionEvent: [{
                type: Output,
                args: ['mouseleavePanelOption']
            }], clickOptionEvent: [{
                type: Output,
                args: ['clickPanelOption']
            }] } });

class MegaMenuModule {
}
MegaMenuModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MegaMenuModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MegaMenuModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: MegaMenuModule, declarations: [MegaMenuComponent], imports: [CommonModule], exports: [MegaMenuComponent] });
MegaMenuModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MegaMenuModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MegaMenuModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    declarations: [
                        MegaMenuComponent
                    ],
                    exports: [MegaMenuComponent]
                }]
        }] });

class ToggleButtonComponent {
    constructor() {
        // tslint:disable-next-line
        this.onClick = new EventEmitter();
    }
    /**
    * toggleButton - function sends selected value of toggle button
    * @param labels
    * @return none
    */
    toggleButton(labels, i, $event) {
        if (this.clickStopPropagation) {
            $event.stopPropagation();
        }
        const enabled = labels[i];
        this.defaultEnabled = enabled['id'];
        this.onClick.emit(enabled);
    }
}
ToggleButtonComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ToggleButtonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ToggleButtonComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: ToggleButtonComponent, selector: "app-toggle-button", inputs: { labels: "labels", defaultEnabled: "defaultEnabled", isDisabled: "isDisabled", clickStopPropagation: "clickStopPropagation" }, outputs: { onClick: "click" }, ngImport: i0, template: "<div class=\"clearfix\">\r\n  <button [attr.aria-pressed]=\"defaultEnabled === label['id']\" *ngFor=\"let label of labels; let i = index;\"\r\n        type=\"button\"\r\n        [attr.aria-label]=\"label.ariaLabel || null\"\r\n        (click)=\"toggleButton(labels, i, $event)\"\r\n        (keyup.enter)=\"toggleButton(labels, i, $event)\"\r\n        class= \"float-left\"\r\n        [class.active]=\"defaultEnabled === label['id']\"\r\n        [ngClass]=\"label.buttonClass || 'button'\"\r\n        [disabled]=\"isDisabled\">\r\n    <span class=\"name\" *ngIf=\"!label.template;\">{{label.name}}</span>\r\n    <ng-container *ngIf=label.template>\r\n      <ng-container *ngTemplateOutlet=\"label.template; context: {$implicit: label}\"></ng-container>\r\n    </ng-container>\r\n</button>\r\n</div>\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.button{cursor:pointer;outline:none;width:120px;height:43px;border-radius:2px;background-color:#fff;border:2px solid #d8d8d8;color:#4a4a4a;text-align:center}.button:not(:last-child){margin-right:15px}.button.disabled,.button[disabled]{color:#4a4a4a;background-color:#d8d8d8;box-sizing:border-box;cursor:not-allowed;border:1px solid #d8d8d8}.button.disabled:hover,.button[disabled]:hover{outline:none;color:#4a4a4a!important;border:none!important;box-shadow:none!important}.button.disabled:focus,.button[disabled]:focus{outline:none;color:#4a4a4a!important;border:none!important;box-shadow:none!important}.button.active{outline:none;border:1px solid rgba(0,89,164,.4);background-color:#e5eef5;box-shadow:inset 0 1px 2px #0000001a,0 1px 4px #0059a433;color:#0059a4;font-weight:600;line-height:22px}.button:focus{outline:none;border:1px solid #0091b6;box-sizing:border-box}.button:hover:not(.active){color:#0059a4;line-height:22px;box-shadow:inset 0 1px 2px #0000001a,0 1px 4px #0059a433;border:1px solid rgba(0,89,164,.3)}\n"], dependencies: [{ kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ToggleButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-toggle-button', template: "<div class=\"clearfix\">\r\n  <button [attr.aria-pressed]=\"defaultEnabled === label['id']\" *ngFor=\"let label of labels; let i = index;\"\r\n        type=\"button\"\r\n        [attr.aria-label]=\"label.ariaLabel || null\"\r\n        (click)=\"toggleButton(labels, i, $event)\"\r\n        (keyup.enter)=\"toggleButton(labels, i, $event)\"\r\n        class= \"float-left\"\r\n        [class.active]=\"defaultEnabled === label['id']\"\r\n        [ngClass]=\"label.buttonClass || 'button'\"\r\n        [disabled]=\"isDisabled\">\r\n    <span class=\"name\" *ngIf=\"!label.template;\">{{label.name}}</span>\r\n    <ng-container *ngIf=label.template>\r\n      <ng-container *ngTemplateOutlet=\"label.template; context: {$implicit: label}\"></ng-container>\r\n    </ng-container>\r\n</button>\r\n</div>\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.button{cursor:pointer;outline:none;width:120px;height:43px;border-radius:2px;background-color:#fff;border:2px solid #d8d8d8;color:#4a4a4a;text-align:center}.button:not(:last-child){margin-right:15px}.button.disabled,.button[disabled]{color:#4a4a4a;background-color:#d8d8d8;box-sizing:border-box;cursor:not-allowed;border:1px solid #d8d8d8}.button.disabled:hover,.button[disabled]:hover{outline:none;color:#4a4a4a!important;border:none!important;box-shadow:none!important}.button.disabled:focus,.button[disabled]:focus{outline:none;color:#4a4a4a!important;border:none!important;box-shadow:none!important}.button.active{outline:none;border:1px solid rgba(0,89,164,.4);background-color:#e5eef5;box-shadow:inset 0 1px 2px #0000001a,0 1px 4px #0059a433;color:#0059a4;font-weight:600;line-height:22px}.button:focus{outline:none;border:1px solid #0091b6;box-sizing:border-box}.button:hover:not(.active){color:#0059a4;line-height:22px;box-shadow:inset 0 1px 2px #0000001a,0 1px 4px #0059a433;border:1px solid rgba(0,89,164,.3)}\n"] }]
        }], propDecorators: { labels: [{
                type: Input
            }], defaultEnabled: [{
                type: Input
            }], isDisabled: [{
                type: Input
            }], clickStopPropagation: [{
                type: Input
            }], onClick: [{
                type: Output,
                args: ['click']
            }] } });

class ToggleButtonModule {
}
ToggleButtonModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ToggleButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ToggleButtonModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: ToggleButtonModule, declarations: [ToggleButtonComponent], imports: [CommonModule], exports: [ToggleButtonComponent] });
ToggleButtonModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ToggleButtonModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ToggleButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [ToggleButtonComponent],
                    exports: [
                        ToggleButtonComponent
                    ]
                }]
        }] });

class ButtonComponent {
    constructor() {
        this.disabled = false;
        this.clickEvent = new EventEmitter();
    }
    ngOnInit() {
        this.type = this.type || 'button';
        this.buttonRole = this.classType === 'text' ? 'link' : 'button';
    }
    /**
    * clickHandler - function sends click action of button to parent which invoked it.
    * @param event
    * @return none
    */
    clickHandler(event) {
        this.clickEvent.emit({ event: event });
    }
}
ButtonComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ButtonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ButtonComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: ButtonComponent, selector: "app-button-common", inputs: { disabled: "disabled", ariaLabel: "ariaLabel", className: "className", classType: "classType", type: "type", loaderType: "loaderType" }, outputs: { clickEvent: "clickEvent" }, ngImport: i0, template: "<button type=\"{{type}}\"\r\ndisabled=\"{{disabled || null}}\"\r\nattr.role=\"{{buttonRole}}\"\r\nattr.aria-label=\"{{ariaLabel || null}}\"\r\nclass=\"btn btn-{{classType}} {{className}}\"\r\n(click)=\"clickHandler($event)\">\r\n  <ng-content></ng-content>\r\n  <div class=\"loader-btn\" *ngIf=\"loaderType === 'dotted'\">\r\n    <div class=\"dot-flashing-btn\"></div>\r\n  </div>\r\n\r\n  <div class=\"loader-btn\" *ngIf=\"loaderType === 'circular'\">\r\n    <div class=\"loader-submit\"></div>\r\n  </div>\r\n</button>\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.btn{cursor:pointer;white-space:normal}.btn.btn-primary{font-weight:600;line-height:24px;text-align:center;color:#fff;background-color:#0059a4;box-shadow:none;border-color:transparent;border-radius:2px;padding:15px 20px 16px}.btn.btn-primary.rounded-left-0{border-top-left-radius:0;border-bottom-left-radius:0}.btn.btn-primary:not([disabled]):not(.disabled):hover,.btn.btn-primary:not([disabled]):not(.disabled):focus{background-color:#103870;box-shadow:none;border-color:transparent}.btn.btn-primary:not([disabled]):not(.disabled):active,.btn.btn-primary:not([disabled]):not(.disabled).active{background-color:#103870;box-shadow:none;border-color:transparent}.btn.btn-primary.disabled,.btn.btn-primary[disabled]{color:#4a4a4a;background-color:#d8d8d8;box-sizing:border-box;cursor:not-allowed;border:1px solid #d8d8d8}.btn.btn-secondary{font-size:14px;font-weight:600;line-height:19px;text-align:center;color:#0059a4;background-color:transparent;box-sizing:border-box;box-shadow:none;border:2px solid #0059a4;border-radius:2px;padding:10px 20px}.btn.btn-secondary:not([disabled]):not(.disabled):hover,.btn.btn-secondary:not([disabled]):not(.disabled):focus{background-color:#103870;box-shadow:none;border-color:transparent;color:#fff}.btn.btn-secondary:not([disabled]):not(.disabled):active,.btn.btn-secondary:not([disabled]):not(.disabled).active{background-color:#103870;box-shadow:none;border-color:transparent;color:#fff}.btn.btn-secondary.disabled,.btn.btn-secondary[disabled]{color:#737373;cursor:not-allowed;border:2px solid #d8d8d8}.btn.btn-text{font-size:14px;font-weight:600;line-height:19px;color:#0059a4;background-color:transparent;border-width:0;border-color:transparent;box-shadow:none;padding:0;border-radius:0;position:relative;vertical-align:inherit;text-align:left}.btn.btn-text:after{content:\"\";position:absolute;bottom:0;left:0;width:0%;border-bottom:2px solid #0059a4;transition:.2s}.btn.btn-text:not([disabled]):not(.disabled):hover,.btn.btn-text:not([disabled]):not(.disabled):active,.btn.btn-text:not([disabled]):not(.disabled).active,.btn.btn-text:not([disabled]):not(.disabled):focus{color:#103870}.btn.btn-text:not([disabled]):not(.disabled):hover:after,.btn.btn-text:not([disabled]):not(.disabled):active:after,.btn.btn-text:not([disabled]):not(.disabled).active:after,.btn.btn-text:not([disabled]):not(.disabled):focus:after{width:100%}.btn.btn-text.disabled,.btn.btn-text[disabled]{color:#737373;cursor:not-allowed}.btn.fixed-width{width:100%}.btn.customized-button,.btn .customized-button:hover{background-color:#0059a4;border-color:#0059a4;color:#fff}.btn.customized-button:not([disabled]):not(.disabled):hover,.btn.customized-button:not([disabled]):not(.disabled):focus,.btn .customized-button:hover:not([disabled]):not(.disabled):hover,.btn .customized-button:hover:not([disabled]):not(.disabled):focus{background-color:#103870;border-color:#103870;color:#fff}.btn.customized-button:not([disabled]):not(.disabled):active,.btn.customized-button:not([disabled]):not(.disabled).active,.btn .customized-button:hover:not([disabled]):not(.disabled):active,.btn .customized-button:hover:not([disabled]):not(.disabled).active{background-color:#0059a4;border-color:#0059a4;color:#fff;box-shadow:none}.btn.customized-button.disabled,.btn.customized-button[disabled],.btn .customized-button:hover.disabled,.btn .customized-button:hover[disabled]{color:#737373;cursor:not-allowed;border:2px solid #d8d8d8;background-color:transparent}.btn.btn-loading{background:#103870;border-color:transparent;color:transparent;position:relative}.btn.btn-loading:hover{color:transparent}.btn.btn-loading:not([disabled]):not(.disabled):hover{color:transparent}.btn.btn-loading:not([disabled]):not(.disabled):active,.btn.btn-loading:not([disabled]):not(.disabled).active{color:transparent}.btn.btn-loading.disabled,.btn.btn-loading[disabled]{color:transparent;cursor:pointer;border:2px solid #103870;opacity:1;background:#103870}.btn.btn-loading .loader-btn{position:absolute;top:calc(50% - 5px);left:calc(50% - 5px);display:inline-block;height:20px}.btn.btn-loading .loader-btn .dot-flashing-btn{position:relative;width:10px;height:10px;border-radius:50%;background-color:#ffffff52;animation:dotFlashingBtn 1s infinite linear alternate;animation-delay:.5s}.btn.btn-loading .loader-btn .dot-flashing-btn:before{content:\"\";display:inline-block;position:absolute;top:0;left:-24px;width:10px;height:10px;border-radius:50%;background-color:#ffffff52;animation:dotFlashingBtn 1s infinite alternate;animation-delay:0s}.btn.btn-loading .loader-btn .dot-flashing-btn:after{content:\"\";display:inline-block;position:absolute;top:0;left:24px;width:10px;height:10px;border-radius:50%;background-color:#ffffff52;animation:dotFlashingBtn 1s infinite alternate;animation-delay:1s}.btn.btn-loading-submit{background:#103870;border-color:#103870;color:transparent;position:relative}.btn.btn-loading-submit:hover{color:transparent}.btn.btn-loading-submit:not([disabled]):not(.disabled):hover{color:transparent}.btn.btn-loading-submit:not([disabled]):not(.disabled):active,.btn.btn-loading-submit:not([disabled]):not(.disabled).active{color:transparent}.btn.btn-loading-submit.disabled,.btn.btn-loading-submit[disabled]{color:transparent;cursor:pointer;border:2px solid #103870;opacity:1;background:#103870}.btn.btn-loading-submit .loader-submit{position:absolute;top:calc(50% - 14px);left:calc(50% - 14px);background-color:transparent;border:5px solid #fff;border-left-color:transparent;border-radius:50%;display:inline-block;width:28px;height:28px;vertical-align:middle;animation:spin 1.2s infinite linear}.btn.btn-loading-check-submit{background:#103870;border-color:#103870;color:transparent;position:relative}.btn.btn-loading-check-submit:hover{color:transparent}.btn.btn-loading-check-submit:not([disabled]):not(.disabled):hover{color:transparent}.btn.btn-loading-check-submit:not([disabled]):not(.disabled):active,.btn.btn-loading-check-submit:not([disabled]):not(.disabled).active{color:transparent}.btn.btn-loading-check-submit.disabled,.btn.btn-loading-check-submit[disabled]{color:transparent;cursor:pointer;border:2px solid #103870;opacity:1;background:#103870}.btn.btn-loading-check-submit .loader-btn{position:absolute;top:calc(50% - 15px);left:calc(50% - 15px)}.btn.btn-loading-check-submit .loader-btn .loader-submit{border:solid 4px #fff;width:30px;margin-top:18px;height:12px;border-top:none;border-right:none;transform:rotate(-45deg);transform-origin:top left;animation:start .5s ease both}.btn.autocomplete-btn{height:57px;font-weight:600;font-size:14px;line-height:19px}@keyframes dotFlashingBtn{0%{background-color:#ffffff52}50%,to{background-color:#fff3}}@keyframes spin{0%{transform:rotate(0)}to{transform:rotate(360deg)}}@keyframes start{0%{opacity:0;width:0;height:0}50%{opacity:1;width:0;height:12px}to{width:30px}}@media (max-width: 767.98px){.btn.autocomplete-btn{width:100%}}\n"], dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-button-common', template: "<button type=\"{{type}}\"\r\ndisabled=\"{{disabled || null}}\"\r\nattr.role=\"{{buttonRole}}\"\r\nattr.aria-label=\"{{ariaLabel || null}}\"\r\nclass=\"btn btn-{{classType}} {{className}}\"\r\n(click)=\"clickHandler($event)\">\r\n  <ng-content></ng-content>\r\n  <div class=\"loader-btn\" *ngIf=\"loaderType === 'dotted'\">\r\n    <div class=\"dot-flashing-btn\"></div>\r\n  </div>\r\n\r\n  <div class=\"loader-btn\" *ngIf=\"loaderType === 'circular'\">\r\n    <div class=\"loader-submit\"></div>\r\n  </div>\r\n</button>\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.btn{cursor:pointer;white-space:normal}.btn.btn-primary{font-weight:600;line-height:24px;text-align:center;color:#fff;background-color:#0059a4;box-shadow:none;border-color:transparent;border-radius:2px;padding:15px 20px 16px}.btn.btn-primary.rounded-left-0{border-top-left-radius:0;border-bottom-left-radius:0}.btn.btn-primary:not([disabled]):not(.disabled):hover,.btn.btn-primary:not([disabled]):not(.disabled):focus{background-color:#103870;box-shadow:none;border-color:transparent}.btn.btn-primary:not([disabled]):not(.disabled):active,.btn.btn-primary:not([disabled]):not(.disabled).active{background-color:#103870;box-shadow:none;border-color:transparent}.btn.btn-primary.disabled,.btn.btn-primary[disabled]{color:#4a4a4a;background-color:#d8d8d8;box-sizing:border-box;cursor:not-allowed;border:1px solid #d8d8d8}.btn.btn-secondary{font-size:14px;font-weight:600;line-height:19px;text-align:center;color:#0059a4;background-color:transparent;box-sizing:border-box;box-shadow:none;border:2px solid #0059a4;border-radius:2px;padding:10px 20px}.btn.btn-secondary:not([disabled]):not(.disabled):hover,.btn.btn-secondary:not([disabled]):not(.disabled):focus{background-color:#103870;box-shadow:none;border-color:transparent;color:#fff}.btn.btn-secondary:not([disabled]):not(.disabled):active,.btn.btn-secondary:not([disabled]):not(.disabled).active{background-color:#103870;box-shadow:none;border-color:transparent;color:#fff}.btn.btn-secondary.disabled,.btn.btn-secondary[disabled]{color:#737373;cursor:not-allowed;border:2px solid #d8d8d8}.btn.btn-text{font-size:14px;font-weight:600;line-height:19px;color:#0059a4;background-color:transparent;border-width:0;border-color:transparent;box-shadow:none;padding:0;border-radius:0;position:relative;vertical-align:inherit;text-align:left}.btn.btn-text:after{content:\"\";position:absolute;bottom:0;left:0;width:0%;border-bottom:2px solid #0059a4;transition:.2s}.btn.btn-text:not([disabled]):not(.disabled):hover,.btn.btn-text:not([disabled]):not(.disabled):active,.btn.btn-text:not([disabled]):not(.disabled).active,.btn.btn-text:not([disabled]):not(.disabled):focus{color:#103870}.btn.btn-text:not([disabled]):not(.disabled):hover:after,.btn.btn-text:not([disabled]):not(.disabled):active:after,.btn.btn-text:not([disabled]):not(.disabled).active:after,.btn.btn-text:not([disabled]):not(.disabled):focus:after{width:100%}.btn.btn-text.disabled,.btn.btn-text[disabled]{color:#737373;cursor:not-allowed}.btn.fixed-width{width:100%}.btn.customized-button,.btn .customized-button:hover{background-color:#0059a4;border-color:#0059a4;color:#fff}.btn.customized-button:not([disabled]):not(.disabled):hover,.btn.customized-button:not([disabled]):not(.disabled):focus,.btn .customized-button:hover:not([disabled]):not(.disabled):hover,.btn .customized-button:hover:not([disabled]):not(.disabled):focus{background-color:#103870;border-color:#103870;color:#fff}.btn.customized-button:not([disabled]):not(.disabled):active,.btn.customized-button:not([disabled]):not(.disabled).active,.btn .customized-button:hover:not([disabled]):not(.disabled):active,.btn .customized-button:hover:not([disabled]):not(.disabled).active{background-color:#0059a4;border-color:#0059a4;color:#fff;box-shadow:none}.btn.customized-button.disabled,.btn.customized-button[disabled],.btn .customized-button:hover.disabled,.btn .customized-button:hover[disabled]{color:#737373;cursor:not-allowed;border:2px solid #d8d8d8;background-color:transparent}.btn.btn-loading{background:#103870;border-color:transparent;color:transparent;position:relative}.btn.btn-loading:hover{color:transparent}.btn.btn-loading:not([disabled]):not(.disabled):hover{color:transparent}.btn.btn-loading:not([disabled]):not(.disabled):active,.btn.btn-loading:not([disabled]):not(.disabled).active{color:transparent}.btn.btn-loading.disabled,.btn.btn-loading[disabled]{color:transparent;cursor:pointer;border:2px solid #103870;opacity:1;background:#103870}.btn.btn-loading .loader-btn{position:absolute;top:calc(50% - 5px);left:calc(50% - 5px);display:inline-block;height:20px}.btn.btn-loading .loader-btn .dot-flashing-btn{position:relative;width:10px;height:10px;border-radius:50%;background-color:#ffffff52;animation:dotFlashingBtn 1s infinite linear alternate;animation-delay:.5s}.btn.btn-loading .loader-btn .dot-flashing-btn:before{content:\"\";display:inline-block;position:absolute;top:0;left:-24px;width:10px;height:10px;border-radius:50%;background-color:#ffffff52;animation:dotFlashingBtn 1s infinite alternate;animation-delay:0s}.btn.btn-loading .loader-btn .dot-flashing-btn:after{content:\"\";display:inline-block;position:absolute;top:0;left:24px;width:10px;height:10px;border-radius:50%;background-color:#ffffff52;animation:dotFlashingBtn 1s infinite alternate;animation-delay:1s}.btn.btn-loading-submit{background:#103870;border-color:#103870;color:transparent;position:relative}.btn.btn-loading-submit:hover{color:transparent}.btn.btn-loading-submit:not([disabled]):not(.disabled):hover{color:transparent}.btn.btn-loading-submit:not([disabled]):not(.disabled):active,.btn.btn-loading-submit:not([disabled]):not(.disabled).active{color:transparent}.btn.btn-loading-submit.disabled,.btn.btn-loading-submit[disabled]{color:transparent;cursor:pointer;border:2px solid #103870;opacity:1;background:#103870}.btn.btn-loading-submit .loader-submit{position:absolute;top:calc(50% - 14px);left:calc(50% - 14px);background-color:transparent;border:5px solid #fff;border-left-color:transparent;border-radius:50%;display:inline-block;width:28px;height:28px;vertical-align:middle;animation:spin 1.2s infinite linear}.btn.btn-loading-check-submit{background:#103870;border-color:#103870;color:transparent;position:relative}.btn.btn-loading-check-submit:hover{color:transparent}.btn.btn-loading-check-submit:not([disabled]):not(.disabled):hover{color:transparent}.btn.btn-loading-check-submit:not([disabled]):not(.disabled):active,.btn.btn-loading-check-submit:not([disabled]):not(.disabled).active{color:transparent}.btn.btn-loading-check-submit.disabled,.btn.btn-loading-check-submit[disabled]{color:transparent;cursor:pointer;border:2px solid #103870;opacity:1;background:#103870}.btn.btn-loading-check-submit .loader-btn{position:absolute;top:calc(50% - 15px);left:calc(50% - 15px)}.btn.btn-loading-check-submit .loader-btn .loader-submit{border:solid 4px #fff;width:30px;margin-top:18px;height:12px;border-top:none;border-right:none;transform:rotate(-45deg);transform-origin:top left;animation:start .5s ease both}.btn.autocomplete-btn{height:57px;font-weight:600;font-size:14px;line-height:19px}@keyframes dotFlashingBtn{0%{background-color:#ffffff52}50%,to{background-color:#fff3}}@keyframes spin{0%{transform:rotate(0)}to{transform:rotate(360deg)}}@keyframes start{0%{opacity:0;width:0;height:0}50%{opacity:1;width:0;height:12px}to{width:30px}}@media (max-width: 767.98px){.btn.autocomplete-btn{width:100%}}\n"] }]
        }], propDecorators: { disabled: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], className: [{
                type: Input
            }], classType: [{
                type: Input
            }], type: [{
                type: Input
            }], loaderType: [{
                type: Input
            }], clickEvent: [{
                type: Output
            }] } });

class ButtonModule {
}
ButtonModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ButtonModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: ButtonModule, declarations: [ButtonComponent], imports: [CommonModule], exports: [ButtonComponent] });
ButtonModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ButtonModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [ButtonComponent],
                    exports: [
                        ButtonComponent
                    ]
                }]
        }] });

class ToolTipComponent {
    constructor() {
        this.isOpen = false;
        this.tooltipOff = false;
    }
    ngOnInit() {
        this.forLabel = this.toolTipText.replace(/ /g, '-');
        if (!this.tabindex) {
            this.tabindex = '0';
        }
    }
}
ToolTipComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ToolTipComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ToolTipComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: ToolTipComponent, selector: "app-tooltip-common", inputs: { toolTipText: "toolTipText", orientation: "orientation", size: "size", isOpen: "isOpen", tooltipOff: "tooltipOff", tabindex: "tabindex" }, ngImport: i0, template: "<div class='tooltip-container' role=\"link\" attr.tabindex={{tabindex}} attr.aria-labelledby={{forLabel}}>\r\n    <ng-content></ng-content> \r\n    <span id={{forLabel}} role=\"tooltip\" class='tooltip-text {{orientation || \"top\"}} {{size}}' [class.open]=\"isOpen\" [class.off]=\"tooltipOff\">{{toolTipText}}</span>\r\n</div>", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.tooltip-container{position:relative;width:100%;outline:none}.tooltip-container:hover .tooltip-text:not(.off),.tooltip-container:focus .tooltip-text:not(.off){visibility:visible}.tooltip-container .tooltip-text{visibility:hidden;font-size:16px;font-family:Open Sans Regular;line-height:22px;text-align:center;color:#4a4a4a;background-color:#fff;border:1px solid rgba(0,89,164,.3);border-radius:2px;box-shadow:inset 0 0 1px #0059a4;padding:15px;position:absolute;z-index:9999;width:250px;max-width:350px}.tooltip-container .tooltip-text.open{visibility:visible}.tooltip-container .tooltip-text.small{font-size:14px;font-weight:600}.tooltip-container .tooltip-text.top{bottom:100%;left:50%;transform:translate(-50%);margin-bottom:10px}.tooltip-container .tooltip-text.top:after{content:\"\";position:absolute;top:100%;left:50%;margin-top:-2px;margin-left:-10px;border-left:10px solid #fff;border-right:10px solid transparent;border-top:10px solid transparent;border-bottom:10px solid #fff;z-index:1;box-sizing:border-box;box-shadow:-2px 2px 2px #0059a466;transform-origin:0 0;transform:rotate(-45deg)}.tooltip-container .tooltip-text.bottom{top:100%;left:50%;transform:translate(-50%);margin-top:10px}.tooltip-container .tooltip-text.bottom:after{content:\"\";position:absolute;bottom:100%;left:50%;margin:-2px 0 -22px 11px;border-left:10px solid #fff;border-right:10px solid transparent;border-top:10px solid transparent;border-bottom:10px solid #fff;z-index:1;box-sizing:border-box;box-shadow:-2px 2px 2px #0059a466;transform-origin:0 0;transform:rotate(135deg)}.tooltip-container .tooltip-text.right{top:50%;left:110%;margin-right:10px;transform:translateY(-50%)}.tooltip-container .tooltip-text.right:after{content:\" \";position:absolute;top:50%;right:100%;margin:-14px -22px 0 0;border-left:10px solid #fff;border-right:10px solid transparent;border-top:10px solid transparent;border-bottom:10px solid #fff;z-index:1;box-sizing:border-box;box-shadow:-2px 2px 2px #0059a466;transform-origin:0 0;transform:rotate(45deg)}.tooltip-container .tooltip-text.left{top:50%;right:105%;margin-right:10px;transform:translateY(-50%)}.tooltip-container .tooltip-text.left:after{content:\" \";position:absolute;top:50%;left:100%;margin:14px 0 0 -2px;border-left:10px solid #fff;border-right:10px solid transparent;border-top:10px solid transparent;border-bottom:10px solid #fff;z-index:1;box-sizing:border-box;box-shadow:-2px 2px 2px #0059a466;transform-origin:0 0;transform:rotate(225deg)}@media (max-width: 767.98px){.tooltip-container .tooltip-text{width:190px}}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ToolTipComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-tooltip-common', template: "<div class='tooltip-container' role=\"link\" attr.tabindex={{tabindex}} attr.aria-labelledby={{forLabel}}>\r\n    <ng-content></ng-content> \r\n    <span id={{forLabel}} role=\"tooltip\" class='tooltip-text {{orientation || \"top\"}} {{size}}' [class.open]=\"isOpen\" [class.off]=\"tooltipOff\">{{toolTipText}}</span>\r\n</div>", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.tooltip-container{position:relative;width:100%;outline:none}.tooltip-container:hover .tooltip-text:not(.off),.tooltip-container:focus .tooltip-text:not(.off){visibility:visible}.tooltip-container .tooltip-text{visibility:hidden;font-size:16px;font-family:Open Sans Regular;line-height:22px;text-align:center;color:#4a4a4a;background-color:#fff;border:1px solid rgba(0,89,164,.3);border-radius:2px;box-shadow:inset 0 0 1px #0059a4;padding:15px;position:absolute;z-index:9999;width:250px;max-width:350px}.tooltip-container .tooltip-text.open{visibility:visible}.tooltip-container .tooltip-text.small{font-size:14px;font-weight:600}.tooltip-container .tooltip-text.top{bottom:100%;left:50%;transform:translate(-50%);margin-bottom:10px}.tooltip-container .tooltip-text.top:after{content:\"\";position:absolute;top:100%;left:50%;margin-top:-2px;margin-left:-10px;border-left:10px solid #fff;border-right:10px solid transparent;border-top:10px solid transparent;border-bottom:10px solid #fff;z-index:1;box-sizing:border-box;box-shadow:-2px 2px 2px #0059a466;transform-origin:0 0;transform:rotate(-45deg)}.tooltip-container .tooltip-text.bottom{top:100%;left:50%;transform:translate(-50%);margin-top:10px}.tooltip-container .tooltip-text.bottom:after{content:\"\";position:absolute;bottom:100%;left:50%;margin:-2px 0 -22px 11px;border-left:10px solid #fff;border-right:10px solid transparent;border-top:10px solid transparent;border-bottom:10px solid #fff;z-index:1;box-sizing:border-box;box-shadow:-2px 2px 2px #0059a466;transform-origin:0 0;transform:rotate(135deg)}.tooltip-container .tooltip-text.right{top:50%;left:110%;margin-right:10px;transform:translateY(-50%)}.tooltip-container .tooltip-text.right:after{content:\" \";position:absolute;top:50%;right:100%;margin:-14px -22px 0 0;border-left:10px solid #fff;border-right:10px solid transparent;border-top:10px solid transparent;border-bottom:10px solid #fff;z-index:1;box-sizing:border-box;box-shadow:-2px 2px 2px #0059a466;transform-origin:0 0;transform:rotate(45deg)}.tooltip-container .tooltip-text.left{top:50%;right:105%;margin-right:10px;transform:translateY(-50%)}.tooltip-container .tooltip-text.left:after{content:\" \";position:absolute;top:50%;left:100%;margin:14px 0 0 -2px;border-left:10px solid #fff;border-right:10px solid transparent;border-top:10px solid transparent;border-bottom:10px solid #fff;z-index:1;box-sizing:border-box;box-shadow:-2px 2px 2px #0059a466;transform-origin:0 0;transform:rotate(225deg)}@media (max-width: 767.98px){.tooltip-container .tooltip-text{width:190px}}\n"] }]
        }], propDecorators: { toolTipText: [{
                type: Input
            }], orientation: [{
                type: Input
            }], size: [{
                type: Input
            }], isOpen: [{
                type: Input
            }], tooltipOff: [{
                type: Input
            }], tabindex: [{
                type: Input
            }] } });

class ToolTipModule {
}
ToolTipModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ToolTipModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ToolTipModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: ToolTipModule, declarations: [ToolTipComponent], imports: [CommonModule], exports: [ToolTipComponent] });
ToolTipModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ToolTipModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ToolTipModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [ToolTipComponent],
                    exports: [
                        ToolTipComponent
                    ]
                }]
        }] });

var copy$2 = {
    outage: 'Outage',
    payBill: 'Pay Bill',
    moving: 'Moving',
    contactUs: 'Contact Us',
    signIn: 'Sign In',
    signOut: 'Sign Out',
    search: 'Search',
    menu: 'Menu',
    contact: 'Contact',
    español: 'Español',
    chinese: '中文',
    close: 'Close',
    register: 'Register'
};

class BootstrapBreakpointService extends SingletonComponentState {
    constructor(platformId) {
        super(platformId);
        this.resizeSubject = new BehaviorSubject(SCREEN_SIZE.XL);
    }
    get resizeEvent() {
        return this.resizeSubject.asObservable().pipe(distinctUntilChanged());
    }
    onResize(size) {
        this.resizeSubject.next(size);
    }
}
BootstrapBreakpointService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BootstrapBreakpointService, deps: [{ token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable });
BootstrapBreakpointService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BootstrapBreakpointService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BootstrapBreakpointService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [PLATFORM_ID]
                    }] }];
    } });

class HeaderComponent {
    constructor(headerService, appAlertService, appSessionService, appConfigService, googleAnalyticsService, bootstrapBreakpointService, cd, location /* TODO: LocationWrapper usage */) {
        this.headerService = headerService;
        this.appAlertService = appAlertService;
        this.appSessionService = appSessionService;
        this.appConfigService = appConfigService;
        this.googleAnalyticsService = googleAnalyticsService;
        this.bootstrapBreakpointService = bootstrapBreakpointService;
        this.cd = cd;
        this.location = location;
        this.copyOutage = copy$2.outage;
        this.copyPayBill = copy$2.payBill;
        this.copyMoving = copy$2.moving;
        this.copyContactUs = copy$2.contactUs;
        this.copySignIn = copy$2.signIn;
        this.copySignOut = copy$2.signOut;
        this.copySearch = copy$2.search;
        this.copyMenu = copy$2.menu;
        this.copyContact = copy$2.contact;
        this.copyEs = copy$2.español;
        this.copyZh = copy$2.chinese;
        this.copyClose = copy$2.close;
        this.copyRegister = copy$2.register;
        this.subscriptions = new Subscription();
        this.sessionChangeEvent = new EventEmitter();
        this.loginErrorEvent = new EventEmitter();
    }
    get copyChangeSignIn() {
        return this.appSessionService.isUserAuthenticated() ? copy$2.signOut : copy$2.signIn;
    }
    get changeSignInClassName() {
        return this.appSessionService.isUserAuthenticated() ? 'button--sign-out' : 'button--sign-in';
    }
    get isMenuOpen() {
        return this._isMenuOpen;
    }
    set isMenuOpen(value) {
        this._isMenuOpen = value;
        if (value) {
            this._isLoginPopupOpen = false;
        }
    }
    get isLoginPopupOpen() {
        return this._isLoginPopupOpen;
    }
    set isLoginPopupOpen(value) {
        this._isLoginPopupOpen = value;
        if (value) {
            this._isMenuOpen = false;
        }
    }
    get isShowingEs() {
        return !!this.headerService.españolUrl;
    }
    get isShowingZh() {
        return !!this.headerService.chineseUrl;
    }
    get isShowingRegistrationLink() {
        return !this.appSessionService.isUserAuthenticated();
    }
    get isSignedIn() {
        return this.appSessionService.isUserAuthenticated();
    }
    get isShowingHamburger() {
        return this.isMobile && this.isMenuOpen;
    }
    ngOnInit() {
        this.subscriptions.add(this.appConfigService.config$.pipe(filter(config => config.opCo != null)).subscribe(config => {
            this.gtmId = this.appConfigService.config.googleTagManagerId || '';
            this.opCo = this.appConfigService.config.opCo;
            const opCo = this.opCo.toLowerCase();
            const opCoConfig = opcoStrings[opCo];
            if (opCoConfig) {
                this.homeUrl = this.appConfigService.config.anonBaseUrl || opcoStrings[opCo].home;
                this.logoDesktop = opcoStrings[opCo].logoDesktop;
                this.logoMobile = opcoStrings[opCo].logoMobile;
                this.logoTitle = opcoStrings[opCo].logoTitle;
            }
            this.setupIsShowingSignIn();
        }));
    }
    ngAfterViewInit() {
        if (this.bootstrapBreakpointService) {
            this.bootstrapBreakpointService.resizeEvent.subscribe(size => {
                // tslint:disable-next-line: max-line-length
                this.isMobile = MEDIA_MOBILE.includes(size);
                this.cd.detectChanges();
            });
        }
        else {
            console.warn('[HeaderComponent] Deprecated: usage of HeaderComponent without mounting BootstrapBreakpointComponent is deprecated, please use BootstrapBreakpointModule');
        }
    }
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }
    setupIsShowingSignIn() {
        const self = this;
        const resolveIsShowingSignIn = alerts => self.isShowingSignIn = Array.isArray(alerts) &&
            alerts.every(alert => alert.AlertType !== AlertType.Login);
        if (this.appAlertService.alerts) {
            resolveIsShowingSignIn(this.appAlertService.alerts);
        }
        else {
            this.appAlertService.getAlerts().subscribe(resolveIsShowingSignIn);
        }
    }
    onClickOutage() {
        this.location.href = this.headerService.outageUrl;
    }
    onClickPayBill() {
        this.location.href = this.headerService.payBillUrl;
    }
    onClickMoving() {
        this.location.href = this.headerService.movingUrl;
    }
    onClickContactUs() {
        this.location.href = this.headerService.contactUsUrl;
    }
    onSearch() {
        if (/\S/.test(this.searchTerm)) {
            this.location.href = this.headerService.generateSearchUrl(this.searchTerm);
        }
    }
    onClickChangeSignIn(foo) {
        if (this.appSessionService.isUserAuthenticated()) {
            this.location.href = this.headerService.signOutUrl;
            this.sessionChangeEvent.emit(false);
        }
        else {
            let dest = this.headerService.signInUrl;
            if (this.headerService.Target && !/\?.*target\=/i.test(dest)) {
                const params = {
                    Target: this.headerService.Target
                };
                const query = Object.entries(params)
                    .map(pair => pair.map(encodeURIComponent).join('='))
                    .join('&');
                dest += query;
            }
            this.location.href = dest;
            this.sessionChangeEvent.emit(true);
        }
    }
    onLoginSuccess(event) {
        this.sessionChangeEvent.emit(event);
    }
    onLoginError(event) {
        this.loginErrorEvent.emit(event);
    }
    onClickEs() {
        this.location.href = this.headerService.españolUrl;
    }
    onClickZh() {
        this.location.href = this.headerService.chineseUrl;
    }
    toggleMenu(state = !this.isMenuOpen) {
        this.isMenuOpen = state;
    }
    onClickRegister() {
        this.googleAnalyticsService.pageNavigated(this.headerService.registerVpv);
        this.location.href = this.headerService.userRegistrationLandingUrl;
    }
}
HeaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: HeaderComponent, deps: [{ token: HeaderService }, { token: AppAlertsService }, { token: AppSessionService }, { token: AppConfigService }, { token: GoogleAnalyticsService }, { token: BootstrapBreakpointService, optional: true }, { token: i0.ChangeDetectorRef }, { token: WINDOW_LOCATION }], target: i0.ɵɵFactoryTarget.Component });
HeaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: HeaderComponent, selector: "app-header", outputs: { sessionChangeEvent: "sessionChange", loginErrorEvent: "loginError" }, viewQueries: [{ propertyName: "signInRef", first: true, predicate: ["signIn"], descendants: true, static: true }, { propertyName: "signOutRef", first: true, predicate: ["signOut"], descendants: true, static: true }], ngImport: i0, template: "<header class=\"header component opco-{{opCo}} bg-{{opCo}}-primaryBrand\">\r\n  <div class=\"container\">\r\n    <div class=\"row\">\r\n      <div class=\"content--wrapper col-12\">\r\n        <div class=\"d-none d-sm-block\">\r\n          <a href=\"{{homeUrl}}\">\r\n            <img src=\"{{logoDesktop}}\" class=\"brand logo-screen\" alt=\"{{logoTitle}}\" />\r\n          </a>\r\n        </div>\r\n        <div class=\"d-sm-none\">\r\n          <a href=\"{{homeUrl}}\">\r\n            <img src=\"{{logoMobile}}\" class=\"brand logo-mobile\" alt=\"{{logoTitle}}\" />\r\n          </a>\r\n        </div>\r\n        <div class=\"link-panel--wrapper\">\r\n          <ul class=link-panel [ngClass]=\"{ mobile: isMobile}\">\r\n            <li class=outage>\r\n              <app-button-common (clickEvent)=\"onClickOutage()\">\r\n                <span class=\"text-button--copy copy\">{{ copyOutage }}</span>\r\n              </app-button-common>\r\n            </li>\r\n            <li class=pay-bill>\r\n              <app-button-common (clickEvent)=\"onClickPayBill()\">\r\n                <span class=\"text-button--copy copy\">{{ copyPayBill }}</span>\r\n              </app-button-common>\r\n            </li>\r\n            <li class=moving>\r\n              <app-button-common (clickEvent)=\"onClickMoving()\">\r\n                <span class=\"text-button--copy copy\">{{ copyMoving }}</span>\r\n              </app-button-common>\r\n            </li>\r\n            <li class=espa\u00F1ol *ngIf=\"isShowingEs\">\r\n              <app-button-common (clickEvent)=\"onClickEs()\">\r\n                <span class=\"text-button--copy copy\">{{ copyEs }}</span>\r\n              </app-button-common>\r\n            </li>\r\n            <li class=chinese *ngIf=\"isShowingZh\">\r\n              <app-button-common (clickEvent)=\"onClickZh()\">\r\n                <span class=\"text-button--copy copy\">{{ copyZh }}</span>\r\n              </app-button-common>\r\n            </li>\r\n            <li class=contact-us>\r\n              <app-button-common (clickEvent)=\"onClickContactUs()\">\r\n                <span class=\"text-button--copy copy\">{{ copyContactUs }}</span>\r\n              </app-button-common>\r\n            </li>\r\n            <li id=search class=search>\r\n              <div class=search--wrapper>\r\n                <ng-container *ngTemplateOutlet=\"search\"></ng-container>\r\n                <button class=\"search--control d-none d-md-block\" (click)=\"onSearch()\"></button>\r\n              </div>\r\n            </li>\r\n            <li id=sign-in class=sign-in *ngIf=\"isShowingSignIn\">\r\n              <button [attr.aria-label]=\"copyChangeSignIn\"\r\n                      [class]=\"changeSignInClassName + ' btn'\"\r\n                      (click)=\"onClickChangeSignIn()\">\r\n                <span class=\"copy\">{{ copyChangeSignIn }}</span>\r\n              </button>\r\n            </li>\r\n            <li id=\"register\" class=\"register\" *ngIf=isShowingRegistrationLink>\r\n              <app-button-common (clickEvent)=\"onClickRegister()\">\r\n                <span class=\"text-button--copy copy\">{{ copyRegister }}</span>\r\n              </app-button-common>\r\n            </li>\r\n          </ul>\r\n          <div class=mobile-account--wrapper *ngIf=\"isMobile\">\r\n            <button class=\"mobile-account mobile-option\"\r\n                    [class.openLoginPopup]=\"isLoginPopupOpen\"\r\n                    [attr.aria-label]=\"isSignedIn ? copySignOut : copySignIn\"\r\n                    (click)=\"onClickChangeSignIn()\">\r\n              <ng-container *ngIf=\"!isSignedIn else MobileSignOut\">\r\n                <ng-container *ngIf=\"!isLoginPopupOpen else CloseSignIn\">\r\n                  <span class=\"exc-icon-account\" aria-label=\"icon-account\"></span>\r\n                  <span id=\"mobile-account\" class=\"mobile-option--copy copy\">{{ copySignIn }}</span>\r\n                </ng-container>\r\n                <ng-template #CloseSignIn>\r\n                    <span class=\"icon-cross\" [attr.aria-label]=\"copyClose\"></span>\r\n                </ng-template>\r\n              </ng-container>\r\n              <ng-template #MobileSignOut>\r\n                <span class=\"exc-icon-account\" aria-label=\"icon-account\"></span>\r\n                <span class=\"mobile-option--copy copy\">{{ copySignOut }}</span>\r\n              </ng-template>\r\n            </button>\r\n          </div>\r\n          <button class=\"mobile-register mobile-option\"\r\n                  attr.aria-labelledby=\"hamburger-mega-menu\"\r\n                  (click)=\"onClickRegister()\"\r\n                  *ngIf=\"isMobile && isShowingRegistrationLink\">\r\n            <ng-container [ngTemplateOutlet]=registerIcon></ng-container>\r\n            <span id=\"hamburger-register\" class=\"mobile-option--copy copy\">{{ copyRegister }}</span>\r\n          </button>\r\n          <button class=\"mobile-menu mobile-option\"\r\n                  [ngClass]=\"{ openMenu: isMenuOpen }\"\r\n                  attr.aria-labelledby=\"hamburger-mega-menu\"\r\n                  (click)=\"toggleMenu()\"\r\n                  *ngIf=\"isMobile\">\r\n            <ng-container *ngIf=\"!isShowingHamburger else CloseHamburger\">\r\n              <span class=\"exc-icon-menu\" aria-label=\"icon-menu\"></span>\r\n              <span id=\"hamburger-mega-menu\" class=\"mobile-option--copy copy\">{{ copyMenu }}</span>\r\n            </ng-container>\r\n            <ng-template #CloseHamburger>\r\n                <span class=\"icon-cross\" [attr.aria-label]=\"copyClose\"></span>\r\n            </ng-template>\r\n          </button>\r\n        </div>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</header>\r\n<ul class=\"hamburger-mega-menu\" *ngIf=\"isShowingHamburger\">\r\n  <li id=search class=search>\r\n    <ng-container *ngTemplateOutlet=\"search\"></ng-container>\r\n  </li>\r\n  <li class=outage>\r\n    <app-button-common (clickEvent)=\"onClickOutage()\">\r\n      <span class=\"text-button--copy copy\">{{ copyOutage }}</span>\r\n    </app-button-common>\r\n  </li>\r\n  <li class=pay-bill>\r\n    <app-button-common (clickEvent)=\"onClickPayBill()\">\r\n      <span class=\"text-button--copy copy\">{{ copyPayBill }}</span>\r\n    </app-button-common>\r\n  </li>\r\n  <li class=moving>\r\n    <app-button-common (clickEvent)=\"onClickMoving()\">\r\n      <span class=\"text-button--copy copy\">{{ copyMoving }}</span>\r\n    </app-button-common>\r\n  </li>\r\n  <li class=espa\u00F1ol *ngIf=\"isShowingEs\">\r\n    <app-button-common (clickEvent)=\"onClickEs()\">\r\n      <span class=\"text-button--copy copy\">{{ copyEs }}</span>\r\n    </app-button-common>\r\n  </li>\r\n   <li class=chinese *ngIf=\"isShowingZh\">\r\n    <app-button-common (clickEvent)=\"onClickZh()\">\r\n      <span class=\"text-button--copy copy\">{{ copyZh }}</span>\r\n    </app-button-common>\r\n  </li>\r\n  <li class=contact-us-mobile>\r\n    <app-button-common (clickEvent)=\"onClickContactUs()\">\r\n      <span class=\"text-button--copy copy\">{{ copyContactUs }}</span>\r\n    </app-button-common>\r\n  </li>\r\n  <app-mega-menu [isHamburger]=\"true\"></app-mega-menu>\r\n</ul>\r\n\r\n<!-- sign-in button templates -->\r\n<ng-template #signIn>\r\n  <span class=\"text-button--copy copy\">{{ copySignIn }}</span>\r\n  <span class=\"icon icon-black-down-pointing-triangle\" aria-label=\"icon-black-down-pointing-triangle\"></span>\r\n</ng-template>\r\n\r\n<ng-template #signOut>\r\n  <span class=\"text-button--copy copy\">{{ copySignOut }}</span>\r\n</ng-template>\r\n\r\n<!-- search input template -->\r\n<ng-template #search>\r\n  <input class=\"input--search\"\r\n         attr.aria-label={{copySearch}}\r\n         [placeholder]=\"copySearch\"\r\n         (keyup.enter)=\"onSearch()\"\r\n         (keyup.shift.enter)=\"onSearch()\"\r\n         [(ngModel)]=\"searchTerm\">\r\n</ng-template>\r\n\r\n<ng-template #registerIcon>\r\n<svg width=\"20px\" height=\"20px\" viewBox=\"0 0 20 20\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\">\r\n  <title>add-friend</title>\r\n  <g id=\"03.01-CRED-RET:-DESIGN---ComEd\" stroke=\"none\" stroke-width=\"1\" fill=\"none\" fill-rule=\"evenodd\">\r\n    <g id=\"mobile-nav-update-signed-out\" transform=\"translate(-245.000000, -21.000000)\" fill=\"#FFFFFF\" fill-rule=\"nonzero\">\r\n      <g id=\"Group-2\" transform=\"translate(234.000000, 21.000000)\">\r\n        <g id=\"Group\" transform=\"translate(11.000000, 0.000000)\">\r\n          <path d=\"M11.2061768,9.8380127 C10.599954,10.0230713 9.07168579,10.0843811 8.26528931,9.88772583 C7.43282989,9.68471461 6.80222783,9.31598828 5.8894043,8.68798828 C4.76069841,9.13426828 3.46607843,10.13668 2.58439216,11.036 C0.917803922,12.73588 0,14.99596 0,17.39996 L0,19.4 C0,19.73136 0.263372549,20 0.588235294,20 L17.0588235,20 C17.3836863,20 17.6470588,19.73136 17.6470588,19.4 L17.6470588,17.39996 C17.6470588,17.36392 17.6460784,17.32776 17.6456471,17.29164 C17.0938039,17.49108 16.5003529,17.6 15.8823529,17.6 C12.9631765,17.6 10.5882353,15.17756 10.5882353,12.2 C10.5882353,11.1728 10.7963867,10.6354736 11.2061768,9.8380127 Z\" id=\"Path\"></path>\r\n          <path d=\"M15.8823529,8 C13.6118824,8 11.7647059,9.88412 11.7647059,12.2 C11.7647059,14.51588 13.6118824,16.4 15.8823529,16.4 C18.1528235,16.4 20,14.51588 20,12.2 C20,9.88412 18.1528235,8 15.8823529,8 Z M17.0588235,12.8 L16.4705882,12.8 L16.4705882,13.4 C16.4705882,13.73136 16.2072157,14 15.8823529,14 C15.5574902,14 15.2941176,13.73136 15.2941176,13.4 L15.2941176,12.8 L14.7058824,12.8 C14.3810196,12.8 14.1176471,12.53136 14.1176471,12.2 C14.1176471,11.86864 14.3810196,11.6 14.7058824,11.6 L15.2941176,11.6 L15.2941176,11 C15.2941176,10.66864 15.5574902,10.4 15.8823529,10.4 C16.2072157,10.4 16.4705882,10.66864 16.4705882,11 L16.4705882,11.6 L17.0588235,11.6 C17.3836863,11.6 17.6470588,11.86864 17.6470588,12.2 C17.6470588,12.53136 17.3836863,12.8 17.0588235,12.8 Z\" id=\"Shape\"></path>\r\n          <path d=\"M9.5,9 C7.01867672,9 5,6.98132328 5,4.5 C5,2.01867672 7.01867672,0 9.5,0 C11.9813233,0 14,2.01867672 14,4.5 C14,6.98132328 11.9813233,9 9.5,9 Z\" id=\"Path\"></path>\r\n        </g>\r\n      </g>\r\n    </g>\r\n  </g>\r\n</svg>\r\n</ng-template>\r\n", styles: ["@charset \"UTF-8\";:root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}@font-face{font-family:Open Sans Bold;font-style:normal;font-weight:800;src:local(\"Open Sans Bold\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-800.woff2) format(\"woff2\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-800.woff) format(\"woff\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-800.ttf) format(\"truetype\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-800.svg#OpenSans) format(\"svg\");font-display:swap}@font-face{font-family:Open Sans Semi Bold;font-style:normal;font-weight:600;src:local(\"Open Sans Semi Bold\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-600.woff2) format(\"woff2\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-600.woff) format(\"woff\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-600.ttf) format(\"truetype\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-600.svg#OpenSans) format(\"svg\");font-display:swap}@font-face{font-family:Open Sans Regular;font-style:normal;font-weight:400;src:local(\"Open Sans Regular\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-regular.woff2) format(\"woff2\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-regular.woff) format(\"woff\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-regular.ttf) format(\"truetype\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-regular.svg#OpenSans) format(\"svg\");font-display:swap}@font-face{font-family:Open Sans Bold Italic;font-style:italic;font-weight:800;src:local(\"Open Sans Bold Italic\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-800italic.woff2) format(\"woff2\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-800italic.woff) format(\"woff\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-800italic.ttf) format(\"truetype\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-800italic.svg#OpenSans) format(\"svg\");font-display:swap}@font-face{font-family:Open Sans Semi Bold Italic;font-style:italic;font-weight:600;src:local(\"Open Sans Semi Bold Italic\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-600italic.woff2) format(\"woff2\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-600italic.woff) format(\"woff\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-600italic.ttf) format(\"truetype\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-600italic.svg#OpenSans) format(\"svg\");font-display:swap}@font-face{font-family:Open Sans Italic;font-style:italic;font-weight:400;src:local(\"Open Sans Italic\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-italic.woff2) format(\"woff2\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-italic.woff) format(\"woff\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-italic.ttf) format(\"truetype\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-italic.svg#OpenSans) format(\"svg\");font-display:swap}@font-face{font-family:icomoon;src:local(\"icomoon\"),url(/Lib/Exelon/exc-fonts/shared/icomoon.ttf?xikojc) format(\"truetype\"),url(/Lib/Exelon/exc-fonts/shared/icomoon.woff?xikojc) format(\"woff\"),url(/Lib/Exelon/exc-fonts/shared/icomoon.svg?xikojc#icomoon) format(\"svg\");font-weight:400;font-style:normal;font-display:swap}@font-face{font-family:exc-icon;src:url(../../assets/fonts/excicon.eot?kqbp4c);src:url(../../assets/fonts/excicon.eot?kqbp4c#iefix) format(\"embedded-opentype\"),url(../../assets/fonts/excicon.ttf?kqbp4c) format(\"truetype\"),url(../../assets/fonts/excicon.woff?kqbp4c) format(\"woff\"),url(../../assets/fonts/excicon.svg?kqbp4c#excicon) format(\"svg\");font-weight:400;font-style:normal}.icon-eye:before{content:\"\\e6a5\"}.icon-eye-crossed:before{content:\"\\e6a6\"}@font-face{font-family:dashboard;src:url(../../assets/fonts/dashboard.eot?2ooj47);src:url(../../assets/fonts/dashboard.eot?2ooj47#iefix) format(\"embedded-opentype\"),url(../../assets/fonts/dashboard.woff2?2ooj47) format(\"woff2\"),url(../../assets/fonts/dashboard.ttf?2ooj47) format(\"truetype\"),url(../../assets/fonts/dashboard.woff?2ooj47) format(\"woff\"),url(../../assets/fonts/dashboard.svg?2ooj47#dashboard) format(\"svg\");font-weight:400;font-style:normal}[class^=exc-icon-]{font-family:exc-icon;speak:none;font-style:normal;font-weight:400;font-feature-settings:normal;font-variant:normal;text-transform:none;font-size:18px;color:#fff;line-height:1;display:block}.exc-icon-contact:before{content:\"\\e911\"}.exc-icon-account:before{content:\"\\e90a\"}.exc-icon-menu:before{content:\"\\e916\"}.icon-cross:before{font-family:dashboard;content:\"\\e900\"}.header.component ::ng-deep button.mobile-account{padding-left:9px;padding-right:9px}.header.component .link-panel--wrapper ::ng-deep button{-webkit-appearance:none;appearance:none}.header.component .link-panel--wrapper ::ng-deep button:not([class^=button--sign-in]):not([class*=button-sign-out]){background-color:inherit;border:none}.mobile-option--copy{font-family:Arial,Helvetica Neue,Helvetica,sans-serif;font-size:11px;color:#fff;line-height:1.8181818182}header{color:#fff}button{cursor:pointer}@media (min-width: 992px){header .container{max-width:1200px}}@media (min-width: 768px) and (max-width: 991.98px){header .container{margin-right:0}}@media (max-width: 767.98px){header{max-width:100vw}}header a{outline:none}header a .logo-screen{width:250px;height:53px;cursor:pointer;margin:15px 0}header a .logo-mobile{width:119px;height:33px;cursor:pointer;margin:15px 0}.content--wrapper{display:flex;flex-direction:row;align-items:center;height:114px}@media (min-width: 768px) and (max-width: 991.98px){.content--wrapper{padding-right:0}}@media (max-width: 767.98px){.content--wrapper{height:72px;padding-left:10px;padding-right:0}}.text-button--copy{color:#fff;font-size:14px;line-height:1.4285714286}@media (max-width: 767.98px){.text-button--copy{font-size:16px;color:#4a4a4a}}.icon-black-down-pointing-triangle:before{color:#fff;font-size:14px;font-family:Open Sans Regular,Helvetica Neue,Helvetica,Arial,sans-serif;content:\"\\25bc\"}.link-panel--wrapper{flex:1;display:flex;flex-direction:row;justify-content:flex-end}@media (min-width: 768px) and (max-width: 991.98px){.link-panel--wrapper{height:100%;padding-top:10px;padding-bottom:10px;margin-right:5px}}@media (max-width: 767.98px){.link-panel--wrapper{height:100%}}.link-panel.mobile{display:none}.link-panel,.link-panel>li{display:flex;flex-direction:row;align-items:center;list-style:none;padding:0;margin:0;height:14px}.link-panel+li,.link-panel>li+li{border-left:solid thin #fff}.link-panel .text-button--copy.copy:hover,.link-panel>li .text-button--copy.copy:hover{text-decoration:underline}.link-panel.contact-us,.link-panel>li.contact-us{border-right:solid thin #fff}@media (min-width: 768px) and (max-width: 991.98px){.link-panel.contact-us,.link-panel>li.contact-us{border-right:none}}.link-panel.sign-in,.link-panel>li.sign-in{max-height:34px;padding-left:12px;padding-right:12px}.button--sign-in,.button--sign-out{color:#0059a4;font-size:14px;padding-left:20px;padding-right:20px}li.contact-us{border-right:none!important}li.sign-in,li.register{border-left:none!important}@media (min-width: 768px) and (max-width: 991.98px){.link-panel{flex-flow:row wrap;flex-wrap:wrap;justify-content:flex-end;max-width:50vw;height:100%}}#search{display:inline-flex;flex-direction:row;align-items:center;padding:0 5px;height:38px;width:182px;border-left:none}@media (max-width: 767.98px){#search{height:52px;padding-top:5px;padding-bottom:5px;width:100%}}.input--search{width:158px;padding-left:12px;padding-right:12px;border:1px solid #ccc;color:#737373;font-family:Open Sans Semi Bold;font-size:14px;font-weight:600;line-height:1.3571428571;height:44px}@media (max-width: 767.98px){.input--search{width:100%}}@media (max-width: 767.98px){button.mobile-option{display:flex;height:100%;flex-direction:column;align-items:center;justify-content:center;outline:none;padding-left:11px;padding-top:9px}.mobile-menu{padding-right:9px}.mobile-menu.openMenu,.mobile-account.openLoginPopup{border:none;background-color:#d8d8d8}.icon-cross{margin-top:-6px;font-weight:600;font-size:28px;font-family:dashboard}.icon-cross:before{color:#737373;margin-top:-2px;content:\"\\e900\"}.hamburger-mega-menu{list-style:none;margin:0;padding:0;background-color:#d8d8d8}.hamburger-mega-menu>li{list-style:none;margin:0;padding:0;background-color:#d8d8d8;height:42px;display:flex;flex-direction:row;align-items:center}.hamburger-mega-menu>li+li{border-top:solid thin #ececec}.hamburger-mega-menu>li:last-of-type{border-bottom:solid thin #ececec}.hamburger-mega-menu>li>app-button-common ::ng-deep button{padding-left:10px;padding-right:10px}}li.sign-in{position:relative}.login-popup--wrapper{position:absolute;z-index:999}.login-popup--wrapper.mobile{left:0}li.sign-in>.login-popup--wrapper{top:30px;right:12px;padding-top:34px}.search--wrapper{position:relative}.search--wrapper .input--search{padding-right:30px;border-radius:2px}.search--wrapper .search--control{position:absolute;top:4px;right:14px}.search--wrapper .search--control:before{font-family:exc-icon;font-size:16px;color:gray;content:\"\\e91c\"}.header.component #sign-in ::ng-deep .button--sign-in,.header.component #sign-in ::ng-deep .button--sign-out{padding-top:5px;padding-bottom:5px;height:44px;border:2px solid transparent;background-color:#fff;border-color:#fff}.header.component #sign-in ::ng-deep .button--sign-in:active,.header.component #sign-in ::ng-deep .button--sign-out:active{outline:0}.header.component #sign-in ::ng-deep .button--sign-in:hover,.header.component #sign-in ::ng-deep .button--sign-out:hover{background-color:transparent;border-color:#fff;border-width:2px;color:#fff}.header.component #sign-in ::ng-deep .button--sign-in:focus,.header.component #sign-in ::ng-deep .button--sign-out:focus{outline-color:-webkit-focus-ring-color;outline-style:auto;outline-width:5px;outline-offset:-2px;box-shadow:inset 0 0 3px 2px #bfd5e8}.header.component #sign-in ::ng-deep [aria-pressed=true] .button--sign-in{background-color:transparent;border-color:#fff;outline-color:-webkit-focus-ring-color;outline-style:auto;outline-width:5px;outline-offset:-2px}.header.component #sign-in ::ng-deep [aria-pressed=true] .button--sign-out{background-color:transparent;border-color:#fff;outline-color:-webkit-focus-ring-color;outline-style:auto;outline-width:5px;outline-offset:-2px}\n"], dependencies: [{ kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: ButtonComponent, selector: "app-button-common", inputs: ["disabled", "ariaLabel", "className", "classType", "type", "loaderType"], outputs: ["clickEvent"] }, { kind: "component", type: MegaMenuComponent, selector: "app-mega-menu", inputs: ["isHamburger"], outputs: ["mouseenterTabOption", "mouseleaveTabOption", "mouseenterMenuPanel", "mouseleaveMenuPanel", "mouseenterPanelOption", "mouseleavePanelOption", "clickPanelOption"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: HeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-header', template: "<header class=\"header component opco-{{opCo}} bg-{{opCo}}-primaryBrand\">\r\n  <div class=\"container\">\r\n    <div class=\"row\">\r\n      <div class=\"content--wrapper col-12\">\r\n        <div class=\"d-none d-sm-block\">\r\n          <a href=\"{{homeUrl}}\">\r\n            <img src=\"{{logoDesktop}}\" class=\"brand logo-screen\" alt=\"{{logoTitle}}\" />\r\n          </a>\r\n        </div>\r\n        <div class=\"d-sm-none\">\r\n          <a href=\"{{homeUrl}}\">\r\n            <img src=\"{{logoMobile}}\" class=\"brand logo-mobile\" alt=\"{{logoTitle}}\" />\r\n          </a>\r\n        </div>\r\n        <div class=\"link-panel--wrapper\">\r\n          <ul class=link-panel [ngClass]=\"{ mobile: isMobile}\">\r\n            <li class=outage>\r\n              <app-button-common (clickEvent)=\"onClickOutage()\">\r\n                <span class=\"text-button--copy copy\">{{ copyOutage }}</span>\r\n              </app-button-common>\r\n            </li>\r\n            <li class=pay-bill>\r\n              <app-button-common (clickEvent)=\"onClickPayBill()\">\r\n                <span class=\"text-button--copy copy\">{{ copyPayBill }}</span>\r\n              </app-button-common>\r\n            </li>\r\n            <li class=moving>\r\n              <app-button-common (clickEvent)=\"onClickMoving()\">\r\n                <span class=\"text-button--copy copy\">{{ copyMoving }}</span>\r\n              </app-button-common>\r\n            </li>\r\n            <li class=espa\u00F1ol *ngIf=\"isShowingEs\">\r\n              <app-button-common (clickEvent)=\"onClickEs()\">\r\n                <span class=\"text-button--copy copy\">{{ copyEs }}</span>\r\n              </app-button-common>\r\n            </li>\r\n            <li class=chinese *ngIf=\"isShowingZh\">\r\n              <app-button-common (clickEvent)=\"onClickZh()\">\r\n                <span class=\"text-button--copy copy\">{{ copyZh }}</span>\r\n              </app-button-common>\r\n            </li>\r\n            <li class=contact-us>\r\n              <app-button-common (clickEvent)=\"onClickContactUs()\">\r\n                <span class=\"text-button--copy copy\">{{ copyContactUs }}</span>\r\n              </app-button-common>\r\n            </li>\r\n            <li id=search class=search>\r\n              <div class=search--wrapper>\r\n                <ng-container *ngTemplateOutlet=\"search\"></ng-container>\r\n                <button class=\"search--control d-none d-md-block\" (click)=\"onSearch()\"></button>\r\n              </div>\r\n            </li>\r\n            <li id=sign-in class=sign-in *ngIf=\"isShowingSignIn\">\r\n              <button [attr.aria-label]=\"copyChangeSignIn\"\r\n                      [class]=\"changeSignInClassName + ' btn'\"\r\n                      (click)=\"onClickChangeSignIn()\">\r\n                <span class=\"copy\">{{ copyChangeSignIn }}</span>\r\n              </button>\r\n            </li>\r\n            <li id=\"register\" class=\"register\" *ngIf=isShowingRegistrationLink>\r\n              <app-button-common (clickEvent)=\"onClickRegister()\">\r\n                <span class=\"text-button--copy copy\">{{ copyRegister }}</span>\r\n              </app-button-common>\r\n            </li>\r\n          </ul>\r\n          <div class=mobile-account--wrapper *ngIf=\"isMobile\">\r\n            <button class=\"mobile-account mobile-option\"\r\n                    [class.openLoginPopup]=\"isLoginPopupOpen\"\r\n                    [attr.aria-label]=\"isSignedIn ? copySignOut : copySignIn\"\r\n                    (click)=\"onClickChangeSignIn()\">\r\n              <ng-container *ngIf=\"!isSignedIn else MobileSignOut\">\r\n                <ng-container *ngIf=\"!isLoginPopupOpen else CloseSignIn\">\r\n                  <span class=\"exc-icon-account\" aria-label=\"icon-account\"></span>\r\n                  <span id=\"mobile-account\" class=\"mobile-option--copy copy\">{{ copySignIn }}</span>\r\n                </ng-container>\r\n                <ng-template #CloseSignIn>\r\n                    <span class=\"icon-cross\" [attr.aria-label]=\"copyClose\"></span>\r\n                </ng-template>\r\n              </ng-container>\r\n              <ng-template #MobileSignOut>\r\n                <span class=\"exc-icon-account\" aria-label=\"icon-account\"></span>\r\n                <span class=\"mobile-option--copy copy\">{{ copySignOut }}</span>\r\n              </ng-template>\r\n            </button>\r\n          </div>\r\n          <button class=\"mobile-register mobile-option\"\r\n                  attr.aria-labelledby=\"hamburger-mega-menu\"\r\n                  (click)=\"onClickRegister()\"\r\n                  *ngIf=\"isMobile && isShowingRegistrationLink\">\r\n            <ng-container [ngTemplateOutlet]=registerIcon></ng-container>\r\n            <span id=\"hamburger-register\" class=\"mobile-option--copy copy\">{{ copyRegister }}</span>\r\n          </button>\r\n          <button class=\"mobile-menu mobile-option\"\r\n                  [ngClass]=\"{ openMenu: isMenuOpen }\"\r\n                  attr.aria-labelledby=\"hamburger-mega-menu\"\r\n                  (click)=\"toggleMenu()\"\r\n                  *ngIf=\"isMobile\">\r\n            <ng-container *ngIf=\"!isShowingHamburger else CloseHamburger\">\r\n              <span class=\"exc-icon-menu\" aria-label=\"icon-menu\"></span>\r\n              <span id=\"hamburger-mega-menu\" class=\"mobile-option--copy copy\">{{ copyMenu }}</span>\r\n            </ng-container>\r\n            <ng-template #CloseHamburger>\r\n                <span class=\"icon-cross\" [attr.aria-label]=\"copyClose\"></span>\r\n            </ng-template>\r\n          </button>\r\n        </div>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</header>\r\n<ul class=\"hamburger-mega-menu\" *ngIf=\"isShowingHamburger\">\r\n  <li id=search class=search>\r\n    <ng-container *ngTemplateOutlet=\"search\"></ng-container>\r\n  </li>\r\n  <li class=outage>\r\n    <app-button-common (clickEvent)=\"onClickOutage()\">\r\n      <span class=\"text-button--copy copy\">{{ copyOutage }}</span>\r\n    </app-button-common>\r\n  </li>\r\n  <li class=pay-bill>\r\n    <app-button-common (clickEvent)=\"onClickPayBill()\">\r\n      <span class=\"text-button--copy copy\">{{ copyPayBill }}</span>\r\n    </app-button-common>\r\n  </li>\r\n  <li class=moving>\r\n    <app-button-common (clickEvent)=\"onClickMoving()\">\r\n      <span class=\"text-button--copy copy\">{{ copyMoving }}</span>\r\n    </app-button-common>\r\n  </li>\r\n  <li class=espa\u00F1ol *ngIf=\"isShowingEs\">\r\n    <app-button-common (clickEvent)=\"onClickEs()\">\r\n      <span class=\"text-button--copy copy\">{{ copyEs }}</span>\r\n    </app-button-common>\r\n  </li>\r\n   <li class=chinese *ngIf=\"isShowingZh\">\r\n    <app-button-common (clickEvent)=\"onClickZh()\">\r\n      <span class=\"text-button--copy copy\">{{ copyZh }}</span>\r\n    </app-button-common>\r\n  </li>\r\n  <li class=contact-us-mobile>\r\n    <app-button-common (clickEvent)=\"onClickContactUs()\">\r\n      <span class=\"text-button--copy copy\">{{ copyContactUs }}</span>\r\n    </app-button-common>\r\n  </li>\r\n  <app-mega-menu [isHamburger]=\"true\"></app-mega-menu>\r\n</ul>\r\n\r\n<!-- sign-in button templates -->\r\n<ng-template #signIn>\r\n  <span class=\"text-button--copy copy\">{{ copySignIn }}</span>\r\n  <span class=\"icon icon-black-down-pointing-triangle\" aria-label=\"icon-black-down-pointing-triangle\"></span>\r\n</ng-template>\r\n\r\n<ng-template #signOut>\r\n  <span class=\"text-button--copy copy\">{{ copySignOut }}</span>\r\n</ng-template>\r\n\r\n<!-- search input template -->\r\n<ng-template #search>\r\n  <input class=\"input--search\"\r\n         attr.aria-label={{copySearch}}\r\n         [placeholder]=\"copySearch\"\r\n         (keyup.enter)=\"onSearch()\"\r\n         (keyup.shift.enter)=\"onSearch()\"\r\n         [(ngModel)]=\"searchTerm\">\r\n</ng-template>\r\n\r\n<ng-template #registerIcon>\r\n<svg width=\"20px\" height=\"20px\" viewBox=\"0 0 20 20\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\">\r\n  <title>add-friend</title>\r\n  <g id=\"03.01-CRED-RET:-DESIGN---ComEd\" stroke=\"none\" stroke-width=\"1\" fill=\"none\" fill-rule=\"evenodd\">\r\n    <g id=\"mobile-nav-update-signed-out\" transform=\"translate(-245.000000, -21.000000)\" fill=\"#FFFFFF\" fill-rule=\"nonzero\">\r\n      <g id=\"Group-2\" transform=\"translate(234.000000, 21.000000)\">\r\n        <g id=\"Group\" transform=\"translate(11.000000, 0.000000)\">\r\n          <path d=\"M11.2061768,9.8380127 C10.599954,10.0230713 9.07168579,10.0843811 8.26528931,9.88772583 C7.43282989,9.68471461 6.80222783,9.31598828 5.8894043,8.68798828 C4.76069841,9.13426828 3.46607843,10.13668 2.58439216,11.036 C0.917803922,12.73588 0,14.99596 0,17.39996 L0,19.4 C0,19.73136 0.263372549,20 0.588235294,20 L17.0588235,20 C17.3836863,20 17.6470588,19.73136 17.6470588,19.4 L17.6470588,17.39996 C17.6470588,17.36392 17.6460784,17.32776 17.6456471,17.29164 C17.0938039,17.49108 16.5003529,17.6 15.8823529,17.6 C12.9631765,17.6 10.5882353,15.17756 10.5882353,12.2 C10.5882353,11.1728 10.7963867,10.6354736 11.2061768,9.8380127 Z\" id=\"Path\"></path>\r\n          <path d=\"M15.8823529,8 C13.6118824,8 11.7647059,9.88412 11.7647059,12.2 C11.7647059,14.51588 13.6118824,16.4 15.8823529,16.4 C18.1528235,16.4 20,14.51588 20,12.2 C20,9.88412 18.1528235,8 15.8823529,8 Z M17.0588235,12.8 L16.4705882,12.8 L16.4705882,13.4 C16.4705882,13.73136 16.2072157,14 15.8823529,14 C15.5574902,14 15.2941176,13.73136 15.2941176,13.4 L15.2941176,12.8 L14.7058824,12.8 C14.3810196,12.8 14.1176471,12.53136 14.1176471,12.2 C14.1176471,11.86864 14.3810196,11.6 14.7058824,11.6 L15.2941176,11.6 L15.2941176,11 C15.2941176,10.66864 15.5574902,10.4 15.8823529,10.4 C16.2072157,10.4 16.4705882,10.66864 16.4705882,11 L16.4705882,11.6 L17.0588235,11.6 C17.3836863,11.6 17.6470588,11.86864 17.6470588,12.2 C17.6470588,12.53136 17.3836863,12.8 17.0588235,12.8 Z\" id=\"Shape\"></path>\r\n          <path d=\"M9.5,9 C7.01867672,9 5,6.98132328 5,4.5 C5,2.01867672 7.01867672,0 9.5,0 C11.9813233,0 14,2.01867672 14,4.5 C14,6.98132328 11.9813233,9 9.5,9 Z\" id=\"Path\"></path>\r\n        </g>\r\n      </g>\r\n    </g>\r\n  </g>\r\n</svg>\r\n</ng-template>\r\n", styles: ["@charset \"UTF-8\";:root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}@font-face{font-family:Open Sans Bold;font-style:normal;font-weight:800;src:local(\"Open Sans Bold\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-800.woff2) format(\"woff2\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-800.woff) format(\"woff\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-800.ttf) format(\"truetype\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-800.svg#OpenSans) format(\"svg\");font-display:swap}@font-face{font-family:Open Sans Semi Bold;font-style:normal;font-weight:600;src:local(\"Open Sans Semi Bold\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-600.woff2) format(\"woff2\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-600.woff) format(\"woff\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-600.ttf) format(\"truetype\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-600.svg#OpenSans) format(\"svg\");font-display:swap}@font-face{font-family:Open Sans Regular;font-style:normal;font-weight:400;src:local(\"Open Sans Regular\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-regular.woff2) format(\"woff2\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-regular.woff) format(\"woff\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-regular.ttf) format(\"truetype\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-regular.svg#OpenSans) format(\"svg\");font-display:swap}@font-face{font-family:Open Sans Bold Italic;font-style:italic;font-weight:800;src:local(\"Open Sans Bold Italic\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-800italic.woff2) format(\"woff2\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-800italic.woff) format(\"woff\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-800italic.ttf) format(\"truetype\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-800italic.svg#OpenSans) format(\"svg\");font-display:swap}@font-face{font-family:Open Sans Semi Bold Italic;font-style:italic;font-weight:600;src:local(\"Open Sans Semi Bold Italic\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-600italic.woff2) format(\"woff2\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-600italic.woff) format(\"woff\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-600italic.ttf) format(\"truetype\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-600italic.svg#OpenSans) format(\"svg\");font-display:swap}@font-face{font-family:Open Sans Italic;font-style:italic;font-weight:400;src:local(\"Open Sans Italic\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-italic.woff2) format(\"woff2\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-italic.woff) format(\"woff\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-italic.ttf) format(\"truetype\"),url(/Lib/Exelon/exc-fonts/shared/open-sans-v18-latin-italic.svg#OpenSans) format(\"svg\");font-display:swap}@font-face{font-family:icomoon;src:local(\"icomoon\"),url(/Lib/Exelon/exc-fonts/shared/icomoon.ttf?xikojc) format(\"truetype\"),url(/Lib/Exelon/exc-fonts/shared/icomoon.woff?xikojc) format(\"woff\"),url(/Lib/Exelon/exc-fonts/shared/icomoon.svg?xikojc#icomoon) format(\"svg\");font-weight:400;font-style:normal;font-display:swap}@font-face{font-family:exc-icon;src:url(../../assets/fonts/excicon.eot?kqbp4c);src:url(../../assets/fonts/excicon.eot?kqbp4c#iefix) format(\"embedded-opentype\"),url(../../assets/fonts/excicon.ttf?kqbp4c) format(\"truetype\"),url(../../assets/fonts/excicon.woff?kqbp4c) format(\"woff\"),url(../../assets/fonts/excicon.svg?kqbp4c#excicon) format(\"svg\");font-weight:400;font-style:normal}.icon-eye:before{content:\"\\e6a5\"}.icon-eye-crossed:before{content:\"\\e6a6\"}@font-face{font-family:dashboard;src:url(../../assets/fonts/dashboard.eot?2ooj47);src:url(../../assets/fonts/dashboard.eot?2ooj47#iefix) format(\"embedded-opentype\"),url(../../assets/fonts/dashboard.woff2?2ooj47) format(\"woff2\"),url(../../assets/fonts/dashboard.ttf?2ooj47) format(\"truetype\"),url(../../assets/fonts/dashboard.woff?2ooj47) format(\"woff\"),url(../../assets/fonts/dashboard.svg?2ooj47#dashboard) format(\"svg\");font-weight:400;font-style:normal}[class^=exc-icon-]{font-family:exc-icon;speak:none;font-style:normal;font-weight:400;font-feature-settings:normal;font-variant:normal;text-transform:none;font-size:18px;color:#fff;line-height:1;display:block}.exc-icon-contact:before{content:\"\\e911\"}.exc-icon-account:before{content:\"\\e90a\"}.exc-icon-menu:before{content:\"\\e916\"}.icon-cross:before{font-family:dashboard;content:\"\\e900\"}.header.component ::ng-deep button.mobile-account{padding-left:9px;padding-right:9px}.header.component .link-panel--wrapper ::ng-deep button{-webkit-appearance:none;appearance:none}.header.component .link-panel--wrapper ::ng-deep button:not([class^=button--sign-in]):not([class*=button-sign-out]){background-color:inherit;border:none}.mobile-option--copy{font-family:Arial,Helvetica Neue,Helvetica,sans-serif;font-size:11px;color:#fff;line-height:1.8181818182}header{color:#fff}button{cursor:pointer}@media (min-width: 992px){header .container{max-width:1200px}}@media (min-width: 768px) and (max-width: 991.98px){header .container{margin-right:0}}@media (max-width: 767.98px){header{max-width:100vw}}header a{outline:none}header a .logo-screen{width:250px;height:53px;cursor:pointer;margin:15px 0}header a .logo-mobile{width:119px;height:33px;cursor:pointer;margin:15px 0}.content--wrapper{display:flex;flex-direction:row;align-items:center;height:114px}@media (min-width: 768px) and (max-width: 991.98px){.content--wrapper{padding-right:0}}@media (max-width: 767.98px){.content--wrapper{height:72px;padding-left:10px;padding-right:0}}.text-button--copy{color:#fff;font-size:14px;line-height:1.4285714286}@media (max-width: 767.98px){.text-button--copy{font-size:16px;color:#4a4a4a}}.icon-black-down-pointing-triangle:before{color:#fff;font-size:14px;font-family:Open Sans Regular,Helvetica Neue,Helvetica,Arial,sans-serif;content:\"\\25bc\"}.link-panel--wrapper{flex:1;display:flex;flex-direction:row;justify-content:flex-end}@media (min-width: 768px) and (max-width: 991.98px){.link-panel--wrapper{height:100%;padding-top:10px;padding-bottom:10px;margin-right:5px}}@media (max-width: 767.98px){.link-panel--wrapper{height:100%}}.link-panel.mobile{display:none}.link-panel,.link-panel>li{display:flex;flex-direction:row;align-items:center;list-style:none;padding:0;margin:0;height:14px}.link-panel+li,.link-panel>li+li{border-left:solid thin #fff}.link-panel .text-button--copy.copy:hover,.link-panel>li .text-button--copy.copy:hover{text-decoration:underline}.link-panel.contact-us,.link-panel>li.contact-us{border-right:solid thin #fff}@media (min-width: 768px) and (max-width: 991.98px){.link-panel.contact-us,.link-panel>li.contact-us{border-right:none}}.link-panel.sign-in,.link-panel>li.sign-in{max-height:34px;padding-left:12px;padding-right:12px}.button--sign-in,.button--sign-out{color:#0059a4;font-size:14px;padding-left:20px;padding-right:20px}li.contact-us{border-right:none!important}li.sign-in,li.register{border-left:none!important}@media (min-width: 768px) and (max-width: 991.98px){.link-panel{flex-flow:row wrap;flex-wrap:wrap;justify-content:flex-end;max-width:50vw;height:100%}}#search{display:inline-flex;flex-direction:row;align-items:center;padding:0 5px;height:38px;width:182px;border-left:none}@media (max-width: 767.98px){#search{height:52px;padding-top:5px;padding-bottom:5px;width:100%}}.input--search{width:158px;padding-left:12px;padding-right:12px;border:1px solid #ccc;color:#737373;font-family:Open Sans Semi Bold;font-size:14px;font-weight:600;line-height:1.3571428571;height:44px}@media (max-width: 767.98px){.input--search{width:100%}}@media (max-width: 767.98px){button.mobile-option{display:flex;height:100%;flex-direction:column;align-items:center;justify-content:center;outline:none;padding-left:11px;padding-top:9px}.mobile-menu{padding-right:9px}.mobile-menu.openMenu,.mobile-account.openLoginPopup{border:none;background-color:#d8d8d8}.icon-cross{margin-top:-6px;font-weight:600;font-size:28px;font-family:dashboard}.icon-cross:before{color:#737373;margin-top:-2px;content:\"\\e900\"}.hamburger-mega-menu{list-style:none;margin:0;padding:0;background-color:#d8d8d8}.hamburger-mega-menu>li{list-style:none;margin:0;padding:0;background-color:#d8d8d8;height:42px;display:flex;flex-direction:row;align-items:center}.hamburger-mega-menu>li+li{border-top:solid thin #ececec}.hamburger-mega-menu>li:last-of-type{border-bottom:solid thin #ececec}.hamburger-mega-menu>li>app-button-common ::ng-deep button{padding-left:10px;padding-right:10px}}li.sign-in{position:relative}.login-popup--wrapper{position:absolute;z-index:999}.login-popup--wrapper.mobile{left:0}li.sign-in>.login-popup--wrapper{top:30px;right:12px;padding-top:34px}.search--wrapper{position:relative}.search--wrapper .input--search{padding-right:30px;border-radius:2px}.search--wrapper .search--control{position:absolute;top:4px;right:14px}.search--wrapper .search--control:before{font-family:exc-icon;font-size:16px;color:gray;content:\"\\e91c\"}.header.component #sign-in ::ng-deep .button--sign-in,.header.component #sign-in ::ng-deep .button--sign-out{padding-top:5px;padding-bottom:5px;height:44px;border:2px solid transparent;background-color:#fff;border-color:#fff}.header.component #sign-in ::ng-deep .button--sign-in:active,.header.component #sign-in ::ng-deep .button--sign-out:active{outline:0}.header.component #sign-in ::ng-deep .button--sign-in:hover,.header.component #sign-in ::ng-deep .button--sign-out:hover{background-color:transparent;border-color:#fff;border-width:2px;color:#fff}.header.component #sign-in ::ng-deep .button--sign-in:focus,.header.component #sign-in ::ng-deep .button--sign-out:focus{outline-color:-webkit-focus-ring-color;outline-style:auto;outline-width:5px;outline-offset:-2px;box-shadow:inset 0 0 3px 2px #bfd5e8}.header.component #sign-in ::ng-deep [aria-pressed=true] .button--sign-in{background-color:transparent;border-color:#fff;outline-color:-webkit-focus-ring-color;outline-style:auto;outline-width:5px;outline-offset:-2px}.header.component #sign-in ::ng-deep [aria-pressed=true] .button--sign-out{background-color:transparent;border-color:#fff;outline-color:-webkit-focus-ring-color;outline-style:auto;outline-width:5px;outline-offset:-2px}\n"] }]
        }], ctorParameters: function () {
        return [{ type: HeaderService }, { type: AppAlertsService }, { type: AppSessionService }, { type: AppConfigService }, { type: GoogleAnalyticsService }, { type: BootstrapBreakpointService, decorators: [{
                        type: Optional
                    }] }, { type: i0.ChangeDetectorRef }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [WINDOW_LOCATION]
                    }] }];
    }, propDecorators: { sessionChangeEvent: [{
                type: Output,
                args: ['sessionChange']
            }], loginErrorEvent: [{
                type: Output,
                args: ['loginError']
            }], signInRef: [{
                type: ViewChild,
                args: ['signIn', { static: true }]
            }], signOutRef: [{
                type: ViewChild,
                args: ['signOut', { static: true }]
            }] } });

class HeaderModule {
}
HeaderModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: HeaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
HeaderModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: HeaderModule, declarations: [HeaderComponent], imports: [CommonModule,
        FormsModule,
        ButtonModule,
        ToolTipModule,
        MegaMenuModule,
        ToggleButtonModule], exports: [HeaderComponent] });
HeaderModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: HeaderModule, providers: [
        HeaderService,
        GoogleAnalyticsService,
        CookieService
    ], imports: [CommonModule,
        FormsModule,
        ButtonModule,
        ToolTipModule,
        MegaMenuModule,
        ToggleButtonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: HeaderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        ButtonModule,
                        ToolTipModule,
                        MegaMenuModule,
                        ToggleButtonModule
                    ],
                    providers: [
                        HeaderService,
                        GoogleAnalyticsService,
                        CookieService
                    ],
                    declarations: [HeaderComponent],
                    exports: [HeaderComponent]
                }]
        }] });

class HeaderSimpleComponent {
    constructor(appConfigService) {
        this.appConfigService = appConfigService;
        this.subscriptions = new Subscription();
    }
    ngOnInit() {
        this.subscriptions.add(this.appConfigService.config$.pipe(filter(config => config.opCo != null)).subscribe(config => {
            this.opCo = this.appConfigService.config.opCo;
            const opCo = this.opCo.toLowerCase();
            const opCoConfig = opcoStrings[opCo];
            if (opCoConfig) {
                const opcoValue = opcoStrings[opCo];
                this.logoImageDesktop = opcoValue.logoDesktop;
                this.logoImageMobile = opcoValue.logoMobile;
                this.logoTitle = opcoValue.logoTitle;
            }
            this.homeUrl = this.appConfigService.config.anonBaseUrl || opcoStrings[opCo].home;
        }));
    }
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }
}
HeaderSimpleComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: HeaderSimpleComponent, deps: [{ token: AppConfigService }], target: i0.ɵɵFactoryTarget.Component });
HeaderSimpleComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: HeaderSimpleComponent, selector: "app-header-simple", ngImport: i0, template: "<!-- hreader component mark up starts here-->\r\n<header class=\"header opco-{{opCo}} bg-{{opCo}}-primaryBrand\">\r\n  <div class=\"container\">\r\n    <div class=\"row\">\r\n      <div class=\"col-12\">\r\n        <a href=\"{{homeUrl}}\">\r\n          <img src=\"{{logoImageDesktop}}\" class=\"logo-image d-none d-md-block\" alt=\"{{logoTitle}}\" />\r\n          <img src=\"{{logoImageMobile}}\" class=\"logo-image logo-mobile-only d-block d-md-none\" alt=\"{{logoTitle}}\" />\r\n        </a>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</header>\r\n<!-- header component mark up starts here-->\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.header{min-height:80px;background:var(--primaryBrand)}.header a{display:inline-block}.header .logo-image{width:185px;height:100%;padding-top:10px;padding-bottom:5px}.header.opco-ComEd .logo-image{width:250px}.header.opco-ComEd .logo-image.logo-mobile-only{width:119px;padding:24px 0 20px}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: HeaderSimpleComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-header-simple', template: "<!-- hreader component mark up starts here-->\r\n<header class=\"header opco-{{opCo}} bg-{{opCo}}-primaryBrand\">\r\n  <div class=\"container\">\r\n    <div class=\"row\">\r\n      <div class=\"col-12\">\r\n        <a href=\"{{homeUrl}}\">\r\n          <img src=\"{{logoImageDesktop}}\" class=\"logo-image d-none d-md-block\" alt=\"{{logoTitle}}\" />\r\n          <img src=\"{{logoImageMobile}}\" class=\"logo-image logo-mobile-only d-block d-md-none\" alt=\"{{logoTitle}}\" />\r\n        </a>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</header>\r\n<!-- header component mark up starts here-->\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.header{min-height:80px;background:var(--primaryBrand)}.header a{display:inline-block}.header .logo-image{width:185px;height:100%;padding-top:10px;padding-bottom:5px}.header.opco-ComEd .logo-image{width:250px}.header.opco-ComEd .logo-image.logo-mobile-only{width:119px;padding:24px 0 20px}\n"] }]
        }], ctorParameters: function () { return [{ type: AppConfigService }]; } });

class HeaderSimpleModule {
}
HeaderSimpleModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: HeaderSimpleModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
HeaderSimpleModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: HeaderSimpleModule, declarations: [HeaderSimpleComponent], imports: [CommonModule,
        FormsModule], exports: [HeaderSimpleComponent] });
HeaderSimpleModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: HeaderSimpleModule, imports: [CommonModule,
        FormsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: HeaderSimpleModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule
                    ],
                    providers: [],
                    declarations: [HeaderSimpleComponent],
                    exports: [HeaderSimpleComponent]
                }]
        }] });

class FooterSimpleComponent {
    constructor(appConfigService) {
        this.appConfigService = appConfigService;
        this.subscriptions = new Subscription();
    }
    ngOnInit() {
        this.subscriptions.add(this.appConfigService.config$.pipe(filter(config => config.opCo != null)).subscribe(config => {
            this.opCo = this.appConfigService.config.opCo;
            const opCoConfig = opcoStrings[this.opCo.toLowerCase()];
            if (opCoConfig) {
                this.footerMessage = opCoConfig.footerMessage;
                this.termsOfUseLink = opCoConfig.termsOfUseLink;
                this.privacyLink = opCoConfig.privacyLink;
                this.termsOfUse = opCoConfig.termsOfUse;
                this.privacy = opCoConfig.privacy;
            }
        }));
    }
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }
}
FooterSimpleComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FooterSimpleComponent, deps: [{ token: AppConfigService }], target: i0.ɵɵFactoryTarget.Component });
FooterSimpleComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: FooterSimpleComponent, selector: "app-footer-simple", ngImport: i0, template: "<!-- footer component mark up starts here-->\r\n<footer class=\"page-footer opco-{{opCo}} bg-{{opCo}}-primaryBrand\">\r\n  <div class=\"container\">\r\n    <div class=\"row\">\r\n      <div class=\"col-lg-12\">\r\n        <span>{{ footerMessage }}</span>\r\n        <a [href]=\"termsOfUseLink\" target=\"_self\" class=\"footer-link\">{{ termsOfUse }}</a>\r\n        <span aria-hidden=\"true\">|</span>\r\n        <a [href]=\"privacyLink\" target=\"_self\" class=\"footer-link\">{{ privacy }}</a>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</footer>\r\n<!-- footer component mark up ends here -->\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.page-footer{bottom:0;background:var(--primaryBrand);height:78px;display:flex;align-items:center}.page-footer .container{height:78px;font-size:14px;padding-top:30px!important;padding-bottom:29px!important;line-height:19px}@media (max-width: 767px){.page-footer .container{height:78px;padding:21px 10px 19px!important}}.page-footer span{color:#fff;font-size:14px;line-height:16px}.page-footer .footer-link{color:#fff!important;padding-left:10px;padding-right:10px;font-size:14px;font-weight:700;cursor:pointer}.page-footer .footer-link:hover{text-decoration:underline;text-decoration-color:#fff!important}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FooterSimpleComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-footer-simple', template: "<!-- footer component mark up starts here-->\r\n<footer class=\"page-footer opco-{{opCo}} bg-{{opCo}}-primaryBrand\">\r\n  <div class=\"container\">\r\n    <div class=\"row\">\r\n      <div class=\"col-lg-12\">\r\n        <span>{{ footerMessage }}</span>\r\n        <a [href]=\"termsOfUseLink\" target=\"_self\" class=\"footer-link\">{{ termsOfUse }}</a>\r\n        <span aria-hidden=\"true\">|</span>\r\n        <a [href]=\"privacyLink\" target=\"_self\" class=\"footer-link\">{{ privacy }}</a>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</footer>\r\n<!-- footer component mark up ends here -->\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.page-footer{bottom:0;background:var(--primaryBrand);height:78px;display:flex;align-items:center}.page-footer .container{height:78px;font-size:14px;padding-top:30px!important;padding-bottom:29px!important;line-height:19px}@media (max-width: 767px){.page-footer .container{height:78px;padding:21px 10px 19px!important}}.page-footer span{color:#fff;font-size:14px;line-height:16px}.page-footer .footer-link{color:#fff!important;padding-left:10px;padding-right:10px;font-size:14px;font-weight:700;cursor:pointer}.page-footer .footer-link:hover{text-decoration:underline;text-decoration-color:#fff!important}\n"] }]
        }], ctorParameters: function () { return [{ type: AppConfigService }]; } });

class FooterSimpleModule {
}
FooterSimpleModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FooterSimpleModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FooterSimpleModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: FooterSimpleModule, declarations: [FooterSimpleComponent], imports: [CommonModule], exports: [FooterSimpleComponent] });
FooterSimpleModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FooterSimpleModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FooterSimpleModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [FooterSimpleComponent],
                    exports: [FooterSimpleComponent]
                }]
        }] });

class SafePipe {
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
    }
    transform(url) {
        return this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }
}
SafePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SafePipe, deps: [{ token: i1$1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe });
SafePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: SafePipe, name: "safe" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SafePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'safe' }]
        }], ctorParameters: function () { return [{ type: i1$1.DomSanitizer }]; } });

class SafeModule {
}
SafeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SafeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SafeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: SafeModule, declarations: [SafePipe], exports: [SafePipe] });
SafeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SafeModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SafeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [],
                    declarations: [
                        SafePipe
                    ],
                    exports: [SafePipe]
                }]
        }] });

class FooterMiddleComponent {
    constructor(footerService) {
        this.footerService = footerService;
    }
    ngOnInit() {
        this.footerService.bottomNavigation$
            .pipe(filter(value => !!(isDefined(value) && Object.keys(value).length)))
            .subscribe(() => {
            this.buildFooterList();
        });
    }
    buildFooterList() {
        this.footerList = [];
        Object.keys(this.footerService.bottomNavEntries).forEach(key => {
            const section = { caption: this.footerService.bottomNavEntries[key].title, links: [] };
            this.footerService.bottomNavEntries[key].topicEntries.forEach(entry => {
                section.links.push(entry);
            });
            this.footerList.push(section);
        });
    }
}
FooterMiddleComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FooterMiddleComponent, deps: [{ token: FooterService }], target: i0.ɵɵFactoryTarget.Component });
FooterMiddleComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: FooterMiddleComponent, selector: "app-footer-middle", ngImport: i0, template: "<footer role=\"contentinfo\" class=\"exc-footer-lists\">\r\n  <div class=\"container\">\r\n    <nav role=\"navigation\" class=\"row\">\r\n      <div class=\"col-md-2\" *ngFor=\"let footerList of footerList\">\r\n        <ul>\r\n          <li aria-hidden=\"true\">{{ footerList.caption }}</li>\r\n          <li *ngFor=\"let link of footerList.links\">\r\n            <a [href]=\"link.url | safe\">{{ link.title }}</a>\r\n          </li>\r\n        </ul>\r\n      </div>\r\n      <div class=\"col-md-4\"></div>\r\n    </nav>\r\n  </div>\r\n</footer>\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.exc-footer-lists{display:none;background-color:#d8d8d8;color:#231f20;padding:26px 0}.exc-footer-lists ul{margin:0;padding:0}.exc-footer-lists ul li{list-style:none;margin-bottom:6px;font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#4a4a4a}.exc-footer-lists ul li:first-child{font-weight:700}.exc-footer-lists ul li a{color:#4a4a4a;font-weight:400}.container{max-width:1200px}@media (min-width: 992px){.exc-footer-lists{display:block}}\n"], dependencies: [{ kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "pipe", type: SafePipe, name: "safe" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FooterMiddleComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-footer-middle', template: "<footer role=\"contentinfo\" class=\"exc-footer-lists\">\r\n  <div class=\"container\">\r\n    <nav role=\"navigation\" class=\"row\">\r\n      <div class=\"col-md-2\" *ngFor=\"let footerList of footerList\">\r\n        <ul>\r\n          <li aria-hidden=\"true\">{{ footerList.caption }}</li>\r\n          <li *ngFor=\"let link of footerList.links\">\r\n            <a [href]=\"link.url | safe\">{{ link.title }}</a>\r\n          </li>\r\n        </ul>\r\n      </div>\r\n      <div class=\"col-md-4\"></div>\r\n    </nav>\r\n  </div>\r\n</footer>\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.exc-footer-lists{display:none;background-color:#d8d8d8;color:#231f20;padding:26px 0}.exc-footer-lists ul{margin:0;padding:0}.exc-footer-lists ul li{list-style:none;margin-bottom:6px;font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#4a4a4a}.exc-footer-lists ul li:first-child{font-weight:700}.exc-footer-lists ul li a{color:#4a4a4a;font-weight:400}.container{max-width:1200px}@media (min-width: 992px){.exc-footer-lists{display:block}}\n"] }]
        }], ctorParameters: function () { return [{ type: FooterService }]; } });

class FooterNavComponent {
    constructor(footerService, appConfigService) {
        this.footerService = footerService;
        this.appConfigService = appConfigService;
    }
    get hasFooterNavs() {
        return this.footerNavs && this.footerNavs.length;
    }
    get hasFooterSocialMedias() {
        return this.footerSocialMedias && this.footerSocialMedias.length;
    }
    ngOnInit() {
        this.footerService.getNavigation().subscribe(data => {
            data = (data || []).map(attributes => {
                if (attributes.hasOwnProperty('exc_Access') &&
                    attributes.exc_Access === 'Secure') {
                    attributes.exc_Url =
                        this.appConfigService.config.baseUrl + attributes.exc_Url;
                }
                else if (/^\//.test(attributes.exc_Url)) {
                    attributes.exc_Url =
                        this.appConfigService.config.anonBaseUrl + attributes.exc_Url;
                }
                return attributes;
            });
            this.footerNavs = data;
        });
        this.footerSocialMedias = this.footerService.getFooterSocialMedias();
    }
}
FooterNavComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FooterNavComponent, deps: [{ token: FooterService }, { token: AppConfigService }], target: i0.ɵɵFactoryTarget.Component });
FooterNavComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: FooterNavComponent, selector: "app-footer-nav", ngImport: i0, template: "<div class=\"exc-footer-nav\">\r\n  <div class=\"container\">\r\n    <div class=\"row\">\r\n      <nav role=\"navigation\" class=\"col-sm-12 col-md-12 col-lg-8\">\r\n        <ul *ngIf=\"hasFooterNavs\">\r\n          <li *ngFor=\"let footerNav of footerNavs\">\r\n            <a\r\n              [href]=\"footerNav.exc_Url | safe\"\r\n              target=\"_self\"\r\n              [title]=\"footerNav.name\"\r\n            >\r\n              {{ footerNav.name }}\r\n            </a>\r\n          </li>\r\n        </ul>\r\n      </nav>\r\n      <div class=\"exc-social-links col-sm-12 col-md-12 col-lg-4\">\r\n        <ul *ngIf=\"hasFooterSocialMedias\">\r\n          <li *ngFor=\"let footerSocialMedia of footerSocialMedias\">\r\n            <a\r\n              [href]=\"footerSocialMedia.url\"\r\n              target=\"_new\"\r\n              [title]=\"footerSocialMedia.title\"\r\n              [attr.data-social-icon]=\"footerSocialMedia.dataSocialIcon\"\r\n            >\r\n              <i class=\"{{ footerSocialMedia.iconClass }}\"></i>\r\n            </a>\r\n          </li>\r\n        </ul>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</div>\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}[class^=exc-icon-]{font-family:exc-icon;speak:none;font-style:normal;font-weight:400;font-feature-settings:normal;font-variant:normal;text-transform:none;line-height:1;-webkit-font-smoothing:antialiased}.exc-icon-social-facebook:before{content:\"\\f09a\"}.exc-icon-social-twitter:before{content:\"\\f099\"}.exc-icon-social-youtube:before{content:\"\\e91e\"}.exc-icon-social-linkedin:before{content:\"\\f0e1\"}.exc-icon-social-instagram:before{content:\"\\e915\"}.exc-icon-social-pinterest:before{content:\"\\f231\"}.exc-icon-social-flickr:before{content:\"\\e912\"}.exc-icon-social-nextdoor{position:relative}.exc-icon-social-nextdoor:before{background-image:url(../../../assets/images/icon-nextdoor.png);background-position:center;background-size:23px 24px;width:24px;height:23px;content:\"\";position:absolute}.exc-footer-nav{background-color:#eee;color:#737373}.exc-footer-nav nav ul{margin:0;padding:0}.exc-footer-nav nav ul li{display:inline-block;list-style:none}.exc-footer-nav nav ul li a,.exc-footer-nav nav ul li a:visited,.exc-footer-nav nav ul li a:active,.exc-footer-nav nav ul li a:link{color:#737373;font-family:Open Sans Regular;font-weight:400}.exc-footer-nav .exc-social-links{border-top:1px solid #909090;padding:6px 0}.exc-footer-nav .exc-social-links ul{display:inline-block;margin-bottom:-4px}.exc-footer-nav .exc-social-links ul li{float:left;list-style:none;line-height:1}.exc-footer-nav .exc-social-links ul li a{display:inline-block;color:#737373;font-size:20px;padding:14px}.exc-footer-nav .exc-social-links ul li a:hover{text-decoration:none}.container{max-width:1200px}@media (min-width: 992px){.exc-footer-nav{border-top:30px solid #fff}.exc-footer-nav nav{padding-top:29px;padding-bottom:29px;font-size:14px;color:#737373}.exc-footer-nav nav ul li:first-child a{padding-left:0;border-left-width:0}.exc-footer-nav nav ul li a{border-left:solid #999999 1px;padding:0 12px 0 10px;line-height:1}.exc-footer-nav .exc-social-links{border-top-width:0;padding-top:15px;padding-bottom:15px;text-align:left;margin:0}.exc-footer-nav .exc-social-links ul{float:right}}@media (max-width: 991.98px){.exc-footer-nav{border-top:12px solid #fff}.exc-footer-nav nav{padding-top:12px;padding-bottom:12px}.exc-footer-nav nav ul li a{padding:0 20px 0 0;line-height:30px;color:#737373}.exc-footer-nav .exc-social-links{border-top:1px solid #909090;text-align:center;padding:6px 0}}\n"], dependencies: [{ kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SafePipe, name: "safe" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FooterNavComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-footer-nav', template: "<div class=\"exc-footer-nav\">\r\n  <div class=\"container\">\r\n    <div class=\"row\">\r\n      <nav role=\"navigation\" class=\"col-sm-12 col-md-12 col-lg-8\">\r\n        <ul *ngIf=\"hasFooterNavs\">\r\n          <li *ngFor=\"let footerNav of footerNavs\">\r\n            <a\r\n              [href]=\"footerNav.exc_Url | safe\"\r\n              target=\"_self\"\r\n              [title]=\"footerNav.name\"\r\n            >\r\n              {{ footerNav.name }}\r\n            </a>\r\n          </li>\r\n        </ul>\r\n      </nav>\r\n      <div class=\"exc-social-links col-sm-12 col-md-12 col-lg-4\">\r\n        <ul *ngIf=\"hasFooterSocialMedias\">\r\n          <li *ngFor=\"let footerSocialMedia of footerSocialMedias\">\r\n            <a\r\n              [href]=\"footerSocialMedia.url\"\r\n              target=\"_new\"\r\n              [title]=\"footerSocialMedia.title\"\r\n              [attr.data-social-icon]=\"footerSocialMedia.dataSocialIcon\"\r\n            >\r\n              <i class=\"{{ footerSocialMedia.iconClass }}\"></i>\r\n            </a>\r\n          </li>\r\n        </ul>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</div>\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}[class^=exc-icon-]{font-family:exc-icon;speak:none;font-style:normal;font-weight:400;font-feature-settings:normal;font-variant:normal;text-transform:none;line-height:1;-webkit-font-smoothing:antialiased}.exc-icon-social-facebook:before{content:\"\\f09a\"}.exc-icon-social-twitter:before{content:\"\\f099\"}.exc-icon-social-youtube:before{content:\"\\e91e\"}.exc-icon-social-linkedin:before{content:\"\\f0e1\"}.exc-icon-social-instagram:before{content:\"\\e915\"}.exc-icon-social-pinterest:before{content:\"\\f231\"}.exc-icon-social-flickr:before{content:\"\\e912\"}.exc-icon-social-nextdoor{position:relative}.exc-icon-social-nextdoor:before{background-image:url(../../../assets/images/icon-nextdoor.png);background-position:center;background-size:23px 24px;width:24px;height:23px;content:\"\";position:absolute}.exc-footer-nav{background-color:#eee;color:#737373}.exc-footer-nav nav ul{margin:0;padding:0}.exc-footer-nav nav ul li{display:inline-block;list-style:none}.exc-footer-nav nav ul li a,.exc-footer-nav nav ul li a:visited,.exc-footer-nav nav ul li a:active,.exc-footer-nav nav ul li a:link{color:#737373;font-family:Open Sans Regular;font-weight:400}.exc-footer-nav .exc-social-links{border-top:1px solid #909090;padding:6px 0}.exc-footer-nav .exc-social-links ul{display:inline-block;margin-bottom:-4px}.exc-footer-nav .exc-social-links ul li{float:left;list-style:none;line-height:1}.exc-footer-nav .exc-social-links ul li a{display:inline-block;color:#737373;font-size:20px;padding:14px}.exc-footer-nav .exc-social-links ul li a:hover{text-decoration:none}.container{max-width:1200px}@media (min-width: 992px){.exc-footer-nav{border-top:30px solid #fff}.exc-footer-nav nav{padding-top:29px;padding-bottom:29px;font-size:14px;color:#737373}.exc-footer-nav nav ul li:first-child a{padding-left:0;border-left-width:0}.exc-footer-nav nav ul li a{border-left:solid #999999 1px;padding:0 12px 0 10px;line-height:1}.exc-footer-nav .exc-social-links{border-top-width:0;padding-top:15px;padding-bottom:15px;text-align:left;margin:0}.exc-footer-nav .exc-social-links ul{float:right}}@media (max-width: 991.98px){.exc-footer-nav{border-top:12px solid #fff}.exc-footer-nav nav{padding-top:12px;padding-bottom:12px}.exc-footer-nav nav ul li a{padding:0 20px 0 0;line-height:30px;color:#737373}.exc-footer-nav .exc-social-links{border-top:1px solid #909090;text-align:center;padding:6px 0}}\n"] }]
        }], ctorParameters: function () { return [{ type: FooterService }, { type: AppConfigService }]; } });

class FooterComponent {
    constructor(footerService) {
        this.footerService = footerService;
    }
    get isShowingNav() {
        return this.footerService.isShowingNav;
    }
    ngOnInit() {
        this.footerService.getNavigation();
    }
}
FooterComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FooterComponent, deps: [{ token: FooterService }], target: i0.ɵɵFactoryTarget.Component });
FooterComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: FooterComponent, selector: "app-footer", ngImport: i0, template: "<app-footer-nav *ngIf=\"isShowingNav\"></app-footer-nav>\r\n<section class=\"middle-footer--wrapper\">\r\n  <ng-content></ng-content>\r\n</section>\r\n<app-footer-simple></app-footer-simple>\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}footer{color:#fff}.footer-container{padding-top:26px;padding-bottom:26px;font-family:Open Sans Regular;font-weight:400;color:#fff}.container{max-width:100vw}@media (min-width: 992px){.container{max-width:1200px}}\n"], dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: FooterSimpleComponent, selector: "app-footer-simple" }, { kind: "component", type: FooterNavComponent, selector: "app-footer-nav" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FooterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-footer', template: "<app-footer-nav *ngIf=\"isShowingNav\"></app-footer-nav>\r\n<section class=\"middle-footer--wrapper\">\r\n  <ng-content></ng-content>\r\n</section>\r\n<app-footer-simple></app-footer-simple>\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}footer{color:#fff}.footer-container{padding-top:26px;padding-bottom:26px;font-family:Open Sans Regular;font-weight:400;color:#fff}.container{max-width:100vw}@media (min-width: 992px){.container{max-width:1200px}}\n"] }]
        }], ctorParameters: function () { return [{ type: FooterService }]; } });

class FooterModule {
}
FooterModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FooterModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FooterModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: FooterModule, declarations: [FooterComponent, FooterNavComponent, FooterMiddleComponent], imports: [CommonModule, FooterSimpleModule, SafeModule], exports: [FooterComponent, FooterNavComponent, FooterMiddleComponent] });
FooterModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FooterModule, providers: [
        FooterService,
    ], imports: [CommonModule, FooterSimpleModule, SafeModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FooterModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, FooterSimpleModule, SafeModule],
                    declarations: [FooterComponent, FooterNavComponent, FooterMiddleComponent],
                    exports: [FooterComponent, FooterNavComponent, FooterMiddleComponent],
                    providers: [
                        FooterService,
                    ],
                }]
        }] });

class EuAccountsAdapter {
    constructor(appSessionService, euAccountsService) {
        this.appSessionService = appSessionService;
        this.euAccountsService = euAccountsService;
        this.accountsObservable = new BehaviorSubject({});
        this.accountDetailsObservable = new BehaviorSubject({});
        this.subscribeToAppSessionChanges();
    }
    get accounts() {
        return this._accounts;
    }
    set accounts(accounts) {
        this._accounts = Object.freeze(Object.assign({}, accounts));
        this.accountsObservable.next(this._accounts);
    }
    get accountDetails() {
        return this._accountDetails;
    }
    set accountDetails(accountDetails) {
        this._accountDetails = Object.freeze(Object.assign({}, accountDetails));
        this.accountDetailsObservable.next(this._accountDetails);
    }
    onDestroy() {
        if (this.sessionSub) {
            this.sessionSub.unsubscribe();
        }
    }
    subscribeToAppSessionChanges() {
        this.sessionSub = this.appSessionService.session$.subscribe(session => {
            if (this._accountDetails) {
                this.euAccountsService.getAccountDetails(this.appSessionService.session.accountNumber)
                    .subscribe(response => this.processAccountDetails(response));
            }
            if (this._accounts) {
                this.euAccountsService.getAccounts().subscribe(response => this.processAccounts(response));
            }
        });
    }
    processAccounts(result) {
        this.accounts = result;
    }
    processAccountDetails(result) {
        if (typeof result.success === 'string') {
            result.success = result.success.toLowerCase() === 'true';
        }
        this.accountDetails = result;
    }
}

class MCSCachingService {
    constructor(appSessionService, euAccountsService) {
        this.appSessionService = appSessionService;
        this.euAccountsService = euAccountsService;
        this.euAccounts = new EuAccountsAdapter(this.appSessionService, this.euAccountsService);
    }
    ngOnDestroy() {
        this.euAccounts.onDestroy();
    }
}
MCSCachingService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MCSCachingService, deps: [{ token: AppSessionService }, { token: EuAccountsService }], target: i0.ɵɵFactoryTarget.Injectable });
MCSCachingService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MCSCachingService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MCSCachingService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: AppSessionService }, { type: EuAccountsService }]; } });

class CheckBoxComponent extends AbstractValueAccessor {
    constructor() {
        // since CheckBoxComponent extends AbstractValueAccessor, we need to call super()
        super();
    }
    ngOnInit() {
        const option = this.option;
        const idKeyName = (option && option.forLabel === undefined) ? 'value' : 'forLabel';
        option.id = option[idKeyName].replace(/ /g, '-');
        option.forLabel = (option && option.forLabel === undefined) ? option.value : option.forLabel;
    }
}
CheckBoxComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CheckBoxComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CheckBoxComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: CheckBoxComponent, selector: "app-check-box", inputs: { option: "option", disabled: "disabled", innerHTMLCheck: "innerHTMLCheck" }, providers: [MakeProvider(CheckBoxComponent)], usesInheritance: true, ngImport: i0, template: "<div class=\"checkbox-options clearfix\">\r\n  <input\r\n    type=\"checkbox\"\r\n    id=\"option-{{option.id}}\"\r\n    attr.aria-label=\"{{option.forLabel}}\"\r\n    class=\"checkbox-input\"\r\n    [(ngModel)]=\"value\"\r\n    [disabled]=\"disabled\">\r\n  <label for=\"option-{{option.id}}\" class=\"checkbox-label\" *ngIf=\"!innerHTMLCheck; else innerHTMLText\">{{option.value}}</label>\r\n  <ng-template #innerHTMLText><label for=\"option-{{option.id}}\" [innerHTML]=\"option.value\" class=\"checkbox-label\"></label></ng-template>\r\n</div>", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.checkbox-options input{margin-top:3px;float:left}.checkbox-options .checkbox-label{color:#4a4a4a;cursor:pointer;padding-left:30px;display:block}\n"], dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.CheckboxControlValueAccessor, selector: "input[type=checkbox][formControlName],input[type=checkbox][formControl],input[type=checkbox][ngModel]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CheckBoxComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-check-box', providers: [MakeProvider(CheckBoxComponent)], template: "<div class=\"checkbox-options clearfix\">\r\n  <input\r\n    type=\"checkbox\"\r\n    id=\"option-{{option.id}}\"\r\n    attr.aria-label=\"{{option.forLabel}}\"\r\n    class=\"checkbox-input\"\r\n    [(ngModel)]=\"value\"\r\n    [disabled]=\"disabled\">\r\n  <label for=\"option-{{option.id}}\" class=\"checkbox-label\" *ngIf=\"!innerHTMLCheck; else innerHTMLText\">{{option.value}}</label>\r\n  <ng-template #innerHTMLText><label for=\"option-{{option.id}}\" [innerHTML]=\"option.value\" class=\"checkbox-label\"></label></ng-template>\r\n</div>", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.checkbox-options input{margin-top:3px;float:left}.checkbox-options .checkbox-label{color:#4a4a4a;cursor:pointer;padding-left:30px;display:block}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { option: [{
                type: Input
            }], disabled: [{
                type: Input
            }], innerHTMLCheck: [{
                type: Input
            }] } });

class CheckBoxModule {
}
CheckBoxModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CheckBoxModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CheckBoxModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: CheckBoxModule, declarations: [CheckBoxComponent], imports: [CommonModule,
        FormsModule], exports: [CheckBoxComponent] });
CheckBoxModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CheckBoxModule, imports: [CommonModule,
        FormsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CheckBoxModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule
                    ],
                    declarations: [CheckBoxComponent],
                    exports: [
                        CheckBoxComponent
                    ]
                }]
        }] });

class InputComponent extends AbstractValueAccessor {
    constructor() {
        // since InputComponent extends AbstractValueAccessor, we need to call super()
        super();
        // object which holds validation error messages for this component
        this.customValidationMessages = {};
        this.isErrorTriggered = true;
        this.triggerErrorDesc = true;
        this.ngModelOptions = '{ updateOn: "blur" }';
        // outgoing properties to parent component
        this.inputEmitter = new EventEmitter();
        this.blurEmitter = new EventEmitter();
        this.keyupEmitter = new EventEmitter();
        this.focusEmitter = new EventEmitter();
    }
    get value() {
        return super.value;
    }
    set value(_value) {
        super.value = _value;
    }
    /**
    * ngOnInit - function ngOnInit place holder.
    * @param none
    * @return none
    */
    ngOnInit() {
        this.placeholder = ' ';
        this.setValidationMessages();
    }
    /**
    * Form validation messages for this component
    * @method setValidationMessages
    * @param none
    * @return none
    */
    setValidationMessages() {
        const customErrorMsg = this.errorMessage;
        this.customValidationMessages.mandatory = customErrorMsg && customErrorMsg.mandatory;
        this.customValidationMessages.minlength = customErrorMsg && customErrorMsg.minlength;
        this.customValidationMessages.maxlength = customErrorMsg && customErrorMsg.maxlength;
        this.customValidationMessages.pattern = customErrorMsg && customErrorMsg.pattern;
    }
    /**
    * Handles input event of textfield
    * @method textfieldInputHandler
    * @param event
    * @return none
    */
    textfieldInputHandler(event) {
        // trigger an inputEmitter of type Event
        const value = (event.target.value);
        this.inputEmitter.emit({ 'value': value });
    }
    /**
    * Handles blur event of textfield
    * @method textfieldBlurHandler
    * @param event
    * @return none
    */
    textfieldBlurHandler(event) {
        // trigger a blurEmitter of type Event
        const value = (event.target.value);
        this.blurEmitter.emit({ 'value': value });
    }
    /**
     * method to handle the key up event
     * @method textfieldKeyUpHandler
     * @param event
     * @return none
     */
    textfieldKeyUpHandler(event) {
        const value = (event.target.value);
        this.keyupEmitter.emit({ 'value': value });
    }
    /**
     * method to handle the focus event
     * @method textfieldFocusHandler
     * @param event
     * @return none
     */
    textfieldFocusHandler(event) {
        const value = (event.target.value);
        this.focusEmitter.emit({ 'value': value });
    }
    /**
    * Function to set the input field to untouched state
    * @method markAsUntouched
    * @param none
    * @return none
    */
    markAsUntouched() {
        this.inputElementModel.control.markAsUntouched();
    }
    /**
    * Function to set the input field to pristine(not dirty) state
    * @method markAsPristine
    * @param none
    * @return none
    */
    markAsPristine() {
        this.inputElementModel.control.markAsPristine();
    }
    /**
    * Function to set the input field to dirty state
    * @method markAsDirty
    * @param none
    * @return none
    */
    markAsDirty() {
        this.inputElementModel.control.markAsDirty();
    }
    /**
    * Function to set the input field to pristine(not dirty) and untouched state
    * @method markAsPristineAndUntouched
    * @param none
    * @return none
    */
    markAsPristineAndUntouched() {
        this.inputElementModel.control.markAsPristine();
        this.inputElementModel.control.markAsUntouched();
    }
}
InputComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: InputComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
InputComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: InputComponent, selector: "app-input-common", inputs: { value: "value", type: "type", label: "label", required: "required", disabled: "disabled", description: "description", minlength: "minlength", maxlength: "maxlength", errorMessage: "errorMessage", className: "className", placeholder: "placeholder", control: "control", pattern: "pattern", triggerErrorDesc: "triggerErrorDesc", ngModelOptions: "ngModelOptions" }, outputs: { inputEmitter: "inputEmitter", blurEmitter: "blurEmitter", keyupEmitter: "keyupEmitter", focusEmitter: "focusEmitter" }, providers: [MakeProvider(InputComponent)], viewQueries: [{ propertyName: "inputElementModel", first: true, predicate: ["inputElementModel"], descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<!-- START: input markup -->\r\n<label class=\"has-float-label\">\r\n  <input #inputElement #inputElementModel=\"ngModel\" [type]=\"type\" [placeholder]=\"placeholder\" \r\n  [disabled]=\"disabled\" [(ngModel)]=\"value\" attr.aria-label={{label}} aria-invalid=false autocomplete=\"off\"\r\n  [minlength]=\"minlength\" [maxlength]=\"maxlength\" [required]=\"required\" [pattern]=\"pattern\"\r\n  [ngClass]=\"className\" [class.invalid]=\"triggerErrorDesc && ((inputElementModel.errors && (inputElementModel.dirty || inputElementModel.touched)) || control?.errors?.customError)\"\r\n  (input)=\"textfieldInputHandler($event)\" (blur)=\"textfieldBlurHandler($event)\" (keyup)=\"textfieldKeyUpHandler($event)\"\r\n  (focus)=\"textfieldFocusHandler($event)\"\r\n  [ngModelOptions]=\"ngModelOptions\" \r\n  [class.has-no-text]=\"(!value || value?.length === 0)\"\r\n  aria-required=false />\r\n  <span>{{label}}</span>\r\n<!-- END: Eye Toggle Field -->\r\n</label>\r\n<!-- END: input markup -->\r\n\r\n<!-- START: Optional Field -->\r\n<div class=\"optional-msg\" *ngIf=\"!required\">\r\n  <p class=\"mb-0\">Optional</p>\r\n</div>\r\n<!-- END: Optional Field -->\r\n\r\n<div [ngClass]=\"{'text': !required}\">\r\n  <!-- Begin mandatory error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"triggerErrorDesc && inputElementModel.errors?.required && (inputElementModel.dirty || inputElementModel.touched) && customValidationMessages.mandatory\">\r\n      <p class=\"mb-0\">{{customValidationMessages.mandatory}}</p>\r\n  </div>\r\n  <!-- End mandatory error message -->\r\n\r\n  <!-- Begin min length error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"triggerErrorDesc && inputElementModel.errors?.minlength && (inputElementModel.dirty || inputElementModel.touched) && customValidationMessages.minlength\">\r\n      <p class=\"mb-0\">{{customValidationMessages.minlength}} </p>\r\n  </div>\r\n  <!-- End min length error message -->\r\n\r\n  <!-- Begin max length error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"inputElementModel.errors?.maxlength && (inputElementModel.dirty || inputElementModel.touched) && customValidationMessages.maxlength\">\r\n      <p class=\"mb-0\">{{customValidationMessages.maxlength}} </p>\r\n  </div>\r\n  <!-- End max length error message -->\r\n\r\n  <!-- Begin pattern error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"triggerErrorDesc && inputElementModel.errors?.pattern && (inputElementModel.dirty || inputElementModel.touched) && customValidationMessages.pattern\">\r\n      <p class=\"mb-0\">{{customValidationMessages.pattern}}</p>\r\n  </div>\r\n  <!-- End pattern error message -->\r\n\r\n  <!-- Begin custom error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"form-control-error error-msg\" *ngIf=\"triggerErrorDesc && control?.errors?.customError && (control?.dirty || control?.touched)\">\r\n      <p class=\"mb-0\">{{control.errors.message}}</p>\r\n  </div>\r\n  <!-- End custom error message -->\r\n\r\n  <!-- Helper Text -->\r\n  <p class=\"description mb-0\" *ngIf=\"description\">{{description}}\r\n</div>\r\n\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.has-float-label{margin:0;width:100%;display:block;position:relative;box-sizing:border-box}.has-float-label span{position:absolute;left:-1px;padding:0 5px 0 0;top:-9.6px;cursor:text;font-size:12px;font-family:Open Sans Regular;font-weight:600;line-height:17px;opacity:1;color:#737373;background-color:#fff;max-width:70%;text-overflow:ellipsis;overflow:hidden;white-space:nowrap;transition:all .2s}.has-float-label input{padding:18px 0 15px 16px;color:#4a4a4a;font-family:Open Sans Regular;font-size:16px;line-height:22px;width:100%;border:1px solid #d8d8d8;background-color:#fff;font-weight:400;border-radius:2px;margin-bottom:0;box-shadow:inset 0 1px 2px #0000001a;box-shadow:inset 0 0 3px #0000001a}.has-float-label input[type=number]{-moz-appearance:textfield}.has-float-label input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none;appearance:none;margin:0}.has-float-label input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;appearance:none;margin:0}.has-float-label input::-ms-clear,.has-float-label input::-ms-reveal{display:none}.has-float-label input.rounded-right-0{border-top-right-radius:0;border-bottom-right-radius:0}.has-float-label input:focus{outline:none;color:#4a4a4a;font-size:16px;font-weight:400;background-color:#fff;padding-left:16px;border:1px solid #0091b6;box-shadow:inset 0 0 2px #0059a4;box-shadow:inset 0 0 3px #0059a4;box-sizing:border-box;opacity:1}.has-float-label input:not(:focus).has-no-text+span{top:0;left:0;font-size:16px;line-height:22px;font-family:Open Sans Regular;color:#737373;padding:19px 0 16px 16px;font-weight:400;opacity:1;background:transparent;transition:all .2s}.has-float-label input.invalid{border-width:1px 1px 1px 4px;border-style:solid;border-color:#bf002f;box-shadow:inset 0 1px 2px #0000001a;box-shadow:inset 0 0 2px #bf002f}.has-float-label input[disabled]{background-color:#f7f7f7;color:#000}.error-msg{margin-top:6px;color:#bf002f;font-family:Open Sans Semi Bold;font-size:12px;font-weight:600;line-height:18px}.description{margin-top:4px;overflow-wrap:break-word;font-weight:600;font-size:12px;line-height:18px;letter-spacing:.3px;color:#737373}.text{padding-left:65px}.optional-msg{margin-top:4px;overflow-wrap:break-word;font-weight:600;font-size:12px;line-height:18px;letter-spacing:.3px;color:#737373;font-style:italic;vertical-align:top;float:left}\n"], dependencies: [{ kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i2.MinLengthValidator, selector: "[minlength][formControlName],[minlength][formControl],[minlength][ngModel]", inputs: ["minlength"] }, { kind: "directive", type: i2.MaxLengthValidator, selector: "[maxlength][formControlName],[maxlength][formControl],[maxlength][ngModel]", inputs: ["maxlength"] }, { kind: "directive", type: i2.PatternValidator, selector: "[pattern][formControlName],[pattern][formControl],[pattern][ngModel]", inputs: ["pattern"] }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: InputComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-input-common', providers: [MakeProvider(InputComponent)], template: "<!-- START: input markup -->\r\n<label class=\"has-float-label\">\r\n  <input #inputElement #inputElementModel=\"ngModel\" [type]=\"type\" [placeholder]=\"placeholder\" \r\n  [disabled]=\"disabled\" [(ngModel)]=\"value\" attr.aria-label={{label}} aria-invalid=false autocomplete=\"off\"\r\n  [minlength]=\"minlength\" [maxlength]=\"maxlength\" [required]=\"required\" [pattern]=\"pattern\"\r\n  [ngClass]=\"className\" [class.invalid]=\"triggerErrorDesc && ((inputElementModel.errors && (inputElementModel.dirty || inputElementModel.touched)) || control?.errors?.customError)\"\r\n  (input)=\"textfieldInputHandler($event)\" (blur)=\"textfieldBlurHandler($event)\" (keyup)=\"textfieldKeyUpHandler($event)\"\r\n  (focus)=\"textfieldFocusHandler($event)\"\r\n  [ngModelOptions]=\"ngModelOptions\" \r\n  [class.has-no-text]=\"(!value || value?.length === 0)\"\r\n  aria-required=false />\r\n  <span>{{label}}</span>\r\n<!-- END: Eye Toggle Field -->\r\n</label>\r\n<!-- END: input markup -->\r\n\r\n<!-- START: Optional Field -->\r\n<div class=\"optional-msg\" *ngIf=\"!required\">\r\n  <p class=\"mb-0\">Optional</p>\r\n</div>\r\n<!-- END: Optional Field -->\r\n\r\n<div [ngClass]=\"{'text': !required}\">\r\n  <!-- Begin mandatory error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"triggerErrorDesc && inputElementModel.errors?.required && (inputElementModel.dirty || inputElementModel.touched) && customValidationMessages.mandatory\">\r\n      <p class=\"mb-0\">{{customValidationMessages.mandatory}}</p>\r\n  </div>\r\n  <!-- End mandatory error message -->\r\n\r\n  <!-- Begin min length error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"triggerErrorDesc && inputElementModel.errors?.minlength && (inputElementModel.dirty || inputElementModel.touched) && customValidationMessages.minlength\">\r\n      <p class=\"mb-0\">{{customValidationMessages.minlength}} </p>\r\n  </div>\r\n  <!-- End min length error message -->\r\n\r\n  <!-- Begin max length error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"inputElementModel.errors?.maxlength && (inputElementModel.dirty || inputElementModel.touched) && customValidationMessages.maxlength\">\r\n      <p class=\"mb-0\">{{customValidationMessages.maxlength}} </p>\r\n  </div>\r\n  <!-- End max length error message -->\r\n\r\n  <!-- Begin pattern error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"triggerErrorDesc && inputElementModel.errors?.pattern && (inputElementModel.dirty || inputElementModel.touched) && customValidationMessages.pattern\">\r\n      <p class=\"mb-0\">{{customValidationMessages.pattern}}</p>\r\n  </div>\r\n  <!-- End pattern error message -->\r\n\r\n  <!-- Begin custom error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"form-control-error error-msg\" *ngIf=\"triggerErrorDesc && control?.errors?.customError && (control?.dirty || control?.touched)\">\r\n      <p class=\"mb-0\">{{control.errors.message}}</p>\r\n  </div>\r\n  <!-- End custom error message -->\r\n\r\n  <!-- Helper Text -->\r\n  <p class=\"description mb-0\" *ngIf=\"description\">{{description}}\r\n</div>\r\n\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.has-float-label{margin:0;width:100%;display:block;position:relative;box-sizing:border-box}.has-float-label span{position:absolute;left:-1px;padding:0 5px 0 0;top:-9.6px;cursor:text;font-size:12px;font-family:Open Sans Regular;font-weight:600;line-height:17px;opacity:1;color:#737373;background-color:#fff;max-width:70%;text-overflow:ellipsis;overflow:hidden;white-space:nowrap;transition:all .2s}.has-float-label input{padding:18px 0 15px 16px;color:#4a4a4a;font-family:Open Sans Regular;font-size:16px;line-height:22px;width:100%;border:1px solid #d8d8d8;background-color:#fff;font-weight:400;border-radius:2px;margin-bottom:0;box-shadow:inset 0 1px 2px #0000001a;box-shadow:inset 0 0 3px #0000001a}.has-float-label input[type=number]{-moz-appearance:textfield}.has-float-label input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none;appearance:none;margin:0}.has-float-label input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;appearance:none;margin:0}.has-float-label input::-ms-clear,.has-float-label input::-ms-reveal{display:none}.has-float-label input.rounded-right-0{border-top-right-radius:0;border-bottom-right-radius:0}.has-float-label input:focus{outline:none;color:#4a4a4a;font-size:16px;font-weight:400;background-color:#fff;padding-left:16px;border:1px solid #0091b6;box-shadow:inset 0 0 2px #0059a4;box-shadow:inset 0 0 3px #0059a4;box-sizing:border-box;opacity:1}.has-float-label input:not(:focus).has-no-text+span{top:0;left:0;font-size:16px;line-height:22px;font-family:Open Sans Regular;color:#737373;padding:19px 0 16px 16px;font-weight:400;opacity:1;background:transparent;transition:all .2s}.has-float-label input.invalid{border-width:1px 1px 1px 4px;border-style:solid;border-color:#bf002f;box-shadow:inset 0 1px 2px #0000001a;box-shadow:inset 0 0 2px #bf002f}.has-float-label input[disabled]{background-color:#f7f7f7;color:#000}.error-msg{margin-top:6px;color:#bf002f;font-family:Open Sans Semi Bold;font-size:12px;font-weight:600;line-height:18px}.description{margin-top:4px;overflow-wrap:break-word;font-weight:600;font-size:12px;line-height:18px;letter-spacing:.3px;color:#737373}.text{padding-left:65px}.optional-msg{margin-top:4px;overflow-wrap:break-word;font-weight:600;font-size:12px;line-height:18px;letter-spacing:.3px;color:#737373;font-style:italic;vertical-align:top;float:left}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { value: [{
                type: Input
            }], type: [{
                type: Input
            }], label: [{
                type: Input
            }], required: [{
                type: Input
            }], disabled: [{
                type: Input
            }], description: [{
                type: Input
            }], minlength: [{
                type: Input
            }], maxlength: [{
                type: Input
            }], errorMessage: [{
                type: Input
            }], className: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], control: [{
                type: Input
            }], pattern: [{
                type: Input
            }], triggerErrorDesc: [{
                type: Input
            }], ngModelOptions: [{
                type: Input
            }], inputEmitter: [{
                type: Output
            }], blurEmitter: [{
                type: Output
            }], keyupEmitter: [{
                type: Output
            }], focusEmitter: [{
                type: Output
            }], inputElementModel: [{
                type: ViewChild,
                args: ['inputElementModel', { static: true }]
            }] } });

class InputModule {
}
InputModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: InputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
InputModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: InputModule, declarations: [InputComponent], imports: [CommonModule,
        FormsModule], exports: [InputComponent] });
InputModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: InputModule, imports: [CommonModule,
        FormsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: InputModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule
                    ],
                    declarations: [InputComponent],
                    exports: [
                        InputComponent
                    ]
                }]
        }] });

class InputMaskingComponent extends InputComponent {
    constructor() {
        super();
        this.copyMaskToggle = 'Toggle Mask';
        // outgoing properties to parent component
        this.inputEmitter = new EventEmitter();
        this.maskEmitter = new EventEmitter();
        this.formatEmitter = new EventEmitter();
        // The formatEmitter subscription is fired when the toggleMasking function is fired
        // The value is masked/unmasked post which based on the emmitted value formatting is applied
        this.formatEmitter.subscribe(value => {
            if (this.value) {
                this.maskUnmaskValue();
                if (value.isFormatRequired) {
                    this.autoFormatInput();
                }
                this.maskEmitter.emit({
                    isMasked: this.maskInput,
                    unmaskedValue: this.unmaskedValue
                });
            }
        });
    }
    /**
    * ngOnInit - function ngOnInit place holder.
    * @param none
    * @return none
    */
    ngOnInit() {
        this.placeholder = ' ';
        this.unmaskedValue = '';
        this.maskedPatternInvalid = false;
        this.maskInput = this.maskInput ? this.maskInput : false;
        this.whiteListedPattern = this.whiteListedPattern ? this.whiteListedPattern : [];
        this.maskPattern = this.maskPattern ? this.maskPattern : '*';
        this.validPattern = this.pattern ? this.pattern : /^[0-9\-]*$/;
        this.formatInputArr = this.formatInputArr ? this.formatInputArr : [3, 2, 4];
        this.isFormatRequired = typeof (this.isFormatRequired) === 'string'
            ? (this.isFormatRequired === 'true')
            : (this.isFormatRequired || true);
        this.formatEmitter.emit({ 'isFormatRequired': this.isFormatRequired });
        this.setErrorMessages();
    }
    /**
    * Form validation messages for this component
    * @method setErrorMessages
    * @param none
    * @return none
    */
    setErrorMessages() {
        const customErrorMsg = this.errorMessage;
        this.customValidationMessages.mandatory = customErrorMsg && customErrorMsg.mandatory;
        this.customValidationMessages.minlength = customErrorMsg && customErrorMsg.minlength;
        this.customValidationMessages.maxlength = customErrorMsg && customErrorMsg.maxlength;
        this.customValidationMessages.pattern = customErrorMsg && customErrorMsg.pattern;
    }
    /**
    * Handles input event of textfield
    * @method textfieldInputHandler
    * @param event
    * @return none
    */
    textfieldInputHandler(event) {
        // trigger an inputEmitter of type Event
        const value = (event.target.value);
        // add or remove character from the end when the input is masked
        if (this.maskInput) {
            this.modifyMaskedInput(event);
        }
        else {
            this.maskedPatternInvalid = false;
            this.unmaskedValue = this.value;
            if (!this.value) {
                this.maskEmitter.emit({ 'isMasked': this.maskInput, 'unmaskedValue': this.unmaskedValue });
            }
            else {
                this.formatEmitter.emit({ 'isFormatRequired': this.isFormatRequired });
            }
        }
        this.inputEmitter.emit({ 'value': value });
    }
    /**
    * method to handle the key up event
    * @method textfieldKeyDownHandler
    * @param event
    * @return none
    */
    textfieldKeyDownHandler(event) {
        this.isAfterTabEvent = (event.key && event.key.toLowerCase() === 'tab')
            || (event.code && event.code.toLowerCase() === 'tab')
            // tslint:disable-next-line: deprecation
            || event.keyCode === 9;
        if (this.isAfterTabEvent) {
            this.tabEvent = event;
            this.currentEl = document.activeElement;
        }
        if (this.maskInput) {
            this.restrictCursorToEnd(event);
        }
    }
    onAfterTabEvent() {
        const event = this.tabEvent;
        this.tabEvent = undefined;
        this.currentEl.focus();
        if (event.shiftKey) {
            this.focusPreviousElement();
        }
        else {
            this.focusNextElement();
        }
        event.preventDefault();
        this.isAfterTabEvent = false;
    }
    /** helper method to focus next element from current */
    focusNextElement() {
        const focusable = this.focusableElements();
        const index = focusable.indexOf(document.activeElement);
        const nextElement = index > -1 && index < focusable.length - 1 ? focusable[index + 1] : focusable[0];
        nextElement.focus();
    }
    /** helper method to focus previous element from current */
    focusPreviousElement() {
        const focusable = this.focusableElements();
        const index = focusable.indexOf(document.activeElement);
        const previousElement = index > 0 ? focusable[index - 1] : focusable[focusable.length - 1];
        previousElement.focus();
    }
    focusableElements() {
        const focusableElements = 'a:not([disabled]), button:not([disabled]), input:not([disabled]), [tabindex]:not([disabled]):not([tabindex="-1"])';
        return Array.prototype.filter.call(document.querySelectorAll(focusableElements), function (element) {
            // check for visibility while always include the current activeElement
            return element.offsetWidth > 0 || element.offsetHeight > 0 || element === document.activeElement;
        });
    }
    /**
    * method to handle the mouse down event
    * @method mouseDownHandler
    * @param event
    * @return none
    */
    mouseDownHandler(event) {
        if (this.maskInput) {
            this.restrictCursorToEnd(event);
        }
    }
    /**
    * Function to restrict the cursor to the end when the input is masked
    *  note: setTimeout is used to ensure the tab has completed first
    *
    * @method restrictCursorToEnd
    * @param event
    * @return none
    */
    restrictCursorToEnd(event) {
        const currentElement = event.target;
        setTimeout(() => {
            currentElement.setSelectionRange(currentElement.value.length, currentElement.value.length);
            if (this.isAfterTabEvent) {
                this.onAfterTabEvent();
            }
        }, 1);
    }
    /**
    * method to edit masked input
    * @method modifyMaskedInput
    * @param event
    * @return none
    */
    modifyMaskedInput(event) {
        if (this.maskInput) {
            this.validPattern = this.pattern || /^[0-9\-]*$/;
            const currentElement = event.target;
            const orValLen = this.unmaskedValue.length;
            const currValLen = currentElement.value.length;
            if (currValLen < orValLen) {
                // Deletion of the input
                const end = (orValLen - (orValLen - currValLen));
                this.unmaskedValue = this.unmaskedValue.substring(0, end);
                if (this.unmaskedValue) {
                    this.maskedPatternInvalid = !(this.unmaskedValue && this.validPattern.test(this.unmaskedValue));
                }
                else {
                    this.maskedPatternInvalid = false;
                }
                this.maskEmitter.emit({
                    isMasked: this.maskInput,
                    unmaskedValue: this.unmaskedValue
                });
            }
            else if (currValLen > orValLen) {
                // Addition of the input
                this.unmaskedValue = this.unmaskedValue + currentElement.value.substring(orValLen, currValLen);
                this.formatEmitter.emit({ isFormatRequired: this.isFormatRequired });
            }
        }
    }
    /**
    * Function to toggle maskInput state
    * @method toggleMasking
    * @param none
    * @return none
    * @description on click of mask/unmask icon the formaEmitter is called which takes care of the masking and formating
    */
    toggleMasking() {
        this.maskInput = !this.maskInput;
        if (this.value) {
            this.formatEmitter.emit({ 'isFormatRequired': this.isFormatRequired });
        }
    }
    /**
    * Function to mask and unmask the given input string
    * @method maskUnmaskValue
    * @param none
    * @return none
    */
    maskUnmaskValue() {
        if (this.value) {
            // Unmasked Value
            this.unmaskedValue = (!this.unmaskedValue && this.maskInput) ? this.value : this.unmaskedValue;
            // Mask||Unmask the input value
            const pattern = new RegExp(`[^${this.whiteListedPattern.join('')}]`, 'g');
            this.value = this.maskInput
                ? (this.value.replace(pattern, this.maskPattern))
                : this.unmaskedValue;
            this.maskEmitter.emit({
                isMasked: this.maskInput,
                unmaskedValue: this.unmaskedValue
            });
        }
    }
    /**
    * method to add formatting to the masked/unmasked value with a pattern validity check
    * @method autoFormatInput
    * @param none
    * @return none
    */
    autoFormatInput() {
        // Format the masked and unmasked values
        this.value = this.formatNumber(this.value, this.formatInputArr, '-');
        this.unmaskedValue = this.formatNumber(this.unmaskedValue, this.formatInputArr, '-');
        // Check if pattern is valid when input is masked
        this.maskedPatternInvalid = !(this.unmaskedValue && this.validPattern.test(this.unmaskedValue));
    }
    /**
    * method to mask the number (adding hyphens)
    * @method formatSSN
    * @param value
    * @param format
    * @param sepeartor
    * @return maskedNumber
    */
    formatNumber(value, format, sepeartor) {
        value = this.removeFormating(value);
        let maskedNumber = '';
        let idx = 0;
        for (let i = 0; i < format.length && idx < value.length; i++) {
            maskedNumber += value.substr(idx, format[i]);
            if (idx + format[i] < value.length) {
                maskedNumber += sepeartor;
            }
            idx += format[i];
        }
        maskedNumber += value.substr(idx);
        return maskedNumber;
    }
    /**
    * method to unmask the number (removing hyphens)
    * @method removeFormating
    * @param value
    * @return unMaskedNumber
    */
    removeFormating(value) {
        const unMaskedNumber = value.replace(/-/g, '');
        return unMaskedNumber;
    }
}
InputMaskingComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: InputMaskingComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
InputMaskingComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: InputMaskingComponent, selector: "app-input-masking-common", inputs: { maskInput: "maskInput", isFormatRequired: "isFormatRequired", maskPattern: "maskPattern", whiteListedPattern: "whiteListedPattern", formatInputArr: "formatInputArr" }, outputs: { inputEmitter: "inputEmitter", maskEmitter: "maskEmitter", formatEmitter: "formatEmitter" }, providers: [MakeProvider(InputMaskingComponent)], viewQueries: [{ propertyName: "inputElementModel", first: true, predicate: ["inputElementModel"], descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<!-- START: input markup -->\r\n<label class=\"has-float-label\">\r\n  <input #inputElement #inputElementModel=\"ngModel\" [type]=\"type\" [placeholder]=\"placeholder\"\r\n  [disabled]=\"disabled\" [(ngModel)]=\"value\" [attr.maskInput]='maskInput' attr.aria-label={{label}} aria-invalid=false autocomplete=\"off\"\r\n  [minlength]=\"minlength\" [maxlength]=\"maxlength\" [required]=\"required\" [pattern]=\"pattern\" attr.maskPattern={{maskPattern}} [attr.whiteListedPattern] = \"whiteListedPattern\"\r\n  [ngClass]=\"className\" [class.invalid]=\"triggerErrorDesc && (((inputElementModel.errors && (inputElementModel.dirty || inputElementModel.touched)) || control?.errors?.customError) || maskedPatternInvalid)\"\r\n  (input)=\"textfieldInputHandler($event)\" (blur)=\"textfieldBlurHandler($event)\" (keydown)=\"textfieldKeyDownHandler($event)\" (mousedown)=\"mouseDownHandler($event)\" (select)=\"mouseDownHandler($event)\"\r\n  [ngModelOptions]=\"ngModelOptions\"\r\n  [class.has-no-text]=\"(!value || value?.length === 0)\"\r\n  aria-required=false />\r\n  <span>{{label}}</span>\r\n  <button class=\"{{'mask-toggle'}}\" (click)=\"toggleMasking()\" role=\"switch\" [attr.aria-checked]=\"maskInput\" [attr.aria-label]=\"copyMaskToggle\">\r\n    <div class=\"{{maskInput ? 'icon-eye-crossed':'icon-eye'}}\" aria-hidden=\"true\"></div>\r\n  </button>\r\n</label>\r\n<!-- END: input markup -->\r\n<!-- START: Optional Field -->\r\n<div class=\"optional-msg\" *ngIf=\"!required\">\r\n  <p class=\"mb-0\">Optional</p>\r\n</div>\r\n<!-- END: Optional Field -->\r\n\r\n<div [ngClass]=\"{'text': !required}\">\r\n  <!-- Begin mandatory error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"triggerErrorDesc && inputElementModel.errors?.required && (inputElementModel.dirty || inputElementModel.touched) && customValidationMessages.mandatory\">\r\n      <p class=\"mb-0\">{{customValidationMessages.mandatory}}</p>\r\n  </div>\r\n  <!-- End mandatory error message -->\r\n\r\n  <!-- Begin min length error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"triggerErrorDesc && inputElementModel.errors?.minlength && (inputElementModel.dirty || inputElementModel.touched) && customValidationMessages.minlength\">\r\n      <p class=\"mb-0\">{{customValidationMessages.minlength}} </p>\r\n  </div>\r\n  <!-- End min length error message -->\r\n\r\n  <!-- Begin max length error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"inputElementModel.errors?.maxlength && (inputElementModel.dirty || inputElementModel.touched) && customValidationMessages.maxlength\">\r\n      <p class=\"mb-0\">{{customValidationMessages.maxlength}} </p>\r\n  </div>\r\n  <!-- End max length error message -->\r\n\r\n  <!-- Begin pattern error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"triggerErrorDesc && ((inputElementModel.errors?.pattern && (inputElementModel.dirty || inputElementModel.touched) && customValidationMessages.pattern) || maskedPatternInvalid)\">\r\n      <p class=\"mb-0\">{{customValidationMessages.pattern}}</p>\r\n  </div>\r\n  <!-- End pattern error message -->\r\n\r\n  <!-- Begin custom error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"form-control-error error-msg\" *ngIf=\"triggerErrorDesc && control?.errors?.customError && (control?.dirty || control?.touched)\">\r\n      <p class=\"mb-0\">{{control.errors.message}}</p>\r\n  </div>\r\n  <!-- End custom error message -->\r\n\r\n  <!-- Helper Text -->\r\n  <p class=\"description mb-0\" *ngIf=\"description\">{{description}}\r\n</div>\r\n\r\n", styles: [".has-float-label{margin:0;width:100%;display:block;position:relative;box-sizing:border-box}.has-float-label span{position:absolute;left:-1px;padding:0 5px 0 0;top:-9.6px;cursor:text;font-size:12px;font-family:Open Sans Regular;font-weight:600;line-height:17px;opacity:1;color:#737373;background-color:#fff;max-width:70%;text-overflow:ellipsis;overflow:hidden;white-space:nowrap;transition:all .2s}.has-float-label input{padding:18px 0 15px 16px;color:#4a4a4a;font-family:Open Sans Regular;font-size:16px;line-height:22px;width:100%;border:1px solid #d8d8d8;background-color:#fff;font-weight:400;border-radius:2px;margin-bottom:0;box-shadow:inset 0 1px 2px #0000001a;box-shadow:inset 0 0 3px #0000001a}.has-float-label input[type=number]{-moz-appearance:textfield}.has-float-label input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none;appearance:none;margin:0}.has-float-label input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;appearance:none;margin:0}.has-float-label input::-ms-clear,.has-float-label input::-ms-reveal{display:none}.has-float-label input.rounded-right-0{border-top-right-radius:0;border-bottom-right-radius:0}.has-float-label input:focus{outline:none;color:#4a4a4a;font-size:16px;font-weight:400;background-color:#fff;padding-left:16px;border:1px solid #0091b6;box-shadow:inset 0 0 2px #0059a4;box-shadow:inset 0 0 3px #0059a4;box-sizing:border-box;opacity:1}.has-float-label input:not(:focus).has-no-text+span{top:0;left:0;font-size:16px;line-height:22px;font-family:Open Sans Regular;color:#737373;padding:19px 0 16px 16px;font-weight:400;opacity:1;background:transparent;transition:all .2s}.has-float-label input.invalid{border-width:1px 1px 1px 4px;border-style:solid;border-color:#bf002f;box-shadow:inset 0 1px 2px #0000001a;box-shadow:inset 0 0 2px #bf002f}.has-float-label input[disabled]{background-color:#f7f7f7;color:#000}.error-msg{margin-top:6px;color:#bf002f;font-family:Open Sans Semi Bold;font-size:12px;font-weight:600;line-height:18px}.description{margin-top:4px;overflow-wrap:break-word;font-weight:600;font-size:12px;line-height:18px;letter-spacing:.3px;color:#737373}.text{padding-left:65px}.optional-msg{margin-top:4px;overflow-wrap:break-word;font-weight:600;font-size:12px;line-height:18px;letter-spacing:.3px;color:#737373;font-style:italic;vertical-align:top;float:left}:root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.has-float-label .mask-toggle{position:absolute;font-weight:400;font-family:icomoon;font-size:24px;color:#0059a4;line-height:22px;right:20px;top:50%;transform:translateY(-50%);width:25px;background-color:inherit;cursor:pointer;border:none;padding:0}.has-float-label .mask-toggle .icon-eye-crossed{pointer-events:none}.has-float-label .mask-toggle:focus,.has-float-label .mask-toggle:hover{outline:none;color:#000}\n"], dependencies: [{ kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i2.MinLengthValidator, selector: "[minlength][formControlName],[minlength][formControl],[minlength][ngModel]", inputs: ["minlength"] }, { kind: "directive", type: i2.MaxLengthValidator, selector: "[maxlength][formControlName],[maxlength][formControl],[maxlength][ngModel]", inputs: ["maxlength"] }, { kind: "directive", type: i2.PatternValidator, selector: "[pattern][formControlName],[pattern][formControl],[pattern][ngModel]", inputs: ["pattern"] }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: InputMaskingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-input-masking-common', providers: [MakeProvider(InputMaskingComponent)], template: "<!-- START: input markup -->\r\n<label class=\"has-float-label\">\r\n  <input #inputElement #inputElementModel=\"ngModel\" [type]=\"type\" [placeholder]=\"placeholder\"\r\n  [disabled]=\"disabled\" [(ngModel)]=\"value\" [attr.maskInput]='maskInput' attr.aria-label={{label}} aria-invalid=false autocomplete=\"off\"\r\n  [minlength]=\"minlength\" [maxlength]=\"maxlength\" [required]=\"required\" [pattern]=\"pattern\" attr.maskPattern={{maskPattern}} [attr.whiteListedPattern] = \"whiteListedPattern\"\r\n  [ngClass]=\"className\" [class.invalid]=\"triggerErrorDesc && (((inputElementModel.errors && (inputElementModel.dirty || inputElementModel.touched)) || control?.errors?.customError) || maskedPatternInvalid)\"\r\n  (input)=\"textfieldInputHandler($event)\" (blur)=\"textfieldBlurHandler($event)\" (keydown)=\"textfieldKeyDownHandler($event)\" (mousedown)=\"mouseDownHandler($event)\" (select)=\"mouseDownHandler($event)\"\r\n  [ngModelOptions]=\"ngModelOptions\"\r\n  [class.has-no-text]=\"(!value || value?.length === 0)\"\r\n  aria-required=false />\r\n  <span>{{label}}</span>\r\n  <button class=\"{{'mask-toggle'}}\" (click)=\"toggleMasking()\" role=\"switch\" [attr.aria-checked]=\"maskInput\" [attr.aria-label]=\"copyMaskToggle\">\r\n    <div class=\"{{maskInput ? 'icon-eye-crossed':'icon-eye'}}\" aria-hidden=\"true\"></div>\r\n  </button>\r\n</label>\r\n<!-- END: input markup -->\r\n<!-- START: Optional Field -->\r\n<div class=\"optional-msg\" *ngIf=\"!required\">\r\n  <p class=\"mb-0\">Optional</p>\r\n</div>\r\n<!-- END: Optional Field -->\r\n\r\n<div [ngClass]=\"{'text': !required}\">\r\n  <!-- Begin mandatory error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"triggerErrorDesc && inputElementModel.errors?.required && (inputElementModel.dirty || inputElementModel.touched) && customValidationMessages.mandatory\">\r\n      <p class=\"mb-0\">{{customValidationMessages.mandatory}}</p>\r\n  </div>\r\n  <!-- End mandatory error message -->\r\n\r\n  <!-- Begin min length error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"triggerErrorDesc && inputElementModel.errors?.minlength && (inputElementModel.dirty || inputElementModel.touched) && customValidationMessages.minlength\">\r\n      <p class=\"mb-0\">{{customValidationMessages.minlength}} </p>\r\n  </div>\r\n  <!-- End min length error message -->\r\n\r\n  <!-- Begin max length error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"inputElementModel.errors?.maxlength && (inputElementModel.dirty || inputElementModel.touched) && customValidationMessages.maxlength\">\r\n      <p class=\"mb-0\">{{customValidationMessages.maxlength}} </p>\r\n  </div>\r\n  <!-- End max length error message -->\r\n\r\n  <!-- Begin pattern error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"triggerErrorDesc && ((inputElementModel.errors?.pattern && (inputElementModel.dirty || inputElementModel.touched) && customValidationMessages.pattern) || maskedPatternInvalid)\">\r\n      <p class=\"mb-0\">{{customValidationMessages.pattern}}</p>\r\n  </div>\r\n  <!-- End pattern error message -->\r\n\r\n  <!-- Begin custom error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"form-control-error error-msg\" *ngIf=\"triggerErrorDesc && control?.errors?.customError && (control?.dirty || control?.touched)\">\r\n      <p class=\"mb-0\">{{control.errors.message}}</p>\r\n  </div>\r\n  <!-- End custom error message -->\r\n\r\n  <!-- Helper Text -->\r\n  <p class=\"description mb-0\" *ngIf=\"description\">{{description}}\r\n</div>\r\n\r\n", styles: [".has-float-label{margin:0;width:100%;display:block;position:relative;box-sizing:border-box}.has-float-label span{position:absolute;left:-1px;padding:0 5px 0 0;top:-9.6px;cursor:text;font-size:12px;font-family:Open Sans Regular;font-weight:600;line-height:17px;opacity:1;color:#737373;background-color:#fff;max-width:70%;text-overflow:ellipsis;overflow:hidden;white-space:nowrap;transition:all .2s}.has-float-label input{padding:18px 0 15px 16px;color:#4a4a4a;font-family:Open Sans Regular;font-size:16px;line-height:22px;width:100%;border:1px solid #d8d8d8;background-color:#fff;font-weight:400;border-radius:2px;margin-bottom:0;box-shadow:inset 0 1px 2px #0000001a;box-shadow:inset 0 0 3px #0000001a}.has-float-label input[type=number]{-moz-appearance:textfield}.has-float-label input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none;appearance:none;margin:0}.has-float-label input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;appearance:none;margin:0}.has-float-label input::-ms-clear,.has-float-label input::-ms-reveal{display:none}.has-float-label input.rounded-right-0{border-top-right-radius:0;border-bottom-right-radius:0}.has-float-label input:focus{outline:none;color:#4a4a4a;font-size:16px;font-weight:400;background-color:#fff;padding-left:16px;border:1px solid #0091b6;box-shadow:inset 0 0 2px #0059a4;box-shadow:inset 0 0 3px #0059a4;box-sizing:border-box;opacity:1}.has-float-label input:not(:focus).has-no-text+span{top:0;left:0;font-size:16px;line-height:22px;font-family:Open Sans Regular;color:#737373;padding:19px 0 16px 16px;font-weight:400;opacity:1;background:transparent;transition:all .2s}.has-float-label input.invalid{border-width:1px 1px 1px 4px;border-style:solid;border-color:#bf002f;box-shadow:inset 0 1px 2px #0000001a;box-shadow:inset 0 0 2px #bf002f}.has-float-label input[disabled]{background-color:#f7f7f7;color:#000}.error-msg{margin-top:6px;color:#bf002f;font-family:Open Sans Semi Bold;font-size:12px;font-weight:600;line-height:18px}.description{margin-top:4px;overflow-wrap:break-word;font-weight:600;font-size:12px;line-height:18px;letter-spacing:.3px;color:#737373}.text{padding-left:65px}.optional-msg{margin-top:4px;overflow-wrap:break-word;font-weight:600;font-size:12px;line-height:18px;letter-spacing:.3px;color:#737373;font-style:italic;vertical-align:top;float:left}:root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.has-float-label .mask-toggle{position:absolute;font-weight:400;font-family:icomoon;font-size:24px;color:#0059a4;line-height:22px;right:20px;top:50%;transform:translateY(-50%);width:25px;background-color:inherit;cursor:pointer;border:none;padding:0}.has-float-label .mask-toggle .icon-eye-crossed{pointer-events:none}.has-float-label .mask-toggle:focus,.has-float-label .mask-toggle:hover{outline:none;color:#000}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { maskInput: [{
                type: Input
            }], isFormatRequired: [{
                type: Input
            }], maskPattern: [{
                type: Input
            }], whiteListedPattern: [{
                type: Input
            }], formatInputArr: [{
                type: Input
            }], inputEmitter: [{
                type: Output
            }], maskEmitter: [{
                type: Output
            }], formatEmitter: [{
                type: Output
            }], inputElementModel: [{
                type: ViewChild,
                args: ['inputElementModel', { static: true }]
            }] } });

class InputMaskingModule {
}
InputMaskingModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: InputMaskingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
InputMaskingModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: InputMaskingModule, declarations: [InputMaskingComponent], imports: [CommonModule,
        FormsModule], exports: [InputMaskingComponent] });
InputMaskingModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: InputMaskingModule, imports: [CommonModule,
        FormsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: InputMaskingModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule
                    ],
                    declarations: [InputMaskingComponent],
                    exports: [
                        InputMaskingComponent
                    ]
                }]
        }] });

class RadioButtonComponent extends AbstractValueAccessor {
    constructor() {
        // since RadioButtonComponent extends AbstractValueAccessor, we need to call super()
        super();
    }
    ngOnInit() {
        const option = this.option;
        if (option && option.forLabel === undefined) {
            option.forLabel = option.value.replace(/ /g, '-');
        }
        else {
            option.forLabel = option.forLabel.replace(/ /g, '-');
        }
    }
    /**
     * @method changeCheckedOption - function to change the checked values of non selected item to false and selected item to true
     * @param options
     * @param option
     * @return none
     */
    changeCheckedOption(options, option) {
        options.forEach((v, i) => {
            v.checked = !(option.value !== v.value);
        });
    }
}
RadioButtonComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RadioButtonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
RadioButtonComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: RadioButtonComponent, selector: "app-radio-button", inputs: { option: "option", radioName: "radioName", options: "options", disabled: "disabled", required: "required" }, providers: [MakeProvider(RadioButtonComponent)], usesInheritance: true, ngImport: i0, template: "<div class=\"radio-button-options clearfix\">\r\n  <input type=\"radio\"\r\n  [name]=\"radioName\"\r\n  id=\"radio-{{option.forLabel}}\"\r\n  class=\"radio-btn-input\"\r\n  [value]=true\r\n  [(ngModel)]=\"option.checked\"\r\n  [checked]=\"option.checked\"\r\n  (ngModelChange)=\"changeCheckedOption(options, option)\"\r\n  [disabled]=\"disabled\"\r\n  [required]=\"required\"\r\n  [attr.aria-required]=\"required\"\r\n  />\r\n  <label for=\"radio-{{option.forLabel}}\">{{option.value}}</label>\r\n</div>\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.radio-button-options .radio-btn-input{margin-top:3px;float:left}.radio-button-options label{color:#4a4a4a;cursor:pointer;padding-left:30px;display:block}\n"], dependencies: [{ kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.RadioControlValueAccessor, selector: "input[type=radio][formControlName],input[type=radio][formControl],input[type=radio][ngModel]", inputs: ["name", "formControlName", "value"] }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RadioButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-radio-button', providers: [MakeProvider(RadioButtonComponent)], template: "<div class=\"radio-button-options clearfix\">\r\n  <input type=\"radio\"\r\n  [name]=\"radioName\"\r\n  id=\"radio-{{option.forLabel}}\"\r\n  class=\"radio-btn-input\"\r\n  [value]=true\r\n  [(ngModel)]=\"option.checked\"\r\n  [checked]=\"option.checked\"\r\n  (ngModelChange)=\"changeCheckedOption(options, option)\"\r\n  [disabled]=\"disabled\"\r\n  [required]=\"required\"\r\n  [attr.aria-required]=\"required\"\r\n  />\r\n  <label for=\"radio-{{option.forLabel}}\">{{option.value}}</label>\r\n</div>\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.radio-button-options .radio-btn-input{margin-top:3px;float:left}.radio-button-options label{color:#4a4a4a;cursor:pointer;padding-left:30px;display:block}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { option: [{
                type: Input
            }], radioName: [{
                type: Input
            }], options: [{
                type: Input
            }], disabled: [{
                type: Input
            }], required: [{
                type: Input
            }] } });

class RadioButtonModule {
}
RadioButtonModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RadioButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
RadioButtonModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: RadioButtonModule, declarations: [RadioButtonComponent], imports: [CommonModule,
        FormsModule], exports: [RadioButtonComponent] });
RadioButtonModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RadioButtonModule, imports: [CommonModule,
        FormsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RadioButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule
                    ],
                    declarations: [RadioButtonComponent],
                    exports: [
                        RadioButtonComponent
                    ]
                }]
        }] });

class DateFormatter {
    constructor() {
        // maintains mappings of primeng datepicker format to moment date format
        this.dateFormatMapping = {
            'mm/dd/yy': 'MM/DD/YYYY',
            'mm/dd/y': 'MM/DD/YY',
            'dd/mm/yy': 'DD/MM/YYYY',
            'dd/mm/y': 'DD/MM/YY',
            'mm-dd-yy': 'MM-DD-YYYY',
            'mm-dd-y': 'MM-DD-YY'
        };
    }
    /**
     * @method format - function to format the date in the required format
     * @param date, format
     * @return date string
     */
    format(date, format) {
        const momentDateFormat = this.dateFormatMapping[format] || 'MM/DD/YYYY';
        return moment(new Date(date)).format(momentDateFormat);
    }
    /**
     * @method getMomentDateFormat - function to map to moment date format
     * @param format
     * @return string
     */
    getMomentDateFormat(format) {
        return this.dateFormatMapping[format] || 'MM/DD/YYYY';
    }
}
DateFormatter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DateFormatter, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
DateFormatter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DateFormatter });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DateFormatter, decorators: [{
            type: Injectable
        }] });

class DatepickerComponent {
    constructor(dateFormatter) {
        this.dateFormatter = dateFormatter;
        // object which holds validation error messages for this component
        this.customValidationMessages = {};
        this.datePickerLabelText = ''; // label text variable
        this.innerValue = ''; // The internal data model
        this.showErrorState = true;
        // Placeholders for the callbacks which are later provided
        // by the Control Value Accessor
        this.onTouchedCallback = this.noop;
        this.onChangeCallback = this.noop;
    }
    // default component life cycle hook
    ngOnInit() {
        this.setValidationMessages();
        this.placeholderText = this.placeholder;
        this.locale = {
            firstDayOfWeek: 0,
            dayNames: ['Sunday', 'Monday', 'Tuesdat', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
            dayNamesShort: ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'],
            dayNamesMin: ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'],
            monthNames: [
                'January',
                'February',
                'March',
                'April',
                'May',
                'June',
                'July',
                'August',
                'September',
                'October',
                'November',
                'December'
            ],
            monthNamesShort: []
        };
    }
    ngAfterViewInit() {
        // no need for previous accessibility fix
        // PrimeNG 10 already sets its datepicker icon (p-button-icon) to aria-hidden=true
    }
    /*Form validation messages for this component
     * @method setValidationMessages
     * @param none
     * @return none
     */
    setValidationMessages() {
        const customErrorMsg = this.errorMessage;
        this.customValidationMessages.mandatory = customErrorMsg && customErrorMsg.mandatory;
    }
    /*function to handle on focus event
     * @method onFocus
     * @param event
     * @return none
     */
    onFocus(event) {
        this.datePickerLabelText = this.placeholderText;
        this.placeholder = this.dateFormatter.getMomentDateFormat(this.dateFormat);
        this.onTouchedCallback();
    }
    /*function to handle on close event
     * @method onClose
     * @param event
     * @return none
     */
    onClose(event) {
        this.placeholder = this.placeholderText;
        this.datePickerLabelText = this.innerValue ? this.placeholderText : '';
    }
    /*Getter for value of element.
    * @method value
    * @param none
    * @return {any} value
    */
    get value() {
        return this.innerValue;
    }
    /*Setter of value to the element and calls onChangeCallback
    * @method value
    * @param {any}
    * @return none
    */
    set value(v) {
        if (v !== this.innerValue) {
            const formattedDate = v ? this.dateFormatter.format(v, this.dateFormat) : null;
            this.innerValue = formattedDate;
            this.onChangeCallback(formattedDate);
        }
    }
    /*Writes a new value to the element.
    * @method writeValue
    * @param {value}
    * @return none
    */
    onBlur() {
        this.onTouchedCallback();
    }
    /*Writes a new value to the element.
    * @method writeValue
    * @param {value}
    * @return none
    */
    writeValue(value) {
        if (value !== this.innerValue) {
            const formattedDate = value ? this.dateFormatter.format(value, this.dateFormat) : null;
            this.innerValue = formattedDate;
            if (this.innerValue) {
                this.datePickerLabelText = this.placeholderText;
                this.placeholder = this.dateFormatter.getMomentDateFormat(this.dateFormat);
            }
        }
    }
    /*Registers a callback function that should be called when the control value changes in the UI.
    * @method registerOnChange
    * @param {Function}
    * @return none
    */
    registerOnChange(fn) {
        this.onChangeCallback = fn;
    }
    /*Registers a callback function that should be called when the control receives a blur event.
    * @method registerOnTouched
    * @param {Function}
    * @return none
    */
    registerOnTouched(fn) {
        this.onTouchedCallback = fn;
    }
    /*no operation function
    * @method noop
    * @param none
    * @return none
    */
    noop() {
    }
    /* Function to set the input field to untouched state
    * @method markAsUntouched
    * @param none
    * @return none
    */
    markAsUntouched() {
        this.datepickerModel.control.markAsUntouched();
    }
    /* Function to set the input field to pristine(not dirty) state
    * @method markAsPristine
    * @param none
    * @return none
    */
    markAsPristine() {
        this.datepickerModel.control.markAsPristine();
    }
    /* Function to set the input field to dirty state
    * @method markAsDirty
    * @param none
    * @return none
    */
    markAsDirty() {
        this.datepickerModel.control.markAsDirty();
    }
    /* Function to set the input field to pristine(not dirty) and untouched state
    * @method markAsPristineAndUntouched
    * @param none
    * @return none
    */
    markAsPristineAndUntouched() {
        this.datepickerModel.control.markAsPristine();
        this.datepickerModel.control.markAsUntouched();
    }
}
DatepickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DatepickerComponent, deps: [{ token: DateFormatter }], target: i0.ɵɵFactoryTarget.Component });
DatepickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: DatepickerComponent, selector: "app-datepicker-common", inputs: { dateFormat: "dateFormat", disabled: "disabled", placeholder: "placeholder", required: "required", description: "description", errorMessage: "errorMessage", datepickerLabelId: "datepickerLabelId", showErrorState: "showErrorState", minDate: "minDate", maxDate: "maxDate", disabledDates: "disabledDates", disabledDays: "disabledDays", readonlyInput: "readonlyInput", control: "control" }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: DatepickerComponent,
            multi: true
        },
        DateFormatter], viewQueries: [{ propertyName: "datepickerModel", first: true, predicate: ["datepickerModel"], descendants: true, static: true }, { propertyName: "datepickerRef", first: true, predicate: ["datepickerRef"], descendants: true, static: true }], ngImport: i0, template: "<div class=\"datepicker-wrapper\">\r\n  <label *ngIf=\"datePickerLabelText\" for={{datepickerLabelId}} class=\"datepicker-label\">{{datePickerLabelText}}</label>\r\n  <p-calendar #datepickerRef id=\"datepicker\" [inputId]=\"datepickerLabelId\" #datepickerModel=\"ngModel\" [class.invalid]=\"showErrorState && datepickerModel.errors && (datepickerModel.dirty || datepickerModel.touched) || control?.errors?.customError\"\r\n    [showIcon]=\"true\" [dateFormat]=\"dateFormat\" inputStyleClass=\"datepicker-control\" [placeholder]=\"placeholder\" [disabled]=disabled\r\n    (onFocus)=\"onFocus($event)\" (onClose)=\"onClose($event)\" [(ngModel)]=\"value\" [locale]=\"locale\" [required]=\"required\" [minDate]=\"minDate\" [maxDate]=\"maxDate\" [disabledDates]=\"disabledDates\" [disabledDays]=\"disabledDays\">\r\n  </p-calendar>\r\n</div>\r\n\r\n<!-- Begin mandatory error message -->\r\n<div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"showErrorState && datepickerModel.errors?.required && (datepickerModel.dirty || datepickerModel.touched) && customValidationMessages.mandatory\">\r\n  <p class=\"mb-0\">{{customValidationMessages.mandatory}}</p>\r\n</div>\r\n<!-- End mandatory error message -->\r\n\r\n<!-- Begin custom error message -->\r\n<div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"showErrorState && control?.errors?.customError\">\r\n  <p class=\"mb-0\">{{control.errors.message}}</p>\r\n</div>\r\n<!-- End custom error message -->\r\n\r\n<!-- Helper Text -->\r\n<p class=\"description mb-0\" *ngIf=\"description\">{{description}}</p>\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.error-msg{margin-top:6px;color:#bf002f;font-family:Open Sans Semi Bold;font-weight:600;font-size:12px;line-height:18px}.description{margin-top:4px;overflow-wrap:break-word;font-weight:600;font-size:12px;line-height:18px;letter-spacing:.3px;color:#737373}#datepicker .p-calendar{width:100%}#datepicker .p-calendar .datepicker-control{width:100%;padding:18px 15px 16px;border-radius:2px;border:1px solid #d8d8d8;border-right:0;box-sizing:border-box;color:#4a4a4a;font-size:16px;height:57px;font-family:Open Sans Regular}#datepicker .p-calendar .datepicker-control:focus{box-shadow:none;-webkit-box-shadow:none}#datepicker .p-calendar button{position:relative;border:none;width:auto;padding:0}#datepicker .p-calendar button span{background-image:none}#datepicker .p-calendar button.p-datepicker-trigger{position:absolute;right:-2px;border:1px solid #d8d8d8;background-color:#f7f7f7;background-repeat:no-repeat;background-image:url(../../assets/images/ic_calendar.png);background-position-x:14px;background-position-y:14px;box-sizing:border-box;border-radius:0 2px 2px 0;padding:14px;color:#8b8b8b;width:57px;height:57px}#datepicker .p-calendar button.p-datepicker-trigger .p-button-icon{display:none}#datepicker .p-calendar .p-datepicker{width:18em;padding:.2em;background:#fff;border:1px solid #d8d8d8}#datepicker .p-calendar .p-datepicker:not(.p-datepicker-inline){background:white;border:0 none;box-shadow:0 1px 3px #0000004d}#datepicker .p-calendar .p-datepicker-header{background:#f7f7f7;padding:12px;font-family:Open Sans Regular}#datepicker .p-calendar .p-datepicker-header .p-datepicker-title{font-size:18px;line-height:24px;color:#4a4a4a}#datepicker .p-calendar .p-datepicker-header .p-datepicker-title .p-datepicker-month{margin-right:.5rem}#datepicker .p-calendar .p-datepicker-header .p-datepicker-prev,#datepicker .p-calendar .p-datepicker-header .p-datepicker-next{width:2rem;height:2rem;border:0 none;background:transparent;border-radius:50%;transition:background-color .2s,color .2s,box-shadow .2s}#datepicker .p-calendar .p-datepicker-header .p-datepicker-prev span.p-datepicker-prev-icon,#datepicker .p-calendar .p-datepicker-header .p-datepicker-prev span.p-datepicker-next-icon,#datepicker .p-calendar .p-datepicker-header .p-datepicker-next span.p-datepicker-prev-icon,#datepicker .p-calendar .p-datepicker-header .p-datepicker-next span.p-datepicker-next-icon{line-height:1;display:inline-block}#datepicker .p-calendar table th{padding:8px 0 0;text-align:center}#datepicker .p-calendar table th span{font-weight:600;font-size:14px;line-height:10px;letter-spacing:.4px;color:#737373}#datepicker .p-calendar table td{border:none;color:#4a4a4a;letter-spacing:.4px;text-align:center;font-size:14px;line-height:19px}#datepicker .p-calendar table td.p-datepicker-today>span{background:rgba(0,89,164,.1)}#datepicker .p-calendar table td span{display:inline-block;padding:.5em .2em;width:34px;height:34px;border-radius:22px;transition:box-shadow .2s;border:1px solid transparent;color:#4a4a4a}#datepicker .p-calendar table td span.p-highlight{background:rgba(0,89,164,.1)}#datepicker .p-calendar table td span:focus{outline:0 none;outline-offset:0;box-shadow:0 0 0 .2rem #0091b6}#datepicker .p-calendar table td span.p-disabled{color:#d8d8d8;cursor:not-allowed;background-color:#fff;border-radius:0}#datepicker .p-calendar .p-datepicker:not(.p-disabled) table td span:not(.p-highlight):not(.p-disabled):hover{background:rgba(0,89,164,.1)}#datepicker .p-calendar .p-datepicker:not(.p-disabled) table td span:not(.p-highlight):not(.p-disabled):focus{outline:0 none;outline-offset:0;box-shadow:0 0 0 .2rem #0091b6}#datepicker.p-inputwrapper-focus .p-calendar .datepicker-control{border:2px solid #0091b6}#datepicker.invalid .p-calendar .datepicker-control,#datepicker.invalid .p-calendar .p-datepicker-trigger{border-width:1px 1px 1px 4px;border-style:solid;border-color:#bf002f;box-shadow:inset 0 0 2px #bf002f}#datepicker.invalid .p-calendar .p-datepicker-trigger{border-left-width:1px}.datepicker-wrapper{position:relative}.datepicker-label{position:absolute;top:-8px;left:0;z-index:1002;display:block;padding-right:10px;background:#fff;font-family:Open Sans Semi Bold;line-height:17px;font-size:12px}.accessible-label{border:0;height:1px;margin:-1px;overflow:hidden;padding:0;position:absolute;width:1px}\n"], dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: i4.Calendar, selector: "p-calendar", inputs: ["style", "styleClass", "inputStyle", "inputId", "name", "inputStyleClass", "placeholder", "ariaLabelledBy", "iconAriaLabel", "disabled", "dateFormat", "multipleSeparator", "rangeSeparator", "inline", "showOtherMonths", "selectOtherMonths", "showIcon", "icon", "appendTo", "readonlyInput", "shortYearCutoff", "monthNavigator", "yearNavigator", "hourFormat", "timeOnly", "stepHour", "stepMinute", "stepSecond", "showSeconds", "required", "showOnFocus", "showWeek", "showClear", "dataType", "selectionMode", "maxDateCount", "showButtonBar", "todayButtonStyleClass", "clearButtonStyleClass", "autoZIndex", "baseZIndex", "panelStyleClass", "panelStyle", "keepInvalid", "hideOnDateTimeSelect", "touchUI", "timeSeparator", "focusTrap", "showTransitionOptions", "hideTransitionOptions", "tabindex", "view", "defaultDate", "minDate", "maxDate", "disabledDates", "disabledDays", "yearRange", "showTime", "responsiveOptions", "numberOfMonths", "firstDayOfWeek", "locale"], outputs: ["onFocus", "onBlur", "onClose", "onSelect", "onClear", "onInput", "onTodayClick", "onClearClick", "onMonthChange", "onYearChange", "onClickOutside", "onShow"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DatepickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-datepicker-common', providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: DatepickerComponent,
                            multi: true
                        },
                        DateFormatter], template: "<div class=\"datepicker-wrapper\">\r\n  <label *ngIf=\"datePickerLabelText\" for={{datepickerLabelId}} class=\"datepicker-label\">{{datePickerLabelText}}</label>\r\n  <p-calendar #datepickerRef id=\"datepicker\" [inputId]=\"datepickerLabelId\" #datepickerModel=\"ngModel\" [class.invalid]=\"showErrorState && datepickerModel.errors && (datepickerModel.dirty || datepickerModel.touched) || control?.errors?.customError\"\r\n    [showIcon]=\"true\" [dateFormat]=\"dateFormat\" inputStyleClass=\"datepicker-control\" [placeholder]=\"placeholder\" [disabled]=disabled\r\n    (onFocus)=\"onFocus($event)\" (onClose)=\"onClose($event)\" [(ngModel)]=\"value\" [locale]=\"locale\" [required]=\"required\" [minDate]=\"minDate\" [maxDate]=\"maxDate\" [disabledDates]=\"disabledDates\" [disabledDays]=\"disabledDays\">\r\n  </p-calendar>\r\n</div>\r\n\r\n<!-- Begin mandatory error message -->\r\n<div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"showErrorState && datepickerModel.errors?.required && (datepickerModel.dirty || datepickerModel.touched) && customValidationMessages.mandatory\">\r\n  <p class=\"mb-0\">{{customValidationMessages.mandatory}}</p>\r\n</div>\r\n<!-- End mandatory error message -->\r\n\r\n<!-- Begin custom error message -->\r\n<div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"showErrorState && control?.errors?.customError\">\r\n  <p class=\"mb-0\">{{control.errors.message}}</p>\r\n</div>\r\n<!-- End custom error message -->\r\n\r\n<!-- Helper Text -->\r\n<p class=\"description mb-0\" *ngIf=\"description\">{{description}}</p>\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.error-msg{margin-top:6px;color:#bf002f;font-family:Open Sans Semi Bold;font-weight:600;font-size:12px;line-height:18px}.description{margin-top:4px;overflow-wrap:break-word;font-weight:600;font-size:12px;line-height:18px;letter-spacing:.3px;color:#737373}#datepicker .p-calendar{width:100%}#datepicker .p-calendar .datepicker-control{width:100%;padding:18px 15px 16px;border-radius:2px;border:1px solid #d8d8d8;border-right:0;box-sizing:border-box;color:#4a4a4a;font-size:16px;height:57px;font-family:Open Sans Regular}#datepicker .p-calendar .datepicker-control:focus{box-shadow:none;-webkit-box-shadow:none}#datepicker .p-calendar button{position:relative;border:none;width:auto;padding:0}#datepicker .p-calendar button span{background-image:none}#datepicker .p-calendar button.p-datepicker-trigger{position:absolute;right:-2px;border:1px solid #d8d8d8;background-color:#f7f7f7;background-repeat:no-repeat;background-image:url(../../assets/images/ic_calendar.png);background-position-x:14px;background-position-y:14px;box-sizing:border-box;border-radius:0 2px 2px 0;padding:14px;color:#8b8b8b;width:57px;height:57px}#datepicker .p-calendar button.p-datepicker-trigger .p-button-icon{display:none}#datepicker .p-calendar .p-datepicker{width:18em;padding:.2em;background:#fff;border:1px solid #d8d8d8}#datepicker .p-calendar .p-datepicker:not(.p-datepicker-inline){background:white;border:0 none;box-shadow:0 1px 3px #0000004d}#datepicker .p-calendar .p-datepicker-header{background:#f7f7f7;padding:12px;font-family:Open Sans Regular}#datepicker .p-calendar .p-datepicker-header .p-datepicker-title{font-size:18px;line-height:24px;color:#4a4a4a}#datepicker .p-calendar .p-datepicker-header .p-datepicker-title .p-datepicker-month{margin-right:.5rem}#datepicker .p-calendar .p-datepicker-header .p-datepicker-prev,#datepicker .p-calendar .p-datepicker-header .p-datepicker-next{width:2rem;height:2rem;border:0 none;background:transparent;border-radius:50%;transition:background-color .2s,color .2s,box-shadow .2s}#datepicker .p-calendar .p-datepicker-header .p-datepicker-prev span.p-datepicker-prev-icon,#datepicker .p-calendar .p-datepicker-header .p-datepicker-prev span.p-datepicker-next-icon,#datepicker .p-calendar .p-datepicker-header .p-datepicker-next span.p-datepicker-prev-icon,#datepicker .p-calendar .p-datepicker-header .p-datepicker-next span.p-datepicker-next-icon{line-height:1;display:inline-block}#datepicker .p-calendar table th{padding:8px 0 0;text-align:center}#datepicker .p-calendar table th span{font-weight:600;font-size:14px;line-height:10px;letter-spacing:.4px;color:#737373}#datepicker .p-calendar table td{border:none;color:#4a4a4a;letter-spacing:.4px;text-align:center;font-size:14px;line-height:19px}#datepicker .p-calendar table td.p-datepicker-today>span{background:rgba(0,89,164,.1)}#datepicker .p-calendar table td span{display:inline-block;padding:.5em .2em;width:34px;height:34px;border-radius:22px;transition:box-shadow .2s;border:1px solid transparent;color:#4a4a4a}#datepicker .p-calendar table td span.p-highlight{background:rgba(0,89,164,.1)}#datepicker .p-calendar table td span:focus{outline:0 none;outline-offset:0;box-shadow:0 0 0 .2rem #0091b6}#datepicker .p-calendar table td span.p-disabled{color:#d8d8d8;cursor:not-allowed;background-color:#fff;border-radius:0}#datepicker .p-calendar .p-datepicker:not(.p-disabled) table td span:not(.p-highlight):not(.p-disabled):hover{background:rgba(0,89,164,.1)}#datepicker .p-calendar .p-datepicker:not(.p-disabled) table td span:not(.p-highlight):not(.p-disabled):focus{outline:0 none;outline-offset:0;box-shadow:0 0 0 .2rem #0091b6}#datepicker.p-inputwrapper-focus .p-calendar .datepicker-control{border:2px solid #0091b6}#datepicker.invalid .p-calendar .datepicker-control,#datepicker.invalid .p-calendar .p-datepicker-trigger{border-width:1px 1px 1px 4px;border-style:solid;border-color:#bf002f;box-shadow:inset 0 0 2px #bf002f}#datepicker.invalid .p-calendar .p-datepicker-trigger{border-left-width:1px}.datepicker-wrapper{position:relative}.datepicker-label{position:absolute;top:-8px;left:0;z-index:1002;display:block;padding-right:10px;background:#fff;font-family:Open Sans Semi Bold;line-height:17px;font-size:12px}.accessible-label{border:0;height:1px;margin:-1px;overflow:hidden;padding:0;position:absolute;width:1px}\n"] }]
        }], ctorParameters: function () { return [{ type: DateFormatter }]; }, propDecorators: { dateFormat: [{
                type: Input
            }], disabled: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], required: [{
                type: Input
            }], description: [{
                type: Input
            }], errorMessage: [{
                type: Input
            }], datepickerLabelId: [{
                type: Input
            }], showErrorState: [{
                type: Input
            }], minDate: [{
                type: Input
            }], maxDate: [{
                type: Input
            }], disabledDates: [{
                type: Input
            }], disabledDays: [{
                type: Input
            }], readonlyInput: [{
                type: Input
            }], control: [{
                type: Input
            }], datepickerModel: [{
                type: ViewChild,
                args: ['datepickerModel', { static: true }]
            }], datepickerRef: [{
                type: ViewChild,
                args: ['datepickerRef', { static: true }]
            }] } });

class DatepickerModule {
}
DatepickerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DatepickerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DatepickerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: DatepickerModule, declarations: [DatepickerComponent], imports: [CommonModule,
        FormsModule,
        CalendarModule], exports: [DatepickerComponent,
        CalendarModule] });
DatepickerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DatepickerModule, imports: [CommonModule,
        FormsModule,
        CalendarModule, CalendarModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DatepickerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        CalendarModule
                    ],
                    declarations: [
                        DatepickerComponent
                    ],
                    exports: [
                        DatepickerComponent,
                        CalendarModule
                    ]
                }]
        }] });

/// <reference path="../../typings.d.ts" />
const aria = {};
/**
 * @desc
 *  Key code constants
 */
aria.KeyCode = {
    BACKSPACE: 8,
    TAB: 9,
    RETURN: 13,
    ESC: 27,
    SPACE: 32,
    PAGE_UP: 33,
    PAGE_DOWN: 34,
    END: 35,
    HOME: 36,
    LEFT: 37,
    UP: 38,
    RIGHT: 39,
    DOWN: 40,
    DELETE: 46
};
aria.Utils = {};
// Polyfill src https://developer.mozilla.org/en-US/docs/Web/API/Element/matches
aria.Utils.matches = function (element, selector) {
    if (!Element.prototype.matches) {
        Element.prototype.matches =
            Element.prototype.msMatchesSelector ||
                Element.prototype.webkitMatchesSelector ||
                function (s) {
                    const matches = element.parentNode.querySelectorAll(s);
                    let i = matches.length;
                    while (--i >= 0 && matches.item(i) !== this) { }
                    return i > -1;
                };
    }
    return element.matches(selector);
};
aria.Utils.remove = function (item) {
    if (item.remove && typeof item.remove === 'function') {
        return item.remove();
    }
    if (item.parentNode &&
        item.parentNode.removeChild &&
        typeof item.parentNode.removeChild === 'function') {
        return item.parentNode.removeChild(item);
    }
    return false;
};
aria.Utils.isFocusable = function (element) {
    if (element.tabIndex > 0 || (element.tabIndex === 0 && element.getAttribute('tabIndex') !== null)) {
        return true;
    }
    if (element.disabled) {
        return false;
    }
    switch (element.nodeName) {
        case 'A':
            return !!element.href && element.rel !== 'ignore';
        case 'INPUT':
            return element.type !== 'hidden' && element.type !== 'file';
        case 'BUTTON':
        case 'SELECT':
        case 'TEXTAREA':
            return true;
        default:
            return false;
    }
};
aria.Utils.getAncestorBySelector = function (element, selector) {
    if (!aria.Utils.matches(element, selector + ' ' + element.tagName)) {
        // Element is not inside an element that matches selector
        return null;
    }
    // Move up the DOM tree until a parent matching the selector is found
    let currentNode = element;
    let ancestor = null;
    while (ancestor === null) {
        if (aria.Utils.matches(currentNode.parentNode, selector)) {
            ancestor = currentNode.parentNode;
        }
        else {
            currentNode = currentNode.parentNode;
        }
    }
    return ancestor;
};
aria.Utils.hasClass = function (element, className) {
    return (new RegExp('(\\s|^)' + className + '(\\s|$)')).test(element.className);
};
aria.Utils.addClass = function (element, className) {
    if (!aria.Utils.hasClass(element, className)) {
        element.className += ' ' + className;
    }
};
aria.Utils.removeClass = function (element, className) {
    const classRegex = new RegExp('(\\s|^)' + className + '(\\s|$)');
    element.className = element.className.replace(classRegex, ' ').trim();
};

class DropdownComponent {
    constructor(elementRef, platformId, window) {
        this.elementRef = elementRef;
        this.platformId = platformId;
        this.window = window;
        this.readOnlyDropdown = true;
        this.showErrorState = true;
        this.ariaHidden = false;
        this.keysSoFar = '';
        this.siblingList = null;
        this.customValidationMessages = {};
        this.dropdownSelectedValue = '';
        // Placeholders for the callbacks which are later provided
        // by the Control Value Accessor
        this.onTouchedCallback = this.noop;
        this.onChangeCallback = this.noop;
    }
    ngOnInit() {
        this.setValidationMessages();
    }
    ngAfterViewInit() {
        this.button = this.elementRef.nativeElement.querySelector('#' + this.dropDownButtonId);
        this.listBox = this.elementRef.nativeElement.querySelector('#' + this.dropDownListId);
    }
    markAsDirty() {
        this.isDirty = true;
    }
    get inputValue() {
        return this.innerValue;
    }
    set inputValue(v) {
        if (v !== this.innerValue) {
            this.innerValue = v;
            const selectedValue = this.options.filter((obj) => {
                return obj['value'] === v;
            });
            const getId = selectedValue[0].id;
            (getId === '') ? this.onChangeCallback(null) : this.onChangeCallback(selectedValue[0]);
        }
    }
    writeValue(value) {
        var _a;
        if (value !== this.innerValue) {
            let selectedValue = [];
            if (value) {
                selectedValue = this.options.filter((obj) => {
                    return obj['id'] === value['id'];
                });
            }
            if (selectedValue.length && selectedValue[0].id) {
                const itemList = this.listBox.querySelectorAll('[role="option"]');
                for (let i = 0; i < itemList.length; i++) {
                    if (((_a = itemList[i].innerText) === null || _a === void 0 ? void 0 : _a.trim().toLowerCase()) === selectedValue[0].value.toLowerCase()) {
                        this.updatedAriaLabel(itemList[i].attributes.id.value);
                    }
                }
            }
            if (selectedValue.length) {
                this.innerValue = selectedValue[0].value;
                if (this.innerValue && this.innerValue.toLowerCase() === 'select an option') {
                    this.onChangeCallback(null);
                }
            }
        }
    }
    registerOnChange(fn) {
        this.onChangeCallback = fn;
    }
    registerOnTouched(fn) {
        this.onTouchedCallback = fn;
    }
    noop() { }
    setValidationMessages() {
        const customErrorMsg = this.errorMessage;
        this.customValidationMessages.mandatory = customErrorMsg && customErrorMsg.mandatory;
    }
    markAsUntouched() {
        this.dropdownElementModel.control.markAsUntouched();
    }
    markAsPristine() {
        this.dropdownElementModel.control.markAsPristine();
    }
    markAsPristineAndUntouched() {
        this.dropdownElementModel.control.markAsPristine();
        this.dropdownElementModel.control.markAsUntouched();
    }
    checkShow(evt) {
        const key = evt.which || evt.keyCode;
        switch (key) {
            case aria.KeyCode.UP:
            case aria.KeyCode.DOWN:
                evt.preventDefault();
                this.showListbox();
                break;
        }
    }
    checkKeyEvent(evt) {
        const key = evt.which || evt.keyCode;
        switch (key) {
            case aria.KeyCode.RETURN:
                evt.preventDefault();
                this.showListbox();
                break;
            case aria.KeyCode.TAB:
                this.isDirty = true;
                break;
        }
    }
    checkHide() {
        this.hideListbox();
        this.button.focus();
    }
    checkKeyPress(evt) {
        var _a, _b, _c;
        const key = evt.which || evt.keyCode;
        let nextItem;
        if (isPlatformBrowser(this.platformId)) {
            nextItem = (_a = this.window.document) === null || _a === void 0 ? void 0 : _a.getElementById(this.activeDescendant);
        }
        if (!nextItem) {
            return;
        }
        switch (key) {
            case aria.KeyCode.UP:
            case aria.KeyCode.DOWN:
                evt.preventDefault();
                if (key === aria.KeyCode.UP) {
                    nextItem = nextItem.previousElementSibling;
                }
                else {
                    nextItem = nextItem.nextElementSibling;
                }
                if (nextItem) {
                    this.focusItem(nextItem);
                }
                break;
            case aria.KeyCode.HOME:
                evt.preventDefault();
                this.focusFirstItem();
                break;
            case aria.KeyCode.END:
                evt.preventDefault();
                this.focusLastItem();
                break;
            case aria.KeyCode.SPACE:
                evt.preventDefault();
                break;
            case aria.KeyCode.ESC:
                evt.preventDefault();
                this.checkHide();
                break;
            case aria.KeyCode.RETURN:
                evt.preventDefault();
                this.inputValue = (_b = nextItem.innerText) === null || _b === void 0 ? void 0 : _b.trim();
                if (((_c = this.inputValue) === null || _c === void 0 ? void 0 : _c.toLowerCase()) !== 'select an option') {
                    this.updatedAriaLabel(nextItem.attributes.id.value);
                }
                else {
                    this.updatedAriaLabel('');
                }
                this.checkHide();
                break;
            default:
                const itemToFocus = this.findItemToFocus(key);
                if (itemToFocus) {
                    this.focusItem(itemToFocus);
                }
                break;
        }
    }
    showListbox() {
        if (!this.disabled) {
            if (aria.Utils.hasClass(this.listBox, 'hidden')) {
                aria.Utils.removeClass(this.listBox, 'hidden');
                this.button.setAttribute('aria-expanded', 'true');
                this.listBox.focus();
                this.isListBoxOpen = true;
            }
            else {
                this.checkHide();
            }
            this.isDirty = true;
        }
    }
    hideListbox() {
        aria.Utils.addClass(this.listBox, 'hidden');
        this.button.removeAttribute('aria-expanded');
        this.isListBoxOpen = false;
    }
    setupFocus() {
        if (this.activeDescendant) {
            return;
        }
        this.focusFirstItem();
    }
    focusFirstItem() {
        let firstItem;
        firstItem = this.listBox.querySelector('[role="option"]');
        if (firstItem) {
            this.focusItem(firstItem);
        }
    }
    focusLastItem() {
        const itemList = this.listBox.querySelectorAll('[role="option"]');
        if (itemList.length) {
            this.focusItem(itemList[itemList.length - 1]);
        }
    }
    focusItem(element) {
        var _a;
        if (isPlatformBrowser(this.platformId)) {
            let activeDescendant;
            activeDescendant = (_a = this.window.document) === null || _a === void 0 ? void 0 : _a.getElementById(this.activeDescendant);
            if (activeDescendant) {
                this.defocusItem(activeDescendant);
            }
            aria.Utils.addClass(element, 'focused');
            this.listBox.setAttribute('aria-activedescendant', element.id);
            this.activeDescendant = element.id;
            if (this.listBox.scrollHeight > this.listBox.clientHeight) {
                const scrollBottom = this.listBox.clientHeight + this.listBox.scrollTop;
                const elementBottom = element.offsetTop + element.offsetHeight;
                if (elementBottom > scrollBottom) {
                    this.listBox.scrollTop = elementBottom - this.listBox.clientHeight;
                }
                else if (element.offsetTop < this.listBox.scrollTop) {
                    this.listBox.scrollTop = element.offsetTop;
                }
            }
        }
    }
    defocusItem(element) {
        if (!element) {
            return;
        }
        aria.Utils.removeClass(element, 'focused');
    }
    findItemToFocus(key) {
        const itemList = this.listBox.querySelectorAll('[role="option"]');
        const character = String.fromCharCode(key);
        if (!this.keysSoFar) {
            for (let i = 0; i < itemList.length; i++) {
                if (itemList[i].getAttribute('id') === this.activeDescendant) {
                    this.searchIndex = i;
                }
            }
        }
        this.keysSoFar += character;
        this.clearKeysSoFarAfterDelay();
        let nextMatch = this.findMatchInRange(itemList, this.searchIndex + 1, itemList.length);
        if (!nextMatch) {
            nextMatch = this.findMatchInRange(itemList, 0, this.searchIndex);
        }
        return nextMatch;
    }
    clearKeysSoFarAfterDelay() {
        if (this.keyClear) {
            clearTimeout(this.keyClear);
            this.keyClear = null;
        }
        this.keyClear = setTimeout((function () {
            this.keysSoFar = '';
            this.keyClear = null;
        }).bind(this), 500);
    }
    findMatchInRange(list, startIndex, endIndex) {
        // Find the first item starting with the keysSoFar substring, searching in
        // the specified range of items
        for (let n = startIndex; n < endIndex; n++) {
            const label = list[n].innerText;
            if (label && label.toUpperCase().indexOf(this.keysSoFar) === 0) {
                return list[n];
            }
        }
        return null;
    }
    checkClickItem(evt) {
        var _a, _b;
        const target = evt.target;
        if (target.getAttribute('role') === 'option') {
            this.focusItem(target);
            this.inputValue = (_a = target.innerText) === null || _a === void 0 ? void 0 : _a.trim();
            if (((_b = this.inputValue) === null || _b === void 0 ? void 0 : _b.toLowerCase()) !== 'select an option') {
                this.updatedAriaLabel(target.attributes.id.value);
            }
            else {
                this.updatedAriaLabel('');
            }
            this.checkHide();
        }
    }
    updatedAriaLabel(value) {
        this.dropdownSelectedValue = value;
    }
    watchClickOnDocument(targetElement) {
        const isClickInside = this.elementRef.nativeElement.contains(targetElement);
        if (!isClickInside) {
            this.hideListbox();
        }
    }
}
DropdownComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DropdownComponent, deps: [{ token: i0.ElementRef }, { token: PLATFORM_ID }, { token: WINDOW }], target: i0.ɵɵFactoryTarget.Component });
DropdownComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: DropdownComponent, selector: "app-dropdown", inputs: { options: "options", labelText: "labelText", dropdownDisplayText: "dropdownDisplayText", dropdownLabelId: "dropdownLabelId", dropDownListId: "dropDownListId", dropDownButtonId: "dropDownButtonId", required: "required", readOnlyDropdown: "readOnlyDropdown", disabled: "disabled", description: "description", errorMessage: "errorMessage", showErrorState: "showErrorState", ariaHidden: "ariaHidden" }, host: { listeners: { "document:click": "watchClickOnDocument($event.target)" } }, providers: [MakeProvider(DropdownComponent)], viewQueries: [{ propertyName: "dropdownElementModel", first: true, predicate: ["dropdownElementModel"], descendants: true }], ngImport: i0, template: "<div class=\"dropdown-wrapper\">\r\n  <div *ngIf=\"labelText\" class=\"dropdown-label\" id={{dropdownLabelId}}>{{labelText}}</div>\r\n  <div *ngIf=\"!inputValue\" class=\"placeholder\">{{dropdownDisplayText}}</div>\r\n  <div class=\"dropdown-select\">\r\n    <input\r\n      type=\"button\"\r\n      [(ngModel)]=\"inputValue\"\r\n      [disabled]=\"disabled\"\r\n      attr.aria-hidden=\"{{ariaHidden}}\"\r\n      required=\"{{required}}\"\r\n      id={{dropDownButtonId}}\r\n      class=\"dropdown-combobox\"\r\n      [class.invalid]=\"isDirty && !isListBoxOpen && showErrorState && required && inputValue && inputValue.toLowerCase() === 'select an option'\"\r\n      [class.dropdown-disabled]=\"disabled\"\r\n      aria-haspopup=\"listbox\"\r\n      attr.aria-labelledby=\"{{dropdownLabelId}}\"\r\n      attr.title=\"{{inputValue}}\"\r\n      attr.value=\"{{inputValue}}\"\r\n      (click)=\"showListbox()\"\r\n      (keyup)=\"checkShow($event)\"\r\n      (keydown)=\"checkKeyEvent($event)\"\r\n      />\r\n      <span class=\"arrow-wrapper\"\r\n        (click)=\"showListbox()\"\r\n      >\r\n        <span class=\"arrow\"></span>\r\n      </span>\r\n    <div class=\"dropdown-listbox-wrapper\">\r\n      <ul\r\n        id={{dropDownListId}}\r\n        class=\"dropdown-listbox hidden\"\r\n        role=\"listbox\"\r\n        tabindex=\"-1\"\r\n        aria-label=\"dropdownmenu\"\r\n        (focus)=\"setupFocus()\"\r\n        (keydown)=\"checkKeyPress($event)\"\r\n        (click)=\"checkClickItem($event)\"\r\n      >\r\n        <li\r\n        *ngFor=\"let option of options; let i = index\"\r\n        id=\"{{dropDownListId}}-{{i}}\"\r\n        class=\"dropdown-option\"\r\n        role=\"option\"\r\n        >\r\n          <span role=\"option\" id=\"{{option.value }} || {{option.template}}\" *ngIf=\"option.value else template\">{{option.value}}</span>\r\n          <ng-template #template>\r\n            <ng-container [ngTemplateOutlet]=\"option.template\"></ng-container>\r\n          </ng-template>\r\n        </li>\r\n      </ul>\r\n    </div>\r\n  </div>\r\n</div>\r\n<p class=\"description mb-0\" *ngIf=\"description\">{{description}}</p>\r\n<!-- Begin mandatory error message -->\r\n<div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"isDirty && !isListBoxOpen && showErrorState && required && inputValue && inputValue.toLowerCase() === 'select an option'\">\r\n  <p class=\"mb-0\">{{customValidationMessages.mandatory}}</p>\r\n</div>\r\n<!-- End mandatory error message -->\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.dropdown-wrapper{position:relative}.dropdown-wrapper .dropdown-label{position:absolute;top:-8px;left:0;z-index:1002;display:block;max-width:70%;padding-right:6px;background:#fff;font-family:Open Sans Semi Bold;font-weight:600;font-size:12px;line-height:17px;text-overflow:ellipsis;overflow:hidden;white-space:nowrap}.dropdown-wrapper .dropdown-select{position:relative}.dropdown-wrapper .dropdown-select .dropdown-combobox{border:1px solid #d8d8d8;border-radius:2px;box-shadow:none;padding:20px 0 15px 15px;height:57px;color:#4a4a4a;outline:0;display:block;width:100%;text-align:left;background-color:#fff}.dropdown-wrapper .dropdown-select .dropdown-combobox:focus{border:1px solid #0091b6;border-radius:2px;box-shadow:inset 0 0 2px #0059a4}.dropdown-wrapper .dropdown-select .dropdown-combobox.dropdown-error{border-width:1px 1px 1px 4px;border-color:#bf002f}.dropdown-wrapper .dropdown-select .dropdown-combobox.dropdown-error:focus{border-color:#bf002f}.dropdown-wrapper .dropdown-select .dropdown-combobox.dropdown-disabled{background-color:#f7f7f7;box-shadow:none}.dropdown-wrapper .dropdown-select .dropdown-combobox.dropdown-disabled:focus{border-color:#d8d8d8;box-shadow:none}.dropdown-wrapper .dropdown-select .dropdown-combobox.invalid{border-width:1px 1px 1px 4px;border-style:solid;border-color:#bf002f;box-shadow:inset 0 1px 2px #0000001a;box-shadow:inset 0 0 2px #bf002f}.dropdown-wrapper .dropdown-select span.arrow-wrapper{border-left:1px solid #d8d8d8;background-color:#f7f7f7;width:57px;height:55px;padding-right:0;position:absolute;right:1px;top:1px}.dropdown-wrapper .dropdown-select span.arrow-wrapper span.arrow{background-repeat:no-repeat;background-image:url(../../assets/images/ic_chevron_down.png);height:11px;width:17px;display:block;margin:23px auto;border:none}.dropdown-wrapper .dropdown-listbox-wrapper{position:absolute;top:57px;left:0;width:100%;z-index:1006}.dropdown-wrapper .dropdown-listbox-wrapper .dropdown-listbox{max-height:280px;padding:0;margin:0;overflow:hidden;overflow-y:auto;position:relative;display:block;background:#fff;border:1px solid #d8d8d8}.dropdown-wrapper .dropdown-listbox-wrapper .dropdown-listbox.hidden{display:none}.dropdown-wrapper .dropdown-listbox-wrapper .dropdown-listbox li{padding:15px;font-family:Open Sans Regular;font-size:16px;line-height:22px;color:#4a4a4a;white-space:normal;overflow:auto;text-overflow:clip;outline:0}.dropdown-wrapper .dropdown-listbox-wrapper .dropdown-listbox li:hover,.dropdown-wrapper .dropdown-listbox-wrapper .dropdown-listbox li:focus,.dropdown-wrapper .dropdown-listbox-wrapper .dropdown-listbox li.active,.dropdown-wrapper .dropdown-listbox-wrapper .dropdown-listbox li.focused{background-color:#0059a41a}.description{margin-top:4px;overflow-wrap:break-word;font-weight:600;font-size:12px;line-height:18px;letter-spacing:.3px;color:#737373}.error-msg{margin-top:6px;color:#bf002f;font-family:Open Sans Semi Bold;font-weight:600;font-size:12px;line-height:18px;outline:0}.dropdown-option,.dropdown-option span{cursor:pointer}.placeholder{-webkit-user-select:none;user-select:none;pointer-events:none;speak:none;margin-top:17.5px;margin-left:15.5px;position:absolute;z-index:1;color:#737373}\n"], dependencies: [{ kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DropdownComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-dropdown', providers: [MakeProvider(DropdownComponent)], template: "<div class=\"dropdown-wrapper\">\r\n  <div *ngIf=\"labelText\" class=\"dropdown-label\" id={{dropdownLabelId}}>{{labelText}}</div>\r\n  <div *ngIf=\"!inputValue\" class=\"placeholder\">{{dropdownDisplayText}}</div>\r\n  <div class=\"dropdown-select\">\r\n    <input\r\n      type=\"button\"\r\n      [(ngModel)]=\"inputValue\"\r\n      [disabled]=\"disabled\"\r\n      attr.aria-hidden=\"{{ariaHidden}}\"\r\n      required=\"{{required}}\"\r\n      id={{dropDownButtonId}}\r\n      class=\"dropdown-combobox\"\r\n      [class.invalid]=\"isDirty && !isListBoxOpen && showErrorState && required && inputValue && inputValue.toLowerCase() === 'select an option'\"\r\n      [class.dropdown-disabled]=\"disabled\"\r\n      aria-haspopup=\"listbox\"\r\n      attr.aria-labelledby=\"{{dropdownLabelId}}\"\r\n      attr.title=\"{{inputValue}}\"\r\n      attr.value=\"{{inputValue}}\"\r\n      (click)=\"showListbox()\"\r\n      (keyup)=\"checkShow($event)\"\r\n      (keydown)=\"checkKeyEvent($event)\"\r\n      />\r\n      <span class=\"arrow-wrapper\"\r\n        (click)=\"showListbox()\"\r\n      >\r\n        <span class=\"arrow\"></span>\r\n      </span>\r\n    <div class=\"dropdown-listbox-wrapper\">\r\n      <ul\r\n        id={{dropDownListId}}\r\n        class=\"dropdown-listbox hidden\"\r\n        role=\"listbox\"\r\n        tabindex=\"-1\"\r\n        aria-label=\"dropdownmenu\"\r\n        (focus)=\"setupFocus()\"\r\n        (keydown)=\"checkKeyPress($event)\"\r\n        (click)=\"checkClickItem($event)\"\r\n      >\r\n        <li\r\n        *ngFor=\"let option of options; let i = index\"\r\n        id=\"{{dropDownListId}}-{{i}}\"\r\n        class=\"dropdown-option\"\r\n        role=\"option\"\r\n        >\r\n          <span role=\"option\" id=\"{{option.value }} || {{option.template}}\" *ngIf=\"option.value else template\">{{option.value}}</span>\r\n          <ng-template #template>\r\n            <ng-container [ngTemplateOutlet]=\"option.template\"></ng-container>\r\n          </ng-template>\r\n        </li>\r\n      </ul>\r\n    </div>\r\n  </div>\r\n</div>\r\n<p class=\"description mb-0\" *ngIf=\"description\">{{description}}</p>\r\n<!-- Begin mandatory error message -->\r\n<div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"isDirty && !isListBoxOpen && showErrorState && required && inputValue && inputValue.toLowerCase() === 'select an option'\">\r\n  <p class=\"mb-0\">{{customValidationMessages.mandatory}}</p>\r\n</div>\r\n<!-- End mandatory error message -->\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.dropdown-wrapper{position:relative}.dropdown-wrapper .dropdown-label{position:absolute;top:-8px;left:0;z-index:1002;display:block;max-width:70%;padding-right:6px;background:#fff;font-family:Open Sans Semi Bold;font-weight:600;font-size:12px;line-height:17px;text-overflow:ellipsis;overflow:hidden;white-space:nowrap}.dropdown-wrapper .dropdown-select{position:relative}.dropdown-wrapper .dropdown-select .dropdown-combobox{border:1px solid #d8d8d8;border-radius:2px;box-shadow:none;padding:20px 0 15px 15px;height:57px;color:#4a4a4a;outline:0;display:block;width:100%;text-align:left;background-color:#fff}.dropdown-wrapper .dropdown-select .dropdown-combobox:focus{border:1px solid #0091b6;border-radius:2px;box-shadow:inset 0 0 2px #0059a4}.dropdown-wrapper .dropdown-select .dropdown-combobox.dropdown-error{border-width:1px 1px 1px 4px;border-color:#bf002f}.dropdown-wrapper .dropdown-select .dropdown-combobox.dropdown-error:focus{border-color:#bf002f}.dropdown-wrapper .dropdown-select .dropdown-combobox.dropdown-disabled{background-color:#f7f7f7;box-shadow:none}.dropdown-wrapper .dropdown-select .dropdown-combobox.dropdown-disabled:focus{border-color:#d8d8d8;box-shadow:none}.dropdown-wrapper .dropdown-select .dropdown-combobox.invalid{border-width:1px 1px 1px 4px;border-style:solid;border-color:#bf002f;box-shadow:inset 0 1px 2px #0000001a;box-shadow:inset 0 0 2px #bf002f}.dropdown-wrapper .dropdown-select span.arrow-wrapper{border-left:1px solid #d8d8d8;background-color:#f7f7f7;width:57px;height:55px;padding-right:0;position:absolute;right:1px;top:1px}.dropdown-wrapper .dropdown-select span.arrow-wrapper span.arrow{background-repeat:no-repeat;background-image:url(../../assets/images/ic_chevron_down.png);height:11px;width:17px;display:block;margin:23px auto;border:none}.dropdown-wrapper .dropdown-listbox-wrapper{position:absolute;top:57px;left:0;width:100%;z-index:1006}.dropdown-wrapper .dropdown-listbox-wrapper .dropdown-listbox{max-height:280px;padding:0;margin:0;overflow:hidden;overflow-y:auto;position:relative;display:block;background:#fff;border:1px solid #d8d8d8}.dropdown-wrapper .dropdown-listbox-wrapper .dropdown-listbox.hidden{display:none}.dropdown-wrapper .dropdown-listbox-wrapper .dropdown-listbox li{padding:15px;font-family:Open Sans Regular;font-size:16px;line-height:22px;color:#4a4a4a;white-space:normal;overflow:auto;text-overflow:clip;outline:0}.dropdown-wrapper .dropdown-listbox-wrapper .dropdown-listbox li:hover,.dropdown-wrapper .dropdown-listbox-wrapper .dropdown-listbox li:focus,.dropdown-wrapper .dropdown-listbox-wrapper .dropdown-listbox li.active,.dropdown-wrapper .dropdown-listbox-wrapper .dropdown-listbox li.focused{background-color:#0059a41a}.description{margin-top:4px;overflow-wrap:break-word;font-weight:600;font-size:12px;line-height:18px;letter-spacing:.3px;color:#737373}.error-msg{margin-top:6px;color:#bf002f;font-family:Open Sans Semi Bold;font-weight:600;font-size:12px;line-height:18px;outline:0}.dropdown-option,.dropdown-option span{cursor:pointer}.placeholder{-webkit-user-select:none;user-select:none;pointer-events:none;speak:none;margin-top:17.5px;margin-left:15.5px;position:absolute;z-index:1;color:#737373}\n"] }]
        }], ctorParameters: function () {
        return [{ type: i0.ElementRef }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [PLATFORM_ID]
                    }] }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [WINDOW]
                    }] }];
    }, propDecorators: { options: [{
                type: Input
            }], labelText: [{
                type: Input
            }], dropdownDisplayText: [{
                type: Input
            }], dropdownLabelId: [{
                type: Input
            }], dropDownListId: [{
                type: Input
            }], dropDownButtonId: [{
                type: Input
            }], required: [{
                type: Input
            }], readOnlyDropdown: [{
                type: Input
            }], disabled: [{
                type: Input
            }], description: [{
                type: Input
            }], errorMessage: [{
                type: Input
            }], showErrorState: [{
                type: Input
            }], ariaHidden: [{
                type: Input
            }], dropdownElementModel: [{
                type: ViewChild,
                args: ['dropdownElementModel']
            }], watchClickOnDocument: [{
                type: HostListener,
                args: ['document:click', ['$event.target']]
            }] } });

class DropdownModule {
}
DropdownModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DropdownModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DropdownModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: DropdownModule, declarations: [DropdownComponent], imports: [CommonModule,
        FormsModule,
        NgSelectModule], exports: [DropdownComponent] });
DropdownModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DropdownModule, imports: [CommonModule,
        FormsModule,
        NgSelectModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DropdownModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        NgSelectModule
                    ],
                    declarations: [DropdownComponent],
                    exports: [
                        DropdownComponent
                    ]
                }]
        }] });

class ChartsComponent {
    constructor() {
        this.chartHeight = 400;
        this.total = 0;
        this.tempChartData = [];
    }
    ngOnInit() {
        this.tooltip = {
            enabled: true,
            backgroundColor: 'rgba(255, 255, 255, 1)',
            bodyFontColor: '#737373',
            bodyFontFamily: 'Open Sans Regular',
            bodySpacing: 10,
            bodyFontSize: 13,
            cornerRadius: 2,
            borderColor: 'rgba(0, 89, 164, 0.3)',
            xPadding: 20,
            yPadding: 15,
            mode: 'index',
            borderWidth: 1,
            labelColors: this.colors,
            caretSize: 10,
            position: 'average',
            callbacks: {
                title: function () { }
            }
        };
        this.chartOptions.tooltips = this.tooltip;
        if (this.chartType === 'doughnut') {
            this.showChartText = true;
            // calculate total amount of data
            this.chartData.forEach(item => {
                this.total = this.total + item;
            });
            this.chartData.forEach(data => {
                const tmp = ((data / this.total) * 100).toFixed(2);
                this.tempChartData.push(tmp);
            });
            this.chartData = this.tempChartData;
        }
    }
}
ChartsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ChartsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ChartsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: ChartsComponent, selector: "app-charts", inputs: { chartLabels: "chartLabels", chartData: "chartData", chartType: "chartType", chartDataSet: "chartDataSet", chartOptions: "chartOptions", chartLegend: "chartLegend", colors: "colors", chartHeight: "chartHeight" }, ngImport: i0, template: "<div class=\"charts\" style=\"display: block;\">\r\n  <canvas baseChart\r\n          [style.height]=\"chartHeight + 'px'\" \r\n          [datasets]=\"chartDataSet\"\r\n          [options]=\"chartOptions\"\r\n          [data]=\"chartData\"\r\n          [labels]=\"chartLabels\"\r\n          [colors]=\"colors\"\r\n          [legend]=\"chartLegend\"\r\n          [chartType]=\"chartType\">\r\n  </canvas>\r\n</div>", styles: [".charts{position:relative}.charts canvas{position:relative;z-index:1}\n"], dependencies: [{ kind: "directive", type: i1$3.BaseChartDirective, selector: "canvas[baseChart]", inputs: ["options", "data", "datasets", "labels", "chartType", "colors", "legend", "plugins"], outputs: ["chartClick", "chartHover"], exportAs: ["base-chart"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ChartsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-charts', template: "<div class=\"charts\" style=\"display: block;\">\r\n  <canvas baseChart\r\n          [style.height]=\"chartHeight + 'px'\" \r\n          [datasets]=\"chartDataSet\"\r\n          [options]=\"chartOptions\"\r\n          [data]=\"chartData\"\r\n          [labels]=\"chartLabels\"\r\n          [colors]=\"colors\"\r\n          [legend]=\"chartLegend\"\r\n          [chartType]=\"chartType\">\r\n  </canvas>\r\n</div>", styles: [".charts{position:relative}.charts canvas{position:relative;z-index:1}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { chartLabels: [{
                type: Input
            }], chartData: [{
                type: Input
            }], chartType: [{
                type: Input
            }], chartDataSet: [{
                type: Input
            }], chartOptions: [{
                type: Input
            }], chartLegend: [{
                type: Input
            }], colors: [{
                type: Input
            }], chartHeight: [{
                type: Input
            }] } });

class ChartModule {
}
ChartModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ChartModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ChartModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: ChartModule, declarations: [ChartsComponent], imports: [CommonModule,
        ChartsModule], exports: [ChartsComponent] });
ChartModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ChartModule, imports: [CommonModule,
        ChartsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ChartModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        ChartsModule
                    ],
                    declarations: [ChartsComponent],
                    exports: [
                        ChartsComponent
                    ]
                }]
        }] });

class FixedValueComponent extends AbstractValueAccessor {
    constructor(cdr) {
        // since FixedValueComponent extends AbstractValueAccessor, we need to call super()
        super();
        this.cdr = cdr;
        this.isDropdownShown = false;
        this.showErrorState = false;
        this.customValidationMessages = {};
        this.valueChange = new EventEmitter();
    }
    /**
    * ngOnInit - lifecycle hook that is called on component initialization.
    * @param none
    * @return none
    */
    ngOnInit() {
        if (this.defaultDropdownValue) {
            this.ddnValue = this.defaultDropdownValue.value;
        }
        // show dropdown only if multiple options.
        if (this.options.length > 1) {
            this.isDropdownShown = true;
        }
        this.setValidationMessages();
        this.cdr.detectChanges();
    }
    /* Form validation messages for this component
    * @method setValidationMessages
    * @param none
    * @return none
    */
    setValidationMessages() {
        const customErrorMsg = this.errorMessage;
        this.customValidationMessages.mandatory = customErrorMsg && customErrorMsg.mandatory;
        this.customValidationMessages.minlength = customErrorMsg && customErrorMsg.minlength;
        this.customValidationMessages.maxlength = customErrorMsg && customErrorMsg.maxlength;
        this.customValidationMessages.pattern = customErrorMsg && customErrorMsg.pattern;
    }
    /**
    * handleDropdownChange - function that is called whenever dropdown selection
    * @param event
    * @return none
    */
    handleDropdownChange($event) {
        this.ddnValue = $event.id;
        this.handleInputChanges();
    }
    /**
    * handleInputChange - function that is called on blur of input field
    * @param event
    * @return none
    */
    handleInputChange() {
        this.handleInputChanges();
    }
    /**
     * handleInputChanges - function that will emit the value based on value changes
     * @param none
     * @return none
     */
    handleInputChanges() {
        const val = { 'inputValue': this.value, 'optionSelected': this.ddnValue };
        this.valueChange.emit({ 'value': val });
    }
    /**
     * setModel - method to set the ngModel of power unit
     * @param none
     * @return none
     */
    setModel() {
        this.options.forEach((v, i) => {
            if (v['id'] === this.ddnValue) {
                this.ddnValue = this.options[i]['value'];
            }
        });
    }
    /* Function to set the input field to untouched state
    * @method markAsUntouched
    * @param none
    * @return none
    */
    markAsUntouched() {
        this.inputElementModel.markAsDirty();
    }
    /* Function to set the input field to pristine(not dirty) state
    * @method markAsPristine
    * @param none
    * @return none
    */
    markAsPristine() {
        this.inputElementModel.markAsPristine();
    }
    /* Function to set the input field to dirty state
    * @method markAsDirty
    * @param none
    * @return none
    */
    markAsDirty() {
        this.inputElementModel.markAsDirty();
    }
    /* Function to set the input field to pristine(not dirty) and untouched state
    * @method markAsPristineAndUntouched
    * @param none
    * @return none
    */
    markAsPristineAndUntouched() {
        this.inputElementModel.markAsPristine();
        this.inputElementModel.markAsUntouched();
    }
}
FixedValueComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FixedValueComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FixedValueComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: FixedValueComponent, selector: "app-fixed-value", inputs: { options: "options", ddnValue: "ddnValue", defaultDropdownValue: "defaultDropdownValue", fixedValueLabelId: "fixedValueLabelId", fixedValueListId: "fixedValueListId", fixedValueDropDownButtonId: "fixedValueDropDownButtonId", label: "label", description: "description", errorMessage: "errorMessage", disabled: "disabled", showErrorState: "showErrorState", required: "required", pattern: "pattern" }, outputs: { valueChange: "valueChange" }, providers: [MakeProvider(FixedValueComponent)], viewQueries: [{ propertyName: "inputElementModel", first: true, predicate: ["inputElementModel"], descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<div class=\"fixed-value-container\">\r\n  <div class=\"input-block\" [class.mr-0]=\"!isDropdownShown\">\r\n    <app-input-common #inputElementModel\r\n      type=\"text\"\r\n      [label]=\"label\"\r\n      placeholder=\" \"\r\n      [required]=\"required\"\r\n      [(ngModel)]=\"value\"\r\n      (blurEmitter)=\"handleInputChange()\"\r\n      [disabled]=\"disabled\"\r\n      [triggerErrorDesc]=\"showErrorState\"\r\n      [pattern]=\"pattern\"\r\n      ></app-input-common>\r\n    <div class=\"option-block\">\r\n      <div class=\"option-block-text\" [class.border-0]=\"!isDropdownShown\">{{ddnValue}}</div>\r\n    </div>\r\n  </div>\r\n  <div *ngIf=\"isDropdownShown\" class=\"dropdown\">\r\n      <app-dropdown\r\n        name=\"dropdown\"\r\n        [ariaHidden]=\"true\"\r\n        [options]=\"options\"\r\n        [ngModel]=\"ddnValue\"\r\n        [dropdownLabelId]=\"fixedValueLabelId\"\r\n        [dropDownListId]=\"fixedValueListId\"\r\n        [dropDownButtonId]=\"fixedValueDropDownButtonId\"\r\n        [labelText]=\"label\"\r\n        (ngModelChange)=\"handleDropdownChange($event)\"\r\n      ></app-dropdown>\r\n  </div>\r\n    <p class=\"description mb-0\" *ngIf=\"description\">{{description}}</p>\r\n  </div>\r\n\r\n  <!-- Begin mandatory error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"errorMessage\" *ngIf=\"showErrorState && inputElementModel.inputElementModel.errors?.required && (inputElementModel.inputElementModel.dirty || inputElementModel.inputElementModel.touched) && customValidationMessages.mandatory\">\r\n    <p class=\"mb-0\">{{customValidationMessages.mandatory}}</p>\r\n  </div>\r\n  <!-- End mandatory error message -->\r\n\r\n  <!-- Begin pattern error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"errorMessage\" *ngIf=\"showErrorState && inputElementModel.inputElementModel.errors?.pattern && (inputElementModel.inputElementModel.dirty || inputElementModel.inputElementModel.touched) && customValidationMessages.pattern\">\r\n    <p class=\"mb-0\">{{customValidationMessages.pattern}}</p>\r\n  </div>\r\n  <!-- End pattern error message -->\r\n\r\n  <!-- Begin min length error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"showErrorState && inputElementModel.inputElementModel.errors?.minlength && (inputElementModel.inputElementModel.dirty || inputElementModel.inputElementModel.touched) && customValidationMessages.minlength\">\r\n    <p class=\"mb-0\">{{customValidationMessages.minlength}} </p>\r\n  </div>\r\n  <!-- End min length error message -->\r\n\r\n  <!-- Begin max length error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"showErrorState && inputElementModel.inputElementModel.errors?.maxlength && (inputElementModel.inputElementModel.dirty || inputElementModel.inputElementModel.touched) && customValidationMessages.maxlength\">\r\n    <p class=\"mb-0\">{{customValidationMessages.maxlength}} </p>\r\n  </div>\r\n  <!-- End max length error message -->\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.fixed-value-container{position:relative}.fixed-value-container .input-block{z-index:1005;position:relative;margin-right:57px;display:block}.fixed-value-container .input-block .option-block{position:absolute;top:0;right:0}.fixed-value-container .input-block .option-block .option-block-text{margin:20px 0 15px;padding-left:15px;padding-right:20px;border-left:1px solid #d8d8d8}.fixed-value-container .dropdown{position:absolute;width:100%;top:0;left:0;z-index:1004}.fixed-value-container .description{margin-top:4px;overflow-wrap:break-word;font-weight:600;font-size:12px;line-height:18px;letter-spacing:.3px;color:#737373}.errorMessage{margin-top:6px;color:#bf002f;font-family:Open Sans Semi Bold;font-size:12px;font-weight:600;line-height:18px}\n"], dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i2.PatternValidator, selector: "[pattern][formControlName],[pattern][formControl],[pattern][ngModel]", inputs: ["pattern"] }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: InputComponent, selector: "app-input-common", inputs: ["value", "type", "label", "required", "disabled", "description", "minlength", "maxlength", "errorMessage", "className", "placeholder", "control", "pattern", "triggerErrorDesc", "ngModelOptions"], outputs: ["inputEmitter", "blurEmitter", "keyupEmitter", "focusEmitter"] }, { kind: "component", type: DropdownComponent, selector: "app-dropdown", inputs: ["options", "labelText", "dropdownDisplayText", "dropdownLabelId", "dropDownListId", "dropDownButtonId", "required", "readOnlyDropdown", "disabled", "description", "errorMessage", "showErrorState", "ariaHidden"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FixedValueComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-fixed-value', providers: [MakeProvider(FixedValueComponent)], template: "<div class=\"fixed-value-container\">\r\n  <div class=\"input-block\" [class.mr-0]=\"!isDropdownShown\">\r\n    <app-input-common #inputElementModel\r\n      type=\"text\"\r\n      [label]=\"label\"\r\n      placeholder=\" \"\r\n      [required]=\"required\"\r\n      [(ngModel)]=\"value\"\r\n      (blurEmitter)=\"handleInputChange()\"\r\n      [disabled]=\"disabled\"\r\n      [triggerErrorDesc]=\"showErrorState\"\r\n      [pattern]=\"pattern\"\r\n      ></app-input-common>\r\n    <div class=\"option-block\">\r\n      <div class=\"option-block-text\" [class.border-0]=\"!isDropdownShown\">{{ddnValue}}</div>\r\n    </div>\r\n  </div>\r\n  <div *ngIf=\"isDropdownShown\" class=\"dropdown\">\r\n      <app-dropdown\r\n        name=\"dropdown\"\r\n        [ariaHidden]=\"true\"\r\n        [options]=\"options\"\r\n        [ngModel]=\"ddnValue\"\r\n        [dropdownLabelId]=\"fixedValueLabelId\"\r\n        [dropDownListId]=\"fixedValueListId\"\r\n        [dropDownButtonId]=\"fixedValueDropDownButtonId\"\r\n        [labelText]=\"label\"\r\n        (ngModelChange)=\"handleDropdownChange($event)\"\r\n      ></app-dropdown>\r\n  </div>\r\n    <p class=\"description mb-0\" *ngIf=\"description\">{{description}}</p>\r\n  </div>\r\n\r\n  <!-- Begin mandatory error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"errorMessage\" *ngIf=\"showErrorState && inputElementModel.inputElementModel.errors?.required && (inputElementModel.inputElementModel.dirty || inputElementModel.inputElementModel.touched) && customValidationMessages.mandatory\">\r\n    <p class=\"mb-0\">{{customValidationMessages.mandatory}}</p>\r\n  </div>\r\n  <!-- End mandatory error message -->\r\n\r\n  <!-- Begin pattern error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"errorMessage\" *ngIf=\"showErrorState && inputElementModel.inputElementModel.errors?.pattern && (inputElementModel.inputElementModel.dirty || inputElementModel.inputElementModel.touched) && customValidationMessages.pattern\">\r\n    <p class=\"mb-0\">{{customValidationMessages.pattern}}</p>\r\n  </div>\r\n  <!-- End pattern error message -->\r\n\r\n  <!-- Begin min length error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"showErrorState && inputElementModel.inputElementModel.errors?.minlength && (inputElementModel.inputElementModel.dirty || inputElementModel.inputElementModel.touched) && customValidationMessages.minlength\">\r\n    <p class=\"mb-0\">{{customValidationMessages.minlength}} </p>\r\n  </div>\r\n  <!-- End min length error message -->\r\n\r\n  <!-- Begin max length error message -->\r\n  <div role=\"alert\" tabindex=\"0\" class=\"error-msg\" *ngIf=\"showErrorState && inputElementModel.inputElementModel.errors?.maxlength && (inputElementModel.inputElementModel.dirty || inputElementModel.inputElementModel.touched) && customValidationMessages.maxlength\">\r\n    <p class=\"mb-0\">{{customValidationMessages.maxlength}} </p>\r\n  </div>\r\n  <!-- End max length error message -->\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.fixed-value-container{position:relative}.fixed-value-container .input-block{z-index:1005;position:relative;margin-right:57px;display:block}.fixed-value-container .input-block .option-block{position:absolute;top:0;right:0}.fixed-value-container .input-block .option-block .option-block-text{margin:20px 0 15px;padding-left:15px;padding-right:20px;border-left:1px solid #d8d8d8}.fixed-value-container .dropdown{position:absolute;width:100%;top:0;left:0;z-index:1004}.fixed-value-container .description{margin-top:4px;overflow-wrap:break-word;font-weight:600;font-size:12px;line-height:18px;letter-spacing:.3px;color:#737373}.errorMessage{margin-top:6px;color:#bf002f;font-family:Open Sans Semi Bold;font-size:12px;font-weight:600;line-height:18px}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { options: [{
                type: Input
            }], ddnValue: [{
                type: Input
            }], defaultDropdownValue: [{
                type: Input
            }], fixedValueLabelId: [{
                type: Input
            }], fixedValueListId: [{
                type: Input
            }], fixedValueDropDownButtonId: [{
                type: Input
            }], label: [{
                type: Input
            }], description: [{
                type: Input
            }], errorMessage: [{
                type: Input
            }], disabled: [{
                type: Input
            }], showErrorState: [{
                type: Input
            }], required: [{
                type: Input
            }], pattern: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], inputElementModel: [{
                type: ViewChild,
                args: ['inputElementModel', { static: true }]
            }] } });

class FixedValueModule {
}
FixedValueModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FixedValueModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FixedValueModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: FixedValueModule, declarations: [FixedValueComponent], imports: [CommonModule,
        FormsModule,
        InputModule,
        DropdownModule], exports: [FixedValueComponent] });
FixedValueModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FixedValueModule, imports: [CommonModule,
        FormsModule,
        InputModule,
        DropdownModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FixedValueModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        InputModule,
                        DropdownModule
                    ],
                    declarations: [FixedValueComponent],
                    exports: [FixedValueComponent]
                }]
        }] });

class AutocompleteComponent {
    constructor() {
        this.customValidationMessages = {};
        this.onSearchItem = new EventEmitter();
        this.onFocus = new EventEmitter();
        this.onSearchButton = new EventEmitter();
        this.onSelection = new EventEmitter();
        this.onBlur = new EventEmitter();
        this.onKeyUp = new EventEmitter();
        this.isDefaultAddressSet = new EventEmitter();
    }
    set searchTerm(value) {
        this.innerSearchTerm = value;
        this.isDefaultAddressSet.emit();
    }
    ngOnInit() {
        if (this.defaultList && this.defaultList.length) {
            this.searchResult = this.defaultList;
        }
        this.setValidationMessages();
    }
    /**
     * function to handle the keyUp event hook from lib
     * @method onSearchItem
     * @param $event
     * @return none
     */
    customSearch($event) {
        const searchTermLen = this.innerSearchTerm.trim().length;
        // Emit the event to parent if the search term length is debounce param
        if (searchTermLen >= this.DEBOUNCE_PARAM) {
            const eventObject = {
                Event: $event,
                innerSearchTerm: this.innerSearchTerm
            };
            this.onSearchItem.emit(eventObject);
        }
    }
    /**
     * function to handle the focus event
     * @method focus
     * @param $event
     * @return none
     */
    focus() {
        if (this.innerSearchTerm) {
            this.autocomplete.show();
        }
        this.onFocus.emit();
    }
    /**
     * function to handle the blur event
     * @method blur
     * @param $event
     * @return none
     */
    blur($event) {
        this.onBlur.emit($event);
    }
    /**
     * function to handle the key up event
     * @method keyUp
     * @param $event
     * @return none
     */
    keyUp($event) {
        this.onKeyUp.emit($event);
    }
    /**
     * function to handle the button click handle
     * @method handleSearchButtonClick
     * @param $event
     * @return none
     */
    handleSearchButtonClick($event) {
        this.onSearchButton.emit($event);
    }
    /**
     * funtion to handle the click event on item selection
     * @method onItemSelection
     * @param $event
     * @return none
     */
    onItemSelection($event) {
        this.onSelection.emit($event);
    }
    /**
    * method to set the error messages on invalid state of the component
    * @method setValidationMessages
    * @param none
    * @return none
    */
    setValidationMessages() {
        const customErrorMsg = this.errorMessage;
        this.customValidationMessages.mandatory = customErrorMsg && customErrorMsg.mandatory;
    }
}
AutocompleteComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AutocompleteComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
AutocompleteComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: AutocompleteComponent, selector: "app-autocomplete", inputs: { placeholderText: "placeholderText", isMultiple: "isMultiple", disabled: "disabled", dataKey: "dataKey", isRequired: "isRequired", emptyMessage: "emptyMessage", debounceParam: "debounceParam", searchButtonText: "searchButtonText", searchResult: "searchResult", defaultList: "defaultList", description: "description", errorMessage: "errorMessage", isActionButton: "isActionButton", autocompleteLabelId: "autocompleteLabelId", isCustomErrorVisible: "isCustomErrorVisible", customAutocompleteErrorMessage: "customAutocompleteErrorMessage", DELAY_PARAM: "DELAY_PARAM", DEBOUNCE_PARAM: "DEBOUNCE_PARAM", searchTerm: "searchTerm" }, outputs: { onSearchItem: "onSearchItem", onFocus: "onFocus", onSearchButton: "onSearchButton", onSelection: "onSelection", onBlur: "onBlur", onKeyUp: "onKeyUp", isDefaultAddressSet: "isDefaultAddressSet" }, providers: [MakeProvider(AutocompleteComponent)], viewQueries: [{ propertyName: "autocomplete", first: true, predicate: ["autocomplete"], descendants: true, static: true }], ngImport: i0, template: "  <div class=\"autocomplete-container\">\r\n    <label for={{autocompleteLabelId}} class=\"accessibleLabelText\">{{placeholderText}}</label>\r\n    <p-autoComplete #autocomplete #autocompleteElementModel=\"ngModel\"\r\n    id = \"autoComplete\"\r\n    [inputId]= \"autocompleteLabelId\"\r\n    [placeholder]=\"placeholderText\"\r\n    inputStyleClass=\"autocomplete-container\"\r\n    [class.component-error]=\"isCustomErrorVisible || (autocompleteElementModel.errors && (autocompleteElementModel.dirty || autocompleteElementModel.touched))\"\r\n    [multiple]=\"isMultiple\"\r\n    [disabled]=\"disabled\"\r\n    [dataKey]=\"dataKey\"\r\n    [required]=\"isRequired\"\r\n    [emptyMessage]=\"emptyMessage\"\r\n    (completeMethod)=\"customSearch($event)\"\r\n    [(ngModel)]=\"innerSearchTerm\"\r\n    [suggestions]=\"searchResult\"\r\n    [delay]=\"DELAY_PARAM\"\r\n    (onFocus)=\"focus()\"\r\n    (onBlur)=\"blur($event)\"\r\n    (onKeyUp)=\"keyUp($event)\"\r\n    (onSelect)=\"onItemSelection($event)\"    \r\n    ></p-autoComplete>\r\n  </div>\r\n  <div class=\"autocomplete-button\">\r\n    <app-button-common\r\n    classType=\"primary\"\r\n    className=\"autocomplete-btn rounded-left-0\"\r\n    (clickEvent)=\"handleSearchButtonClick($event)\"\r\n    >{{searchButtonText}}</app-button-common>      \r\n  </div>\r\n\r\n<!-- Description Text for autocomplete starts here -->\r\n<p class=\"description mb-0\" *ngIf=\"description\">{{description}}</p>\r\n<!-- Description Text for autocomplete ends here -->\r\n\r\n<!-- Begin mandatory error message -->\r\n<div class=\"autocomplete-error-msg\" *ngIf=\"autocompleteElementModel.errors?.required && (autocompleteElementModel.dirty || autocompleteElementModel.touched) && customValidationMessages.mandatory\">\r\n  <p class=\"mb-0\">{{customValidationMessages.mandatory}}</p>\r\n</div>\r\n<!-- End mandatory error message -->\r\n\r\n<!-- Begin custom error message -->\r\n<div class=\"autocomplete-error-msg\" *ngIf=\"isCustomErrorVisible\">\r\n  <p class=\"mb-0\">{{customAutocompleteErrorMessage}}</p>\r\n</div>\r\n<!-- End custom error message -->", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.autocomplete-container{height:57px}.autocomplete-container:focus{box-shadow:none;-webkit-box-shadow:none}#autoComplete input{width:100%;padding:17px 0 12px 15px;border-radius:2px 2px 0;color:#737373;line-height:22px;border:1px solid #d8d8d8;font-size:16px;border-right:0;box-shadow:inset 0 1px 2px #0000001a}#autoComplete input::-ms-clear,#autoComplete input::-ms-reveal{display:none}#autoComplete.component-error input{border-width:1px 1px 1px 4px;border-style:solid;border-color:#bf002f;box-shadow:inset 0 1px 2px #0000001a;box-shadow:inset 0 0 2px #bf002f}#autoComplete .ui-autocomplete{display:block;position:inherit}#autoComplete .ui-autocomplete-loader{display:none}#autoComplete .ui-autocomplete-panel{border:1px solid #d8d8d8;border-top:0;box-shadow:none;background:#fff}#autoComplete .ui-autocomplete-panel .ui-autocomplete-list{padding:0}#autoComplete .ui-corner-all{border-radius:2px 0 0 2px}#autoComplete .ui-state-highlight{color:#4a4a4a;background-color:#0059a41a}#autoComplete .ui-autocomplete-list-item{color:#4a4a4a;border-bottom:2px solid #f7f7f7;line-height:22px;padding:17px 15px;margin:0}#autoComplete .ui-autocomplete-list-item:last-child{border:none}#autoComplete .ui-autocomplete-list-item:hover{background-color:#0059a41a}#autoComplete .ui-autocomplete-list-item .ui-widget-content{color:#4a4a4a}.autocomplete-button{position:absolute;right:0;top:0}@media (max-width: 767.98px){#autoComplete input{border-right:1px solid #d8d8d8}#autoComplete .ui-corner-all{border-radius:2px}.autocomplete-button{position:relative;margin-top:15px}}@media (min-width: 1200px){.autocomplete-container{width:380px}}.autocomplete-error-msg{margin-top:6px;color:#bf002f;font-family:Open Sans Semi Bold;font-size:12px;font-weight:600;line-height:18px}.accessibleLabelText{border:0;height:1px;margin:-1px;overflow:hidden;padding:0;position:absolute;width:1px}\n"], dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: i3.AutoComplete, selector: "p-autoComplete", inputs: ["minLength", "delay", "style", "panelStyle", "styleClass", "panelStyleClass", "inputStyle", "inputId", "inputStyleClass", "placeholder", "readonly", "disabled", "scrollHeight", "lazy", "virtualScroll", "virtualScrollItemSize", "virtualScrollOptions", "maxlength", "name", "required", "size", "appendTo", "autoHighlight", "forceSelection", "type", "autoZIndex", "baseZIndex", "ariaLabel", "dropdownAriaLabel", "ariaLabelledBy", "dropdownIcon", "unique", "group", "completeOnFocus", "showClear", "field", "dropdown", "showEmptyMessage", "dropdownMode", "multiple", "tabindex", "dataKey", "emptyMessage", "showTransitionOptions", "hideTransitionOptions", "autofocus", "autocomplete", "optionGroupChildren", "optionGroupLabel", "itemSize", "suggestions"], outputs: ["completeMethod", "onSelect", "onUnselect", "onFocus", "onBlur", "onDropdownClick", "onClear", "onKeyUp", "onShow", "onHide", "onLazyLoad"] }, { kind: "component", type: ButtonComponent, selector: "app-button-common", inputs: ["disabled", "ariaLabel", "className", "classType", "type", "loaderType"], outputs: ["clickEvent"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AutocompleteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-autocomplete', providers: [MakeProvider(AutocompleteComponent)], template: "  <div class=\"autocomplete-container\">\r\n    <label for={{autocompleteLabelId}} class=\"accessibleLabelText\">{{placeholderText}}</label>\r\n    <p-autoComplete #autocomplete #autocompleteElementModel=\"ngModel\"\r\n    id = \"autoComplete\"\r\n    [inputId]= \"autocompleteLabelId\"\r\n    [placeholder]=\"placeholderText\"\r\n    inputStyleClass=\"autocomplete-container\"\r\n    [class.component-error]=\"isCustomErrorVisible || (autocompleteElementModel.errors && (autocompleteElementModel.dirty || autocompleteElementModel.touched))\"\r\n    [multiple]=\"isMultiple\"\r\n    [disabled]=\"disabled\"\r\n    [dataKey]=\"dataKey\"\r\n    [required]=\"isRequired\"\r\n    [emptyMessage]=\"emptyMessage\"\r\n    (completeMethod)=\"customSearch($event)\"\r\n    [(ngModel)]=\"innerSearchTerm\"\r\n    [suggestions]=\"searchResult\"\r\n    [delay]=\"DELAY_PARAM\"\r\n    (onFocus)=\"focus()\"\r\n    (onBlur)=\"blur($event)\"\r\n    (onKeyUp)=\"keyUp($event)\"\r\n    (onSelect)=\"onItemSelection($event)\"    \r\n    ></p-autoComplete>\r\n  </div>\r\n  <div class=\"autocomplete-button\">\r\n    <app-button-common\r\n    classType=\"primary\"\r\n    className=\"autocomplete-btn rounded-left-0\"\r\n    (clickEvent)=\"handleSearchButtonClick($event)\"\r\n    >{{searchButtonText}}</app-button-common>      \r\n  </div>\r\n\r\n<!-- Description Text for autocomplete starts here -->\r\n<p class=\"description mb-0\" *ngIf=\"description\">{{description}}</p>\r\n<!-- Description Text for autocomplete ends here -->\r\n\r\n<!-- Begin mandatory error message -->\r\n<div class=\"autocomplete-error-msg\" *ngIf=\"autocompleteElementModel.errors?.required && (autocompleteElementModel.dirty || autocompleteElementModel.touched) && customValidationMessages.mandatory\">\r\n  <p class=\"mb-0\">{{customValidationMessages.mandatory}}</p>\r\n</div>\r\n<!-- End mandatory error message -->\r\n\r\n<!-- Begin custom error message -->\r\n<div class=\"autocomplete-error-msg\" *ngIf=\"isCustomErrorVisible\">\r\n  <p class=\"mb-0\">{{customAutocompleteErrorMessage}}</p>\r\n</div>\r\n<!-- End custom error message -->", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.autocomplete-container{height:57px}.autocomplete-container:focus{box-shadow:none;-webkit-box-shadow:none}#autoComplete input{width:100%;padding:17px 0 12px 15px;border-radius:2px 2px 0;color:#737373;line-height:22px;border:1px solid #d8d8d8;font-size:16px;border-right:0;box-shadow:inset 0 1px 2px #0000001a}#autoComplete input::-ms-clear,#autoComplete input::-ms-reveal{display:none}#autoComplete.component-error input{border-width:1px 1px 1px 4px;border-style:solid;border-color:#bf002f;box-shadow:inset 0 1px 2px #0000001a;box-shadow:inset 0 0 2px #bf002f}#autoComplete .ui-autocomplete{display:block;position:inherit}#autoComplete .ui-autocomplete-loader{display:none}#autoComplete .ui-autocomplete-panel{border:1px solid #d8d8d8;border-top:0;box-shadow:none;background:#fff}#autoComplete .ui-autocomplete-panel .ui-autocomplete-list{padding:0}#autoComplete .ui-corner-all{border-radius:2px 0 0 2px}#autoComplete .ui-state-highlight{color:#4a4a4a;background-color:#0059a41a}#autoComplete .ui-autocomplete-list-item{color:#4a4a4a;border-bottom:2px solid #f7f7f7;line-height:22px;padding:17px 15px;margin:0}#autoComplete .ui-autocomplete-list-item:last-child{border:none}#autoComplete .ui-autocomplete-list-item:hover{background-color:#0059a41a}#autoComplete .ui-autocomplete-list-item .ui-widget-content{color:#4a4a4a}.autocomplete-button{position:absolute;right:0;top:0}@media (max-width: 767.98px){#autoComplete input{border-right:1px solid #d8d8d8}#autoComplete .ui-corner-all{border-radius:2px}.autocomplete-button{position:relative;margin-top:15px}}@media (min-width: 1200px){.autocomplete-container{width:380px}}.autocomplete-error-msg{margin-top:6px;color:#bf002f;font-family:Open Sans Semi Bold;font-size:12px;font-weight:600;line-height:18px}.accessibleLabelText{border:0;height:1px;margin:-1px;overflow:hidden;padding:0;position:absolute;width:1px}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { placeholderText: [{
                type: Input
            }], isMultiple: [{
                type: Input
            }], disabled: [{
                type: Input
            }], dataKey: [{
                type: Input
            }], isRequired: [{
                type: Input
            }], emptyMessage: [{
                type: Input
            }], debounceParam: [{
                type: Input
            }], searchButtonText: [{
                type: Input
            }], searchResult: [{
                type: Input
            }], defaultList: [{
                type: Input
            }], description: [{
                type: Input
            }], errorMessage: [{
                type: Input
            }], isActionButton: [{
                type: Input
            }], autocompleteLabelId: [{
                type: Input
            }], isCustomErrorVisible: [{
                type: Input
            }], customAutocompleteErrorMessage: [{
                type: Input
            }], DELAY_PARAM: [{
                type: Input
            }], DEBOUNCE_PARAM: [{
                type: Input
            }], onSearchItem: [{
                type: Output
            }], onFocus: [{
                type: Output
            }], onSearchButton: [{
                type: Output
            }], onSelection: [{
                type: Output
            }], onBlur: [{
                type: Output
            }], onKeyUp: [{
                type: Output
            }], isDefaultAddressSet: [{
                type: Output
            }], autocomplete: [{
                type: ViewChild,
                args: ['autocomplete', { static: true }]
            }], searchTerm: [{
                type: Input
            }] } });

class AutocompleteModule {
}
AutocompleteModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AutocompleteModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AutocompleteModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: AutocompleteModule, declarations: [AutocompleteComponent], imports: [CommonModule, FormsModule, AutoCompleteModule, ButtonModule], exports: [AutocompleteComponent, AutoCompleteModule] });
AutocompleteModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AutocompleteModule, imports: [CommonModule, FormsModule, AutoCompleteModule, ButtonModule, AutoCompleteModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AutocompleteModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, FormsModule, AutoCompleteModule, ButtonModule],
                    declarations: [AutocompleteComponent],
                    exports: [AutocompleteComponent, AutoCompleteModule]
                }]
        }] });

class LoaderComponent {
    constructor() { }
    ngOnInit() {
    }
    getLoaderPosition() {
        return (window.pageYOffset + window.innerHeight / 2 - 30) + 'px';
    }
}
LoaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LoaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
LoaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: LoaderComponent, selector: "app-loader-common", inputs: { showPageLoader: "showPageLoader", type: "type", showPortionLoader: "showPortionLoader" }, ngImport: i0, template: "<div *ngIf=\"showPageLoader\">\r\n  <div *ngIf=\"type === 'pageLoader'\" aria-label=\"Loading\">\r\n    <div class=\"loading-overlay\" *ngIf=\"showPageLoader\">\r\n      <div class=\"loader-background\">\r\n        <div class=\"loading\"></div>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</div>\r\n\r\n<div *ngIf=\"showPortionLoader\">\r\n  <div *ngIf=\"type === 'portionLoader'\" class=\"portion-loader\" aria-label=\"Loading\">\r\n    <div class=\"loader\">\r\n      <div class=\"dot-flashing\"></div>\r\n    </div>\r\n  </div>\r\n</div>\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.loader-shown{overflow-y:hidden}@keyframes spin{0%{transform:rotate(360deg)}to{transform:rotate(0)}}@keyframes dotFlashing{0%{background-color:#00000038}50%,to{background-color:#0000001a}}.loading{background-color:transparent;border:10px solid #fff;border-left-color:transparent;border-radius:50%;display:inline-block;width:60px;height:60px;vertical-align:middle;animation:spin 1.2s infinite linear}.loading-overlay{position:fixed;inset:0;text-align:center;background:rgba(0,0,0,.2);z-index:10000;display:flex;justify-content:center;align-items:center}.loading-overlay .loader-background{position:absolute}.portion-loader .loader{display:flex;justify-content:center;align-items:center;overflow:hidden}.portion-loader .loader .dot-flashing{position:relative;width:12px;height:12px;border-radius:50%;background-color:#00000038;animation:dotFlashing 1s infinite linear alternate;animation-delay:.5s}.portion-loader .loader .dot-flashing:before{content:\"\";display:inline-block;position:absolute;top:0;left:-24px;width:12px;height:12px;border-radius:50%;background-color:#00000038;animation:dotFlashing 1s infinite alternate;animation-delay:0}.portion-loader .loader .dot-flashing:after{content:\"\";display:inline-block;position:absolute;top:0;left:24px;width:12px;height:12px;border-radius:50%;background-color:#00000038;animation:dotFlashing 1s infinite alternate;animation-delay:1s}\n"], dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LoaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-loader-common', template: "<div *ngIf=\"showPageLoader\">\r\n  <div *ngIf=\"type === 'pageLoader'\" aria-label=\"Loading\">\r\n    <div class=\"loading-overlay\" *ngIf=\"showPageLoader\">\r\n      <div class=\"loader-background\">\r\n        <div class=\"loading\"></div>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</div>\r\n\r\n<div *ngIf=\"showPortionLoader\">\r\n  <div *ngIf=\"type === 'portionLoader'\" class=\"portion-loader\" aria-label=\"Loading\">\r\n    <div class=\"loader\">\r\n      <div class=\"dot-flashing\"></div>\r\n    </div>\r\n  </div>\r\n</div>\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.loader-shown{overflow-y:hidden}@keyframes spin{0%{transform:rotate(360deg)}to{transform:rotate(0)}}@keyframes dotFlashing{0%{background-color:#00000038}50%,to{background-color:#0000001a}}.loading{background-color:transparent;border:10px solid #fff;border-left-color:transparent;border-radius:50%;display:inline-block;width:60px;height:60px;vertical-align:middle;animation:spin 1.2s infinite linear}.loading-overlay{position:fixed;inset:0;text-align:center;background:rgba(0,0,0,.2);z-index:10000;display:flex;justify-content:center;align-items:center}.loading-overlay .loader-background{position:absolute}.portion-loader .loader{display:flex;justify-content:center;align-items:center;overflow:hidden}.portion-loader .loader .dot-flashing{position:relative;width:12px;height:12px;border-radius:50%;background-color:#00000038;animation:dotFlashing 1s infinite linear alternate;animation-delay:.5s}.portion-loader .loader .dot-flashing:before{content:\"\";display:inline-block;position:absolute;top:0;left:-24px;width:12px;height:12px;border-radius:50%;background-color:#00000038;animation:dotFlashing 1s infinite alternate;animation-delay:0}.portion-loader .loader .dot-flashing:after{content:\"\";display:inline-block;position:absolute;top:0;left:24px;width:12px;height:12px;border-radius:50%;background-color:#00000038;animation:dotFlashing 1s infinite alternate;animation-delay:1s}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { showPageLoader: [{
                type: Input
            }], type: [{
                type: Input
            }], showPortionLoader: [{
                type: Input
            }] } });

class LoaderModule {
}
LoaderModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LoaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
LoaderModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: LoaderModule, declarations: [LoaderComponent], imports: [CommonModule], exports: [LoaderComponent] });
LoaderModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LoaderModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LoaderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [LoaderComponent],
                    exports: [
                        LoaderComponent
                    ]
                }]
        }] });

class AdvancedAutoCompleteComponent {
    constructor() {
        this.onSearchItem = new EventEmitter();
        this.onFocus = new EventEmitter();
        this.onSearchButton = new EventEmitter();
        this.onSelection = new EventEmitter();
        this.onBlur = new EventEmitter();
        this.onKeyUp = new EventEmitter();
        this.isDefaultAddressSet = new EventEmitter();
        this.onDropdownClick = new EventEmitter();
        this.customValidationMessages = {};
    }
    set searchTerm(value) {
        this.innerSearchTerm = value;
        this.isDefaultAddressSet.emit();
    }
    ngOnInit() {
        if (this.defaultList && this.defaultList.length) {
            this.searchResult = this.defaultList;
        }
        this.setValidationMessages();
    }
    /**
     * method to handle the keyUp event hook from lib
     * @method onSearchItem
     * @param $event
     * @return none
     */
    customSearch($event) {
        const searchTermLen = this.innerSearchTerm.trim().length;
        // Emit the event to parent if the search term length is debounce param
        if (searchTermLen >= this.DEBOUNCE_PARAM) {
            const eventObject = {
                Event: $event,
                innerSearchTerm: this.innerSearchTerm
            };
            this.onSearchItem.emit(eventObject);
        }
    }
    /**
     * method to handle the focus event
     * @method focus
     * @param $event
     * @return none
     */
    focus() {
        this.advancedAutoComplete.show();
        this.onFocus.emit();
    }
    /**
     * method to handle the blur event
     * @method blur
     * @param $event
     * @return none
     */
    blur($event) {
        this.onBlur.emit($event);
    }
    /**
     * method to handle the key up event
     * @method keyUp
     * @param $event
     * @return none
     */
    keyUp($event) {
        this.onKeyUp.emit($event);
    }
    /**
     * method to handle the button click handle
     * @method handleSearchButtonClick
     * @param $event
     * @return none
     */
    handleSearchButtonClick($event) {
        this.onSearchButton.emit($event);
    }
    /**
     * method to handle the click event on item selection
     * @method onItemSelection
     * @param $event
     * @return none
     */
    onItemSelection($event) {
        this.onSelection.emit($event);
    }
    /**
      * method to set the error messages on invalid state of the component
      * @method setValidationMessages
      * @param none
      * @return none
      */
    setValidationMessages() {
        const customErrorMsg = this.errorMessage;
        this.customValidationMessages.mandatory = customErrorMsg && customErrorMsg.mandatory;
    }
    /**
      * method to handle the click event on dropdown click
      * @method onDropDownClick
      * @param none
      * @return none
      */
    onDropDownClick($event) {
        this.onDropdownClick.emit($event);
    }
}
AdvancedAutoCompleteComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AdvancedAutoCompleteComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
AdvancedAutoCompleteComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: AdvancedAutoCompleteComponent, selector: "app-advanced-auto-complete", inputs: { placeholderText: "placeholderText", isDisabled: "isDisabled", isMultiple: "isMultiple", dataKey: "dataKey", DELAY_PARAM: "DELAY_PARAM", DEBOUNCE_PARAM: "DEBOUNCE_PARAM", emptyMessage: "emptyMessage", description: "description", minLength: "minLength", searchResult: "searchResult", defaultList: "defaultList", errorMessage: "errorMessage", isCustomErrorVisible: "isCustomErrorVisible", tabIndex: "tabIndex", sizeParam: "sizeParam", isRequired: "isRequired", advancedAutocompleteLabelId: "advancedAutocompleteLabelId", customAutocompleteErrorMessage: "customAutocompleteErrorMessage", searchTerm: "searchTerm" }, outputs: { onSearchItem: "onSearchItem", onFocus: "onFocus", onSearchButton: "onSearchButton", onSelection: "onSelection", onBlur: "onBlur", onKeyUp: "onKeyUp", isDefaultAddressSet: "isDefaultAddressSet", onDropdownClick: "onDropdownClick" }, providers: [MakeProvider(AdvancedAutoCompleteComponent)], viewQueries: [{ propertyName: "advancedAutoComplete", first: true, predicate: ["advancedAutoComplete"], descendants: true, static: true }], ngImport: i0, template: "<div class=\"advanced-auto-complete-container ui-fluid\">\r\n  <label for={{advancedAutocompleteLabelId}} class=\"accessibleLabelText\">{{placeholderText}}</label>\r\n  <p-autoComplete #advancedAutoComplete #advancedAutocompleteElementModel=\"ngModel\" id=\"advancedAutoComplete\"\r\n    [inputId]=\"advancedAutocompleteLabelId\" [placeholder]=\"placeholderText\"\r\n    inputStyleClass=\"advanced-auto-complete-container\" [multiple]=\"isMultiple\" [disabled]=\"isDisabled\"\r\n    [dataKey]=\"dataKey\" [required]=\"isRequired\" [delay]=\"DELAY_PARAM\" [(ngModel)]=\"innerSearchTerm\"\r\n    [suggestions]=\"searchResult\" [size]=\"sizeParam\" [minLength]=\"minLength\" [dropdown]=\"true\" [tabindex]=\"tabIndex\"\r\n    (onFocus)=\"focus()\" (onBlur)=\"blur($event)\" (onKeyUp)=\"keyUp($event)\" (onSelect)=\"onItemSelection($event)\"\r\n    [class.component-error]=\"isCustomErrorVisible || (advancedAutocompleteElementModel.errors && (advancedAutocompleteElementModel.dirty || advancedAutocompleteElementModel.touched))\"\r\n    (completeMethod)=\"customSearch($event)\" (onDropdownClick)=\"onDropDownClick($event)\">\r\n  </p-autoComplete>\r\n</div>\r\n\r\n<!-- Description Text for advanced autocomplete starts here -->\r\n<p class=\"description mb-0\" *ngIf=\"description\" tabindex=\"0\">{{description}}</p>\r\n<!-- Description Text for advanced autocomplete ends here -->\r\n\r\n<!-- Begin mandatory error message -->\r\n<div class=\"advanced-autocomplete-error-msg\" tabindex=\"0\"\r\n  *ngIf=\"advancedAutocompleteElementModel.errors?.required && (advancedAutocompleteElementModel.dirty || advancedAutocompleteElementModel.touched) && customValidationMessages.mandatory\">\r\n  <p class=\"mb-0\">{{customValidationMessages.mandatory}}</p>\r\n</div>\r\n<!-- End mandatory error message -->\r\n\r\n<!-- Begin custom error message -->\r\n<div class=\"advanced-autocomplete-error-msg\" *ngIf=\"isCustomErrorVisible\" tabindex=\"0\">\r\n  <p class=\"mb-0\">{{customAutocompleteErrorMessage}}</p>\r\n</div>\r\n<!-- End custom error message -->", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.advanced-auto-complete-container{height:57px}.advanced-auto-complete-container #advancedAutoComplete input{padding:17px 0 16px 17px;border-radius:2px 2px 0;color:#8b8b8b;border:1px solid #4a4a4a;font-size:16px;border-right:0;line-height:22px;box-shadow:inset 0 1px 2px #0000001a}.advanced-auto-complete-container #advancedAutoComplete input::-ms-clear,.advanced-auto-complete-container #advancedAutoComplete input::-ms-reveal{display:none}.advanced-auto-complete-container #advancedAutoComplete input:hover{box-shadow:inset 0 0 2px #0059a4c4;border:1px solid rgba(0,89,164,.5);border-right:none}.advanced-auto-complete-container #advancedAutoComplete input:hover+button{box-shadow:inset 0 0 2px #0059a4c4!important;border:1px solid rgba(0,89,164,.5)!important}.advanced-auto-complete-container #advancedAutoComplete input:disabled{box-sizing:border-box;border:1px solid #d8d8d8;background-color:#f2f2f2}.advanced-auto-complete-container #advancedAutoComplete input:disabled+button{border:1px solid #d8d8d8;background-color:#f2f2f2}.advanced-auto-complete-container #advancedAutoComplete.component-error input{border-width:1px 1px 1px 4px;border-style:solid;border-color:#bf002f;box-shadow:inset 0 1px 2px #0000001a;box-shadow:inset 0 0 2px #bf002f}.advanced-auto-complete-container #advancedAutoComplete .ui-autocomplete{display:block;position:inherit;width:100!important}.advanced-auto-complete-container #advancedAutoComplete .ui-autocomplete-loader{display:none}.advanced-auto-complete-container #advancedAutoComplete .ui-autocomplete-panel{border:1px solid #d8d8d8;border-top:0;box-shadow:none;background:#fff}.advanced-auto-complete-container #advancedAutoComplete .ui-autocomplete-panel .ui-autocomplete-list{padding:0}.advanced-auto-complete-container #advancedAutoComplete .ui-corner-all{border-radius:2px 0 0 2px}.advanced-auto-complete-container #advancedAutoComplete .ui-state-highlight{color:#4a4a4a;background-color:#0059a41a}.advanced-auto-complete-container #advancedAutoComplete .ui-autocomplete-list-item{color:#4a4a4a;border-bottom:2px solid #f7f7f7;line-height:22px;padding:17px 15px;margin:0}.advanced-auto-complete-container #advancedAutoComplete .ui-autocomplete-list-item:last-child{border:none}.advanced-auto-complete-container #advancedAutoComplete .ui-autocomplete-list-item:hover{background-color:#0059a41a}.advanced-auto-complete-container #advancedAutoComplete .ui-autocomplete-list-item .ui-widget-content{color:#4a4a4a}.advanced-auto-complete-container #advancedAutoComplete .ui-button{box-sizing:border-box;height:57px;width:57px;border-radius:0 2px 2px 0;background-color:#f7f7f7;border:1px solid #d8d8d8;box-shadow:inset 0 1px 2px #0000001a}.advanced-auto-complete-container #advancedAutoComplete .ui-button .fa-caret-down:before{content:\"\"}.advanced-auto-complete-container #advancedAutoComplete .ui-button-icon-only .fa{top:56%!important;padding:23px 20px;background:url(../../assets/images/ic_chevron_down.png) no-repeat!important}.ui-fluid .ui-autocomplete.ui-autocomplete-dd .ui-autocomplete-input,.ui-fluid .ui-autocomplete.ui-autocomplete-dd .ui-autocomplete-multiple-container{width:calc(100% - 4em)!important}@media (max-width: 767.98px){#autoComplete input{border-right:1px solid #d8d8d8}#autoComplete .ui-corner-all{border-radius:2px}}@media (min-width: 1200px){.ui-autocomplete{width:100%!important}}.advanced-autocomplete-error-msg{margin-top:6px;color:#bf002f;font-family:Open Sans Semi Bold;font-size:12px;font-weight:600;line-height:18px}.advanced-auto-complete-container input:not(:focus) .accessibleLabelText{top:0;left:0;font-size:16px;line-height:22px;font-family:Open Sans Regular;color:#737373;padding:19px 0 16px 16px;font-weight:400;opacity:1;background:transparent;transition:all .2s}.accessibleLabelText{border:0;height:auto;margin:-1px;overflow:hidden;position:absolute;width:auto;padding:0 5px 0 0;top:-9.6px;cursor:text;font-size:12px;font-family:Open Sans Semi Bold;font-weight:600;line-height:17px;opacity:1;color:#737373;background-color:#fff;max-width:70%;text-overflow:ellipsis;white-space:nowrap;transition:all .2s}\n"], dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: i3.AutoComplete, selector: "p-autoComplete", inputs: ["minLength", "delay", "style", "panelStyle", "styleClass", "panelStyleClass", "inputStyle", "inputId", "inputStyleClass", "placeholder", "readonly", "disabled", "scrollHeight", "lazy", "virtualScroll", "virtualScrollItemSize", "virtualScrollOptions", "maxlength", "name", "required", "size", "appendTo", "autoHighlight", "forceSelection", "type", "autoZIndex", "baseZIndex", "ariaLabel", "dropdownAriaLabel", "ariaLabelledBy", "dropdownIcon", "unique", "group", "completeOnFocus", "showClear", "field", "dropdown", "showEmptyMessage", "dropdownMode", "multiple", "tabindex", "dataKey", "emptyMessage", "showTransitionOptions", "hideTransitionOptions", "autofocus", "autocomplete", "optionGroupChildren", "optionGroupLabel", "itemSize", "suggestions"], outputs: ["completeMethod", "onSelect", "onUnselect", "onFocus", "onBlur", "onDropdownClick", "onClear", "onKeyUp", "onShow", "onHide", "onLazyLoad"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AdvancedAutoCompleteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-advanced-auto-complete', providers: [MakeProvider(AdvancedAutoCompleteComponent)], template: "<div class=\"advanced-auto-complete-container ui-fluid\">\r\n  <label for={{advancedAutocompleteLabelId}} class=\"accessibleLabelText\">{{placeholderText}}</label>\r\n  <p-autoComplete #advancedAutoComplete #advancedAutocompleteElementModel=\"ngModel\" id=\"advancedAutoComplete\"\r\n    [inputId]=\"advancedAutocompleteLabelId\" [placeholder]=\"placeholderText\"\r\n    inputStyleClass=\"advanced-auto-complete-container\" [multiple]=\"isMultiple\" [disabled]=\"isDisabled\"\r\n    [dataKey]=\"dataKey\" [required]=\"isRequired\" [delay]=\"DELAY_PARAM\" [(ngModel)]=\"innerSearchTerm\"\r\n    [suggestions]=\"searchResult\" [size]=\"sizeParam\" [minLength]=\"minLength\" [dropdown]=\"true\" [tabindex]=\"tabIndex\"\r\n    (onFocus)=\"focus()\" (onBlur)=\"blur($event)\" (onKeyUp)=\"keyUp($event)\" (onSelect)=\"onItemSelection($event)\"\r\n    [class.component-error]=\"isCustomErrorVisible || (advancedAutocompleteElementModel.errors && (advancedAutocompleteElementModel.dirty || advancedAutocompleteElementModel.touched))\"\r\n    (completeMethod)=\"customSearch($event)\" (onDropdownClick)=\"onDropDownClick($event)\">\r\n  </p-autoComplete>\r\n</div>\r\n\r\n<!-- Description Text for advanced autocomplete starts here -->\r\n<p class=\"description mb-0\" *ngIf=\"description\" tabindex=\"0\">{{description}}</p>\r\n<!-- Description Text for advanced autocomplete ends here -->\r\n\r\n<!-- Begin mandatory error message -->\r\n<div class=\"advanced-autocomplete-error-msg\" tabindex=\"0\"\r\n  *ngIf=\"advancedAutocompleteElementModel.errors?.required && (advancedAutocompleteElementModel.dirty || advancedAutocompleteElementModel.touched) && customValidationMessages.mandatory\">\r\n  <p class=\"mb-0\">{{customValidationMessages.mandatory}}</p>\r\n</div>\r\n<!-- End mandatory error message -->\r\n\r\n<!-- Begin custom error message -->\r\n<div class=\"advanced-autocomplete-error-msg\" *ngIf=\"isCustomErrorVisible\" tabindex=\"0\">\r\n  <p class=\"mb-0\">{{customAutocompleteErrorMessage}}</p>\r\n</div>\r\n<!-- End custom error message -->", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.advanced-auto-complete-container{height:57px}.advanced-auto-complete-container #advancedAutoComplete input{padding:17px 0 16px 17px;border-radius:2px 2px 0;color:#8b8b8b;border:1px solid #4a4a4a;font-size:16px;border-right:0;line-height:22px;box-shadow:inset 0 1px 2px #0000001a}.advanced-auto-complete-container #advancedAutoComplete input::-ms-clear,.advanced-auto-complete-container #advancedAutoComplete input::-ms-reveal{display:none}.advanced-auto-complete-container #advancedAutoComplete input:hover{box-shadow:inset 0 0 2px #0059a4c4;border:1px solid rgba(0,89,164,.5);border-right:none}.advanced-auto-complete-container #advancedAutoComplete input:hover+button{box-shadow:inset 0 0 2px #0059a4c4!important;border:1px solid rgba(0,89,164,.5)!important}.advanced-auto-complete-container #advancedAutoComplete input:disabled{box-sizing:border-box;border:1px solid #d8d8d8;background-color:#f2f2f2}.advanced-auto-complete-container #advancedAutoComplete input:disabled+button{border:1px solid #d8d8d8;background-color:#f2f2f2}.advanced-auto-complete-container #advancedAutoComplete.component-error input{border-width:1px 1px 1px 4px;border-style:solid;border-color:#bf002f;box-shadow:inset 0 1px 2px #0000001a;box-shadow:inset 0 0 2px #bf002f}.advanced-auto-complete-container #advancedAutoComplete .ui-autocomplete{display:block;position:inherit;width:100!important}.advanced-auto-complete-container #advancedAutoComplete .ui-autocomplete-loader{display:none}.advanced-auto-complete-container #advancedAutoComplete .ui-autocomplete-panel{border:1px solid #d8d8d8;border-top:0;box-shadow:none;background:#fff}.advanced-auto-complete-container #advancedAutoComplete .ui-autocomplete-panel .ui-autocomplete-list{padding:0}.advanced-auto-complete-container #advancedAutoComplete .ui-corner-all{border-radius:2px 0 0 2px}.advanced-auto-complete-container #advancedAutoComplete .ui-state-highlight{color:#4a4a4a;background-color:#0059a41a}.advanced-auto-complete-container #advancedAutoComplete .ui-autocomplete-list-item{color:#4a4a4a;border-bottom:2px solid #f7f7f7;line-height:22px;padding:17px 15px;margin:0}.advanced-auto-complete-container #advancedAutoComplete .ui-autocomplete-list-item:last-child{border:none}.advanced-auto-complete-container #advancedAutoComplete .ui-autocomplete-list-item:hover{background-color:#0059a41a}.advanced-auto-complete-container #advancedAutoComplete .ui-autocomplete-list-item .ui-widget-content{color:#4a4a4a}.advanced-auto-complete-container #advancedAutoComplete .ui-button{box-sizing:border-box;height:57px;width:57px;border-radius:0 2px 2px 0;background-color:#f7f7f7;border:1px solid #d8d8d8;box-shadow:inset 0 1px 2px #0000001a}.advanced-auto-complete-container #advancedAutoComplete .ui-button .fa-caret-down:before{content:\"\"}.advanced-auto-complete-container #advancedAutoComplete .ui-button-icon-only .fa{top:56%!important;padding:23px 20px;background:url(../../assets/images/ic_chevron_down.png) no-repeat!important}.ui-fluid .ui-autocomplete.ui-autocomplete-dd .ui-autocomplete-input,.ui-fluid .ui-autocomplete.ui-autocomplete-dd .ui-autocomplete-multiple-container{width:calc(100% - 4em)!important}@media (max-width: 767.98px){#autoComplete input{border-right:1px solid #d8d8d8}#autoComplete .ui-corner-all{border-radius:2px}}@media (min-width: 1200px){.ui-autocomplete{width:100%!important}}.advanced-autocomplete-error-msg{margin-top:6px;color:#bf002f;font-family:Open Sans Semi Bold;font-size:12px;font-weight:600;line-height:18px}.advanced-auto-complete-container input:not(:focus) .accessibleLabelText{top:0;left:0;font-size:16px;line-height:22px;font-family:Open Sans Regular;color:#737373;padding:19px 0 16px 16px;font-weight:400;opacity:1;background:transparent;transition:all .2s}.accessibleLabelText{border:0;height:auto;margin:-1px;overflow:hidden;position:absolute;width:auto;padding:0 5px 0 0;top:-9.6px;cursor:text;font-size:12px;font-family:Open Sans Semi Bold;font-weight:600;line-height:17px;opacity:1;color:#737373;background-color:#fff;max-width:70%;text-overflow:ellipsis;white-space:nowrap;transition:all .2s}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { placeholderText: [{
                type: Input
            }], isDisabled: [{
                type: Input
            }], isMultiple: [{
                type: Input
            }], dataKey: [{
                type: Input
            }], DELAY_PARAM: [{
                type: Input
            }], DEBOUNCE_PARAM: [{
                type: Input
            }], emptyMessage: [{
                type: Input
            }], description: [{
                type: Input
            }], minLength: [{
                type: Input
            }], searchResult: [{
                type: Input
            }], defaultList: [{
                type: Input
            }], errorMessage: [{
                type: Input
            }], isCustomErrorVisible: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], sizeParam: [{
                type: Input
            }], isRequired: [{
                type: Input
            }], advancedAutocompleteLabelId: [{
                type: Input
            }], customAutocompleteErrorMessage: [{
                type: Input
            }], onSearchItem: [{
                type: Output
            }], onFocus: [{
                type: Output
            }], onSearchButton: [{
                type: Output
            }], onSelection: [{
                type: Output
            }], onBlur: [{
                type: Output
            }], onKeyUp: [{
                type: Output
            }], isDefaultAddressSet: [{
                type: Output
            }], onDropdownClick: [{
                type: Output
            }], searchTerm: [{
                type: Input
            }], advancedAutoComplete: [{
                type: ViewChild,
                args: ['advancedAutoComplete', { static: true }]
            }] } });

class AdvancedAutoCompleteModule {
}
AdvancedAutoCompleteModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AdvancedAutoCompleteModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AdvancedAutoCompleteModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: AdvancedAutoCompleteModule, declarations: [AdvancedAutoCompleteComponent], imports: [CommonModule,
        FormsModule,
        AutoCompleteModule], exports: [AutoCompleteModule, AdvancedAutoCompleteComponent] });
AdvancedAutoCompleteModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AdvancedAutoCompleteModule, imports: [CommonModule,
        FormsModule,
        AutoCompleteModule, AutoCompleteModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AdvancedAutoCompleteModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        AutoCompleteModule
                    ],
                    declarations: [AdvancedAutoCompleteComponent],
                    exports: [AutoCompleteModule, AdvancedAutoCompleteComponent]
                }]
        }] });

class CardComponent {
    constructor() {
        this.sectionLevel = 2;
        this.isShowingHeading = true;
        this.isShowingLoader = true;
        this.isShowingColor = true;
    }
    set color(color) {
        this.borderColor = this.isValidColor(color) ? color : '#cecece';
    }
    get hasNoContent() {
        const nodes = Array.from((this.cardContent && this.cardContent.nativeElement.childNodes)) || [];
        return nodes.every(node => !(node instanceof Element));
    }
    get computedComponentClass() {
        return (this.componentClass || '') + (!this.isShowingHeading && ' without-heading');
    }
    ngOnInit() {
        this.color = this.borderColor;
    }
    isValidColor(color) {
        const style = new Option().style;
        style.color = color;
        return style.color !== '';
    }
}
CardComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CardComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CardComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: CardComponent, selector: "app-card-common", inputs: { color: "color", heading: "heading", componentClass: "componentClass", headerClass: "headerClass", headingClass: "headingClass", headingCopyClass: "headingCopyClass", sectionLevel: "sectionLevel", iconClass: "iconClass", iconAriaLabel: "iconAriaLabel", bodyClass: "bodyClass", wrapperClass: "wrapperClass", isShowingHeading: "isShowingHeading", isShowingLoader: "isShowingLoader", isShowingColor: "isShowingColor" }, viewQueries: [{ propertyName: "cardContent", first: true, predicate: ["cardContent"], descendants: true, static: true }, { propertyName: "cardHeader", first: true, predicate: ["cardHeader"], descendants: true }], ngImport: i0, template: "<section class=\"card common component\" [ngClass]=\"computedComponentClass\">\r\n  <header #cardHeader class=card-header [ngClass]=\"headerClass\" *ngIf=\"isShowingHeading\">\r\n    <div class=card-title\r\n         [ngClass]=\"headingClass\"\r\n         [ngStyle]=\"(isShowingColor && {'border-left-color': borderColor}) || {border: 'none'}\"\r\n         role=\"heading\"\r\n         [attr.aria-level]=\"sectionLevel\"\r\n         tabindex=0>\r\n      <span class=\"title wrapper\">\r\n        <span class=card-icon [ngClass]=\"iconClass\" [attr.aria-label]=\"iconAriaLabel\" *ngIf=\"iconClass\"></span>\r\n        <span class=copy [ngClass]=\"headingCopyClass\">{{ heading }}</span>\r\n      </span>\r\n    </div>\r\n  </header>\r\n  <div class=card-body [ngClass]=\"bodyClass\">\r\n    <div class=\"loader wrapper\" *ngIf=\"hasNoContent && isShowingLoader\">\r\n      <app-loader-common type=\"portionLoader\" [showPortionLoader]=\"hasNoContent\"></app-loader-common>\r\n    </div>\r\n    <div #cardContent class=\"content wrapper\" [ngClass]=\"wrapperClass\">\r\n      <ng-content></ng-content>\r\n    </div>\r\n  </div>\r\n</section>\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.card.component{flex:1 1 auto}.card.component:not(.without-heading){padding-top:20px}.card-body{flex:1 1 auto;display:flex;justify-content:center;align-items:center;border:none;padding:0}@media screen and (-ms-high-contrast: active),(-ms-high-contrast: none){.content.wrapper{width:100%}}.wrapper{flex:1 1 auto;display:block}.wrapper.loader+.content{flex:0 0 auto}.card-header{padding:0;background-color:inherit;border:none}.card-icon+.copy{margin-left:10px}.card-title{display:flex;align-items:center;flex-direction:row;max-height:20px;margin-bottom:20px;border-left-style:solid;border-left-width:3px}.card-title .title.wrapper{display:flex;flex-direction:row;align-items:center;height:60px;margin:0 30px;border-bottom:solid 2px #ececec}@media screen and (-ms-high-contrast: active),(-ms-high-contrast: none){.card-title .title.wrapper{height:auto;margin-bottom:20px}}@media (max-width: 991.98px){.card-title .title.wrapper{margin:0 20px}}.card-title .copy{height:20px;display:inline-flex;flex-direction:row;align-items:center;color:#4a4a4a;font-family:Open Sans Bold,sans-serif;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1px;line-height:1.3636363636}\n"], dependencies: [{ kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: LoaderComponent, selector: "app-loader-common", inputs: ["showPageLoader", "type", "showPortionLoader"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CardComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-card-common', template: "<section class=\"card common component\" [ngClass]=\"computedComponentClass\">\r\n  <header #cardHeader class=card-header [ngClass]=\"headerClass\" *ngIf=\"isShowingHeading\">\r\n    <div class=card-title\r\n         [ngClass]=\"headingClass\"\r\n         [ngStyle]=\"(isShowingColor && {'border-left-color': borderColor}) || {border: 'none'}\"\r\n         role=\"heading\"\r\n         [attr.aria-level]=\"sectionLevel\"\r\n         tabindex=0>\r\n      <span class=\"title wrapper\">\r\n        <span class=card-icon [ngClass]=\"iconClass\" [attr.aria-label]=\"iconAriaLabel\" *ngIf=\"iconClass\"></span>\r\n        <span class=copy [ngClass]=\"headingCopyClass\">{{ heading }}</span>\r\n      </span>\r\n    </div>\r\n  </header>\r\n  <div class=card-body [ngClass]=\"bodyClass\">\r\n    <div class=\"loader wrapper\" *ngIf=\"hasNoContent && isShowingLoader\">\r\n      <app-loader-common type=\"portionLoader\" [showPortionLoader]=\"hasNoContent\"></app-loader-common>\r\n    </div>\r\n    <div #cardContent class=\"content wrapper\" [ngClass]=\"wrapperClass\">\r\n      <ng-content></ng-content>\r\n    </div>\r\n  </div>\r\n</section>\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.card.component{flex:1 1 auto}.card.component:not(.without-heading){padding-top:20px}.card-body{flex:1 1 auto;display:flex;justify-content:center;align-items:center;border:none;padding:0}@media screen and (-ms-high-contrast: active),(-ms-high-contrast: none){.content.wrapper{width:100%}}.wrapper{flex:1 1 auto;display:block}.wrapper.loader+.content{flex:0 0 auto}.card-header{padding:0;background-color:inherit;border:none}.card-icon+.copy{margin-left:10px}.card-title{display:flex;align-items:center;flex-direction:row;max-height:20px;margin-bottom:20px;border-left-style:solid;border-left-width:3px}.card-title .title.wrapper{display:flex;flex-direction:row;align-items:center;height:60px;margin:0 30px;border-bottom:solid 2px #ececec}@media screen and (-ms-high-contrast: active),(-ms-high-contrast: none){.card-title .title.wrapper{height:auto;margin-bottom:20px}}@media (max-width: 991.98px){.card-title .title.wrapper{margin:0 20px}}.card-title .copy{height:20px;display:inline-flex;flex-direction:row;align-items:center;color:#4a4a4a;font-family:Open Sans Bold,sans-serif;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1px;line-height:1.3636363636}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { color: [{
                type: Input
            }], heading: [{
                type: Input
            }], componentClass: [{
                type: Input
            }], headerClass: [{
                type: Input
            }], headingClass: [{
                type: Input
            }], headingCopyClass: [{
                type: Input
            }], sectionLevel: [{
                type: Input
            }], iconClass: [{
                type: Input
            }], iconAriaLabel: [{
                type: Input
            }], bodyClass: [{
                type: Input
            }], wrapperClass: [{
                type: Input
            }], isShowingHeading: [{
                type: Input
            }], isShowingLoader: [{
                type: Input
            }], isShowingColor: [{
                type: Input
            }], cardContent: [{
                type: ViewChild,
                args: ['cardContent', { static: true }]
            }], cardHeader: [{
                type: ViewChild,
                args: ['cardHeader']
            }] } });

class CardModule {
}
CardModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CardModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: CardModule, declarations: [CardComponent], imports: [CommonModule,
        LoaderModule], exports: [CardComponent] });
CardModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CardModule, imports: [CommonModule,
        LoaderModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CardModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        LoaderModule
                    ],
                    declarations: [CardComponent],
                    exports: [CardComponent]
                }]
        }] });

var copy$1 = {
    close: 'close'
};

class AlertComponent {
    constructor() {
        this.copyClose = copy$1.close;
        this.closeEvent = new EventEmitter();
        this._open = true;
    }
    get isOpen() {
        return (this.alert.IsActive !== false) && this._open;
    }
    get backgroundColor() {
        return this.alert.BackgroundColor || defaultBackgroundColor;
    }
    ngOnInit() {
    }
    onClose(event) {
        this._open = false;
        this.closeEvent.emit({ event: event });
    }
}
AlertComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AlertComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
AlertComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: AlertComponent, selector: "app-alert-common", inputs: { alert: "alert" }, outputs: { closeEvent: "closeEvent" }, ngImport: i0, template: "<section class=\"alert common component\" [ngStyle]=\"{ 'background-color': backgroundColor }\" *ngIf=\"isOpen\">\r\n  <div class=alert--icon>\r\n    <span class=\"status-icon icon-alert\" aria-label=\"icon-alert\"></span>\r\n  </div>\r\n  <div class=alert--content>\r\n    <div class=container [innerHTML]=\"alert.Message\"></div>\r\n  </div>\r\n  <div class=alert--controls>\r\n    <div class=control--close (click)=\"onClose($event)\">\r\n      <span class=\"status-icon icon-cross2\" [attr.aria-label]=\"copyClose\"></span>\r\n    </div>\r\n  </div>\r\n</section>\r\n", styles: ["[class^=icon-],[class*=\" icon-\"]{font-family:icons,icomoon,sans-serif!important;speak:none;font-style:normal;font-weight:400;font-feature-settings:normal;font-variant:normal;text-transform:none;line-height:1;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale}.icon-chevron-up1:before{content:\"\\e939\"}.icon-chevron-down1:before{content:\"\\e93a\"}.icon-alert:before{content:\"\\e900\"}.icon-confirmation:before{content:\"\\e905\"}.icon-add:before{content:\"\\e901\"}.icon-register:before{content:\"\\e801\"}.icon-disconnect:before{content:\"\\e8bd\"}.icon-cross2:before{content:\"\\e946\"}.icon-chevron-up:before{content:\"\\e94a\"}.icon-chevron-down:before{content:\"\\e94b\"}.icon-custom-acrobat:before{content:\"\\e9ff\"}.icon-custom-question:before{content:\"\\ea04\"}.icon-search:before{content:\"\\e922\"}:root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.alert.common.component{display:flex;flex-direction:row;margin:0}.alert--icon{flex:1 1 auto;display:flex;justify-content:flex-end;align-items:flex-start;margin-top:12px;min-width:22px}.icon-alert:before{font-size:22px;color:#4a4a4a}.alert--content{flex:100 100 auto;display:flex;flex-direction:column;max-width:1132px}.alert--controls{flex:1 1 auto;min-width:22px}.control--close{margin-top:12px;cursor:pointer}.icon-cross2:before{font-size:22px;color:#4a4a4a}.container{flex:1 1 auto;width:100%;max-width:1100px;padding:12px 0}.alert.common.component ::ng-deep p{color:#4a4a4a;font-family:Arial,Helvetica Neue,Helvetica,sans-serif;font-size:14px;line-height:20px;margin-bottom:10px}.alert.common.component ::ng-deep strong{font-weight:700}\n"], dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AlertComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-alert-common', template: "<section class=\"alert common component\" [ngStyle]=\"{ 'background-color': backgroundColor }\" *ngIf=\"isOpen\">\r\n  <div class=alert--icon>\r\n    <span class=\"status-icon icon-alert\" aria-label=\"icon-alert\"></span>\r\n  </div>\r\n  <div class=alert--content>\r\n    <div class=container [innerHTML]=\"alert.Message\"></div>\r\n  </div>\r\n  <div class=alert--controls>\r\n    <div class=control--close (click)=\"onClose($event)\">\r\n      <span class=\"status-icon icon-cross2\" [attr.aria-label]=\"copyClose\"></span>\r\n    </div>\r\n  </div>\r\n</section>\r\n", styles: ["[class^=icon-],[class*=\" icon-\"]{font-family:icons,icomoon,sans-serif!important;speak:none;font-style:normal;font-weight:400;font-feature-settings:normal;font-variant:normal;text-transform:none;line-height:1;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale}.icon-chevron-up1:before{content:\"\\e939\"}.icon-chevron-down1:before{content:\"\\e93a\"}.icon-alert:before{content:\"\\e900\"}.icon-confirmation:before{content:\"\\e905\"}.icon-add:before{content:\"\\e901\"}.icon-register:before{content:\"\\e801\"}.icon-disconnect:before{content:\"\\e8bd\"}.icon-cross2:before{content:\"\\e946\"}.icon-chevron-up:before{content:\"\\e94a\"}.icon-chevron-down:before{content:\"\\e94b\"}.icon-custom-acrobat:before{content:\"\\e9ff\"}.icon-custom-question:before{content:\"\\ea04\"}.icon-search:before{content:\"\\e922\"}:root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.alert.common.component{display:flex;flex-direction:row;margin:0}.alert--icon{flex:1 1 auto;display:flex;justify-content:flex-end;align-items:flex-start;margin-top:12px;min-width:22px}.icon-alert:before{font-size:22px;color:#4a4a4a}.alert--content{flex:100 100 auto;display:flex;flex-direction:column;max-width:1132px}.alert--controls{flex:1 1 auto;min-width:22px}.control--close{margin-top:12px;cursor:pointer}.icon-cross2:before{font-size:22px;color:#4a4a4a}.container{flex:1 1 auto;width:100%;max-width:1100px;padding:12px 0}.alert.common.component ::ng-deep p{color:#4a4a4a;font-family:Arial,Helvetica Neue,Helvetica,sans-serif;font-size:14px;line-height:20px;margin-bottom:10px}.alert.common.component ::ng-deep strong{font-weight:700}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { alert: [{
                type: Input
            }], closeEvent: [{
                type: Output
            }] } });

class AlertModule {
}
AlertModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AlertModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AlertModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: AlertModule, declarations: [AlertComponent], imports: [CommonModule], exports: [AlertComponent] });
AlertModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AlertModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AlertModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [AlertComponent],
                    exports: [AlertComponent]
                }]
        }] });

var copy = {
    label: 'MENU BUTTON'
};

// tslint:disable: no-output-rename
class PopupMenuComponent {
    constructor(appLoggerService = AppLoggerService.nullLogger) {
        this.appLoggerService = appLoggerService;
        this.menuId = 'popup-menu-' + UUID();
        this.menuClassName = '';
        this.menuRole = 'menu'; // suggested options: 'menu', 'listbox'
        this.menuTabIndex = -1;
        this.hrClassName = '';
        this.optionClassName = '';
        this.buttonId = 'popup-menu-button-' + UUID();
        this.buttonClassName = '';
        this.buttonType = 'button';
        this.buttonRole = 'link';
        this.isShowingMenu = false;
        this.isHovering = {};
        this.buttonClickEvent = new EventEmitter();
        this.buttonMouseenterEvent = new EventEmitter();
        this.buttonMouseleaveEvent = new EventEmitter();
        this.menuOpenEvent = new EventEmitter();
        this.menuCloseEvent = new EventEmitter();
        this.menuFocusEvent = new EventEmitter();
        this.menuKeyDownEvent = new EventEmitter();
        this.menuClickEvent = new EventEmitter();
        this.optionClickEvent = new EventEmitter();
        this.optionMouseenterEvent = new EventEmitter();
        this.optionMouseleaveEvent = new EventEmitter();
    }
    get buttonClassNames() {
        return /btn-/.test(this.buttonClassName) ? this.buttonClassName : 'btn-text ' + this.buttonClassName;
    }
    get buttonLabel() {
        if (this.buttonTemplate) {
            return;
        }
        return this.label ? this.label : copy.label;
    }
    ngOnInit() { }
    /** an exposed method of changing the menu show state - the logic here allows click outside directive to operative to close the popup */
    toggleIsShowingMenu(value = !this.isShowingMenu) {
        if (value && !this.isShowingMenu) {
            this.hasBubbled = false;
            this.menuOpenEvent.emit(null);
            this.isShowingMenu = value;
        }
        else if (!value && this.isShowingMenu) {
            if (this.hasBubbled) {
                this.menuCloseEvent.emit(null);
                this.isShowingMenu = value;
            }
            else {
                this.hasBubbled = true;
            }
        }
    }
    // MARK -- BUTTON EVENTS
    onButtonClick(event) {
        this.toggleIsShowingMenu();
        this.buttonClickEvent.emit(event);
    }
    onButtonMouseenter(event) {
        this.buttonMouseenterEvent.emit(event);
    }
    onButtonMouseleave(event) {
        this.buttonMouseleaveEvent.emit(event);
    }
    // MARK -- MENU EVENTS
    onMenuFocus(event) {
        this.menuFocusEvent.emit(event);
    }
    onMenuKeyDown(event) {
        this.menuKeyDownEvent.emit(event);
    }
    onMenuClick(event) {
        this.menuClickEvent.emit(event);
    }
    onOptionClick(option) {
        this.appLoggerService.debug('onOptionClick', option);
        this.optionClickEvent.emit(option);
        this.toggleIsShowingMenu(false);
    }
    onOptionMouseenter(option) {
        this.isHovering[option.id] = true;
        this.optionMouseenterEvent.emit(option);
    }
    onOptionMouseleave(option) {
        this.isHovering[option.id] = false;
        this.optionMouseleaveEvent.emit(option);
    }
}
PopupMenuComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PopupMenuComponent, deps: [{ token: AppLoggerService, optional: true }], target: i0.ɵɵFactoryTarget.Component });
PopupMenuComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: PopupMenuComponent, selector: "app-popup-menu", inputs: { menuId: "menuId", menuClassName: "menuClassName", menuRole: "menuRole", menuTabIndex: "menuTabIndex", hrClassName: "hrClassName", optionClassName: "optionClassName", options: "options", buttonId: "buttonId", buttonClassName: "buttonClassName", buttonType: "buttonType", buttonRole: "buttonRole", label: "label", buttonTemplate: "buttonTemplate" }, outputs: { buttonClickEvent: "buttonClick", buttonMouseenterEvent: "buttonMouseenter", buttonMouseleaveEvent: "buttonMouseleave", menuOpenEvent: "menuOpen", menuCloseEvent: "menuClose", menuFocusEvent: "menuFocus", menuKeyDownEvent: "menuKeyDown", menuClickEvent: "menuClick", optionClickEvent: "optionClick", optionMouseenterEvent: "optionMouseenter", optionMouseleaveEvent: "optionMouseleave" }, ngImport: i0, template: "<button type={{buttonType}}\r\n        attr.role={{buttonRole}}\r\n        attr.aria-label={{buttonLabel}}\r\n        attr.aria-haspopup=\"popupRole\"\r\n        attr.aria-controls={{menuId}}\r\n        attr.aria-expanded={{isShowingMenu}}\r\n        class=\"btn popup-menu-button {{buttonClassNames}}\"\r\n        id={{buttonId}}\r\n        (click)=\"onButtonClick($event)\"\r\n        (mouseenter)=\"onButtonMouseenter($event)\"\r\n        (mouseleave)=\"onButtonMouseleave($event)\">\r\n  <span *ngIf=\"!buttonTemplate else buttonTemplateOutlet\">{{buttonLabel}}</span>\r\n  <ng-template #buttonTemplateOutlet>\r\n    <ng-container [ngTemplateOutlet]=\"buttonTemplate\"></ng-container>\r\n  </ng-template>\r\n</button>\r\n\r\n<div class=\"popup-menu-wrapper\"\r\n     (appClickOutside)=\"toggleIsShowingMenu(false)\"\r\n     attr.aria-labbelledBy={{buttonId}}\r\n     attr.aria-hidden=\"{{ '' + !isShowingMenu }}\"\r\n     [hidden]=\"!isShowingMenu\">\r\n  <ul id={{menuId}}\r\n      class=\"popup-menu-list hidden {{menuClassName}}\"\r\n      role=\"menuRole\"\r\n      tabindex=\"menuTabIndex\"\r\n      (focus)=\"onMenuFocus($event)\"\r\n      (keydown)=\"onMenuKeyDown($event)\"\r\n      (click)=\"onMenuClick($event)\">\r\n    <ng-container *ngFor=\"let option of options; let i = index\">\r\n      <li id=\"{{menuId}}-{{i}}\"\r\n          class=\"popup-menu-option {{optionClassName}} {{isHovering[option.id] ? 'hover': ''}}\"\r\n          role=\"option\"\r\n          (mouseenter)=\"onOptionMouseenter(option)\"\r\n          (mouseleave)=\"onOptionMouseleave(option)\"\r\n          (click)=\"onOptionClick(option)\">\r\n        <span class=\"option copy\" *ngIf=\"option.value else template\">{{option.value}}</span>\r\n        <ng-template #template>\r\n          <ng-container [ngTemplateOutlet]=\"option.template\"></ng-container>\r\n        </ng-template>\r\n      </li>\r\n      <li *ngIf=\"i < options.length - 1\">\r\n        <hr class=\"option-hr {{hrClassName}}\" />\r\n      </li>\r\n    </ng-container>\r\n  </ul>\r\n</div>\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.btn{cursor:pointer;white-space:normal}.btn.btn-primary{font-weight:600;line-height:24px;text-align:center;color:#fff;background-color:#0059a4;box-shadow:none;border-color:transparent;border-radius:2px;padding:15px 20px 16px}.btn.btn-primary.rounded-left-0{border-top-left-radius:0;border-bottom-left-radius:0}.btn.btn-primary:not([disabled]):not(.disabled):hover,.btn.btn-primary:not([disabled]):not(.disabled):focus{background-color:#103870;box-shadow:none;border-color:transparent}.btn.btn-primary:not([disabled]):not(.disabled):active,.btn.btn-primary:not([disabled]):not(.disabled).active{background-color:#103870;box-shadow:none;border-color:transparent}.btn.btn-primary.disabled,.btn.btn-primary[disabled]{color:#4a4a4a;background-color:#d8d8d8;box-sizing:border-box;cursor:not-allowed;border:1px solid #d8d8d8}.btn.btn-secondary{font-size:14px;font-weight:600;line-height:19px;text-align:center;color:#0059a4;background-color:transparent;box-sizing:border-box;box-shadow:none;border:2px solid #0059a4;border-radius:2px;padding:10px 20px}.btn.btn-secondary:not([disabled]):not(.disabled):hover,.btn.btn-secondary:not([disabled]):not(.disabled):focus{background-color:#103870;box-shadow:none;border-color:transparent;color:#fff}.btn.btn-secondary:not([disabled]):not(.disabled):active,.btn.btn-secondary:not([disabled]):not(.disabled).active{background-color:#103870;box-shadow:none;border-color:transparent;color:#fff}.btn.btn-secondary.disabled,.btn.btn-secondary[disabled]{color:#737373;cursor:not-allowed;border:2px solid #d8d8d8}.btn.btn-text{font-size:14px;font-weight:600;line-height:19px;color:#0059a4;background-color:transparent;border-width:0;border-color:transparent;box-shadow:none;padding:0;border-radius:0;position:relative;vertical-align:inherit;text-align:left}.btn.btn-text:after{content:\"\";position:absolute;bottom:0;left:0;width:0%;border-bottom:2px solid #0059a4;transition:.2s}.btn.btn-text:not([disabled]):not(.disabled):hover,.btn.btn-text:not([disabled]):not(.disabled):active,.btn.btn-text:not([disabled]):not(.disabled).active,.btn.btn-text:not([disabled]):not(.disabled):focus{color:#103870}.btn.btn-text:not([disabled]):not(.disabled):hover:after,.btn.btn-text:not([disabled]):not(.disabled):active:after,.btn.btn-text:not([disabled]):not(.disabled).active:after,.btn.btn-text:not([disabled]):not(.disabled):focus:after{width:100%}.btn.btn-text.disabled,.btn.btn-text[disabled]{color:#737373;cursor:not-allowed}.btn.fixed-width{width:100%}.btn.customized-button,.btn .customized-button:hover{background-color:#0059a4;border-color:#0059a4;color:#fff}.btn.customized-button:not([disabled]):not(.disabled):hover,.btn.customized-button:not([disabled]):not(.disabled):focus,.btn .customized-button:hover:not([disabled]):not(.disabled):hover,.btn .customized-button:hover:not([disabled]):not(.disabled):focus{background-color:#103870;border-color:#103870;color:#fff}.btn.customized-button:not([disabled]):not(.disabled):active,.btn.customized-button:not([disabled]):not(.disabled).active,.btn .customized-button:hover:not([disabled]):not(.disabled):active,.btn .customized-button:hover:not([disabled]):not(.disabled).active{background-color:#0059a4;border-color:#0059a4;color:#fff;box-shadow:none}.btn.customized-button.disabled,.btn.customized-button[disabled],.btn .customized-button:hover.disabled,.btn .customized-button:hover[disabled]{color:#737373;cursor:not-allowed;border:2px solid #d8d8d8;background-color:transparent}.btn.btn-loading{background:#103870;border-color:transparent;color:transparent;position:relative}.btn.btn-loading:hover{color:transparent}.btn.btn-loading:not([disabled]):not(.disabled):hover{color:transparent}.btn.btn-loading:not([disabled]):not(.disabled):active,.btn.btn-loading:not([disabled]):not(.disabled).active{color:transparent}.btn.btn-loading.disabled,.btn.btn-loading[disabled]{color:transparent;cursor:pointer;border:2px solid #103870;opacity:1;background:#103870}.btn.btn-loading .loader-btn{position:absolute;top:calc(50% - 5px);left:calc(50% - 5px);display:inline-block;height:20px}.btn.btn-loading .loader-btn .dot-flashing-btn{position:relative;width:10px;height:10px;border-radius:50%;background-color:#ffffff52;animation:dotFlashingBtn 1s infinite linear alternate;animation-delay:.5s}.btn.btn-loading .loader-btn .dot-flashing-btn:before{content:\"\";display:inline-block;position:absolute;top:0;left:-24px;width:10px;height:10px;border-radius:50%;background-color:#ffffff52;animation:dotFlashingBtn 1s infinite alternate;animation-delay:0s}.btn.btn-loading .loader-btn .dot-flashing-btn:after{content:\"\";display:inline-block;position:absolute;top:0;left:24px;width:10px;height:10px;border-radius:50%;background-color:#ffffff52;animation:dotFlashingBtn 1s infinite alternate;animation-delay:1s}.btn.btn-loading-submit{background:#103870;border-color:#103870;color:transparent;position:relative}.btn.btn-loading-submit:hover{color:transparent}.btn.btn-loading-submit:not([disabled]):not(.disabled):hover{color:transparent}.btn.btn-loading-submit:not([disabled]):not(.disabled):active,.btn.btn-loading-submit:not([disabled]):not(.disabled).active{color:transparent}.btn.btn-loading-submit.disabled,.btn.btn-loading-submit[disabled]{color:transparent;cursor:pointer;border:2px solid #103870;opacity:1;background:#103870}.btn.btn-loading-submit .loader-submit{position:absolute;top:calc(50% - 14px);left:calc(50% - 14px);background-color:transparent;border:5px solid #fff;border-left-color:transparent;border-radius:50%;display:inline-block;width:28px;height:28px;vertical-align:middle;animation:spin 1.2s infinite linear}.btn.btn-loading-check-submit{background:#103870;border-color:#103870;color:transparent;position:relative}.btn.btn-loading-check-submit:hover{color:transparent}.btn.btn-loading-check-submit:not([disabled]):not(.disabled):hover{color:transparent}.btn.btn-loading-check-submit:not([disabled]):not(.disabled):active,.btn.btn-loading-check-submit:not([disabled]):not(.disabled).active{color:transparent}.btn.btn-loading-check-submit.disabled,.btn.btn-loading-check-submit[disabled]{color:transparent;cursor:pointer;border:2px solid #103870;opacity:1;background:#103870}.btn.btn-loading-check-submit .loader-btn{position:absolute;top:calc(50% - 15px);left:calc(50% - 15px)}.btn.btn-loading-check-submit .loader-btn .loader-submit{border:solid 4px #fff;width:30px;margin-top:18px;height:12px;border-top:none;border-right:none;transform:rotate(-45deg);transform-origin:top left;animation:start .5s ease both}.btn.autocomplete-btn{height:57px;font-weight:600;font-size:14px;line-height:19px}@keyframes dotFlashingBtn{0%{background-color:#ffffff52}50%,to{background-color:#fff3}}@keyframes spin{0%{transform:rotate(0)}to{transform:rotate(360deg)}}@keyframes start{0%{opacity:0;width:0;height:0}50%{opacity:1;width:0;height:12px}to{width:30px}}@media (max-width: 767.98px){.btn.autocomplete-btn{width:100%}}.popup-menu-wrapper{border:1px solid #e6e6e6;border-radius:2px;background-color:#fff;box-shadow:1px 1px 13px #d8d8d8;position:absolute;z-index:1000}.popup-menu-list{list-style:none;margin:0;padding:0}.popup-menu-option{padding:20px;cursor:pointer}.popup-menu-option:hover{background-color:#d8d8d8}.option-hr{display:flex;height:1px;margin:0 20px;border:0;border-top:1px solid rgba(0,0,0,.1)}.option.copy{cursor:text}.button{cursor:pointer;outline:none;border-radius:2px;background-color:#fff;border:2px solid #d8d8d8;color:#4a4a4a;text-align:center}.button.disabled,.button[disabled]{color:#4a4a4a;background-color:#d8d8d8;box-sizing:border-box;cursor:not-allowed;border:1px solid #d8d8d8}.button.disabled:hover,.button[disabled]:hover{outline:none;color:#4a4a4a!important;border:none!important;box-shadow:none!important}.button.disabled:focus,.button[disabled]:focus{outline:none;color:#4a4a4a!important;border:none!important;box-shadow:none!important}.button.active{outline:none;border:1px solid rgba(0,89,164,.4);background-color:#e5eef5;box-shadow:inset 0 1px 2px #0000001a,0 1px 4px #0059a433;color:#0059a4;font-weight:600;line-height:22px}.button:focus{outline:none;border:1px solid #0091b6;box-sizing:border-box}.button:hover:not(.active){color:#0059a4;line-height:22px;box-shadow:inset 0 1px 2px #0000001a,0 1px 4px #0059a433;border:1px solid rgba(0,89,164,.3)}\n"], dependencies: [{ kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: ClickOutsideDirective, selector: "[appClickOutside]", outputs: ["appClickOutside"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PopupMenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-popup-menu', template: "<button type={{buttonType}}\r\n        attr.role={{buttonRole}}\r\n        attr.aria-label={{buttonLabel}}\r\n        attr.aria-haspopup=\"popupRole\"\r\n        attr.aria-controls={{menuId}}\r\n        attr.aria-expanded={{isShowingMenu}}\r\n        class=\"btn popup-menu-button {{buttonClassNames}}\"\r\n        id={{buttonId}}\r\n        (click)=\"onButtonClick($event)\"\r\n        (mouseenter)=\"onButtonMouseenter($event)\"\r\n        (mouseleave)=\"onButtonMouseleave($event)\">\r\n  <span *ngIf=\"!buttonTemplate else buttonTemplateOutlet\">{{buttonLabel}}</span>\r\n  <ng-template #buttonTemplateOutlet>\r\n    <ng-container [ngTemplateOutlet]=\"buttonTemplate\"></ng-container>\r\n  </ng-template>\r\n</button>\r\n\r\n<div class=\"popup-menu-wrapper\"\r\n     (appClickOutside)=\"toggleIsShowingMenu(false)\"\r\n     attr.aria-labbelledBy={{buttonId}}\r\n     attr.aria-hidden=\"{{ '' + !isShowingMenu }}\"\r\n     [hidden]=\"!isShowingMenu\">\r\n  <ul id={{menuId}}\r\n      class=\"popup-menu-list hidden {{menuClassName}}\"\r\n      role=\"menuRole\"\r\n      tabindex=\"menuTabIndex\"\r\n      (focus)=\"onMenuFocus($event)\"\r\n      (keydown)=\"onMenuKeyDown($event)\"\r\n      (click)=\"onMenuClick($event)\">\r\n    <ng-container *ngFor=\"let option of options; let i = index\">\r\n      <li id=\"{{menuId}}-{{i}}\"\r\n          class=\"popup-menu-option {{optionClassName}} {{isHovering[option.id] ? 'hover': ''}}\"\r\n          role=\"option\"\r\n          (mouseenter)=\"onOptionMouseenter(option)\"\r\n          (mouseleave)=\"onOptionMouseleave(option)\"\r\n          (click)=\"onOptionClick(option)\">\r\n        <span class=\"option copy\" *ngIf=\"option.value else template\">{{option.value}}</span>\r\n        <ng-template #template>\r\n          <ng-container [ngTemplateOutlet]=\"option.template\"></ng-container>\r\n        </ng-template>\r\n      </li>\r\n      <li *ngIf=\"i < options.length - 1\">\r\n        <hr class=\"option-hr {{hrClassName}}\" />\r\n      </li>\r\n    </ng-container>\r\n  </ul>\r\n</div>\r\n", styles: [":root{--primaryBrand: #ce1141;--primaryColor1: #ce1141}.c-pri-col1{color:var(--primaryColor1)}.c-pri-col2{color:#ba1c3d}.c-pri-col3{color:#a61936}.c-pri-col4{color:#bf002f}.c-pri-col5{color:#0059a4}.c-pri-col6{color:#103870}.c-pri-col7{color:#0091b6}.c-pri-col8{color:#78be20}.c-pri-col9{color:#ffd100}.c-gs-col1{color:#fff}.c-gs-col2{color:#f7f7f7}.c-gs-col3{color:#d8d8d8}.c-gs-col4{color:#8b8b8b}.c-gs-col5{color:#737373}.c-gs-col6{color:#4a4a4a}.c-gs-col7{color:#231f20}.c-gs-col8{color:#000}.btn{cursor:pointer;white-space:normal}.btn.btn-primary{font-weight:600;line-height:24px;text-align:center;color:#fff;background-color:#0059a4;box-shadow:none;border-color:transparent;border-radius:2px;padding:15px 20px 16px}.btn.btn-primary.rounded-left-0{border-top-left-radius:0;border-bottom-left-radius:0}.btn.btn-primary:not([disabled]):not(.disabled):hover,.btn.btn-primary:not([disabled]):not(.disabled):focus{background-color:#103870;box-shadow:none;border-color:transparent}.btn.btn-primary:not([disabled]):not(.disabled):active,.btn.btn-primary:not([disabled]):not(.disabled).active{background-color:#103870;box-shadow:none;border-color:transparent}.btn.btn-primary.disabled,.btn.btn-primary[disabled]{color:#4a4a4a;background-color:#d8d8d8;box-sizing:border-box;cursor:not-allowed;border:1px solid #d8d8d8}.btn.btn-secondary{font-size:14px;font-weight:600;line-height:19px;text-align:center;color:#0059a4;background-color:transparent;box-sizing:border-box;box-shadow:none;border:2px solid #0059a4;border-radius:2px;padding:10px 20px}.btn.btn-secondary:not([disabled]):not(.disabled):hover,.btn.btn-secondary:not([disabled]):not(.disabled):focus{background-color:#103870;box-shadow:none;border-color:transparent;color:#fff}.btn.btn-secondary:not([disabled]):not(.disabled):active,.btn.btn-secondary:not([disabled]):not(.disabled).active{background-color:#103870;box-shadow:none;border-color:transparent;color:#fff}.btn.btn-secondary.disabled,.btn.btn-secondary[disabled]{color:#737373;cursor:not-allowed;border:2px solid #d8d8d8}.btn.btn-text{font-size:14px;font-weight:600;line-height:19px;color:#0059a4;background-color:transparent;border-width:0;border-color:transparent;box-shadow:none;padding:0;border-radius:0;position:relative;vertical-align:inherit;text-align:left}.btn.btn-text:after{content:\"\";position:absolute;bottom:0;left:0;width:0%;border-bottom:2px solid #0059a4;transition:.2s}.btn.btn-text:not([disabled]):not(.disabled):hover,.btn.btn-text:not([disabled]):not(.disabled):active,.btn.btn-text:not([disabled]):not(.disabled).active,.btn.btn-text:not([disabled]):not(.disabled):focus{color:#103870}.btn.btn-text:not([disabled]):not(.disabled):hover:after,.btn.btn-text:not([disabled]):not(.disabled):active:after,.btn.btn-text:not([disabled]):not(.disabled).active:after,.btn.btn-text:not([disabled]):not(.disabled):focus:after{width:100%}.btn.btn-text.disabled,.btn.btn-text[disabled]{color:#737373;cursor:not-allowed}.btn.fixed-width{width:100%}.btn.customized-button,.btn .customized-button:hover{background-color:#0059a4;border-color:#0059a4;color:#fff}.btn.customized-button:not([disabled]):not(.disabled):hover,.btn.customized-button:not([disabled]):not(.disabled):focus,.btn .customized-button:hover:not([disabled]):not(.disabled):hover,.btn .customized-button:hover:not([disabled]):not(.disabled):focus{background-color:#103870;border-color:#103870;color:#fff}.btn.customized-button:not([disabled]):not(.disabled):active,.btn.customized-button:not([disabled]):not(.disabled).active,.btn .customized-button:hover:not([disabled]):not(.disabled):active,.btn .customized-button:hover:not([disabled]):not(.disabled).active{background-color:#0059a4;border-color:#0059a4;color:#fff;box-shadow:none}.btn.customized-button.disabled,.btn.customized-button[disabled],.btn .customized-button:hover.disabled,.btn .customized-button:hover[disabled]{color:#737373;cursor:not-allowed;border:2px solid #d8d8d8;background-color:transparent}.btn.btn-loading{background:#103870;border-color:transparent;color:transparent;position:relative}.btn.btn-loading:hover{color:transparent}.btn.btn-loading:not([disabled]):not(.disabled):hover{color:transparent}.btn.btn-loading:not([disabled]):not(.disabled):active,.btn.btn-loading:not([disabled]):not(.disabled).active{color:transparent}.btn.btn-loading.disabled,.btn.btn-loading[disabled]{color:transparent;cursor:pointer;border:2px solid #103870;opacity:1;background:#103870}.btn.btn-loading .loader-btn{position:absolute;top:calc(50% - 5px);left:calc(50% - 5px);display:inline-block;height:20px}.btn.btn-loading .loader-btn .dot-flashing-btn{position:relative;width:10px;height:10px;border-radius:50%;background-color:#ffffff52;animation:dotFlashingBtn 1s infinite linear alternate;animation-delay:.5s}.btn.btn-loading .loader-btn .dot-flashing-btn:before{content:\"\";display:inline-block;position:absolute;top:0;left:-24px;width:10px;height:10px;border-radius:50%;background-color:#ffffff52;animation:dotFlashingBtn 1s infinite alternate;animation-delay:0s}.btn.btn-loading .loader-btn .dot-flashing-btn:after{content:\"\";display:inline-block;position:absolute;top:0;left:24px;width:10px;height:10px;border-radius:50%;background-color:#ffffff52;animation:dotFlashingBtn 1s infinite alternate;animation-delay:1s}.btn.btn-loading-submit{background:#103870;border-color:#103870;color:transparent;position:relative}.btn.btn-loading-submit:hover{color:transparent}.btn.btn-loading-submit:not([disabled]):not(.disabled):hover{color:transparent}.btn.btn-loading-submit:not([disabled]):not(.disabled):active,.btn.btn-loading-submit:not([disabled]):not(.disabled).active{color:transparent}.btn.btn-loading-submit.disabled,.btn.btn-loading-submit[disabled]{color:transparent;cursor:pointer;border:2px solid #103870;opacity:1;background:#103870}.btn.btn-loading-submit .loader-submit{position:absolute;top:calc(50% - 14px);left:calc(50% - 14px);background-color:transparent;border:5px solid #fff;border-left-color:transparent;border-radius:50%;display:inline-block;width:28px;height:28px;vertical-align:middle;animation:spin 1.2s infinite linear}.btn.btn-loading-check-submit{background:#103870;border-color:#103870;color:transparent;position:relative}.btn.btn-loading-check-submit:hover{color:transparent}.btn.btn-loading-check-submit:not([disabled]):not(.disabled):hover{color:transparent}.btn.btn-loading-check-submit:not([disabled]):not(.disabled):active,.btn.btn-loading-check-submit:not([disabled]):not(.disabled).active{color:transparent}.btn.btn-loading-check-submit.disabled,.btn.btn-loading-check-submit[disabled]{color:transparent;cursor:pointer;border:2px solid #103870;opacity:1;background:#103870}.btn.btn-loading-check-submit .loader-btn{position:absolute;top:calc(50% - 15px);left:calc(50% - 15px)}.btn.btn-loading-check-submit .loader-btn .loader-submit{border:solid 4px #fff;width:30px;margin-top:18px;height:12px;border-top:none;border-right:none;transform:rotate(-45deg);transform-origin:top left;animation:start .5s ease both}.btn.autocomplete-btn{height:57px;font-weight:600;font-size:14px;line-height:19px}@keyframes dotFlashingBtn{0%{background-color:#ffffff52}50%,to{background-color:#fff3}}@keyframes spin{0%{transform:rotate(0)}to{transform:rotate(360deg)}}@keyframes start{0%{opacity:0;width:0;height:0}50%{opacity:1;width:0;height:12px}to{width:30px}}@media (max-width: 767.98px){.btn.autocomplete-btn{width:100%}}.popup-menu-wrapper{border:1px solid #e6e6e6;border-radius:2px;background-color:#fff;box-shadow:1px 1px 13px #d8d8d8;position:absolute;z-index:1000}.popup-menu-list{list-style:none;margin:0;padding:0}.popup-menu-option{padding:20px;cursor:pointer}.popup-menu-option:hover{background-color:#d8d8d8}.option-hr{display:flex;height:1px;margin:0 20px;border:0;border-top:1px solid rgba(0,0,0,.1)}.option.copy{cursor:text}.button{cursor:pointer;outline:none;border-radius:2px;background-color:#fff;border:2px solid #d8d8d8;color:#4a4a4a;text-align:center}.button.disabled,.button[disabled]{color:#4a4a4a;background-color:#d8d8d8;box-sizing:border-box;cursor:not-allowed;border:1px solid #d8d8d8}.button.disabled:hover,.button[disabled]:hover{outline:none;color:#4a4a4a!important;border:none!important;box-shadow:none!important}.button.disabled:focus,.button[disabled]:focus{outline:none;color:#4a4a4a!important;border:none!important;box-shadow:none!important}.button.active{outline:none;border:1px solid rgba(0,89,164,.4);background-color:#e5eef5;box-shadow:inset 0 1px 2px #0000001a,0 1px 4px #0059a433;color:#0059a4;font-weight:600;line-height:22px}.button:focus{outline:none;border:1px solid #0091b6;box-sizing:border-box}.button:hover:not(.active){color:#0059a4;line-height:22px;box-shadow:inset 0 1px 2px #0000001a,0 1px 4px #0059a433;border:1px solid rgba(0,89,164,.3)}\n"] }]
        }], ctorParameters: function () {
        return [{ type: AppLoggerService, decorators: [{
                        type: Optional
                    }] }];
    }, propDecorators: { menuId: [{
                type: Input
            }], menuClassName: [{
                type: Input
            }], menuRole: [{
                type: Input
            }], menuTabIndex: [{
                type: Input
            }], hrClassName: [{
                type: Input
            }], optionClassName: [{
                type: Input
            }], options: [{
                type: Input
            }], buttonId: [{
                type: Input
            }], buttonClassName: [{
                type: Input
            }], buttonType: [{
                type: Input
            }], buttonRole: [{
                type: Input
            }], label: [{
                type: Input
            }], buttonTemplate: [{
                type: Input
            }], buttonClickEvent: [{
                type: Output,
                args: ['buttonClick']
            }], buttonMouseenterEvent: [{
                type: Output,
                args: ['buttonMouseenter']
            }], buttonMouseleaveEvent: [{
                type: Output,
                args: ['buttonMouseleave']
            }], menuOpenEvent: [{
                type: Output,
                args: ['menuOpen']
            }], menuCloseEvent: [{
                type: Output,
                args: ['menuClose']
            }], menuFocusEvent: [{
                type: Output,
                args: ['menuFocus']
            }], menuKeyDownEvent: [{
                type: Output,
                args: ['menuKeyDown']
            }], menuClickEvent: [{
                type: Output,
                args: ['menuClick']
            }], optionClickEvent: [{
                type: Output,
                args: ['optionClick']
            }], optionMouseenterEvent: [{
                type: Output,
                args: ['optionMouseenter']
            }], optionMouseleaveEvent: [{
                type: Output,
                args: ['optionMouseleave']
            }] } });

class PopupMenuModule {
}
PopupMenuModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PopupMenuModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PopupMenuModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: PopupMenuModule, declarations: [PopupMenuComponent,
        ClickOutsideDirective], imports: [CommonModule], exports: [PopupMenuComponent] });
PopupMenuModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PopupMenuModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PopupMenuModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [
                        PopupMenuComponent,
                        ClickOutsideDirective
                    ],
                    exports: [
                        PopupMenuComponent
                    ]
                }]
        }] });

class ScriptLoaderComponent {
    constructor(ngZone) {
        this.ngZone = ngZone;
    }
    ngOnInit() {
        if (this.isScriptValid()) {
            this.loadScript();
        }
    }
    isScriptValid() {
        return ((this.script || this.src) && this.scriptId);
    }
    loadScript() {
        this.ngZone.runOutsideAngular(() => {
            if (!(this.isScriptValid()) || document.getElementById(this.scriptId)) {
                return; // invalid or already loaded
            }
            const s = document.createElement('script');
            s.type = 'text/javascript';
            s.id = this.scriptId;
            if (this.script) {
                s.innerText = this.script;
            }
            if (this.src) {
                s.src = this.src;
            }
            if (this.onLoad) {
                s.onload = this.onLoad;
            }
            // tslint:disable-next-line: forin
            for (const key in this.attributes) {
                s.setAttribute(key, this.attributes[key]);
            }
            document.getElementsByTagName('head')[0].appendChild(s);
        });
    }
}
ScriptLoaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ScriptLoaderComponent, deps: [{ token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
ScriptLoaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: ScriptLoaderComponent, selector: "app-script-loader", inputs: { scriptId: "scriptId", script: "script", src: "src", onLoad: "onLoad", attributes: "attributes" }, ngImport: i0, template: "<!-- script-loader -->\r\n", styles: [""] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ScriptLoaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-script-loader', template: "<!-- script-loader -->\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.NgZone }]; }, propDecorators: { scriptId: [{
                type: Input
            }], script: [{
                type: Input
            }], src: [{
                type: Input
            }], onLoad: [{
                type: Input
            }], attributes: [{
                type: Input
            }] } });

class ScriptLoaderModule {
}
ScriptLoaderModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ScriptLoaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ScriptLoaderModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: ScriptLoaderModule, declarations: [ScriptLoaderComponent], imports: [CommonModule], exports: [ScriptLoaderComponent] });
ScriptLoaderModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ScriptLoaderModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ScriptLoaderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [
                        ScriptLoaderComponent
                    ],
                    exports: [
                        ScriptLoaderComponent
                    ]
                }]
        }] });

class GtmLoaderComponent {
    constructor() {
        this.scriptId = 'google-analytics';
    }
    get isReadyToLoadGTM() {
        return !!this.gtmId;
    }
    get script() {
        return ''
            + `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':`
            + `new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],`
            + `j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=`
            + `'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);`
            + `})(window,document,'script','dataLayer','${this.gtmId}');`;
    }
}
GtmLoaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GtmLoaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
GtmLoaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: GtmLoaderComponent, selector: "app-gtm-loader", inputs: { gtmId: "gtmId" }, ngImport: i0, template: "<!-- adds gtm script tag programmatically from ngOnInit() -->\r\n<ng-container *ngIf=\"isReadyToLoadGTM\">\r\n  <app-script-loader [script]=\"script\" [scriptId]=\"scriptId\"></app-script-loader>\r\n</ng-container>\r\n", dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: ScriptLoaderComponent, selector: "app-script-loader", inputs: ["scriptId", "script", "src", "onLoad", "attributes"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GtmLoaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-gtm-loader', template: "<!-- adds gtm script tag programmatically from ngOnInit() -->\r\n<ng-container *ngIf=\"isReadyToLoadGTM\">\r\n  <app-script-loader [script]=\"script\" [scriptId]=\"scriptId\"></app-script-loader>\r\n</ng-container>\r\n" }]
        }], ctorParameters: function () { return []; }, propDecorators: { gtmId: [{
                type: Input
            }] } });

class GtmLoaderModule {
}
GtmLoaderModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GtmLoaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
GtmLoaderModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: GtmLoaderModule, declarations: [GtmLoaderComponent], imports: [CommonModule,
        ScriptLoaderModule], exports: [GtmLoaderComponent] });
GtmLoaderModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GtmLoaderModule, imports: [CommonModule,
        ScriptLoaderModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GtmLoaderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        ScriptLoaderModule
                    ],
                    declarations: [GtmLoaderComponent],
                    exports: [GtmLoaderComponent]
                }]
        }] });

class ForeSeeLoaderComponent {
    constructor(appConfigService) {
        this.appConfigService = appConfigService;
        this.foreseeScriptId = 'foresee-file';
        this.foreseeInitScriptId = 'foresee-init';
    }
    get src() {
        return this.appConfigService.config.forseeUrl;
    }
    get isReadyToLoadForeSee() {
        return !!this.appConfigService.config.forseeUrl;
    }
    get script() {
        return ''
            + `(function (g) {`
            + `g['fsReady'] || (g['fsReady'] = function () { var aT = '__' + 'fsReady' + '_stk__'; `
            + `g[aT] = g[aT] || []; g[aT].push(arguments); }); `
            + `})(window);`;
    }
    get attributes() {
        return {
            'async': 'true',
            'data-vendor': 'fs',
            'data-role': 'gateway'
        };
    }
}
ForeSeeLoaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ForeSeeLoaderComponent, deps: [{ token: AppConfigService }], target: i0.ɵɵFactoryTarget.Component });
ForeSeeLoaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: ForeSeeLoaderComponent, selector: "app-foresee-loader", ngImport: i0, template: "<!-- adds gtm script tag programmatically from ngOnInit() -->\r\n<ng-container *ngIf=\"isReadyToLoadForeSee\">\r\n  <app-script-loader [src]=\"src\" [scriptId]=\"foreseeScriptId\" [attributes]=\"attributes\"></app-script-loader>\r\n  <app-script-loader [script]=\"script\" [scriptId]=\"foreseeInitScriptId\" ></app-script-loader>\r\n</ng-container>\r\n\r\n", dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: ScriptLoaderComponent, selector: "app-script-loader", inputs: ["scriptId", "script", "src", "onLoad", "attributes"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ForeSeeLoaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-foresee-loader', template: "<!-- adds gtm script tag programmatically from ngOnInit() -->\r\n<ng-container *ngIf=\"isReadyToLoadForeSee\">\r\n  <app-script-loader [src]=\"src\" [scriptId]=\"foreseeScriptId\" [attributes]=\"attributes\"></app-script-loader>\r\n  <app-script-loader [script]=\"script\" [scriptId]=\"foreseeInitScriptId\" ></app-script-loader>\r\n</ng-container>\r\n\r\n" }]
        }], ctorParameters: function () { return [{ type: AppConfigService }]; } });

class ForeSeeLoaderModule {
}
ForeSeeLoaderModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ForeSeeLoaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ForeSeeLoaderModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: ForeSeeLoaderModule, declarations: [ForeSeeLoaderComponent], imports: [CommonModule,
        ScriptLoaderModule], exports: [ForeSeeLoaderComponent] });
ForeSeeLoaderModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ForeSeeLoaderModule, imports: [CommonModule,
        ScriptLoaderModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ForeSeeLoaderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        ScriptLoaderModule
                    ],
                    declarations: [ForeSeeLoaderComponent],
                    exports: [ForeSeeLoaderComponent],
                }]
        }] });

class BootstrapBreakpointDetectorComponent {
    constructor(elementRef, window /* TODO: WindowWrapper usage */, platformId, bootstrapBreakpointService) {
        this.elementRef = elementRef;
        this.window = window;
        this.platformId = platformId;
        this.bootstrapBreakpointService = bootstrapBreakpointService;
        this.prefix = 'is-';
        this.sizes = [
            { id: SCREEN_SIZE.XS, css: 'd-block d-sm-none' },
            { id: SCREEN_SIZE.SM, css: 'd-none d-sm-block d-md-none' },
            { id: SCREEN_SIZE.MD, css: 'd-none d-md-block d-lg-none' },
            { id: SCREEN_SIZE.LG, css: 'd-none d-lg-block d-xl-none' },
            { id: SCREEN_SIZE.XL, css: 'd-none d-xl-block' },
        ];
        this.bootstrapBreakpointService.mount();
    }
    onResize() {
        this.detectScreenSize();
    }
    ngAfterViewInit() {
        this.detectScreenSize();
    }
    detectScreenSize() {
        if (isPlatformBrowser(this.platformId)) {
            const currentSize = this.sizes.find((x) => {
                // get the HTML element
                const el = this.elementRef.nativeElement.querySelector(`.${this.prefix}${x.id}`);
                // check its display property value
                return this.window.getComputedStyle(el).display !== 'none';
            });
            this.bootstrapBreakpointService.onResize(currentSize.id);
        }
    }
}
BootstrapBreakpointDetectorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BootstrapBreakpointDetectorComponent, deps: [{ token: i0.ElementRef }, { token: WINDOW }, { token: PLATFORM_ID }, { token: BootstrapBreakpointService }], target: i0.ɵɵFactoryTarget.Component });
BootstrapBreakpointDetectorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: BootstrapBreakpointDetectorComponent, selector: "app-bootstrap-breakpoint-detector", host: { listeners: { "window:resize": "onResize()" } }, ngImport: i0, template: "<!-- https://scotch.io/tutorials/detect-responsive-screen-sizes-in-angular -->\r\n<div *ngFor=\"let s of sizes\" class=\"{{s.css + ' ' + (prefix + s.id) }}\"></div>\r\n", styles: ["::ng-deep app-bootstrap-breakpoint-detector{position:fixed}\n"], dependencies: [{ kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BootstrapBreakpointDetectorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-bootstrap-breakpoint-detector', template: "<!-- https://scotch.io/tutorials/detect-responsive-screen-sizes-in-angular -->\r\n<div *ngFor=\"let s of sizes\" class=\"{{s.css + ' ' + (prefix + s.id) }}\"></div>\r\n", styles: ["::ng-deep app-bootstrap-breakpoint-detector{position:fixed}\n"] }]
        }], ctorParameters: function () {
        return [{ type: i0.ElementRef }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [WINDOW]
                    }] }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [PLATFORM_ID]
                    }] }, { type: BootstrapBreakpointService }];
    }, propDecorators: { onResize: [{
                type: HostListener,
                args: ['window:resize', []]
            }] } });

class BootstrapBreakpointDetectorModule {
}
BootstrapBreakpointDetectorModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BootstrapBreakpointDetectorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
BootstrapBreakpointDetectorModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: BootstrapBreakpointDetectorModule, declarations: [BootstrapBreakpointDetectorComponent], imports: [CommonModule], exports: [BootstrapBreakpointDetectorComponent] });
BootstrapBreakpointDetectorModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BootstrapBreakpointDetectorModule, providers: [
        BootstrapBreakpointService
    ], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BootstrapBreakpointDetectorModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [
                        BootstrapBreakpointDetectorComponent
                    ],
                    providers: [
                        BootstrapBreakpointService
                    ],
                    exports: [
                        BootstrapBreakpointDetectorComponent
                    ]
                }]
        }] });

class FeatureFlagService {
    constructor(appConfig) {
        this.appConfig = appConfig;
        this.featureFlags = null;
        this.featureFlagsSubject = new BehaviorSubject(null);
        this.featureFlags$ = this.featureFlagsSubject.asObservable();
        this.featureFlags$.subscribe(newValue => (this.featureFlags = newValue));
    }
    hasFlag(featureName) {
        if (!this.featureFlags) {
            return false;
        }
        return get(this.featureFlags, featureName, false);
    }
    getFeatureFlags(reload = false) {
        if (reload) {
            // wait for OpCo if it's not available yet
            const waitForOpCo = (this.appConfig.config && this.appConfig.config.opCo != null)
                ? of(this.appConfig.config)
                : this.appConfig.config$.pipe(first(config => config.opCo != null));
            const getFlags = this.readFeatureFlags()
                .pipe(first(), map(globalFlags => this.mergeAppFeatureFlags(globalFlags, this.appFeatureFlags)), tap(flags => this.featureFlagsSubject.next(flags || {})));
            return waitForOpCo.pipe(switchMap(() => getFlags));
        }
        else {
            return of(this.featureFlags);
        }
    }
    mergeAppFeatureFlags(globalFlags = {}, appFeatureFlags = {}) {
        let featureFlags = globalFlags;
        featureFlags = merge(globalFlags, appFeatureFlags.common, appFeatureFlags[this.appConfig.config.opCo]);
        return featureFlags;
    }
    /**
     * Used by APP_INITIALIZER, has to return a Promise that resolves once initialization is complete.
     * @param appFeatureFlags
     */
    initialize(appFeatureFlags = {}) {
        this.appFeatureFlags = appFeatureFlags;
        return this.getFeatureFlags(true).toPromise();
    }
}

const DEFAULT_FEATURE_FLAGS_ENDPOINT = '/api/Services/MyAccountService.svc/GetFeatureFlags';
class EuFeatureFlagService extends FeatureFlagService {
    constructor(httpClient, appConfig) {
        super(appConfig);
        this.httpClient = httpClient;
    }
    readFeatureFlags() {
        return this.httpClient
            .get(DEFAULT_FEATURE_FLAGS_ENDPOINT)
            .pipe(catchError(() => of({})));
    }
}
EuFeatureFlagService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EuFeatureFlagService, deps: [{ token: i1.HttpClient }, { token: AppConfigService }], target: i0.ɵɵFactoryTarget.Injectable });
EuFeatureFlagService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EuFeatureFlagService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EuFeatureFlagService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: AppConfigService }]; } });

const FEATURE_FLAG_MOCK = new InjectionToken('FeatureFlagsMock');
class MockFeatureFlagService extends FeatureFlagService {
    constructor(mock, appConfig) {
        super(appConfig);
        this.mock = mock;
    }
    readFeatureFlags() {
        return of(this.mock);
    }
}
MockFeatureFlagService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MockFeatureFlagService, deps: [{ token: FEATURE_FLAG_MOCK }, { token: AppConfigService }], target: i0.ɵɵFactoryTarget.Injectable });
MockFeatureFlagService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MockFeatureFlagService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MockFeatureFlagService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [FEATURE_FLAG_MOCK]
                    }] }, { type: AppConfigService }];
    } });

const APP_FEATURE_FLAGS = new InjectionToken('Eu App Features');
class EuFeatureFlagsModule {
    static forRoot(appFeatureFlags = {}) {
        return {
            ngModule: EuFeatureFlagsModule,
            providers: [
                {
                    provide: FeatureFlagService,
                    useClass: EuFeatureFlagService
                },
                {
                    provide: APP_FEATURE_FLAGS,
                    useValue: appFeatureFlags
                }
            ]
        };
    }
    static mockFeatureFlagService(mock, appFeatureFlags = {}) {
        return [
            {
                provide: FEATURE_FLAG_MOCK,
                useValue: mock
            },
            {
                provide: FeatureFlagService,
                useClass: MockFeatureFlagService
            },
            {
                provide: APP_FEATURE_FLAGS,
                useValue: appFeatureFlags
            },
        ];
    }
    static initFeatureFlags(featureFlagService, appFeatureFlags = {}) {
        const factory = () => featureFlagService.initialize(appFeatureFlags);
        return factory;
    }
}
EuFeatureFlagsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EuFeatureFlagsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
EuFeatureFlagsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: EuFeatureFlagsModule, imports: [HttpClientModule] });
EuFeatureFlagsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EuFeatureFlagsModule, providers: [
        {
            provide: APP_INITIALIZER,
            useFactory: EuFeatureFlagsModule.initFeatureFlags,
            deps: [FeatureFlagService, APP_FEATURE_FLAGS, AppConfigService], multi: true
        },
        {
            provide: FeatureFlagService,
            useClass: EuFeatureFlagService
        },
        {
            provide: APP_FEATURE_FLAGS, useValue: {}
        }
    ], imports: [HttpClientModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EuFeatureFlagsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        HttpClientModule,
                    ],
                    providers: [
                        {
                            provide: APP_INITIALIZER,
                            useFactory: EuFeatureFlagsModule.initFeatureFlags,
                            deps: [FeatureFlagService, APP_FEATURE_FLAGS, AppConfigService], multi: true
                        },
                        {
                            provide: FeatureFlagService,
                            useClass: EuFeatureFlagService
                        },
                        {
                            provide: APP_FEATURE_FLAGS, useValue: {}
                        }
                    ]
                }]
        }] });

class FeatureOnDirective {
    constructor(vcr, tpl, featureFlagsService) {
        this.vcr = vcr;
        this.tpl = tpl;
        this.featureFlagsService = featureFlagsService;
    }
    ngOnInit() {
        if (this.featureFlagsService.hasFlag(this.appFeatureFlag)) {
            this.vcr.createEmbeddedView(this.tpl);
        }
    }
}
FeatureOnDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FeatureOnDirective, deps: [{ token: i0.ViewContainerRef }, { token: i0.TemplateRef }, { token: FeatureFlagService }], target: i0.ɵɵFactoryTarget.Directive });
FeatureOnDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.12", type: FeatureOnDirective, selector: "[appFeatureFlag]", inputs: { appFeatureFlag: "appFeatureFlag" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FeatureOnDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[appFeatureFlag]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ViewContainerRef }, { type: i0.TemplateRef }, { type: FeatureFlagService }]; }, propDecorators: { appFeatureFlag: [{
                type: Input
            }] } });

class FeatureOnModule {
}
FeatureOnModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FeatureOnModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FeatureOnModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: FeatureOnModule, declarations: [FeatureOnDirective], exports: [FeatureOnDirective] });
FeatureOnModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FeatureOnModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FeatureOnModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [],
                    declarations: [FeatureOnDirective],
                    exports: [
                        FeatureOnDirective
                    ]
                }]
        }] });

class AppInsightsService {
    constructor(appSession, appConfig) {
        this.appSession = appSession;
        this.appConfig = appConfig;
        this.appSession.session$.subscribe(session => {
            // If you want to log UserId in metrices
            // Logic to get logged in User
            this.appInsights.setAuthenticatedUserContext(this.appConfig.config.opCo + '/' + session.username, session.accountNumber);
        });
    }
    get appInsights() {
        if (!this._appInsights) {
            this.init();
        }
        return this._appInsights;
    }
    init() {
        this._appInsights = new ApplicationInsights({
            config: {
                instrumentationKey: this.appConfig.config
                    .applicationInsightsInstrumentationKey
                /* ...Other Configuration Options... */
            }
        });
        this._appInsights.loadAppInsights();
    }
    logPageView(name, url, properties, duration) {
        const pageTelemetry = {
            name,
            uri: url,
            properties: Object.assign(Object.assign({}, properties), { duration })
        };
        this.appInsights.trackPageView(pageTelemetry);
    }
    logEvent(name, properties) {
        const event = {
            name
        };
        const customProperties = properties;
        this.appInsights.trackEvent(event, customProperties);
    }
    logException(exception, id) {
        const exceptionTelemetry = {
            id,
            exception
        };
        this.appInsights.trackException(exceptionTelemetry);
    }
    logTrace(message, properties, severityLevel) {
        const traceTelemetry = {
            message,
            severityLevel
        };
        this.appInsights.trackTrace(traceTelemetry, properties);
    }
}
AppInsightsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AppInsightsService, deps: [{ token: AppSessionService }, { token: AppConfigService }], target: i0.ɵɵFactoryTarget.Injectable });
AppInsightsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AppInsightsService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AppInsightsService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: AppSessionService }, { type: AppConfigService }]; } });

class AppInsightsModule {
}
AppInsightsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AppInsightsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AppInsightsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: AppInsightsModule, imports: [CommonModule] });
AppInsightsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AppInsightsModule, providers: [
        {
            provide: AppInsightsService,
            useClass: AppInsightsService,
            deps: [AppSessionService, AppConfigService]
        }
    ], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AppInsightsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    providers: [
                        {
                            provide: AppInsightsService,
                            useClass: AppInsightsService,
                            deps: [AppSessionService, AppConfigService]
                        }
                    ]
                }]
        }] });

/**
 * Provides deep copy of a javascript object.
 * */
class DeepCopyService {
    copy(value) {
        const type = typeof value;
        switch (type) {
            case 'object':
                // null and undefined
                if (value == null) {
                    return value;
                }
                let result;
                if (value instanceof Date) {
                    result = new Date();
                    result.setTime(value.getTime());
                    return result;
                }
                else if (value instanceof RegExp) {
                    result = this.newRegExp(value);
                    return result;
                }
                result = JSON.parse(JSON.stringify(value));
                this.fixTypes(value, result);
                return result;
            default:
                return value;
        }
    }
    fixPropertyValue(original, copy, key) {
        const originalValue = original[key];
        const originalType = typeof originalValue;
        switch (originalType) {
            case 'object':
                if (originalValue instanceof Date) {
                    const newValue = new Date();
                    newValue.setTime(originalValue.getTime());
                    copy[key] = newValue;
                }
                else if (originalValue instanceof RegExp) {
                    copy[key] = this.newRegExp(originalValue);
                }
                else if (originalValue == null) {
                    copy[key] = originalValue;
                }
                else {
                    this.fixTypes(originalValue, copy[key]);
                }
                break;
            case 'number':
                if (isNaN(originalValue)) {
                    copy[key] = NaN;
                }
                else if (originalValue === Infinity) {
                    copy[key] = Infinity;
                }
                break;
            default:
                break;
        }
    }
    fixTypes(original, copy) {
        if (original instanceof Array) {
            for (let index = 0; index < original.length; index++) {
                this.fixPropertyValue(original, copy, index);
            }
        }
        else {
            const keys = Object.getOwnPropertyNames(original);
            keys.forEach(key => {
                this.fixPropertyValue(original, copy, key);
            });
        }
    }
    newRegExp(value) {
        const regexpText = String(value);
        const slashIndex = regexpText.lastIndexOf('/');
        return new RegExp(regexpText.slice(1, slashIndex), regexpText.slice(slashIndex + 1));
    }
}
DeepCopyService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DeepCopyService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
DeepCopyService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DeepCopyService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DeepCopyService, decorators: [{
            type: Injectable
        }] });

class GlobalState {
    constructor(deepCopy) {
        this.stateHistory = [];
        this._state = null;
        this.globalStateDispatcher = new BehaviorSubject(null);
        this._deepCopy = deepCopy;
    }
    static getInstance() {
        if (!GlobalState._instance) {
            GlobalState._instance = new GlobalState(new DeepCopyService);
        }
        return GlobalState._instance;
    }
    getStoreState() {
        return this.copy(this._state);
    }
    setStoreState(state) {
        const clonedState = this.copy(state);
        this._state = clonedState;
        return clonedState;
    }
    copy(obj) {
        return this._deepCopy.copy(obj);
    }
}
GlobalState._instance = null;

class AppStoreSettings {
}
/** Default App Store Settings */
const defaultAppStoreSettings = {
    trackStateHistory: false,
    logStateChanges: false,
    stateSliceSelector: null
};
class AppStore {
    constructor(settings, logger) {
        this.logger = logger;
        this.stateDispatcher = new BehaviorSubject(null);
        this.settings = settings || defaultAppStoreSettings;
        this.state = GlobalState.getInstance();
        this.stateChanged$ = this.stateDispatcher.asObservable();
        this.globalStateChanged$ = this.state.globalStateDispatcher.asObservable();
    }
    get stateHistory() {
        return this.state.stateHistory;
    }
    /** return current state or its slice (based on settings) */
    getState() {
        const stateOrSlice = this.getStateOrSlice();
        return stateOrSlice;
    }
    /**
     * Update state
     * @param state new state object or
     * a "reducer": a function that takes current state and returns new state
     * @param action name of the action being performed
     * @param dispatchState optional flag to allow suppressing state dispatch.
     */
    setState(state, action, dispatchState = true) {
        // Needed for state history tracking below
        const beginState = this.state.getStoreState();
        const updatedState = typeof state === 'function'
            ? state(this.getState())
            : Object.assign({}, beginState, state);
        const currentState = this.updateState(updatedState);
        if (dispatchState) {
            this.dispatchState();
        }
        this.trackStateAction(action, beginState, currentState);
        if (this.settings.logStateChanges) {
            this.logger.info(`State change: action: ${action}. `, 'Begin State', beginState, 'Current State', currentState);
        }
        return currentState;
    }
    trackStateAction(action, beginState, endState) {
        if (this.settings.trackStateHistory) {
            beginState = beginState || this.getState();
            endState = endState || this.getState();
            this.state.stateHistory.push({ action, beginState, endState });
        }
    }
    resetStateHistory() {
        this.state.stateHistory = [];
    }
    updateState(state) {
        return this.state.setStoreState(state);
    }
    getStateOrSlice() {
        const storeState = this.state.getStoreState();
        if (this.settings.stateSliceSelector) {
            return this.settings.stateSliceSelector(storeState);
        }
        return storeState;
    }
    dispatchState() {
        // Get store state or slice of state
        const stateOrSlice = this.getStateOrSlice();
        this.stateDispatcher.next(Object.freeze(stateOrSlice));
        //  Get full store state
        const globalState = this.state.getStoreState();
        this.state.globalStateDispatcher.next(Object.freeze(globalState));
    }
}

var EuAppStoreActions;
(function (EuAppStoreActions) {
    EuAppStoreActions["InitializeState"] = "initialize_state";
    EuAppStoreActions["LoadAccountList"] = "load_account_list";
    EuAppStoreActions["LoadAccountListSuccess"] = "load_account_list_success";
    EuAppStoreActions["LoadAccountListFailure"] = "load_account_list_failure";
    EuAppStoreActions["LoadAccountDetails"] = "load_account_details";
    EuAppStoreActions["LoadAccountDetailsSuccess"] = "load_account_details_success";
    EuAppStoreActions["LoadAccountDetailsFailure"] = "load_account_details_failure";
    EuAppStoreActions["SelectAccount"] = "select_account";
    EuAppStoreActions["SelectAccountSuccess"] = "select_account_success";
    EuAppStoreActions["SelectAccountFailure"] = "select_account_failure";
})(EuAppStoreActions || (EuAppStoreActions = {}));
const EU_APP_STORE_SETTINGS = new InjectionToken('App Store Settings');
const EU_APP_STORE_INITIAL_STATE = new InjectionToken('App Store Initial State');
class EuAppStoreServiceDependencies {
    constructor(appConfig, appSession, logger, accountService, httpClient) {
        this.appConfig = appConfig;
        this.appSession = appSession;
        this.logger = logger;
        this.accountService = accountService;
        this.httpClient = httpClient;
    }
}
EuAppStoreServiceDependencies.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EuAppStoreServiceDependencies, deps: [{ token: AppConfigService }, { token: AppSessionService }, { token: AppLoggerService }, { token: EuAccountsService }, { token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
EuAppStoreServiceDependencies.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EuAppStoreServiceDependencies });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EuAppStoreServiceDependencies, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: AppConfigService }, { type: AppSessionService }, { type: AppLoggerService }, { type: EuAccountsService }, { type: i1.HttpClient }]; } });
class EuAppStoreService extends AppStore {
    constructor(dependencies, settings = EuAppStoreService.DEFAULT_SETTINGS, initialState = EuAppStoreService.DEFAULT_INITIAL_STATE) {
        super(settings, dependencies.logger);
        this.appConfig = dependencies.appConfig;
        this.appSession = dependencies.appSession;
        this.logger = dependencies.logger;
        this.accountService = dependencies.accountService;
        this.httpClient = dependencies.httpClient;
        const state = Object.assign({}, EuAppStoreService.DEFAULT_INITIAL_STATE, initialState);
        state.userDetails = this.getUserDetails();
        this.setState(state, EuAppStoreActions.InitializeState);
        this.accountList$ = concat(this.getAccountList().pipe(catchError(_ => of({}))), this.stateChanged$.pipe(map(_ => _.accountList)));
        this.accountDetails$ = concat(this.getAccountDetails().pipe(catchError(_ => of({}))), this.stateChanged$.pipe(map(_ => _.accountDetails)));
    }
    getUserDetails() {
        if (this.appSession.session && this.appSession.isUserAuthenticated()) {
            return {
                username: this.appSession.session.username,
                customerName: this.appSession.session.customerName,
                customerNumber: this.appSession.session.customerNumber,
                accountNumber: this.appSession.session.accountNumber,
                primaryEmail: this.appSession.session.primaryEmail
            };
        }
        else {
            return {};
        }
    }
    selectAccount(accountNumber) {
        this.trackStateAction(EuAppStoreActions.SelectAccount);
        return this.appSession.selectAccount(accountNumber)
            .pipe(mergeMap(res => {
            if (res === true) {
                return this.fetchAccountDetails(accountNumber);
            }
            else {
                this.trackStateAction(EuAppStoreActions.SelectAccountFailure);
                return throwError('Select Account failed');
            }
        }), tap(accountDetails => {
            if (accountDetails) {
                const update = (state) => {
                    state.accountDetails = accountDetails;
                    state.userDetails.accountNumber = accountNumber;
                    return state;
                };
                this.setState(update, EuAppStoreActions.SelectAccountSuccess);
            }
        }));
    }
    fetchAccountDetails(accountNumber) {
        this.trackStateAction(EuAppStoreActions.LoadAccountDetails);
        return this.accountService
            .getAccountDetails(accountNumber)
            .pipe(tap((a) => {
            this.setState({ accountDetails: a }, EuAppStoreActions.LoadAccountDetailsSuccess);
        }), publishLast(), // avoid multiple http calls for simultaneous subscribers
        refCount(), // auto unsubscribe from multiple subscribers after finishing
        catchError(err => {
            this.trackStateAction(EuAppStoreActions.LoadAccountDetailsFailure);
            return throwError(err);
        }));
    }
    getAccountDetails(reload = false) {
        const state = this.getState();
        const loadNewAccount = state.userDetails
            && state.userDetails.accountNumber
            && state.accountDetails == null;
        const loadDifferentAccount = state.userDetails
            && state.userDetails.accountNumber
            && state.accountDetails
            && state.userDetails.accountNumber !== state.accountDetails.data.accountNumber;
        if (reload || loadNewAccount || loadDifferentAccount) {
            return this.fetchAccountDetails(state.userDetails.accountNumber);
        }
        else {
            return of(state.accountDetails);
        }
    }
    fetchAccountList() {
        this.trackStateAction(EuAppStoreActions.LoadAccountList);
        return this.accountService
            .getAccounts()
            .pipe(tap(list => {
            const update = {
                accountList: list
            };
            this.setState(update, EuAppStoreActions.LoadAccountListSuccess);
        }), publishLast(), // avoid multiple http calls for simultaneous subscribers
        refCount(), // auto unsubscribe from multiple subscribers after finishing
        catchError(err => {
            this.trackStateAction(EuAppStoreActions.LoadAccountListFailure);
            return throwError(err);
        }));
    }
    getAccountList(reload) {
        const state = this.getState();
        if (reload || !state.accountList) {
            return this.fetchAccountList();
        }
        else {
            return of(state.accountList);
        }
    }
}
EuAppStoreService.DEFAULT_INITIAL_STATE = {
    userDetails: null,
    accountList: null,
    accountDetails: null
};
EuAppStoreService.DEFAULT_SETTINGS = {
    trackStateHistory: true,
    logStateChanges: true,
    stateSliceSelector: null
};
EuAppStoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EuAppStoreService, deps: [{ token: EuAppStoreServiceDependencies }, { token: EU_APP_STORE_SETTINGS, optional: true }, { token: EU_APP_STORE_INITIAL_STATE, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
EuAppStoreService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EuAppStoreService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EuAppStoreService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: EuAppStoreServiceDependencies }, { type: AppStoreSettings, decorators: [{
                        type: Inject,
                        args: [EU_APP_STORE_SETTINGS]
                    }, {
                        type: Optional
                    }] }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [EU_APP_STORE_INITIAL_STATE]
                    }, {
                        type: Optional
                    }] }];
    } });

class EuAppStoreModule {
}
EuAppStoreModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EuAppStoreModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
EuAppStoreModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: EuAppStoreModule, imports: [CommonModule] });
EuAppStoreModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EuAppStoreModule, providers: [
        EuAppStoreService,
        EuAppStoreServiceDependencies
    ], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EuAppStoreModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [
                        CommonModule
                    ],
                    providers: [
                        EuAppStoreService,
                        EuAppStoreServiceDependencies
                    ]
                }]
        }] });

class ChatbotLoaderComponent {
    constructor(appConfigService) {
        this.appConfigService = appConfigService;
        this.webSdkscriptId = 'web-sdk';
        this.digitalAssistancescriptId = 'digitalassistant';
    }
    ngOnInit() {
        this.appConfigService.config$
            .pipe(first(c => c.opCo != null))
            .subscribe({
            next: (config) => this.configureToApp(config)
        });
    }
    configureToApp(config) {
        this.secureURLBase = config.baseUrl;
        this.anonURLBase = config.anonBaseUrl;
        this.opCo = config.opCo.toLowerCase();
        this.odaUri = config.odaUri;
        this.chatBotAppId = config.chatBotAppId;
        this.euApiUrl = config.euApiUrl;
        this.webSdkSrc = this.secureURLBase + '/Lib/Exelon/exc-js/web-sdk.js';
        this.digitalAsstSrc = this.secureURLBase + '/Lib/DigitalAssistant/scripts/digitalassistant.js';
        this.script = () => {
            var _a, _b, _c, _d;
            //This is used to show we are on the anon site.
            //digitalassistant.js assumes assets are available at the secure path; this fix forces it to use the secure site.
            if (config.contentApiBaseUrl) {
                DIGITALASSISTANTPATH = `${config.baseUrl}/Lib/DigitalAssistant`;
                redirectUrl = `/pages/secureredirect.aspx?destination=${DIGITALASSISTANTPATH}`;
                chatLaunchImg = `${DIGITALASSISTANTPATH}/images/chat-launch-new.svg`;
                audioIconOffUrl = `${DIGITALASSISTANTPATH}/images/SpeechOff.svg`;
                audioIconOnUrl = `${DIGITALASSISTANTPATH}/images/speechOn.svg`;
                let opcoName = (['ace', 'dpl', 'pepco'].includes((_b = (_a = config.opCo) === null || _a === void 0 ? void 0 : _a.toLowerCase()) !== null && _b !== void 0 ? _b : '') ? 'phi' : (_c = config.opCo) === null || _c === void 0 ? void 0 : _c.toLowerCase());
                chatHoverImg = `${DIGITALASSISTANTPATH}/images/chat-hover-new${((_d = config.opCo) === null || _d === void 0 ? void 0 : _d.toLowerCase()) ? ('-' + opcoName) : ''}.svg`;
            }
            loadChatbot(this.secureURLBase, this.anonURLBase, this.opCo, this.odaUri, this.chatBotAppId, null, this.euApiUrl);
        };
    }
}
ChatbotLoaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ChatbotLoaderComponent, deps: [{ token: AppConfigService }], target: i0.ɵɵFactoryTarget.Component });
ChatbotLoaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: ChatbotLoaderComponent, selector: "app-chatbot-loader", ngImport: i0, template: "<!-- adds chatbots script programmatically from ngOnInit() -->\r\n<ng-container>\r\n  <app-script-loader [src]=\"webSdkSrc\" [scriptId]=\"webSdkscriptId\"></app-script-loader>\r\n  <app-script-loader [src]=\"digitalAsstSrc\" [onLoad]=\"script\" [scriptId]=\"digitalAssistancescriptId\"></app-script-loader>\r\n</ng-container>\r\n", styles: [""], dependencies: [{ kind: "component", type: ScriptLoaderComponent, selector: "app-script-loader", inputs: ["scriptId", "script", "src", "onLoad", "attributes"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ChatbotLoaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-chatbot-loader', template: "<!-- adds chatbots script programmatically from ngOnInit() -->\r\n<ng-container>\r\n  <app-script-loader [src]=\"webSdkSrc\" [scriptId]=\"webSdkscriptId\"></app-script-loader>\r\n  <app-script-loader [src]=\"digitalAsstSrc\" [onLoad]=\"script\" [scriptId]=\"digitalAssistancescriptId\"></app-script-loader>\r\n</ng-container>\r\n" }]
        }], ctorParameters: function () { return [{ type: AppConfigService }]; } });

class ChatbotLoaderModule {
}
ChatbotLoaderModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ChatbotLoaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ChatbotLoaderModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: ChatbotLoaderModule, declarations: [ChatbotLoaderComponent], imports: [CommonModule,
        ScriptLoaderModule], exports: [ChatbotLoaderComponent] });
ChatbotLoaderModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ChatbotLoaderModule, imports: [CommonModule,
        ScriptLoaderModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ChatbotLoaderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        ScriptLoaderModule
                    ],
                    declarations: [ChatbotLoaderComponent],
                    exports: [ChatbotLoaderComponent]
                }]
        }] });

/*
 * Public API Surface of eucoms-common-components
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ALERTS_ENDPOINT, API_BROKER_OPTIONS, APP_ALERTS_OPTIONS, APP_CONFIG_OPTIONS, APP_FEATURE_FLAGS, APP_LOGGER_OPTIONS, APP_SESSION_OPTIONS, AbstractValueAccessor, AdvancedAutoCompleteComponent, AdvancedAutoCompleteModule, AlertComponent, AlertModule, AlertPriority, AlertType, ApiBrokerService, AppAlertsService, AppBrandingService, AppConfigService, AppConfigServiceBase, AppInsightsModule, AppInsightsService, AppLoggerService, AppLoggerServiceOptions, AppSessionService, AppStore, AppStoreSettings, AutocompleteComponent, AutocompleteModule, BOTTOM_NAV_ENDPOINT, BootstrapBreakpointDetectorComponent, BootstrapBreakpointDetectorModule, BootstrapBreakpointService, BottomNavigationParser, ButtonComponent, ButtonModule, CORE_SERVICES_INIT_OPTIONS, CardComponent, CardModule, ChartModule, ChartsComponent, ChatbotLoaderComponent, ChatbotLoaderModule, CheckBoxComponent, CheckBoxModule, ClickOutsideDirective, CookieInterceptor, DEFAULT_CONFIG_ENDPOINT, DEFAULT_SESSION_ENDPOINT, DatepickerComponent, DatepickerModule, DeepCopyService, DropdownComponent, DropdownModule, Dummyable, EUAppConfig, EU_APP_CONFIG, EU_APP_STORE_INITIAL_STATE, EU_APP_STORE_SETTINGS, EuAccountsService, EuAppStoreActions, EuAppStoreModule, EuAppStoreService, EuAppStoreServiceDependencies, EuCoreServicesModule, EuDataServicesModule, EuFeatureFlagsModule, FeatureFlagService, FeatureOnDirective, FeatureOnModule, FixedValueComponent, FixedValueModule, FooterComponent, FooterMiddleComponent, FooterModule, FooterNavComponent, FooterService, FooterSimpleComponent, FooterSimpleModule, ForeSeeLoaderComponent, ForeSeeLoaderModule, GlobalState, GtmLoaderComponent, GtmLoaderModule, HeaderComponent, HeaderModule, HeaderService, HeaderSimpleComponent, HeaderSimpleModule, InputComponent, InputMaskingComponent, InputMaskingModule, InputModule, LOCAL_STORAGE, LoaderComponent, LoaderModule, LogLevel, LogWriterConfig, LogWriterType, MCSCachingService, MEDIA_DESKTOP, MEDIA_DEVICES_NOT_DESKTOP, MEDIA_DEVICES_NOT_MOBILE, MEDIA_MOBILE, MEDIA_TABLET, MOCK_API_BROKER_OPTIONS, MakeProvider, MegaMenuComponent, MegaMenuModule, MegaMenuService, MockApiBrokerService, MockAppConfigService, MockNotFoundBehavior, NAV_JSON_PARSER__entryProcessor, NO_ALERTS_ENDPOINT, NO_BOTTOM_NAV_ENDPOINT, NO_CONFIG_ENDPOINT, NO_SESSION_ENDPOINT, NO_UTILITY_NAV_ENDPOINT, NavEntryDelegate, NavEntryType, NavJSONParser, OpCo, PopupMenuComponent, PopupMenuModule, RadioButtonComponent, RadioButtonModule, SCREEN_SIZE, SESSION_STORAGE, SIGNIN_URL, SafeModule, SafePipe, ScriptLoaderComponent, ScriptLoaderModule, SingletonComponentState, ToggleButtonComponent, ToggleButtonModule, ToolTipComponent, ToolTipModule, TopNavigationParser, UTILITY_NAV_ENDPOINT, UUID, UrlSanitizer, WINDOW, WINDOW_LOCATION, cleanStringify, contactUsUrl, defaultBackgroundColor, defaultDisplayAlerts, defaultLoggingOptions, factoryDummyableConfig, factoryPopupmenuOption, forgotCredentialsUrl, forgotPasswordUrl, forgotUsernameUrl, getLocalStorage, getNullLogger, getSessionStorage, getWindow, getWindowLocation, iconSocialFacebook, iconSocialFlickr, iconSocialInstagram, iconSocialLinkedIn, iconSocialNextdoor, iconSocialPinterest, iconSocialTwitter, iconSocialYouTube, init_base_href, init_core_services, isDefined, mergeEUAppConfig, opcoStrings, registerVpv, registrationUrl, signInUrl, signOutUrl, socialMediaLinks, userRegistrationLandingUrl };
